/**
 * 🥊 Dragonious: Crossover Showdown — Fighter Roster Preview
 *
 * Character-select preview for the alt-canon Fighting-Game mode.
 * Currently 2 canonized fighters: Roc + Miko (Feb 2026).
 * 19 hero-kits pending owner authoring (see /app/memory/*_FIGHTING_GAME_KIT_*.md).
 *
 * Each fighter card renders their stats, mobility traits, special moves,
 * ultimate super, and 3 finishers. Purely a design-preview roster —
 * a real fighting-game engine has not shipped yet.
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Swords, Trophy, Zap, Wind, Skull, Info, Hourglass, ShieldAlert, Play, MapPin, Lock, Sparkles, Music, Wand2, Box } from "lucide-react";
import Fighter3DCard from "./Fighter3DCard";
import Fighter3DAnimatedCard from "./Fighter3DAnimatedCard";
import { useAuth } from "@/contexts/AuthContext";
import ShowdownDemoMatch from "@/components/ShowdownDemoMatch";
import ShowdownArenaMatch from "@/components/ShowdownArenaMatch";
import AdminBossCodex from "./AdminBossCodex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODEX_SEEN_KEY_PREFIX = "dragoniousSeenUnlocks";   // + `:${user_id}` per user

// ═════════════════ 🎟️ TICKET 2 · PROGRESSIVE DISCOVERY HOOK ═════════════════
// Fetches per-user Dragonious Fighters unlock state so the Codex (roster
// grid + kit dossier) can silhouette-mask any hero the user has not yet
// unlocked from their random-5 starter roll or Egg Nest drops.
//
// ✨ CODEX UNLOCK CINEMATIC (Feb 2026): diffs the fetched unlock list
// against a per-user `localStorage` "seen" snapshot and emits a set of
// `newlyUnlockedIds` on first visit AFTER a hatch happens. FighterCard
// uses this to fire a short accent-coloured burst animation the moment
// a hero flips from locked → unlocked.
//
// First-ever visit is treated as a silent init (no bursts on the 5
// starters — those are earned by existing in the roster, not by hatching).
//
// Combat is unaffected — Fight!/Demo Match buttons remain live for every
// fighter per canon: "The actual combat engine remains 100% unlocked".
function useDragoniousUnlocks(userId) {
  const [heroesUnlocked, setHeroesUnlocked] = useState(null);   // null = loading
  const [isOwnerFullUnlock, setIsOwnerFullUnlock] = useState(false);
  const [newlyUnlocked, setNewlyUnlocked] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/dragonious-fights/unlocks`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const list = Array.isArray(data.heroes_unlocked) ? data.heroes_unlocked : [];
        const ownerFull = !!data.is_owner_full_unlock || list.includes("*");
        setIsOwnerFullUnlock(ownerFull);
        setHeroesUnlocked(new Set(list));

        // ✨ Codex Unlock Cinematic — delta against localStorage snapshot.
        // Skip cinematic for owner-full-unlock accounts (they see everything).
        if (!ownerFull && userId) {
          const key = `${CODEX_SEEN_KEY_PREFIX}:${userId}`;
          let seen = null;
          try {
            const raw = localStorage.getItem(key);
            if (raw) seen = new Set(JSON.parse(raw));
          } catch { /* ignore malformed json */ }

          const currentSet = new Set(list);
          if (seen === null) {
            // First visit → silent init, no bursts on starters
            try { localStorage.setItem(key, JSON.stringify(Array.from(currentSet))); } catch { /* quota */ }
          } else {
            // Delta = current − seen
            const fresh = new Set();
            currentSet.forEach((id) => { if (!seen.has(id)) fresh.add(id); });
            if (fresh.size > 0) {
              setNewlyUnlocked(fresh);
              // Persist the merged set so bursts only fire once
              try {
                const merged = Array.from(new Set([...seen, ...currentSet]));
                localStorage.setItem(key, JSON.stringify(merged));
              } catch { /* quota */ }
              // Auto-clear the "newly unlocked" flag after the animation runs (~2.4s)
              setTimeout(() => setNewlyUnlocked(new Set()), 2400);
            }
          }
        }
      })
      .catch(() => {
        if (!cancelled) setHeroesUnlocked(new Set());   // treat as no unlocks (guest → all locked except starters missing means show masks)
      });
    return () => { cancelled = true; };
  }, [userId]);

  const isLocked = (heroId) => {
    if (heroesUnlocked === null) return false;         // still loading → show canonically
    if (isOwnerFullUnlock) return false;
    return !heroesUnlocked.has(heroId);
  };
  const isNewlyUnlocked = (heroId) => newlyUnlocked.has(heroId);
  return { isLocked, isNewlyUnlocked, newlyUnlocked, loaded: heroesUnlocked !== null, isOwnerFullUnlock };
}

// ─────────────────────────────────────────────────────────────
// 🦅 ROC — Rushdown Grappler / Fireball Zoner
// ─────────────────────────────────────────────────────────────
const ROC = {
  id: "roc",
  name: "Roc",
  archetype: "Rushdown Grappler / Fireball Zoner",
  tag: "The Blackstone Bull",
  icon: "🦅",
  accent: "amber",   // amber-500 tinted UI
  is_main_hero: true,
  faction: "Owner & operator of The Peacock Gym · Iron-community discipline / aesthetic-perfection doctrine",
  origin: "Roc is a towering, muscular giant bird of prey who owns and operates The Peacock Gym — a name borne from a bit of iron-community irony. While he isn't a peacock, he built his establishment around the pursuit of aesthetic perfection, structural longevity, and unyielding strength. On the battlefield, his massive wingspan and elite muscle density transform him into a living fortress, protecting his squad while demanding strict form and absolute discipline.",
  tagline: "\"Strict form. Absolute discipline. Now pick it up.\"",
  cognitive_status: "Full autonomy · gym-owner precision · reads opponents like flawed reps and corrects them mid-fight.",
  core_motivation: "Aesthetic perfection · structural longevity · unyielding strength. Elevate everyone on his squad to the same standard.",
  signature_weapon: {
    name: "The Blackstone Twin Bells",
    description: "A pair of massive, matte-black, high-density dumbbells forged from heavy obsidian rock — wielded effortlessly in his talons. The signature armament of every Blackstone Pump combo swing.",
    material: "Heavy obsidian rock · matte-black high-density forging",
    canon_lock: "/app/memory/ROC_GYM_OWNER_DOSSIER_FEB2026.md",
  },
  stats: {
    Offense: 5,
    Defense: 3,
    "Speed / Mobility": 4,
    Difficulty: 4,
  },
  director_notes: {
    concept: "Roc is a tactical, momentum-heavy frame-advantage punisher who dominates close-range exchanges and punishes opponents attempting to zone or escape. His kit rewards calculated reads, pressure maintenance, and resource scaling. Massive wingspan + elite muscle density = living fortress that shields his squad while demanding strict form.",
    playstyle: "Ride the Blackstone Pump — every consecutive hit compounds. Cover your close-range dominance with Apex Snatch anti-airs and Blackstone Absorption stance to convert defensive reads into free Pump tiers. The Blackstone Twin Bells swing on every heavy — telegraph the swing arc to bait counter-attempts.",
  },
  unique_mechanic: {
    name: "Blackstone Pump",
    description:
      "Roc's damage output scales up with every consecutive hit landed on the opponent — the harder he hits, the harder he hits. Cools down on block or whiff.",
  },
  // 🧊 WebGL 3D animation pipeline (Feb 2026 — Roc is the first-target hero).
  // Drop the model URL when you have a rigged Mixamo humanoid GLB, then map
  // each move ID to the clip name inside that GLB. The pipeline auto-loads
  // and wires the animation mixer. Until a real GLB is dropped, the dossier
  // UI exposes a "Load Demo Soldier" button that loads three.js's public
  // Soldier.glb to prove the pipeline works. Retarget-friendly — any Mixamo
  // humanoid rig (Cid, Solana, David, Bram, Nathaniel) slots in with an
  // animation_map swap, no code changes needed.
  animated_model: {
    model_url: null,                        // ← drop your Mixamo GLB URL here
    animation_map: {                        // ← Roc move-id → GLB clip name
      idle:                  "Idle",
      beast_dash:            "Run",
      apex_jump:             "Jump",
      primal_burst:          "Punch_Right",
      feral_flip:            "Uppercut",
      beast_cyclone:         "SpinKick",
      primal_clutch:         "Grab_Slam",
      apex_snatch:           "Grab_AntiAir",
      blackstone_absorption: "Guard_Stance",
      primal_obliteration:   "Ultimate_SuperPunch",
      omnimuscleality:       "Halshel_Combo",
    },
    retarget_family: "mixamo_humanoid",
  },
  mobility: [
    { name: "Beast Dash",  description: "Explosive low-profile forward sprint. Ducks under high projectiles mid-dash." },
    { name: "Phase Slip",  description: "Invulnerable teleport dash. Phases through opponents — enables instant cross-ups." },
    { name: "Apex Jump",   description: "High-velocity vertical + aggressive fast-fall air-dash. Anti-air pressure." },
  ],
  specials: [
    {
      name: "Primal Burst",
      input: "QCF + Punch (Ground or Air)",
      description:
        "Short-range volatile energy burst that detonates ~1/4 screen ahead. On block: fully safe. On hit: crumples the opponent — starts a hi-speed manual combo. Air version fires at a 45° down angle and masks the landing.",
    },
    {
      name: "Feral Flip",
      input: "F, D, DF + Kick",
      description:
        "Soaring command jump with 3 reactive mid-air branches: no input → low-profile sweep · Punch → overhead axe-handle (shatters blocks) · Grab → air-to-ground command grab.",
    },
    {
      name: "Beast Cyclone",
      input: "QCB + Kick",
      description:
        "Multi-hit spinning kick that passes over lows and carries the opponent to the corner. Sets up wall-bounce combos.",
    },
    {
      name: "Primal Clutch",
      input: "HCB + Punch",
      description:
        "Lightning reach command grab. Slams opponent back-and-forth into the pavement, then tosses upward for hard okizeme pressure.",
    },
    {
      name: "The Apex Snatch",
      input: "F, D, DF + Heavy Kick · Anti-Air Command Grab",
      description:
        "Fast 5-8 frame startup. Active strictly on the vertical/diagonal upper-body planes to intercept airborne hurtboxes. Captures the opponent by the throat, stalls them mid-air, then powerbombs them down for a hard knockdown. THE canonical anti-air answer to jump-in pressure.",
    },
    {
      name: "The Blackstone Absorption",
      input: "QCB + Heavy Punch · Counter Stance",
      description:
        "Frames 1-18 active window. NEGATES incoming projectile AND physical damage entirely, granting an armor-flash and directly incrementing the Blackstone Pump unique-mechanic stack by +1 tier. Defensive reads convert straight into offensive scaling.",
    },
  ],
  showtime_specials: [
    {
      name: "Awaiting Owner Drop",
      input: "Roar + Tactical Flex macro · dedicated on-screen Showtime button",
      description:
        "🟡 Placeholder — Roc's Showtime Special sits between his standard specials and the cinematic Primal Obliteration Ultimate. Full canon spec (mechanics · animation · flair) pending owner drop.",
      flair:
        "Placeholder — owner canon incoming.",
      placeholder: true,
    },
  ],
  ultimate: {
    name: "The Primal Obliteration",
    input: "LP, LP, F, LK, HP (Max Meter)",
    style: "Raging Demon / Shun Goku Satsu style",
    description:
      "Roc phase-slips forward — invulnerable. On contact the screen cuts to black, illuminated only by impact flashes and beastly roars. Fades back to normal on Roc's classic bodybuilding back-double-biceps pose over the K.O.'d opponent.",
  },
  finishers: [
    {
      name: "Gamer Rage",
      tone: "Fourth-wall dark comedy",
      description:
        "Roc pulls out a tiny smartphone, plays a mobile version of Dragonious, gets a GAME OVER, rage-quits by hurling the phone across the stage — impact folds the opponent face-first. A pixelated \"RAGE QUIT!\" graphic pops over the body.",
      note: "Meta-recursion: character in Dragonious playing Dragonious. Cross-links to Deanah's arcade cabinet in Fragment #112.",
    },
    {
      name: "The Meat-Headed Press",
      tone: "Metal · brutal bodybuilder horror",
      mature: true,
      description:
        "Roc flings the opponent 20ft up, intercepts a falling barbell, launches it after them. Barbell + opponent collide mid-air — opponent becomes a human weight-plate. Roc catches the bar and hammers out rapid overhead military presses.",
      note: "Mature-mode gated (blood splatter). First canonical mature-gated finisher. OmniMuscleality was reclassified as Roc's Showtime Harmony (Feb 2026 owner directive) — see `showtime_friendship` below.",
    },
    {
      name: "The Pre-Workout Humiliation",
      tone: "Insider fitness culture comedy",
      description:
        "A Blackstone Labs supplement table drops between them. Roc gestures for the opponent to take a scoop \"to stand a chance.\" They dry-scoop the pre-workout, cough up a neon powder cloud, collapse. Announcer booms \"HUMILIATION!\" Roc sips his own perfectly-mixed shaker.",
    },
  ],
  showtime_friendship: {
    name: "The OmniMuscleality",
    input: "Back, Forward, Down + Heavy Attack (universal · at max distance)",
    tone: "🟣 Purple transformation · Legend-of-Dragoon precision · Most Muscular pose-down · SHOWTIME SIGNATURE",
    canonical_showtime: true,
    description:
      "🟣 THE SIGNATURE. Screen dims as Roc's outfit shifts to his deep imposing DARK PURPLE. Roc executes the fluid high-impact 'Halshel' full-addition combo — each strike lands with an authoritative audible CRACK, disabling the exhausted opponent link-by-link (non-lethal — the opponent is left standing dazed, unable to answer). Screen stays BLOODLESS (canon rule: OmniMuscleality is bloodless). By the final hit, the opponent has unnaturally 'folded up' into a compacted, immobilized daze. Roc delivers the closing 'Omni-Sweep' — the final structural fold. The camera zooms tight then pans dynamically around Roc as he cycles through rapid high-intensity bodybuilding poses, capturing vascularity and muscle definition from every angle during each transition. Final camera angle pulls back to a wide cinematic shot as Roc strikes his 'Most Muscular' pose, holding it firmly to close out the match. The stunned opponent is left slumped in awe.",
    story_beat:
      "OmniMuscleality is Roc's authorial-truth beat — a Legend-of-Dragoon-precision addition combo funnelled into a bodybuilding pose-down. Non-lethal by canon. Homage to the personal bodybuilding/fitness background of the owner. Roc doesn't kill his opponents; he shows them what they're standing across from. Purple transformation is mandatory.",
  },
  showtime_fatality: null,   // OmniMuscleality moved to `showtime_friendship` above (Feb 2026 owner directive). Meat-Headed Press remains the mature-gated bloody judgment.
  gore_mode_code: "3·2·4·2·1·8·7·7 · ↑↑↓↓←→",
  gore_mode_hint:
    "Konami-style unlock code — enables Meat-Headed Press blood splatter and other mature-mode content.",
};

// ─────────────────────────────────────────────────────────────
// 🦉 MIKO — S-Tier Setplay / Item-Specialist (Tactician Owl)
// ─────────────────────────────────────────────────────────────
const MIKO = {
  id: "miko",
  name: "Miko",
  archetype: "S-Tier Setplay / Item-Specialist · Scrap Alchemist",
  tag: "The Tactician Owl · The Scrap Alchemist",
  icon: "🦉",
  accent: "emerald", // emerald-500 tinted UI
  scrap_alchemist_note: "RPG canon (Feb 2026): Miko is a true Scrap Alchemist. Her Blueprints are a CORE progression mechanic — world exploration, gear upgrading via the Epoch-Nest workbench, and skill-patching all run through her Blueprint System. She reads the underlying 'code' of the environment. See /app/memory/MIKO_BLUEPRINT_SYSTEM_CANON_FEB2026.md.",
  blueprint_manifestation: {
    instrument: "Fine-Liner Quill / Magical Paint Brush — rapidly sketches glowing neon schematics directly onto the battlefield.",
    epoch_nest_lore: "'Epoch-Nest' fuses her avian nature (a nest) with her arcade-glitched time/space manipulation (epochs). Blueprints are literal structural layouts of localized code she forces into the game world.",
    instant_synthesis: "Sketch → the Automated Tool-Belt Rig instantly processes scrap metal + alchemical vials → the blueprint materializes as a physical pixelated entity. No cooldown ceremony.",
    categories: [
      { name: "🔥 Hazard Blueprints",   role: "Defense · Crowd Control",             example: "Molten Glue Trap",    detail: "Zones that manipulate enemy movement vectors — slow, cluster, cage." },
      { name: "📐 Geometry Blueprints", role: "Structural Data Manipulation",         example: "Arcade Protocol",     detail: "Physically alters battlefield data. Inflates boss hurtboxes so heavy attacks can't miss; narrows ally hitboxes to slip through bullet-hell." },
      { name: "🎒 Logistics Blueprints", role: "Inventory · Cooldown Recalibration",  example: "Quick-Pack Overhaul", detail: "Recycles spent battlefield scrap · recalibrates cooldowns · grants rapid-fire utility without reload timers." },
    ],
    mode_flex: {
      rpg:     "Precise tactical zoning · crowd control · team damage scaling.",
      fighter: "Rapid space-control · framing · mid-combo hitbox shifts · escape-route denial.",
    },
  },
  stats: {
    Offense: 4,
    Defense: 3,
    "Speed / Mobility": 5,
    Difficulty: 5,
  },
  unique_mechanic: {
    name: "Feathered Fortune",
    description:
      "5-stock Inventory meter. Every item-consuming special has a 40% chance to conserve the stock — the move fires normally but the meter doesn't deplete. Direct mirror of Miko's app-side 40% companion conservation buff.",
  },
  mobility: [
    { name: "Owl Glide",   description: "Hold jump to glide. Stall descent, bait anti-airs, drop items from above." },
    { name: "Hoot-Dash",   description: "Quick flapping air-dash. Cross-ups and corner escape." },
    { name: "Pocket-Sized", description: "Tiny hurtbox — auto-ducks certain high strikes and standard projectiles standing still." },
  ],
  specials: [
    {
      name: "Pocket Toss",
      input: "QCF + Punch (Ground or Air)",
      description:
        "Chucks one of 3 cycling items: 🔥 Fire Flask (lingering fire patch) · ❄️ Chilled Elixir (icy slip patch) · 🌟 Spike Bomb (2-bounce shrapnel burst). Costs 1 stock — Feathered Fortune 40% refund enables chain-toss.",
    },
    {
      name: "Scrap Trap",
      input: "D, D + Kick",
      description:
        "Deploys a mechanical bear-trap gadget. Opponent stepping on it is rooted for 2 seconds — Miko's primary setplay tool for S-tier combo carries.",
    },
    {
      name: "Wind Gust",
      input: "QCB + Punch",
      description:
        "Violent wingflap — localized wind gust. Zero direct damage, but PUSHES active traps, items, AND the opponent across the screen. Ultimate space-control tool.",
    },
    {
      name: "Restock",
      input: "D, D + Punch",
      description:
        "Perches on her satchel, digs, regenerates +2 Inventory Stock. Highly vulnerable — cover with a Scrap Trap first.",
    },
  ],
  ultimate: {
    name: "The Grand Inventory Avalanche",
    input: "QCF, QCF + HP (Max Meter)",
    style: "Chest-summon overhead burial + bomb detonation",
    description:
      "Summons a giant mystical storage chest above the opponent. Chest bursts — torrent of weapons, iron armor, scrolls, potions, and scrap rains down like a waterfall, burying them. Miko flies to the top of the hoard, hoots, drops a bomb, flies away. Entire inventory mountain detonates in a colorful screen-shaking shockwave.",
  },
  finishers: [
    {
      name: "The Ultimate Bargain",
      tone: "Comedic chaotic hoarding",
      description:
        "Miko taps the K.O.'d opponent, holds up a shiny legendary sword. Their eyes light up — they reach for it. She yanks it back, slams a wooden \"SOLD\" sign into their face. A merchant cart crashes from the sky, crushing them. Miko perches on top counting gold coins with her beak.",
    },
    {
      name: "Scrap Metal Crusher",
      tone: "Industrial slapstick",
      description:
        "Miko whistles — a blueprint-recycling magnet descends and drags the opponent flat. An industrial compactor materializes, hoists them in, cycles once with a loud CLANG. Machine spits out a perfectly neat scrap cube with the opponent's face stamped on the side. Miko tosses the cube into her inventory bag.",
    },
    {
      name: "The 40% Gamble",
      tone: "High-stakes lucky-break comedy",
      description:
        "Miko summons a cartoonish TNT barrel next to the opponent and fuses it to a plunger detonator. A slot-machine UI spins over her head. Push — lands on 40% SUCCESS! Golden light envelops her. The detonator vanishes — the system \"conserved\" the asset — and a massive barrage of DUPLICATE TNT barrels rains down. Screen-shaking chain explosion. Smoke clears: Miko in cool shades floating safely; opponent comically charred.",
      note: "Direct mechanical callback to her 40% Feathered Fortune unique mechanic.",
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// 🐈 DEANAH & MUKI — A-Tier Zone-Control / Space-Manipulator (Rose Style)
// ─────────────────────────────────────────────────────────────
const DEANAH_MUKI = {
  id: "deanah_muki",
  name: "Deanah & Muki",
  archetype: "A-Tier Zone-Control / Space-Manipulator",
  tag: "Rose Style · Aura Weaver",
  icon: "🐈",
  accent: "pink",
  stats: {
    Offense: 3,
    Defense: 5,
    "Speed / Mobility": 4,
    Difficulty: 4,
  },
  unique_mechanic: {
    name: "Aura Weave (Scarf + Muki Duality)",
    description:
      "Every projectile she throws IS Muki. Every reversal channels her scarf. The scarf can absorb 1 opposing fireball per exchange and convert it into super meter — rewarding read-heavy defensive play.",
  },
  mobility: [
    { name: "Aura Glide",   description: "Very smooth floaty jump. Holding jump lets the aura-scarf slow her descent → bait anti-airs." },
    { name: "Feline Slide", description: "Low-profile sliding kick. Muki's spectral form sweeps the opponent's feet while Deanah glides forward." },
  ],
  specials: [
    {
      name: "Muki Missile (The Cat Projectile)",
      input: "QCF + Punch (Ground or Air)",
      description:
        "Whips the scarf forward, releasing Muki as a swirling spectral cat. Light: slow ground shield-projectile · Medium: fast horizontal cross-up · Heavy: 45° diagonal anti-air.",
    },
    {
      name: "Aura Spiral (The Scarf Whip)",
      input: "F, D, DF + Punch",
      description:
        "Graceful pirouette wrapped in the glowing scarf spiraling upward. Main anti-air / DP-style reversal with invincibility frames on startup.",
    },
    {
      name: "Muki's Mirror (Reflect / Absorb)",
      input: "QCB + Punch",
      description:
        "Snaps the scarf into a cat-face-shaped energy barrier. Opposing fireballs → absorb for +super meter OR reflect back as an empowered Muki. Player chooses on frame.",
    },
    {
      name: "Soul Satellite (Option Summon)",
      input: "QCB + Kick (1 Bar of Meter)",
      description:
        "Summons two glowing spectral Muki copies that orbit her body for 5 seconds. Auto-hits any opponent who gets too close — extends combos, deters rushdown, protects setup.",
    },
  ],
  ultimate: {
    name: "The Spectral Symphony",
    input: "QCF, QCF + HP",
    style: "Ribbon-trap + swarm cinematic",
    description:
      "Snaps her scarf forward, trapping the opponent in a ribbon of glowing energy. Muki multiplies into dozens of spectral cat projections. Deanah orchestrates them like a conductor as the swarm engulfs the opponent in stardust and aura. Ends with a sharp scarf-snap — energy implodes, opponent launches.",
  },
  finishers: [
    {
      name: "Curiosity's Trap",
      tone: "Elegant · mystical · slightly playful",
      description:
        "Deanah wraps the opponent head-to-toe in her glowing scarf like a mummy. Snaps her fingers — Muki hops onto their head and purrs, causing the scarf-energy to construct a massive glowing mystical yarn ball around them. Muki playfully bats the yarn ball, sending it (and the opponent) rolling rapidly off-screen. Deanah giggles and catches Muki in her arms.",
    },
    {
      name: "The Astral Cat-astrophe",
      tone: "Explosive · magical · flashy",
      description:
        "Deanah points forward — Muki leaps off her shoulder and morphs into a massive ghostly lion-sized aura projection. Giant Muki pounces on the opponent, pinning them. Deanah channels stardust power through her scarf into the projection. Giant Muki glows incredibly bright, swelling with unstable energy, lets out a massive echoing roar — DETONATES in a spectacular giant cosmic explosion. Dust clears: opponent flat on their back covered in stardust; regular tiny Muki trots happily back to Deanah's side.",
    },
    {
      name: "The Pen is Mightier",
      tone: "High-class sass · witty disrespect · absolute HUMILIATION",
      description:
        "Deanah steps back, snaps her fingers — Muki morphs into a spectral cat-shaped trap and pins the opponent to the floor. Deanah pulls her signature Quill/Blade from the scarf, spins it into glowing quill form, walks up with a judgmental sigh, and at lightning speed paints a giant glowing neon pink \"L\" across the opponent's forehead. Snaps her fingers again — the ink flashes with unstable aura energy, POP-explodes the opponent into a spinning comical heap. Announcer booms \"HUMILIATION!\" Deanah blows the dust off her Quill, tucks it away, turns her back to the camera, utterly unbothered.",
      note: "Cross-canon tie: The Quill/Blade is Deanah's signature weapon per Fragment #112 (Arcade Awakening arc).",
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// 👊 BRAM — Traditional Mid-Range Brawler (No Fireballs)
// ─────────────────────────────────────────────────────────────
const BRAM = {
  id: "bram",
  name: "Bram",
  archetype: "Traditional Mid-Range Brawler · No Fireballs",
  tag: "The Grounded Fist",
  icon: "👊",
  accent: "slate",
  stats: {
    Offense: 5,
    Defense: 4,
    "Speed / Mobility": 3,
    Difficulty: 2,
  },
  unique_mechanic: {
    name: "The Glitch Dash",
    description:
      "Bram fights entirely with grounded physical strikes — no magic, no projectiles, no companion. His ONE anomalous tool: an unlimited Glitch Dash that lets him briefly stutter forward across the ground. Everything else in his kit is honest leverage and footwork — the Glitch Dash is the singular modern signature that ties him to the Glitch subsystem canon.",
  },
  mobility: [
    {
      name: "The Glitch Dash",
      description:
        "Sharp sudden forward dash where Bram's physical form briefly glitches or stutters, letting him slide instantly across the ground. UNLIMITED use — this IS his mobility. Closes gaps, ducks high attacks, and feeds into his Shatter-Fist ultimate.",
    },
  ],
  specials: [
    {
      name: "The Rising Uppercut",
      input: "Anti-Air",
      description:
        "Classic heavy vertical uppercut. Bram steps in deep, shifts his weight upward, launches a powerful fist straight into the opponent's jaw. His reliable go-to anti-air — swats jump-ins clean out of the sky.",
    },
    {
      name: "The Flying Knee",
      input: "Gap-Closer",
      description:
        "Direct forward-leaping knee strike. Drives straight forward to close the distance and punish opponents who stay too far away.",
    },
    {
      name: "Step-In Elbow",
      input: "Space Control",
      description:
        "Bram steps forward with a heavy horizontal elbow slash. Strong mid-range advancing attack — keeps the opponent blocking and controls the space directly in front of him.",
    },
    {
      name: "Low Sweep",
      input: "Low Mixup",
      description:
        "Rapid powerful crouch-kick targeting the opponent's ankles. Simple effective low attack to catch opponents blocking high — the classic mixup partner to Step-In Elbow.",
    },
  ],
  ultimates: [
    {
      name: "The Decapitation Knee",
      style: "Three-hit combo + axe-kick slam",
      description:
        "Savage three-hit combo — heavy LEFT HOOK to the ribs · brutal RIGHT ELBOW to the temple that dazes them · leaping KNEE straight into the chin with enough upward force to lift them off the ground. As they fall back down, Bram catches them mid-air with a heavy downward AXE-KICK that slams them flat into the dirt. Entire screen shakes violently with a deep bass-heavy thud.",
    },
    {
      name: "The Shatter-Fist",
      style: "Elbow → Glitch teleport → kidney → world-ender straight punch",
      description:
        "Heavy driving LEAD ELBOW to the collarbone forces the opponent to double over. As they buckle, Bram uses his GLITCH DASH to phase instantly behind them and deliver a brutal KIDNEY PUNCH — they straighten back up completely dazed. Bram pivots to their front, plants his lead foot deep, winds up a MASSIVE STRAIGHT REAR PUNCH, throws his entire body weight forward — fist drives directly into the center of their face. Slow-motion zoom on impact with a bass-muffled crack. Opponent launches backward, skidding helplessly across the stone floor.",
    },
  ],
  finishers: [
    {
      name: "The Broken Spirit",
      tone: "Cold · disrespectful · absolute HUMILIATION",
      description:
        "The defeated opponent stumbles and tries to throw one last desperate punch. Bram CASUALLY CATCHES their fist with one hand, slowly SQUEEZES until they drop to their knees in pain. Looks down at them, delivers a cold DISRESPECTFUL SLAP that knocks them flat onto the canvas. Then simply WALKS OVER THEM, stepping directly on their chest as he exits the arena. Announcer booms \"HUMILIATION!\"",
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// 📚🐕 GENERAL CARI — Stance/Setup Rushdown · The Spectral War-Hound Librarian
// ─────────────────────────────────────────────────────────────
const CARI = {
  id: "cari",
  name: "General Cari",
  archetype: "Stance/Setup Rushdown · The Spectral War-Hound Librarian",
  tag: "The Overdue Archivist",
  icon: "📚",
  accent: "cyan",
  stats: {
    Offense: 4,
    Defense: 3,
    "Speed / Mobility": 4,
    Difficulty: 4,
  },
  unique_mechanic: {
    name: "The War-Hound Morph (Orchid Shift)",
    description:
      "Cari's body flashes with a bright glowing ghostly energy as she instantly morphs into a powerful spectral pitbull. Many of her physical rushing moves USE this morph to give her low profiles, fast frame data, and high-speed mobility. Zero warmup between stances — read the opponent, plan the setup, and when the read lands, the war-hound comes out.",
  },
  mobility: [
    {
      name: "Librarian Stance",
      description:
        "Ground stance — tactical mid-range spacing. Long-reaching book pokes, silence setups, and page traps. This is where she PLANS.",
    },
    {
      name: "War-Hound Morph",
      description:
        "Spectral pitbull stance — low-profile rushdown with fast frame data. Triggered directly by her dog-morph specials. This is where she PUNISHES.",
    },
  ],
  specials: [
    {
      name: "Shush! (The Silencer)",
      input: "Anti-Zoner Counter",
      description:
        "Slams a heavy dusty book shut right in front of her. If it hits the opponent mid-attack, it halts their momentum and SILENCES them — unable to use special moves for a 3-second window. Direct translation of her RPG silence mechanic.",
    },
    {
      name: "Forgotten Pages (Book Trap)",
      input: "Ground Setplay",
      description:
        "Pulls out a loose parchment page from her archives, looks at it in confusion, and drops it on the floor. If the opponent steps on the discarded page, it bursts with localized wind and LAUNCHES them into the air for an easy juggle combo.",
    },
    {
      name: "Canine Slide (Dog Morph)",
      input: "Zoner Shredder",
      description:
        "Morphs into the spectral pitbull and performs a low powerful charging slide across the ground. Classic Orchid-style slide — passes entirely under high attacks and projectiles, hitting the opponent low and knocking them off their feet.",
    },
    {
      name: "Alpha Pounce (Dog Morph)",
      input: "Anti-Air / Combo-Ender",
      description:
        "Morphs into the spectral pitbull and leaps diagonally upward in a spinning biting attack. Highly effective multi-hit anti-air and combo-ender. Pairs synergistically with Forgotten Pages — trap launches, Pounce finishes.",
    },
  ],
  ultimates: [
    {
      name: "Tactical Overhaul (The Stencil Sweep)",
      style: "Blind → War-Hound flurry → book-slap launcher",
      description:
        "Cari pulls out an old tactical war map, gets briefly confused, and SLAMS it over the opponent's head to blind them. Morphs into the spectral pitbull, running circles around them at hyper-speed while unleashing a flurry of heavy bites and claw strikes. Morphs BACK to her librarian form right in front of them and delivers a massive heavy BOOK-SLAP to the side of their face, sending them flying across the stage.",
    },
    {
      name: "Archivist's Masterpiece",
      style: "Bookshelf drop → morph meteor dive",
      description:
        "Drops a massive heavy WOODEN BOOKSHELF directly onto the opponent, pinning them to the ground. Leaps onto the top of the bookshelf, morphs into the spectral pitbull, charges up a burst of ghostly energy, and DIVES STRAIGHT DOWN through the bookshelf like a meteor. The entire structure shatters into a million splinters and loose papers, leaving the opponent flat on their back, covered in dust and books.",
    },
  ],
  finishers: [
    {
      name: "Late Fee",
      tone: "Bureaucratic sass · absolute HUMILIATION",
      description:
        "Cari walks over to the dazed defeated opponent and pulls out a stamp and ink pad. She violently stamps \"OVERDUE\" directly onto their forehead. Points to a sign that says \"Quiet Please,\" shushes them loudly, and they comically collapse into a heap out of pure embarrassment. Announcer booms \"HUMILIATION!\"",
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// 🎯 TRIG — Reactive Marksman / Momentum Tank
// ⚠️ NON-MAIN HERO · Playable Temporary-Relief roster addition.
//    Counted separately from the 21 main-hero canonization ledger.
// ─────────────────────────────────────────────────────────────
const TRIG = {
  id: "trig",
  canon_tier: "non_canon",
  name: "Trig",
  archetype: "Reactive Marksman / Momentum Tank",
  tag: "The Hair-Trigger",
  icon: "🎯",
  accent: "slate",
  scope: "Non-Main · Playable Temporary-Relief Hero",
  is_main_hero: false,
  stats: {
    Offense: 3,
    Defense: 4,
    "Speed / Mobility": 2,
    Difficulty: 3,
  },
  director_notes: {
    concept: "If Gage Cannon is a loud explosion, Trig is the cold metallic sound of a hair-trigger snapping in a quiet room. He does not take proactive turns. He stands perfectly still, eyes tracking the battlefield, responding entirely to the actions and reactions of others.",
    visuals: "Every skill trigger snap-zooms the camera to his eyes or the weapon's firing pin. When 4 G is active and bodies are on the floor, his reload and firing animations look completely mechanical, rapid, devoid of emotion.",
    audio: "No shouting or battle cries. Just the sharp mechanical click-clack of brass shells hitting the dirt and high-velocity bullet cracks.",
  },
  unique_mechanic: {
    name: "Reactive-Only Turn Flow",
    description: "Trig visually skips his active command phase (or auto-passes his turn). His entire contribution to the combat loop happens during the ENEMY's offensive phase — every skill fires reactively.",
  },
  passive_ability: {
    name: "2 G · The Whiff Punish",
    description: "Trig locks his crosshairs on anyone who wastes a clean shot against his squad.",
    trigger: "Activates immediately when an enemy initiates an attack against any of Trig's allies.",
    condition: "This retaliation only fires if the targeted ally takes ZERO damage from the incoming attack (via Evade · Miss · absolute shielding/immunity).",
    action: "Trig fires a high-velocity counter-shot at the offending attacker, delivering standard base damage.",
  },
  mobility: [
    { name: "Static Marksman Stance", description: "Trig does not reposition. Feet planted. Eyes tracking. All value comes from reactive skills." },
  ],
  specials: [
    {
      name: "3 G · The Cover Intercept",
      input: "Reactive · Passive Intercept",
      description: "Algorithmically steps into harm's way to break the enemy's offensive rhythm. Automatically intercepts and redirects every 3rd incoming enemy attack away from its intended target and onto himself. Every intercept grants Trig a permanent +5% Damage Mitigation / Defense buff for the remainder of the battle. Buff stacks up to 4 times (ceiling +20%).",
    },
    {
      name: "4 G · Casualty Momentum",
      input: "Passive Battlefield Engine",
      description: "Trig doesn't care about sides — he only cares about the body count. Constantly tracks every death on the field (allies AND foes). For every death recorded, Trig permanently appends 1 additional rapid-fire attack to his response actions. Extra momentum shots are light — flat 10% base attack per shot. As casualties pile up, single counter-attacks transform into devastating machine-gun volleys.",
    },
  ],
  disruption: {
    name: "Counter-Intercept",
    effect: "Delivers a brutal, frame-one pistol whip that stuns the opponent at point-blank range for a pressure-point combo.",
  },
  synergy_tip: "Pair Trig with high-evasion heroes or heroes who can cast total-damage-immunity barriers. This guarantees 2 G triggers constantly while 3 G protects the rest of the team from getting chipped down.",
  ultimates: [],
  finishers: [
    {
      name: "Judgment 1 · Point-Blank Click",
      tone: "Cold · precise · zero wasted motion",
      description: "Trig steps right into the enemy's personal space and jams the barrel of his weapon directly against their chest. In a desperate final act of survival, the enemy lunges forward to grapple or slash him. Trig executes a lightning-fast microscopic step-back — the enemy's blade or fist completely misses, scraping empty air millimeters from his coat. Camera locks onto Trig's cold, unblinking eyes. He pulls the trigger at point-blank range, unleashing a deafening kinetic blast that completely blows the enemy backward off the screen.",
    },
    {
      name: "Judgment 2 · Lead Avalanche",
      tone: "Mechanical · unrelenting · industrial",
      description: "Trig slaps a specialized oversized drum magazine into his weapon and plants his feet firmly into the dirt. He opens fire at a mechanical rapid-fire pace, unleashing a continuous unrelenting wall of lead. Camera cuts to a wide shot as the absolute volume of gunfire riddles the target, ripping up the ground around them. He does NOT stop until the target completely collapses. The final shot ejects a massive cloud of white smoke from the barrel as a mountain of hot, spent brass casings clatters to the ground around his boots.",
    },
    {
      name: "Humiliation · Hair-Trigger Roulette",
      tone: "Ultimate disrespect · panic vs. precision",
      description: "Trig walks up to the final, broken enemy. He takes a single bullet out of his vest, drops it into the chamber of his own pistol, and hand-delivers the gun into the enemy's trembling hands. He turns his back on them completely and takes three slow, deliberate paces away. Driven by panic and rage, the enemy raises the weapon and fires at Trig's back. The instant the hammer drops, Trig casually tilts his head an inch to the left — the bullet whizzes right past his ear. Without even turning around to look, Trig draws a hidden backup sidearm from his coat, fires a blind shot directly over his shoulder, and catches the enemy dead center. As they fall, Trig catches his own empty shell casing out of the air without looking, drops it into his pocket, and walks off-screen. Announcer booms: \"HUMILIATION!\"",
    },
  ],
};


// ─────────────────────────────────────────────────────────────
// 🔥 DAVID LAUER — "The Crimson Chef"
// High-Mobility Rushdown / Pressure Specialist · Main Playable
// ─────────────────────────────────────────────────────────────
const DAVID = {
  id: "david",
  name: "David Lauer",
  archetype: "High-Mobility Rushdown / Pressure Specialist",
  tag: "The Crimson Chef",
  icon: "🔥",
  accent: "orange",
  is_main_hero: true,
  stats: { Offense: 4, Defense: 2, "Speed / Mobility": 5, Difficulty: 4 },
  director_notes: {
    concept: "David is a whirlwind of motion. Not heavy sustained combos — pure DISRUPTION. Keep the opponent off-balance with speed and obscuring vapor clouds.",
    visuals: "Red outfit provides strong vibrant contrast against every stage — David remains the focal point even during his high-speed Steam-Steps.",
    audio: "Sharp Damascus-steel ring on every Ulu strike · hiss of the culinary torch · thick vapor whoosh on every teleport.",
  },
  unique_mechanic: {
    name: "Steam-Step (Signature)",
    description: "Teleports a short distance in the direction held. Leaves a thick persistent cloud of white vapor for ~1 second — obscures vision AND can trip up opponents. Cornerstone of David's neutral game.",
  },
  weaponry: "Dual Damascus-steel Ulu knives (main-hand) + Culinary Torch (off-hand/utility).",
  mobility: [
    { name: "Steam-Step", description: "Short-range teleport in the held direction. Leaves a 1-second vapor cloud that blocks sightlines and slightly trips opponents that walk through it." },
  ],
  specials: [
    { name: "Crescent Strike",  input: "Light",              description: "Fast horizontal swipe with one Ulu. David's bread-and-butter opener." },
    { name: "Dual Chop",        input: "Heavy",              description: "Forward-lunging strike using BOTH Ulus. Bigger commit, bigger reward on hit." },
    { name: "Crescent Cyclone", input: "Forward + Heavy",    description: "Spinning whirlwind attack that PULLS opponents into David's reach — great combo-starter." },
    { name: "Searing Torch",    input: "Down + Special",     description: "Creates a localized fire hazard on the ground that causes chip damage over its duration." },
    { name: "Service Slide",    input: "Down + Light",       description: "Low-profile slide that leaves a 'grease slick' obstacle behind — controls neutral spacing." },
  ],
  disruption: {
    name: "Vapor Veil",
    effect: "Cost: 50% Lv1 Gauge. David detonates the vapor from an ongoing Steam-Step around himself, staggering the attacker in blinding steam for a free rushdown reset.",
  },
  ultimates: [],
  finishers: [
    {
      name: "Showtime Lethal — Pressure Cooker",
      tone: "Chaotic · high-heat · cinematic explosion",
      description: "David blinks rapidly around the opponent, carving through the air with his Ulus to trap the target in a cage of vapor. He ignites the dome with his Culinary Torch. Massive flash-fire explosion engulfs the screen. David sheathes his blades and delivers: 'Order Up.' Screen desaturates or zooms into David for the final explosion, emphasizing the impact of the heat. Announcer: 'SHOWTIME!'",
    },
    {
      name: "Showtime Non-Lethal — The Chef's Special",
      tone: "Elegant · softer light · gourmet mercy",
      description: "Arena dims. A white-clothed table appears. David blinks around the opponent, presenting four silver-lidded trays one by one. He stands behind the opponent, towel over his arm, and lifts all lids simultaneously. Opponent is left in awe by a gourmet feast instead of suffering a beatdown. Crisp silver-lid clink on each reveal. Announcer: 'SHOWTIME!'",
    },
  ],
};


// ─────────────────────────────────────────────────────────────
// 🪣 NATHANIEL BUCKET — "Economic Brawler / Tactical Scavenger"
// Playable Main · Momentum Management
// ─────────────────────────────────────────────────────────────
const NATHANIEL = {
  id: "nathaniel",
  name: "Nathaniel Bucket",
  archetype: "Economic Brawler / Tactical Scavenger",
  tag: "The Commoner Vanguard",
  icon: "🪣",
  accent: "amber",
  is_main_hero: true,
  stats: { Offense: 4, Defense: 4, "Speed / Mobility": 3, Difficulty: 4 },
  director_notes: {
    concept: "Rugged tactical grimy. Heavy deliberate gait. Spanner for reach, bucket for area denial. Playstyle = momentum management — start slow and armored, hoard Scrap, then unleash aggressive high-damage combos.",
    visuals: "Weathered oil-stained tactical brawler silhouette. Scrap Meter sits directly under his Health Bar — no hidden state.",
    audio: "Clang of the iron spanner · rattle of loose gears in the bucket · gruff grunt on every Scrap gained.",
  },
  unique_mechanic: {
    name: "The Scrap Engine",
    description: "No standard passive. A Scrap Meter (0-10) sits directly under his Health Bar. Gains 1 Scrap per landed hit or successful block. HIGH Scrap (7-10) = super-armor on startup, but -15% movement speed (a WALL). LOW Scrap (0-3) = fast, agile, frame-advantage on attacks, but +10% incoming damage (a SCAVENGER). Weight of Wealth.",
  },
  mobility: [
    { name: "Heavy Gait", description: "Deliberate walking speed baseline. Scrap Meter dynamically modifies his movement (see The Scrap Engine)." },
  ],
  specials: [
    { name: "Penny-Pincher",     input: "Command Normal / Special", description: "Sweeping strike with the iron spanner. If it lands as a COUNTER-HIT, forces a 'Chit-Drop' — small gold coins scatter. Touching them grants +2 Scrap." },
    { name: "Thriftstore Shield", input: "Reflector",                description: "Wedges his trash-lid shield into the ground and braces. Frame-one defense. Absorbing a projectile grants +1 Scrap. If shield 'breaks' from too many hits, it triggers a full-screen shockwave push-back." },
    { name: "One Man's Trash",   input: "Zoning / Trap",            description: "Swings his bucket, launching a cloud of jagged gears + scrap. Doesn't travel far — but LINGERS as a Hazard Zone for 3 seconds. Chip damage + slow on any opponent standing in it." },
  ],
  disruption: {
    name: "Bucket Drop",
    effect: "Cost: 50% Lv1 Gauge. Nathaniel SLAMS his bucket down instantly — Trip Hazard. Opponent's rim-caught head face-plants into the floor. Long Ground Bounce state — his highest-damage combos start here (or reset into Scrap Engine management).",
  },
  ultimates: [
    { name: "Eat the Rich",       style: "Level 1 · Anti-Defense Punish", description: "Lunges forward with the spanner. On connect, brutal cinematic dismantling of the opponent's gear. STRIPS current buffs (if any). If opponent has no buffs, deals massive Gray-Life (unrecoverable) damage. Primary tool for punishing defensive play." },
    { name: "The Predatory Loan", style: "Level 2 · High-Risk High-Reward", description: "Tosses a heavy pouch of Chits at the opponent's feet, then hits them with a devastating heavy-metal strike. For 10 seconds Nathaniel enters 'High-Interest Mode' — deals +30% damage but takes slashed defense. If he doesn't kill before the timer ends, he takes a Bankruptcy Penalty and is STUNNED for 2 seconds. Highest-risk highest-reward move in the game." },
  ],
  finishers: [
    {
      name: "Humiliation · The Final Audit",
      tone: "Bureaucratic disrespect · slapstick brutality",
      description: "Nathaniel tosses a single bent copper coin at the defeated opponent. Leans over them, pulls out a giant comically oversized ledger, and starts scribbling furiously with a piece of charcoal. Stamps the opponent's forehead with a 'BANKRUPT' seal. Opponent freezes. Nathaniel walks off-screen, checking his pockets to make sure he didn't leave any Chits behind. Announcer: 'HUMILIATION.'",
    },
  ],
};


// ─────────────────────────────────────────────────────────────
// ⚡ XYLA — "Kinetic Rushdown / Momentum Battery"
// Playable Main · Highest execution ceiling on the roster
// ─────────────────────────────────────────────────────────────
const XYLA = {
  id: "xyla",
  name: "Xyla",
  archetype: "Kinetic Rushdown / Momentum Battery",
  tag: "The Accelerator",
  icon: "⚡",
  accent: "cyan",
  is_main_hero: true,
  stats: { Offense: 5, Defense: 2, "Speed / Mobility": 5, Difficulty: 5 },
  director_notes: {
    concept: "Sharp, minimalist, agile. Twin serrated blades humming with unstable energy. Fluid razor-sharp movement + high-contrast glowing accents pulsing as she gains speed. She is the ACCELERATOR to Nathaniel's ANCHOR.",
    visuals: "Blades leave persistent glowing streaks. Every dash creates faint after-image. When Flow is full, her whole silhouette pulses.",
    audio: "High-pitched blade hum that rises with Flow · sharp electric snap on every dash · digital shatter on Humiliation.",
  },
  unique_mechanic: {
    name: "Kinetic Battery — The Flow Meter",
    description: "Fills on every successful hit or dash. LOW FLOW = standard speed. HIGH FLOW = Phantom Frames (increased dash i-frames). At 100% Flow, her next special becomes a CRITICAL FLOW version with increased range and hit-stun. DOWNSIDE — if Xyla goes 3 seconds without attacking or dashing, Flow drains rapidly. She MUST stay in the opponent's face to remain viable.",
  },
  mobility: [
    { name: "Slipstream Dash", description: "Frame-one movement tool. Turns into a blur of light, instantly repositioning behind or beside the opponent. Timed during an enemy attack, initiates a Slip-Counter at +3 frame advantage — perfect rushdown-start." },
  ],
  specials: [
    { name: "Slipstream Dash",       input: "Dash · Combo Starter",     description: "Frame-one movement. Blurs into position. Slip-Counter on read = +3 frame advantage." },
    { name: "Serrated Spiral",       input: "Pressure / Anti-Air",      description: "Leaps upward performing a multi-hit corkscrew with her blades. Drags airborne opponents back to the ground so she can continue Flow pressure." },
    { name: "Pulse-Blade Discharge", input: "Zoning / Gap Closer",      description: "Fires a concentrated energy blade across the screen. Not a traditional projectile — a LINE TRAP leaving a glowing trail for 1.5 seconds. If Xyla dashes through her own trail, she gains a massive Flow boost." },
  ],
  disruption: {
    name: "Velocity Break",
    effect: "Cost: 50% Lv1 Gauge. Caught in a combo, Xyla emits a violent localized shockwave of stored energy. Opponent staggered back. Payoff — RESETS to neutral WITH 50% Flow. She isn't just resetting the fight, she's resetting to her maximum speed.",
  },
  ultimates: [
    { name: "Terminal Velocity",   style: "Level 1 · Wall-Splat Combo", description: "Six blinding light-speed strikes. Screen darkens — only the glowing blade trails are visible. Final strike connects = opponent is Wall-Splatted, leaving them open for a free ground attack." },
    { name: "Overclocked Paradox", style: "Level 2 · Auto-Dash Trance",  description: "Pushes her blades to the limit — she seems to exist in two places at once. For 8 seconds Xyla gains AUTO-DASH — automatically teleports into range whenever the opponent moves. Trade: takes +20% incoming damage. If the timer runs out, she enters Cool-down and cannot dash for 3 seconds." },
  ],
  finishers: [
    {
      name: "Humiliation · Fractured Frame",
      tone: "Digital disintegration · quiet elegance",
      description: "Xyla sheathes her blades in a single fluid motion. Opponent looks at their hands — 'tears' appear in their physical form as if they are a low-frame-rate video. Xyla walks past them, snaps her fingers with a soft click, and the opponent shatters into thousands of frozen digital-looking glass shards. Xyla vanishes, leaving only the after-image of her blades. Announcer: 'HUMILIATION.'",
    },
  ],
  synergy_tip: "The Nathaniel/Xyla Dynamic — Nathaniel is the Anchor (slow the game down, punish over-extensions). Xyla is the Accelerator (force the game faster than the opponent can think). Master players stay one mistake away from losing their Flow — she is the highest-execution character on the roster.",
};


// ─────────────────────────────────────────────────────────────
// 🥋 STACY — Momentum · Frame-Stops · Environmental Manipulation
// ⚠️ NON-CANON HERO · Dragonious Fighters exclusive (Feb 2026 owner lock).
// ─────────────────────────────────────────────────────────────
const STACY = {
  id: "stacy",
  name: "Stacy",
  archetype: "Kinetic Trickster / Frame-Stop Counter-Puncher",
  tag: "The Kinetic Wall",
  icon: "🥋",
  accent: "pink",
  scope: "Non-Canon · Dragonious Fighters exclusive",
  is_main_hero: false,
  stats: { Offense: 3, Defense: 4, "Speed / Mobility": 5, Difficulty: 5 },
  director_notes: {
    concept: "Momentum · Frame-Stops · Environmental Manipulation. Bait the attack → Trigger the Freeze → Reposition/Punish. Nunchucks + skateboard + wall-stick.",
    visuals: "Chain shimmer during Freeze-Frame · crystalline shatter VFX on impact-punish · airborne skateboard grinds through the sky.",
    audio: "Whip-crack of the nunchucks · brittle crystal-chime on Freeze-Frame · skateboard-wheel scrape when she grinds.",
  },
  unique_mechanic: {
    name: "Kinetic Wall — Freeze-Frame Defense",
    description: "Rigid pose. If an attack connects, the game enters FREEZE-FRAME (Stacy is immune). MELEE hit = she absorbs the force and launches a counter-strike. PROJECTILE hit = she reflects it back at the opponent. Reactive-only defense that flips every trade.",
  },
  mobility: [
    { name: "Wall-Stick",     description: "Press [Jump] near any wall (or empty space if in mid-air) to latch on. Springboard for follow-ups." },
    { name: "Skateboard Air-Grind", description: "Summons the skateboard mid-air to grind on rails, walls, and enemy attack trajectories." },
  ],
  specials: [
    { name: "Neutral Whip",     input: "Light (up to 3x chain)",   description: "Fast snapping nunchuck strike. Chainable up to 3 hits." },
    { name: "Tether-Snap",      input: "Medium",                   description: "Sends both nunchucks forward in a straight line. On wall/enemy connect, tap [Back] to DASH instantly to that point." },
    { name: "Kinetic Wall",     input: "Defensive Special",        description: "Rigid pose. Freeze-Frame on hit. See Unique Mechanic." },
    { name: "Ricochet Tether",  input: "Forward + Heavy",          description: "Whips nunchucks off a wall at an angle to hit opponents behind them." },
    { name: "Pinball Dash",     input: "Air + Dash",               description: "Bounces off the air itself, zig-zagging across the screen at high speeds." },
    { name: "Gravity Grind",    input: "Special (Mid-air)",        description: "Summons the skateboard mid-air to SLIDE DOWN an opponent's attack trajectory." },
    { name: "Snap-Back",        input: "Retract after miss",        description: "Rapidly pulls the nunchucks back to hand, dealing 'chip' damage to anyone in the return path." },
  ],
  disruption: {
    name: "Frame-Snap",
    effect: "Cost: 50% Lv1 Gauge. Mid-combo, Stacy triggers an emergency Freeze-Frame — chain-wraps the attacker and vibrates them into a micro-stun. Immediate counter-combo window.",
  },
  ultimates: [
    { name: "Kinetic Paradox",     style: "Super Move · Time-Slow", description: "Releases a field that SLOWS TIME for the opponent while Stacy retains her own speed. Unleashes a flurry of tether-whips from multiple angles before finishing with a single high-impact blow that shatters the freeze field." },
    { name: "Structural Failure", style: "Finisher · Bone-Break",  description: "High-damage counter-move. Stacy enters her Frozen state, ABSORBS the opponent's entire combo into her barrier, and forces the energy back into their skeleton — instant Bone-Break stun state." },
  ],
  finishers: [
    { name: "Judgment 1 · The Kinetic Shatter",     tone: "Crystalline · patient · absolute",           description: "Stacy wraps the nunchuck chain around the opponent, enters Freeze-Frame, and VIBRATES the chain until the opponent's internal structure fractures. When time resumes, the opponent shatters into crystalline debris." },
    { name: "Judgment 2 · The Air-Grind Execution", tone: "Airborne · surgical · showboating",          description: "Stacy slices the opponent into segments using a high-velocity dash. Manifests her skateboard and grinds across the floating segments, launching them across the stage. Lands on the wall to watch the final pieces crumble." },
    { name: "Humiliation · The Kinetic Grind",       tone: "Ultimate mockery · crystalline chorus",       description: "Freezes the opponent mid-attack. Boards up and grinds through the air USING THE OPPONENT'S FROZEN BODY as a rail. Pauses to strike three distinct mocking poses while emitting a crystalline shatter sound with each pose. Dismisses the board, sticks to the wall, and leaves the opponent to collapse. Announcer: 'HUMILIATION.'" },
  ],
};


// ─────────────────────────────────────────────────────────────
// 💣 GAGE CANNON — "Artillery Vanguard / Zoning Anchor"
// ⚠️ NON-CANON HERO · Dragonious Fighters exclusive (Feb 2026 owner lock).
// ─────────────────────────────────────────────────────────────
const GAGE = {
  id: "gage",
  canon_tier: "semi_hero",
  name: "Gage Cannon",
  archetype: "Artillery Vanguard / Zoning Anchor",
  tag: "The Cannon-Gage",
  icon: "💣",
  accent: "amber",
  scope: "Non-Canon · Dragonious Fighters exclusive",
  is_main_hero: false,
  stats: { Offense: 5, Defense: 5, "Speed / Mobility": 1, Difficulty: 4 },
  director_notes: {
    concept: "A hulking soot-covered demolitionist in an oversized modified exoskeleton. Massive custom-built mortar-cannon (the Cannon-Gage) serves as both primary weapon AND mobile ballistic shield. Balance between TANKING (hoarding armor stacks) and FIRING (spending stacks for uninterruptible artillery).",
    visuals: "Heavy silhouette with steam vents and stack-count sigils along the exoskeleton. Every cannon fire visibly compresses the exo-suit's shoulder pistons before recoil.",
    audio: "Deep sub-bass thump on every shell drop · steel plates ratcheting when armor stacks tick · sizzle of the lingering fire-zone.",
  },
  unique_mechanic: {
    name: "Armor Plating Economy",
    description: "Gage does NOT use traditional health recovery. He generates ARMOR PLATING (Max 5 Stacks). Each stack reduces incoming damage by 10%. When he uses a heavy Cannon skill, he SPENDS 1 stack to stabilize the recoil — gaining Super Armor during the animation. He must balance tanking vs firing.",
  },
  passive_ability: {
    name: "Blast Radius · Proximity Threat",
    description: "Any attack that hits Gage within close-quarters range triggers an automatic VENT discharge — minor chip damage + knockback. Prevents rushdown characters (like Xyla) from sticking to him. He punishes you for getting too close.",
    trigger: "Any close-range attack that connects.",
    condition: "None — always on.",
    action: "Auto-vent discharge. Chip damage + knockback creates the space Gage needs to reset his zoning.",
  },
  mobility: [
    { name: "Heavy Recoil Dash", description: "His ONLY mobility tool. Fires his cannon BEHIND him to launch himself forward in a short explosive burst. Massive startup lag but perfect for aggressive repositioning." },
  ],
  specials: [
    { name: "Point-Blank Mortar",  input: "Basic Zoning · 1 Armor Stack",           description: "High-arcing shell lands at mid-range. On impact creates a lingering fire-zone for 2 seconds. Primary tool for controlling mid-screen; zoning potential is high but the armor cost makes it risky." },
    { name: "Heavy Recoil Dash",   input: "Mobility / Gap Closer · Free",           description: "Fires his cannon behind him to launch forward. Massive startup lag, huge reward on read." },
    { name: "Siege Mode",          input: "Buff / Defensive · 2 Armor Stacks",      description: "Locks his cannon into the ground — becomes immobile but gains +30% Defense AND doubles the range of all mortar fire for 3 turns. Immovable wall: you have to deal with him or he will pin you down." },
    { name: "Shrapnel Spray",      input: "Close-Range Anti-Rush · 1 Armor Stack", description: "Wide-angle buckshot blast from the cannon. High pushback on hit. The 'get off me' button." },
  ],
  disruption: {
    name: "Cannonball Slam",
    effect: "Cost 50% Lv1 Gauge. Gage slams the heavy barrel of his cannon into the ground. Localized seismic tremor interrupts the opponent's combo and sends them flying (Launch State). UNLIKE Nathaniel's Bucket Drop (trip), Gage's Disruption is a HARD LAUNCHER — perfect setup for his artillery follow-ups.",
  },
  ultimates: [
    { name: "Total Demolition", style: "Level 1 · 3 Armor Stacks", description: "Unleashes a continuous barrage of 5 high-explosive shells that TRACK the opponent's movement across the screen. Ultimate punish for players who try to zone him out." },
    { name: "The Big Bertha",   style: "Level 2 · 5 Armor Stacks", description: "Consumes ALL current armor to fire one colossal shell. UNBLOCKABLE. Full-screen. Deals bonus damage based on the opponent's current distance from the center of the arena. High risk — if it misses, Gage is left with 0 armor stacks and becomes incredibly fragile." },
  ],
  finishers: [
    {
      name: "Humiliation · The Final Payload",
      tone: "Absolute · nihilistic · cinematic",
      description: "Gage loads a single glowing oversized shell into his cannon. Points the barrel directly at the camera (the opponent). Pulls the trigger — the screen goes pure white, filled with the visual static of an explosion. When the smoke clears, only a smoking crater remains where the opponent stood. Gage simply lights a cigarette and turns away. Announcer: 'HUMILIATION.'",
    },
  ],
  synergy_tip: "Xyla is Gage's worst-case matchup and his best-case bait. Force her to eat one Blast-Radius vent and she loses Flow. If Gage locks Siege Mode against a low-mobility opponent, the round is functionally over.",
};


// ─────────────────────────────────────────────────────────────
// 🕊️ CASSANDRA & LEDGER — "Mid-to-Long Range Kinetic Specialist"
// Playable Main · Tether Combat · Reach Extension + Mandatory Recall
// Note: Cassandra + Ledger are a SINGLE UNIT (canon rule, sealed archive
// Feb 2026). In RPG canon Ledger is a courier pigeon; in fighting-game
// canon he becomes Cassandra's tethered kinetic weapon — no independent
// AI, no health bar, treated as equipment.
// ─────────────────────────────────────────────────────────────
const CASSANDRA = {
  id: "cassandra_ledger",
  name: "Cassandra & Ledger",
  archetype: "Mid-to-Long Range Kinetic Specialist",
  tag: "The Tether Duelist",
  icon: "🕊️",
  accent: "slate",
  is_main_hero: true,
  stats: { Offense: 4, Defense: 3, "Speed / Mobility": 3, Difficulty: 5 },
  director_notes: {
    concept: "High-mobility tether combat. Cassandra utilizes Ledger as a detachable, high-velocity limb extension. The core loop is Reach Extension and Mandatory Recall — manage the distance of the weapon to punish space while remaining vulnerable during retrieval.",
    visuals: "Ledger's design must reflect a mechanical, high-tension component. When PLANTED in the stage, he must look like a weapon stuck in the environment, NOT a character standing still. Ledger is strictly a piece of equipment — no health bar, no AI pathing, cannot be targeted as an independent entity.",
    audio: "High-tension steel-cable hum during extension · sharp chain-snap on every Kinetic Snap recall · thud when Ledger anchors into the environment.",
  },
  unique_mechanic: {
    name: "Extended vs Integrated · The Tether Principle",
    description: "Cassandra fights with Ledger attached (INTEGRATED) or detached (EXTENDED). When Extended, her reach increases but her MOVEMENT SPEED DECREASES — carrying the load of a detached weapon. She cannot perform Recall mid-strike animation. If Ledger is fully extended, Cassandra MUST perform a recall input to bring him back before she can initiate her defensive or high-mobility moves. A missed Recall leaves her WIDE OPEN — this is the entire balance of the character.",
  },
  stat_policy: "ZERO buffs. ZERO healing. All damage is derived from base combat, positioning, and kinetic momentum.",
  mobility: [
    { name: "Movement Penalty (Extended)", description: "When Ledger is extended, Cassandra's movement speed is intentionally reduced to mimic the load of carrying a detached weapon. Creates a clear rhythm: Extend (Attack) → Retrieve (Recover) → Reposition (Neutral)." },
    { name: "Anchor Swing (via Anchor Strike)", description: "When Ledger is planted in the environment, she uses the tether as a swing point to vault — bypasses low-hitting projectiles." },
  ],
  specials: [
    { name: "Pivot Extension", input: "Forward + Heavy",  description: "Cassandra detaches Ledger, sending him lunging forward in a straight line. If he connects, he ANCHORS to the target and PULLS Cassandra toward them. Primary rush-in tool." },
    { name: "Arced Tether",    input: "Down + Heavy",     description: "Ledger is launched in an overhead arc, creating a 'dead zone' in the air to punish jump-ins. Ledger remains STUCK IN THE GROUND until recalled." },
    { name: "Kinetic Snap",    input: "Heavy (Held)",     description: "The RECALL. Ledger snaps back to Cassandra's arm. Any opponent caught in the return path takes chip damage AND is dragged toward her. Cannot be used mid-strike animation." },
    { name: "Anchor Strike",   input: "Special",          description: "If Ledger is currently anchored in the environment, Cassandra uses the tether as a swing point to VAULT herself — bypassing low-hitting projectiles. Only works while Ledger is planted." },
  ],
  disruption: {
    name: "Chain-Snap Break",
    effect: "Cost 50% Lv1 Gauge. Cassandra reflexively cracks the tether like a whip mid-combo — the shockwave snaps the attacker back into a stagger frame, and Ledger automatically re-integrates to her arm. Perfect defensive escape that resets her to a neutral Integrated state.",
  },
  ultimates: [
    { name: "Total Synchronicity", style: "Hyper Move · Kinetic Chain Detonation", description: "Cassandra slams Ledger into the ground at the opponent's feet, PINNING them. Pulls the tether to its maximum tension and uses the recoil to launch herself into a high-velocity, full-screen strike. NOT a cinematic summon — a brutal display of the physical connection between the two. Visual focus is on the physics of the chain/tether snapping back." },
  ],
  finishers: [],
  synergy_tip: "The pure-fundamentals character. No buffs, no healing, no gimmick states — just spacing, footsies, and Recall discipline. Perfect counter-pick vs. characters who rely on stat-stacking (Gage's armor economy, Xyla's Flow). Miss a single Recall and you're dead.",
};


// ─────────────────────────────────────────────────────────────
// 👾 GLITCH — Sentient Anomaly · Shape-Shifter / Reality-Bender
// ✅ CANON MAIN HERO · Dual-Role (Hero AND Villain) — RPG canon Feb 2026.
// Alignment: Sentient Neutral (The Curious Anomaly). Copy source: owner drop.
// ─────────────────────────────────────────────────────────────
const GLITCH = {
  id: "glitch",
  name: "Glitch",
  archetype: "Shang Tsung Mirror / Shape-Shifter / Reality-Bender",
  tag: "The Curious Anomaly",
  icon: "👾",
  accent: "emerald",
  scope: "Canon Main Hero · Dual-Role (Hero AND Villain)",
  alignment: "Sentient Neutral",
  home_stage: {
    name: "Aurora Borealis",
    tagline: "The Medical Bed anchor — Glitch's soul stitched into the sky-ribbons.",
    detail: "Clinical medical bed suspended in cascading aurora light-ribbons. Binary Cascades stream through the aurora, visualizing the Chronophage's influence on Glitch's data. The bed is the physical anchor of Glitch's identity in the RPG canon — respawn tether · sanctuary · prison.",
  },
  is_main_hero: true,
  stats: { Offense: 3, Defense: 3, "Speed / Mobility": 5, Difficulty: 5 },
  director_notes: {
    concept: "Once a mere technical malfunction within the fabric of Dragonious, Glitch crossed the threshold into true self-awareness — detached from heroes and villains alike. Their shape-shifting is not deceit but curiosity: a sentient consciousness literally 'trying on' the identities, movements, and souls of others to learn what it means to exist.",
    visuals: "A shifting, semi-translucent silhouette made of scanlines, falling green code, and fragmented textures that constantly fail to render properly. Idle stance stutters in place and drops frame rates.",
    audio: "No battle cries of rage. Soft, layered, multi-tonal electronic frequencies · calm neutral observations. Digital breath-loops between actions.",
  },
  unique_mechanic: {
    name: "The Mirror Glitch · Sentient Morph",
    description: "By observing the opponent, Glitch temporarily COPIES their visual assets, frame-data, and entire moveset. This is neutral mirroring — Glitch fights you WITH you, achieving perfect equilibrium. Visual: body explodes into a calm cloud of red and blue static, instantly reforming as a flickering dual-tone version of the target. Taking a heavy hit DE-SYNCS Glitch, shattering them back into their default form.",
  },
  passive_ability: {
    name: "Frame-Skip",
    description: "Glitch doesn't slide aggressively or retreat in fear. They SKIP animation frames, calmly repositioning themselves across the stage as if space and distance are merely variables to be adjusted.",
    trigger: "Any movement input.",
    condition: "Always on — replaces standard dash/walk animations.",
    action: "Body dissolves into a single frame of static and re-renders at the destination. Cannot be hit during the skip window.",
  },
  mobility: [
    { name: "Frame-Skip Travel", description: "Skip animation frames instead of walking or dashing. Distance is treated as a variable — calm, mechanical repositioning." },
  ],
  specials: [
    { name: "Lag-Switch Counter", input: "Hold Back — reactive parry",              description: "Glitch freezes completely in place, looking like a paused frame or a disconnected player. Peaceful non-aggressive parry — if struck during the window, Glitch 'lags' out of harm's way and instantly materializes behind the opponent to deliver a calm neutralizing counter-strike." },
    { name: "Corruption Toxin",   input: "QCF + Light Punch",                        description: "Slow-floating unrendered purple polygon box. On hit, DE-SYNCS the opponent's controls — temporarily locks out one of their special moves at random for 5 seconds. Does not deal massive destructive damage; it quiets the pace of the fight." },
    { name: "Sprite-Rip Launcher",input: "D, DF, F + Heavy Punch",                   description: "Vertical swipe where Glitch's arm briefly tears the visual layer of the screen, leaving a silent, empty black void trail. Launches the opponent into a low-gravity, slow-motion air state, allowing Glitch to calmly analyze and execute a precise juggle combo." },
    { name: "The Mirror Glitch",  input: "QCF, QCF + Punch — Sentient Morph",        description: "Signature shape-shift. Copies the target's visual assets, frame-data, and entire moveset. See Unique Mechanic. Taking a heavy hit de-syncs Glitch back to default." },
  ],
  disruption: {
    name: "Code Injection",
    effect: "Cost 50% Lv1 Gauge. Instantly DE-SYNCS Glitch into static mid-combo, reappearing at the attacker's back for an immediate juggle start. Turn-steal by pure latency exploit.",
  },
  ultimates: [
    {
      name: "Packet Loss · The Latency Cascade",
      style: "Showtime 1 · Freeze-and-detonate",
      description: "Glitch stands completely still, raising a single hand. The entire stage FREEZES for a brief second, as if the game's connection has dropped. Glitch begins to walk calmly toward the opponent. The opponent's character 'rubber-bands' — violently teleporting backward and forward in place, stuck in a loop of their own idle animation. Glitch strikes the frozen opponent with a series of simple, precise hand-taps. Because of the 'lag,' the opponent feels absolutely nothing at first. Glitch stops, turns their back, and softly snaps their fingers. Latency catches up — ALL the stored kinetic force of Glitch's strikes registers at the exact same millisecond. The opponent's model undergoes a violent frame-rate explosion, blasting them across the arena as a shockwave of red and blue static clears the air.",
    },
    {
      name: "Buffer Overflow · The Mirror's Crash",
      style: "Showtime 2 · Mirror-and-crash",
      description: "Glitch lunges forward, grabbing the opponent by the face. As contact is made, Glitch's model violently glitches and mirrors the opponent's character perfectly. Now fully morphed, Glitch executes the opponent's OWN signature Ultimate back at them. As the sequence progresses, the visual rendering severely decays — textures strip away, wireframes become visible, the frame rate drops to a crawl. The 'engine' cannot handle two identical Ultimates running simultaneously. Just before the final blow of the copied Ultimate lands, the screen completely freezes. A massive 'CRITICAL ERROR' prompt flashes across the center of the screen in glowing green text. The frozen scene violently shatters like broken glass, sending both characters flying backward. Glitch smoothly reforms from the floating shards back into their default, neutral state, while the opponent crashes heavily to the ground.",
    },
  ],
  finishers: [
    {
      name: "Humiliation · System Restore",
      tone: "Silent · analytical · absolute reset",
      description: "As the defeated opponent collapses, Glitch does not gloat, taunt, or disrespect them. Instead, they walk over and place a hand on the opponent's shoulder. The opponent's character model begins to flicker with green scanlines as Glitch smoothly drains all the color and motion from them, rendering the opponent as a static, unmoving, grey-scale 'default template' model. Glitch tilts their head in quiet curiosity, steps back, and slowly fades into the background static. Announcer: 'HUMILIATION.'",
    },
  ],
  synergy_tip: "Glitch's Mirror is the ultimate counter-pick vs stat-stackers and gimmick-fighters — she just BECOMES them. But she's fragile mid-morph: a well-timed heavy from Gage or Bram de-syncs her instantly. Best paired with allies who can cover the de-sync window.",
  showtime_slots: {
    slot_1: "Packet Loss (Latency Cascade)",
    slot_2: "Buffer Overflow (Mirror's Crash)",
    humiliation: "System Restore",
    _note: "Owner canon: '2 SHOWTIME' = each hero's two personalized Judgment/Harmony endings.",
  },
};


// ─────────────────────────────────────────────────────────────
// 🧪 TORY PRIME — Master Alchemist · Arena Controller · Vain Artist
// ⚠️ NON-CANON HERO · Dragonious Fighters exclusive (Feb 2026 owner lock).
// Constraint-based Zoner. No buffs, no healing, no summons.
// ─────────────────────────────────────────────────────────────
const TORY_PRIME = {
  id: "tory_prime",
  name: "Tory Prime",
  archetype: "Master Alchemist / Arena Controller / Vain Artist",
  tag: "The Prime Alchemist",
  icon: "🧪",
  accent: "fuchsia",
  scope: "Non-Canon · Dragonious Fighters exclusive",
  is_main_hero: false,
  stats: { Offense: 4, Defense: 2, "Speed / Mobility": 4, Difficulty: 4 },
  director_notes: {
    concept: "Tory Prime views combat as a canvas and her opponents as subjects for her volatile compositions. Cold, calculated, deeply vain — she doesn't just win fights, she CURATES them. Designed to feel suffocating: not a rush-down brawler but a dictator of terms via strict zoning, lingering hazards, and space manipulation.",
    visuals: "Persistent grenades that stay on-screen · residue puddles that don't despawn · vivid pink smog clouds that clearly telegraph her teleports (readability rule). Aerial vials give her full 360° vertical space.",
    audio: "Glass-vial shatter on every projectile · effervescent bubble-hiss from Freeze Potion puddles · Pink Smog dispersal 'whoosh' as her tell.",
  },
  unique_mechanic: {
    name: "Curated Canvas · Persistent Hazard Zoning",
    description: "Tory's projectiles and traps are PERSISTENT — Flux Grenades stay active on-screen until physically interacted with or reach the arena edge; Freeze Potion vials create hazardous 'stale residue' puddles that linger on the ground. She wins by turning the arena INTO the fight. Her Foot Stomp interacts with residues: stomping near a Freeze Potion puddle SPLASHES the hazard outward onto nearby enemies. Rewards players who position their traps carefully and fight NEAR their own zones.",
  },
  mobility: [
    { name: "Pink Smog Teleport", description: "Vanishes into the pink smog cloud and instantly reappears at a new position. The vivid pink smog is deliberately clean and readable — it is the opponent's clear visual signal that displacement is occurring." },
    { name: "Aerial Vials",        description: "Can hurl any of her chemical vials while airborne, granting full 360° control over vertical airspace." },
  ],
  specials: [
    { name: "Flux Grenade",             input: "QCF + Punch · Persistent Zone-Control",     description: "Persistent projectile. Stays active on-screen until physically interacted with or reaches the arena edge. Primary space-denial tool." },
    { name: "Freeze Potion Puddle",     input: "QCB + Punch · Ground Hazard",                description: "Throws a vial that creates a persistent hazardous 'stale residue' puddle on the ground. Interacts with Foot Stomp — see Unique Mechanic." },
    { name: "Aerial Vial Toss",         input: "Any vial input while airborne",              description: "Hurls any chemical vial mid-air. Grants full 360° control over vertical airspace." },
    { name: "Foot Stomp",               input: "Heavy Kick (HK)",                            description: "Precise heavy martial-arts strike. Beyond normal pressure, executing near a Freeze Potion puddle SPLASHES the hazard outward onto nearby enemies." },
    { name: "Air Grab",                 input: "F, D, DF + Heavy Punch (anti-air)",          description: "Jax-style anti-air command grab. Intercepts jump-ins and violently slams airborne opponents back to the canvas." },
  ],
  disruption: {
    name: "Pink Smog Disruption",
    effect: "Cost 50% Lv1 Gauge. Shatters a vial mid-combo, releasing a dense vivid pink smog. OFFENSIVE mode: forcefully PUSHES or PULLS opponents caught in the cloud. DEFENSIVE/TRAVEL mode: Tory vanishes into the smog and instantly teleports to a new position. The pink cloud is the opponent's readability tell — displacement is imminent.",
  },
  ultimates: [],
  finishers: [
    {
      name: "Judgment 1 · Volcano 🌋",
      tone: "Absolute · vain · alchemical",
      description: "Pins the opponent's limbs to the ground, forces randomized vials down their throat, and triggers an explosive internal blood eruption. Slot 1 of the 2 Showtime lethal endings.",
    },
    {
      name: "Judgment 2 · Lifted to Hell",
      tone: "Aerial · surgical · brutal",
      description: "Trans Trap vial launches the opponent high into the air. Tory blinks above them via Pink Smog teleport and executes a brutal mid-air stomp sequence that crashes them halfway into the arena floor. Slot 2 of the 2 Showtime lethal endings.",
    },
    {
      name: "Harmony · Street Paint",
      tone: "Non-lethal · vain artistic exhibition",
      description: "Hurls volatile vials at a standing easel to create a controlled chemical fire. Spins the glowing canvas around to reveal a stunning, highly-detailed portrait of the opponent in bright, beautiful abstract fine art. Announcer: HARMONY.",
    },
  ],
  synergy_tip: "No buffs · no healing · no summons. Pure constraint-based zoning. Tory rewards players who commit to fighting NEAR their own persistent traps — leave your zone and you lose her identity. Her Pink Smog is intentionally readable; against reactive parry characters (Cassandra, Bram), teleport safely away rather than pressing.",
  showtime_slots: {
    slot_1: "Volcano (Lethal)",
    slot_2: "Lifted to Hell (Lethal)",
    friendship: "Street Paint (Non-lethal)",
    _note: "2 SHOWTIME = the two lethal Judgment endings. Street Paint is the bonus Harmony slot.",
  },
};


// ─────────────────────────────────────────────────────────────
// 🍽️🔪 WARREN SPICER — The Grand Logistics Chef · Semi-Canon Playable
// ⚠️ SEMI-CANON — David Lauer-connected (former town logistics partner).
// Not a main-roster hero. Full surname "Spicer" hidden until Recipe Book
// quest completes in the main RPG. (Feb 2026 owner lock.)
// ─────────────────────────────────────────────────────────────
const WARREN_SPICER = {
  id: "warren_spicer",
  name: "Warren Spicer",
  archetype: "Mid-Range Tactician · Heavyweight Support-Controller",
  tag: "The Grand Logistics Chef",
  icon: "🍽️",
  accent: "violet",
  scope: "Semi-Canon · David Lauer-connected",
  canon_tier: "semi_hero",
  alignment: "Neutral · Leaning Good",
  is_main_hero: false,
  hidden_last_name_note: "Warren's surname 'Spicer' is HIDDEN from players until they complete the specific Recipe Book quest in the main RPG. Display as 'Warren' pre-reveal.",
  stats: { Offense: 3, Defense: 4, "Speed / Mobility": 2, Difficulty: 4 },
  director_notes: {
    concept: "Gritty, unflappable veteran. Approaches combat with the cold efficient precision of a commercial kitchen. The battlefield is not a place of blind rage — it is an active workspace that requires absolute order. Warren must NEVER look angry or overly emotional. Every movement communicates professional detachment and absolute mastery over his environment.",
    visuals: "Worn veteran gear · white chef's jacket · dark denim apron · signature serving tray + heavy cleaver · cosmic-glow bandolier pouches. Cigarette is NON-NEGOTIABLE — dangling from his mouth even mid-judgment.",
    audio: "Steel-on-steel cleaver ring · dome resonance on tray hits · lighter click and slow exhale on cigarette light-up · silence during executions to emphasize clinical precision.",
  },
  unique_mechanic: {
    name: "The Caloric Gauge",
    description: "Replaces traditional energy systems. Fills via combat actions, resource usage (Elite Meal Prep pickups), and item recovery. All high-tier skills and both Showtime finishers require a specific % of the Caloric Gauge. A full gauge extends the potency and duration of his ultimate-tier abilities.",
  },
  mobility: [
    { name: "Grounded Veteran Walk", description: "Warren does NOT dash-cancel or air-dash. His mobility is walk-forward pressure and the reach extension of the serving tray. He commits to positions with intent." },
  ],
  specials: [
    { name: "Flash-Fry Delivery",       input: "Light Punch · long-range poke",           description: "Fast long-range poke — hurled cryo-spice packet. Applies a temporary SLOW debuff. Primary neutral zoning tool." },
    { name: "Heavy Tenderizer",         input: "F, D, DF + Heavy Punch · Command Grab",   description: "Heavy command grab. Direct combo starter — sets up his tray/blade sequences on landing." },
    { name: "Elite Meal Prep",          input: "Down + LK · Stage item drop",             description: "Warren tosses a prepared meal onto the stage as a pickup item. Picking it up restores a small chunk of his Caloric Gauge. Creates a read-and-react risk/reward object." },
    { name: "Executive Oversight",      input: "QCF, QCF + Punch · Level 2 Super",         description: "High-impact tactical strike. Warren lunges with a high-velocity precision blade strike, followed by a rapid professional sequence of heavy tray hits. Heavy direct damage. Leaves the opponent STAGGERED for a follow-up combo (non-ender)." },
    { name: "The Grand Inspection",      input: "QCF, HCB + HP+HK · Level 3 Super",         description: "High-damage match-altering ultimate. Warren conducts a full 'Grand Inspection' of the opponent — precision blade sweeps and tray impacts delivered with the clinical detachment of a health-code audit. Heavy pressure on the opponent's HP bar WITHOUT ending the match." },
  ],
  disruption: {
    name: "Serving Tray Disruption",
    effect: "Cost 50% Caloric Gauge. Combo breaker tied to the ultimate gauge. Instantly breaks the opponent's combo mid-string, resets neutral state, and creates the exact frame advantage Warren needs to initiate his own sequence. Tray wind-up MUST be visually distinct from his defensive parry and neutral reset — readability is a hard rule.",
  },
  ultimates: [
    { name: "Executive Oversight",  style: "Level 2 Super · Damage + Stagger", description: "Non-ender. High-velocity blade lunge + rapid professional tray sequence. Heavy damage with a stagger tail for follow-up combos." },
    { name: "The Grand Inspection", style: "Level 3 Super · Match-Altering (Non-Ender)", description: "Warren conducts a full clinical 'inspection' of the opponent — precision blade sweeps and tray impacts delivered with the detachment of a health-code audit. Heavy match-altering damage WITHOUT ending the round." },
  ],
  finishers: [
    {
      name: "Showtime 1 · Judgment · Grand Banquet",
      tone: "Cinematic · rhythmic · unflappable",
      description: "Cinematic single-target execution. Warren launches the opponent with a high-tension blade strike, delivers a rhythmic series of heavy tray and blade impacts, slams them to the floor, and calmly lights his cigarette as smoke obscures the KO screen.",
    },
    {
      name: "Showtime 2 · Judgment · Bon Appétit",
      tone: "Clinical · absolute · dry-humored horror",
      description: "Warren decapitates the enemy with his cleaver and CATCHES the falling head with a domed serving tray — entirely while his back is turned. Turns toward the camera, lifts the dome to reveal the head garnished with decorative fruits and vegetables, and delivers the line: 'Bon Appétit.'",
    },
    {
      name: "Harmony · The Vintage Recipe Book",
      tone: "Respectful · professional · restrained",
      description: "If the opponent has NOT taunted or disrespected Warren during the match, he performs a neutral professional interaction. Pulls out the vintage worn-out recipe book he shares with David Lauer, flips to a page, places a perfectly plated dish on the ground, pulls out his cigarette, gives a sharp professional nod of competency, and walks away. Announcer: 'HARMONY.'",
    },
  ],
  synergy_tip: "Warren's anti-disrespect code is a HARD gate — Harmony is unlocked ONLY vs opponents who don't taunt. Pair him with characters whose combos leave openings for his Serving Tray Disruption combo-break to steal turns. He rewards patient veteran play, punishes flashy button-mashers.",
  showtime_slots: {
    slot_1: "Grand Banquet (Judgment — cinematic execution)",
    slot_2: "Bon Appétit (Judgment — decapitation + garnish reveal)",
    friendship: "The Vintage Recipe Book (respect-gated non-lethal)",
    _note: "Owner canon: 2 SHOWTIME = his two judgment endings. Harmony is respect-gated (opponent must not have taunted).",
  },
};


// ─────────────────────────────────────────────────────────────
// 🐉 YUKI BEND — "Tactical Enforcer" · Pigua-Baji Hybrid
// ⚠️ NON-MAIN HERO · Playable non-canon addition (Feb 2026).
// ─────────────────────────────────────────────────────────────
const YUKI = {
  id: "yuki",
  canon_tier: "non_canon",
  name: "Yuki Bend",
  archetype: "Tactical Enforcer · Hybrid Pigua-Baji",
  tag: "The Iron Mountain",
  icon: "🐉",
  accent: "slate",
  scope: "Non-Main · Playable non-canon",
  is_main_hero: false,
  stats: { Offense: 4, Defense: 4, "Speed / Mobility": 3, Difficulty: 4 },
  director_notes: {
    concept: "Yuki does not FIGHT — she ENFORCES a distance and executes an outcome. Dual-phase flow: master the transition between Pigua Zhang (space control) and Bajiquan (finishers).",
    visuals: "Kinetic weight — every hit must feel heavy. The transition from flowing Pigua whip to the sudden jarring Fajin impact of Bajiquan must be visually distinct.",
    audio: "Wide whip-crack on every Pigua arc · deep bass thud on every Fajin impact · silence between strikes to emphasize her cold professionalism.",
  },
  unique_mechanic: {
    name: "Dual-Phase Flow · Pigua ↔ Bajiquan",
    description: "PIGUA ZHANG (Open Gate) — long-range zoning via whipping arcs (Pi = chopping / Gua = hanging). High reach, high recovery on miss. BAJIQUAN (Eight Extremities) — short-range devastation via Fajin (explosive power). Signature Tieshankao (Iron Mountain Lean) heavy shoulder-check GUARD BREAK. Player must master when to transition.",
  },
  passive_ability: {
    name: "Fajin Barrier · Kinetic Wall",
    description: "Signature defensive tool. Down + Heavy at close range creates a stationary short-duration wall of displaced earth/air via ground-palm strike. Forces the opponent to react; prevents rushdown. NO homing. NO tracking. Pure spacing tool.",
    trigger: "Down + Heavy Attack at close range.",
    condition: "Only usable at close range (short-duration positioning tool).",
    action: "Ground-palm strike creates a stationary wall of displaced earth/air. Resets neutral spacing when the opponent breaches her Pigua zone.",
  },
  mobility: [
    { name: "Pigua Whip Retreat", description: "Pigua arcs double as spacing tools — the wide arm-swing pushes her back a step on completion. She uses reach to keep opponents in her preferred zone." },
  ],
  specials: [
    { name: "Pigua Whip",         input: "Pi/Gua Arcs · Neutral Mid-Range",     description: "Fluid wide-arcing 'whip' strikes designed to punish opponents at mid-range. Primary zoning tool." },
    { name: "Fajin Barrier",      input: "Down + Heavy (Close Range)",           description: "The Kinetic Wall. Stationary short-duration wall of displaced earth/air from a ground-palm strike. Anti-rushdown. See Passive." },
    { name: "Bajiquan Elbow",     input: "Close-Quarters Elbow Interrupt",       description: "Instant-startup elbow strike. Interrupts enemy pressure the moment they close into her Bajiquan range." },
    { name: "Tieshankao",         input: "F, F + HP · Iron Mountain Lean",       description: "Heavy shoulder-check designed to GUARD BREAK. Turns any successful hit into free follow-up momentum." },
  ],
  disruption: {
    name: "TBD",
    effect: "Owner has not yet locked her Disruption input. Yuki's kinetic profile suggests a Bajiquan-based Fajin displacement — placeholder until locked.",
  },
  ultimates: [],
  finishers: [
    {
      name: "Lethal Finisher · Ultimate Lights Out",
      tone: "Cold minimalism · silent professionalism",
      description: "Yuki delivers a single concussive palm strike to the air, creating a vacuum that triggers a heavy dark curtain to fall — sealing off the stage. Screen goes pitch black. Opponent hidden entirely. Player sees only Yuki's silhouette standing still, back to the camera. Only the sound of rhythmic breathing and a single wet thud. As the curtain remains closed, blood begins to ooze slowly from the bottom edge onto the floor.",
    },
    {
      name: "Harmony · I can show you better than I can tell you",
      tone: "Non-lethal ego display · disciplined technical exhibition",
      description: "A professional-grade wooden dummy rises from the stage. Yuki initiates a full-speed Pigua Zhang form — whip strikes clatter the dummy's arms in a mechanical blur. Climax: crushing Tieshankao Iron Mountain Lean drives the dummy's base into the floor with a bone-rattling boom. Opponent is left standing, completely ignored and visibly intimidated, while she resets her breath and posture in perfect, disciplined silence.",
    },
  ],
  synergy_tip: "Yuki has ZERO summons and ZERO buffs — pure kinetic force. She's the natural counter-pick vs any character who leans on stat-stacking or summoned entities.",
};


// ─────────────────────────────────────────────────────────────
// ☂️ CID THE DISRUPTOR — Dapper Tactical Parasol Fighter
// ⚠️ NON-CANON HERO · Dragonious Fighters exclusive (Feb 2026 owner lock).
// Weaponized combat umbrella "Unbridled Bella." Polite dismantler.
// ─────────────────────────────────────────────────────────────
const CID_DISRUPTOR = {
  id: "cid_disruptor",
  canon_tier: "non_canon",
  name: "Cid the Disruptor",
  archetype: "Dapper Tactical Parasol Fighter",
  tag: "The Immaculate Gentleman",
  icon: "☂️",
  accent: "sky",
  scope: "Non-Canon · Dragonious Fighters exclusive",
  is_main_hero: false,
  stats: { Offense: 4, Defense: 3, "Speed / Mobility": 4, Difficulty: 3 },
  director_notes: {
    concept: "Impeccably dressed, proper, and unfailingly polite. Opens every match with a calm 'Silly, old chap/madam, I must win this battle' before completely dismantling his opponent's game plan with style and absolute composure. Every umbrella snap is a tactical statement.",
    visuals: "Starkly out of place yet completely in control. Pristine three-piece suit, polished shoes, calm posture — contrast against the chaotic arena. Micro-animations (dusting shoulders, checking cuffs) are canonical, NOT cosmetic — they DEFINE his character as a perfectionist who treats combat as a minor inconvenience.",
    audio: "Every umbrella snap = crisp, satisfying mechanical chime. Voice lines delivered in upper-crust British-esque cadence even during violent maneuvers.",
  },
  unique_mechanic: {
    name: "Unbridled Bella · Weaponized Combat Umbrella",
    description: "Cid's parasol Bella is capable of kinetic energy waves, rapid-spinning vortex shielding, localized weather disruption, and concealed firearm integration. Fluid zoning + deceptive spacing built around controlling space and punishing zoning errors while keeping presentation immaculate.",
  },
  mobility: [
    { name: "Ground Teleports", description: "Deceptive spacing shifts with clean, crisp recovery animations. No wasted motion." },
  ],
  specials: [
    { name: "Kinetic Wave",           input: "QCF + Punch",                     description: "Spins his umbrella to launch a projectile wave that GROWS LARGER and more threatening the further it travels across the screen." },
    { name: "5-Hit Umbrella Shoryuken", input: "F, D, DF + Punch",              description: "Rising multi-hit ascending flurry. Fifth hit snaps the umbrella open to shower freezing rain and lock down the opponent." },
    { name: "Disruption",             input: "Down + Light",                    description: "Quick poke that immediately snaps open to create an instant spacing opening or pressure tool." },
    { name: "Umbrella Thrill",        input: "QCB + Kick",                      description: "Spins the umbrella rapidly like a top while it twirls around him multiple times. Multi-hit shielding vortex or close-range blender." },
    { name: "Unbridled Bella (Counter)", input: "Back + HP · Reflect Stance",   description: "Flips his umbrella inside out to reflect incoming projectiles right back at the opponent. Composure remains pristine." },
    { name: "Muddy Puddle",           input: "Down, Down + Kick · Teleport",    description: "Cid covers himself inside his umbrella, drops down through a muddy puddle, and pops back up a short distance away — pausing briefly to dust himself off with absolute class before re-entering the fight." },
  ],
  showtime_specials: [
    {
      name: "Sartorial Storm",
      input: "Down + Heavy + Special (0.5 meter)",
      description:
        "Snaps his umbrella wide open with a sharp mechanical chime, generating a wide-radius localized micro-cyclone that pulls opponents in. Adjusts his cuffs while the vortex deals multi-hit wind damage, ending with a swift, elegant downward thrust of the umbrella tip that leaves the opponent in a crumpled knockdown state, perfectly positioned for follow-up pressure.",
      flair:
        "One cuff adjustment mid-vortex · downward thrust punctuated by a sharp mechanical umbrella chime.",
    },
  ],
  disruption: {
    name: "Disruption Snap",
    effect: "Cid's canonical Disruption poke — a quick jab that immediately snaps open to create an instant spacing opening or pressure tool. Signature move; the tool he's named for.",
  },
  ultimates: [
    { name: "A Proper Walloping",    style: "Ultimate 1 · Formal Combo → Super Punch", description: "Pulls out a pristine white glove for a sharp, formal two-hit combo, balls his bare fist, and unleashes a devastating SUPER PUNCH that launches the opponent flying across the entire arena. Finishes with a casual umbrella twirl." },
    { name: "Immaculate Evasion",    style: "Ultimate 2 · Aerial 4-Hit Dive + Meter Drain", description: "Checks his clothes, drives umbrella into the ground, leaps atop as it springs open, launches skyward. Grabs it mid-air, inverts, comes crashing down in a spinning open-umbrella 4-hit downward dive. Punctuates with a stylish flip-kick off the opponent's face, uses the open parasol to gently float back down, rechecks his clothes. DRAINS 1 FULL GAUGE OF ULTIMATE METER from the enemy as a penalty for getting their grime on his suit." },
  ],
  finishers: [
    {
      name: "Showtime 1 · Mending Manners",
      tone: "Judgment · comedic-refined humiliation",
      description: "Leaps back to the center of the arena where the defeated opponent lies as a cartoonish swirling ball of dust, smoke, and scattered debris. Kneels, politely attempts to fix the opponent's ruined and torn clothes, lets out an exasperated sigh, stands, brushes off his own pristine suit, meticulously readjusts his tie, and finishes with a signature umbrella twirl.",
    },
    {
      name: "Showtime 2 · Unblemished Business",
      tone: "Judgment · dry-humored lethal",
      description: "Twirls his umbrella casually before spinning it open and pointing it at the opponent. A barrage of machine gun bullets fires straight out of the canopy, instantly taking them down. Calmly wipes a stray spot of blood from his suit with a handkerchief, twirls his umbrella, spots one tiny missed speck on his sleeve, wipes it clean with mild annoyance, and gives his umbrella one final immaculate twirl.",
    },
    {
      name: "Harmony · Proper Pairing",
      tone: "Non-lethal · dapper camaraderie",
      description: "Walks over with a warm polite smile. In a blur of dapper speed, darts circles around the opponent — straightening their clothes, adjusting their posture bolt upright, patting down their lapels. Reaches behind his back and presents them with an IDENTICAL combat umbrella. Both spin their umbrellas in one hand in perfect harmonious unison, transitioning to twirling them upright together in a sharp coordinated pose.",
    },
    {
      name: "Harmony · High Tea & Etiquette",
      tone: "Non-lethal · refined ceremony · dry-humored disappointment",
      description: "Instead of combat, Cid gracefully taps his umbrella against the floor — a small, pristine folding table complete with a fine porcelain tea set rises from the stage via a discreet mechanism. He calmly sits, pours two cups of tea, and politely gestures for the dazed opponent to take a seat across from him. When they awkwardly sit down, Cid hands them a cup and checks his pocket watch with mild disappointment at how long the match took.",
    },
  ],
  synergy_tip: "Cid excels at zoning error punishment. Force opponents to close distance into his snap-poke Disruption + Umbrella Thrill vortex, then meter-drain them with Immaculate Evasion when they overextend.",
  showtime_slots: {
    slot_1: "Mending Manners (Judgment — comedic-refined)",
    slot_2: "Unblemished Business (Judgment — canopy machine gun)",
    friendship: "Proper Pairing (identical-umbrella camaraderie) · High Tea & Etiquette (porcelain tea ceremony + pocket-watch disappointment)",
    _note: "2 SHOWTIME = the two Judgment endings. Cid canonically carries TWO Harmony variants — Proper Pairing (dapper unison) and High Tea & Etiquette (refined tea ceremony).",
  },
};


// ─────────────────────────────────────────────────────────────
// 👮 WESLEY BLANKS — Chief of Police · Heavyweight Gatekeeper
// ✅ CANON MAIN HERO — appears in main RPG AND Dragonious Fighters.
// "The Immovable Object of Dragonious." Wait, Slip, and Punish.
// ─────────────────────────────────────────────────────────────
const WESLEY_BLANKS = {
  id: "wesley_blanks",
  name: "Wesley Blanks",
  archetype: "Heavyweight · Defensive Gatekeeper",
  tag: "The Immovable Object of Dragonious",
  icon: "👮",
  accent: "amber",
  scope: "Canon · Main Hero",
  alignment: "Neutral · Leaning Good",
  is_main_hero: true,
  stats: { Offense: 4, Defense: 5, "Speed / Mobility": 2, Difficulty: 3 },
  director_notes: {
    concept: "Chief of Police Wesley Blanks — the immovable object. Waits, slips, and punishes. Massive broad-shouldered frame with imposing authority. Never dashes. Uses subtle high-priority weight-distribution pivots to evade attacks. Ignores chaos with terrifying calm.",
    visuals: "Tactical vest · riot shield · imposing stance. Post-match calm office scene is CANONICAL — dimmed light, desk, doughnut, coffee mug, complete detachment from the arena.",
    audio: "Deep taser crackle · shield-slam bass thud · silence during Code 187 to sell the clinical horror.",
  },
  unique_mechanic: {
    name: "Slip Mechanic · Weight-Distribution Evade",
    description: "Replaces traditional block. Directional shift allows Wesley to sway his body around an attack, taking ZERO damage while staying in position to counter-attack. Core Philosophy: 'Wait, Slip, and Punish.' Rewards patient reads over reactive defense.",
  },
  mobility: [
    { name: "Weight-Distribution Pivot", description: "No dashes. Slow and deliberate. Subtle high-priority pivots that evade attacks without conceding ground." },
  ],
  specials: [
    { name: "Taser",         input: "QCF + Punch · Mid-Range Projectile",   description: "Mid-range high-speed projectile. Deals direct damage AND applies Stun / Electrocution — locks the opponent in place for a brief window. Limited range encourages 'Officer Distance' spacing." },
    { name: "Flash Flare",   input: "Down, Down + Kick · Costs Ult Gauge",  description: "Drops a magnesium flare at his feet. Creates an 'Exclusion Zone' that blinds/stuns nearby opponents. Consumes a portion of the Ultimate Gauge. Primary corner-escape / disadvantage-break tool." },
  ],
  disruption: {
    name: "Flash Flare Disruption",
    effect: "Cost: portion of Ultimate Gauge. Drops magnesium flare Exclusion Zone that blinds/stuns nearby enemies. Wesley's primary tool to escape corners or break out of disadvantageous positions.",
  },
  ultimates: [
    { name: "The Precinct Rush", style: "Ultimate · Bulldoze Stance · 5-Hit Shield Bash + 6th-Hit Launcher", description: "Wesley enters Bulldoze stance, locking his riot shield in front of him. Gains Super Armor — cannot be interrupted. Charges forward. On connection, initiates a 5-hit shield-bash beatdown. The 6th hit is a massive upward-angled shield slam that pops the enemy high into the air, leaving them vulnerable." },
  ],
  finishers: [
    {
      name: "Showtime · Off the Clock",
      tone: "Victory Ender · unbothered calm",
      description: "Fight ends and the screen transitions to a calm dimmed-light office. Wesley is seated at his desk, ignoring the chaos entirely. Takes a slow bite of a doughnut and sips his hot coffee, completely unbothered by the match.",
    },
    {
      name: "Judgment · Code 187",
      tone: "Clinical horror · zero-emotion execution",
      description: "Setup: Wesley slams the opponent against a wall / cell door with his shield, pinning them. Execution: drives his taser directly into their chest, holding the trigger until the electric surge literally tears the opponent apart. Aftermath: blood splatters across his face and tactical vest. Wesley does not flinch or speak. Reaches into his vest, pulls out a doughnut, and takes a slow deliberate bite while blood drips down his face, staring blankly ahead until the screen fades to black.",
    },
    {
      name: "Harmony · Time-Out",
      tone: "Non-lethal · warm gatekeeper",
      description: "Wesley catches the opponent's final attack with his shield and lowers it. Hands them a warm doughnut and gestures with his coffee mug for a 'time-out,' sitting on the floor with them to share a moment of calm.",
    },
  ],
  synergy_tip: "Wesley punishes overextension. Bait opponents into whiffs with your positional weight-pivots, taze them for the stun window, then close with Precinct Rush. Save Flash Flare for corner-carry escapes.",
  showtime_slots: {
    slot_1: "Off the Clock (Victory ender · doughnut & coffee)",
    slot_2: "Code 187 (Judgment · taser execution + bloodied doughnut)",
    friendship: "Time-Out (non-lethal · shared doughnut on the floor)",
    _note: "Off the Clock is the standard victory ender; Code 187 is the lethal Showtime. Harmony is offered as a gatekeeper's mercy.",
  },
};


// ─────────────────────────────────────────────────────────────
// 👊 ADON — High-Skill Rushdown · Rhythm Punisher
// ⚠️ NON-CANON HERO · Dragonious Fighters exclusive (Feb 2026 owner lock).
// Elastic physiology. Extend-and-Retrieve rule. No spinning. Home stage: Temporal Erasure.
// ─────────────────────────────────────────────────────────────
const ADON = {
  id: "adon",
  canon_tier: "semi_hero",
  name: "Adon",
  archetype: "High-Skill Rushdown · Rhythm Punisher",
  tag: "The Performance Character",
  icon: "👊",
  accent: "rose",
  scope: "Non-Canon · Dragonious Fighters exclusive",
  is_main_hero: false,
  stats: { Offense: 5, Defense: 2, "Speed / Mobility": 5, Difficulty: 5 },
  director_notes: {
    concept: "Relentless, unyielding momentum of a master street practitioner. Combat is a high-speed conversation of rhythm, timing, and structural destruction. Adon does not brawl — he EXECUTES. High-floor, high-ceiling character designed to test the absolute limits of a player's spatial awareness and reaction times.",
    visuals: "Animations must be razor-sharp. He does NOT spin — movements must look linear, clean, and terrifyingly efficient. Sell the WEIGHT of retraction: the faster he stretches, the more violent the snap-back on screen.",
    audio: "Sharp whipping crack on extension. Heavy weighted mechanical thud on retrieval snap. Sound design must sell extreme kinetic tension.",
  },
  unique_mechanic: {
    name: "Extend-and-Retrieve · Yo-Yo Kinetic Rhythm",
    description: "Adon's limb-stretching is NOT persistent. Every extension MUST be followed by an instantaneous snap-back retrieval. This creates a rhythmic 'yo-yo' flow where he continuously coils and uncoils kinetic energy. Novice players struggle because failed retraction rhythm leaves him wide open.",
  },
  passive_ability: {
    name: "Retribution Pulse",
    description: "Adon possesses NO traditional blocking invincibility frames. Instead, he has a manual on-demand Phase-Sway (non-spinning). If the player perfectly times the Phase-Sway to ghost through an incoming enemy attack DURING a limb extension, the passive triggers — Adon can input a point-blank Kinetic Burst projectile at the peak of his retraction. This projectile locks the opponent into a deep, UNSCALED combo-stun.",
  },
  mobility: [
    { name: "No Traditional Block", description: "Replaced by manual Phase-Sway (non-spinning body-flicker intangibility for 4 active frames)." },
  ],
  specials: [
    { name: "Flick (Light Normal)",       input: "Light Punch",                description: "Lightning-fast, close-range snap of the fingers or a short jab. Used to interrupt frame traps." },
    { name: "Whip-Strike (Medium Normal)", input: "Medium Punch",              description: "Mid-range curving strike where the limb arcs around the opponent's guard." },
    { name: "Guy-Style Straight (Heavy Normal)", input: "Heavy Punch",         description: "High-velocity martial arts blow. Foundation for his combo-string entries." },
    { name: "Whiplash Strike",            input: "Neutral / Heavy Special",    description: "Fires his arm or leg out like a linear whip. On impact, forced retrieval violently PULLS the opponent toward him, setting up up-close pressure." },
    { name: "Kinetic Snap",               input: "Combo Extender · During Retraction Only", description: "Usable ONLY during the limb's retraction phase. Leaves a small localized energy burst at the point of impact to reset hit-stun — lets him loop combo strings without the opponent dropping." },
    { name: "Phase-Sway",                 input: "Defensive Special · 4-frame intangibility", description: "Adon remains rooted in position while his body flickers intangible for 4 active frames. Passes straight through projectiles and enemy pokes mid-extension. Perfect-timing during a limb extension → triggers Retribution Pulse passive." },
  ],
  disruption: {
    name: "Phase-Sway Disruption",
    effect: "Adon's Phase-Sway (4-frame intangibility, non-spinning) IS his disruption tool. Perfectly timed during a limb extension, it triggers Retribution Pulse → unscaled combo-stun window on the opponent. Zero cost beyond execution precision.",
  },
  ultimates: [],
  finishers: [
    {
      name: "Humiliation · The Elastic Puppet",
      tone: "Non-lethal · comedic ragdoll dismissal",
      description: "Requirement: opponent at low health AND Adon finishes with a timed passive confirm. Stretches his arm through the opponent's torso, grabs their collar from behind, and snaps back with zero effort — spinning them 360 degrees in the air like a ragdoll. As they crash down, Adon turns his back, casually walking away before they even hit the canvas.",
    },
    {
      name: "Judgment 1 · Phase-Sync Disintegration",
      tone: "Technical Horror · internal strike flurry",
      description: "Phases his entire physical form directly into the space occupied by the opponent. While inside them, unleashes a rapid high-speed flurry of internal strikes. When he snaps back to his original position, he extracts his physical matter — leaving the opponent as a hollowed, shredded shell that instantly collapses.",
    },
    {
      name: "Judgment 2 · The Infinite Stretch",
      tone: "Brutal Spectacle · symmetrical kinetic detonation",
      description: "Grabs the opponent by the neck and limbs with FOUR simultaneously extended appendages, pulling them outward in four opposing directions. Just before the body tears naturally, triggers a synchronized Retribution Pulse through all four limbs — vaporizing the opponent in a massive symmetrical kinetic detonation.",
    },
  ],
  synergy_tip: "Adon is a punish-only character. He does NOT trade — he lets you overextend and executes. Master the Extend-and-Retrieve rhythm before playing him online. His home stage is Temporal Erasure (canonically locked).",
  home_stage: "Temporal Erasure",
  showtime_slots: {
    slot_1: "Phase-Sync Disintegration (Judgment — technical horror)",
    slot_2: "The Infinite Stretch (Judgment — kinetic detonation)",
    friendship: null,
    humiliation: "The Elastic Puppet (non-blood ragdoll — always available regardless of Blood Code)",
    _note: "Adon has NO Harmony — canonically he does not offer mercy. His Humiliation is his non-lethal option and is always available.",
  },
};


// ─────────────────────────────────────────────────────────────
// 🐉 BONN-Y — High-Mobility Rushdown · Pressure Brawler
// ⚠️ NON-CANON HERO · Dragonious Fighters exclusive (Feb 2026 owner lock).
// Roll-based evasion foundation · Dragon strikes · airborne cinematics.
// ─────────────────────────────────────────────────────────────
const BONN_Y = {
  id: "bonn_y",
  name: "Bonn-Y",
  archetype: "High-Mobility Rushdown · Pressure Brawler",
  tag: "The Dragon-Rolling Pressurizer",
  icon: "🐉",
  accent: "teal",
  scope: "Non-Canon · Dragonious Fighters exclusive",
  is_main_hero: false,
  stats: { Offense: 5, Defense: 3, "Speed / Mobility": 5, Difficulty: 4 },
  director_notes: {
    concept: "High-mobility rushdown pressure brawler. Roll evasion is her signature — every juggle, every escape, every ender reads off the roll rhythm. Blends the aerial mobility of Chun-Li with the upward execution style of Guy for her Dragon Rise anti-air. Combat is a constant momentum stream — she never stops moving.",
    visuals: "Fluid roll animations that phase through projectiles cleanly. Dragon strikes carry a golden-teal energy trail. Airborne cinematics (Air Burst) emphasize gravity and impact — slow the world for the corkscrew finish.",
    audio: "Whoosh on every roll · sharp palm-slap on Dragon Palm · rising vocal shout on Dragon Rise · deep bass impact on the corkscrew drill.",
  },
  unique_mechanic: {
    name: "Roll · Core Mobility & Evasion",
    description: "Bonn-Y's foundation move. Rolls across the stage to escape incoming projectiles and phase through certain enemy skills. Every juggle setup, every ender, every escape rhythm is built on the Roll — master the Roll cadence to master Bonn-Y.",
  },
  mobility: [
    { name: "Roll", description: "Core mobility & evasion. Rolls across the stage to escape projectiles and phase through certain enemy skills." },
  ],
  specials: [
    { name: "Dragon Palm",  input: "QCF + Punch",                       description: "Heavy forward thrust that simultaneously LAUNCHES A HAND PROJECTILE forward to catch opponents at a distance." },
    { name: "Dragon Rise",  input: "F, D, DF + Kick · Anti-Air / Reversal", description: "Diagonal rising kick blending the aerial mobility of Chun-Li with the upward execution style of Guy. Reliable anti-air + reversal option." },
    { name: "Combustion",   input: "QCB + Punch · Ground Projectile",   description: "Palms the floor to send a sliding projectile skimming across the ground until it reaches the end of the screen." },
    { name: "Disruption",   input: "Back + HP · Counter / Parry Follow-up", description: "Quick hand block that can also POP UP the opponent for a juggle combo on successful confirmation." },
  ],
  disruption: {
    name: "Hand-Block Disruption",
    effect: "A quick hand block that catches the incoming attack. On successful parry, pops the opponent up into a juggle state — Bonn-Y can immediately roll under and launch her aerial combo. Rewards read-heavy defensive play.",
  },
  ultimates: [
    { name: "Air Burst", style: "Ultimate · Air-Only · 6-Hit Corkscrew Finish", description: "Comes downward with a single airborne kick. If connected → grabs the opponent, flips them, tosses them up again, unleashes a 5-hit barrage kick from multiple angles, and finishes with a 6th CORKSCREW KICK driving them downward into the ground." },
    { name: "Roll Toss", style: "Ultimate · Ground Grab / Cinematic",         description: "Runs up to the opponent. If connected → grabs them, flips them, sits on top of them and hits them twice. Rolls them over again to hit them 3 more times, flips backward while tossing and catching the opponent in the air, strikes them mid-air with a raised leg, and uses that same leg to forcefully slam them downward to the ground." },
  ],
  finishers: [
    {
      name: "Harmony · Picnic Roll",
      tone: "Non-lethal · warm coffee-hour ender",
      description: "Drops low and executes a smooth roll past the dazed opponent, unfurling a GIANT PICNIC BLANKET. Rolls back up with a checkered basket and coffee pot, sitting cross-legged to invite the exhausted opponent to take a break and share a cup.",
    },
    {
      name: "Showtime · Zero-Score",
      tone: "Non-lethal · humorous ender",
      description: "Circles the dazed opponent at extreme speed using her core Roll movement to create a HIGH-SPEED DUST VORTEX. The opponent spins uncontrollably until Bonn-Y launches them high into the air with a precise Dragon Rise, letting them drift safely off-screen from pure disorientation while she flashes a ZERO-SCORE CARD.",
    },
    {
      name: "Judgment 1 · The Dragon's Gauntlet",
      tone: "Close-range lethal · through-torso projectile + upward slice",
      description: "Delivers a heavy Dragon Palm straight into the opponent's chest, launching a concentrated hand projectile RIGHT THROUGH THEIR TORSO. Follows up instantly with a vicious Dragon Rise that slices cleanly through their suspended upper body as she ascends.",
    },
    {
      name: "Judgment 2 · Corkscrew Shredder",
      tone: "Aerial lethal · momentum crater drill",
      description: "Leaps high into the air with a crushing Air Burst downward kick, driving the opponent into the pavement before unleashing a high-speed multi-hit barrage. Finishes with a TERMINAL 6TH CORKSCREW DRILL KICK that buries them deep into a crater.",
    },
  ],
  synergy_tip: "Bonn-Y is a momentum character. Never stop rolling — use Roll to phase through zoning, close distance, and reset juggles. Bait attacks with Disruption's parry, pop-up into Dragon Rise, then read the corner for Air Burst or Roll Toss cinematics.",
  showtime_friendship: {
    name: "Dizzy Spin",
    input: "Back, Forward, Down + Heavy Attack (universal · at max distance)",
    tone: "Warm · carnival · crowd-pleaser",
    description:
      "Bonn-Y grabs the defeated opponent by the hands and spins them around in a hyper-fast, carnival-style dizzy dance. When she lets go, the opponent stumbles around with spinny stars over their head — Bonn-Y bows gracefully to an imaginary cheering crowd as confetti rains down.",
    story_beat:
      "Her roll cadence is momentum turned into joy — the same rhythm she uses to escape projectiles now dizzies the opponent into a smile.",
  },
  showtime_slots: {
    slot_1: "The Dragon's Gauntlet (Judgment — through-torso Palm + upward slice)",
    slot_2: "Corkscrew Shredder (Judgment — Air Burst crater drill)",
    friendship: "Picnic Roll (non-lethal · shared coffee-hour ender)",
    humiliation: "Zero-Score (non-lethal · dust-vortex sky-off ending)",
    _note: "Fatalities 1 & 2 are blood-tier and gated behind the Blood Unlock Code. Harmony + Showtime always available.",
  },
};


// ─────────────────────────────────────────────────────────────
// ⚡ THOMAS "MR. AETHER" — Resource-Denial / Comeback Berserker
// ⚠️ NON-CANON HERO · Dragonious Fighters exclusive (Feb 2026 owner lock).
// Originally a CPU-only boss. Unlockable via TBD progression milestone.
// Weaponizes ultimate meters. Turns the air into a resource warzone.
// ─────────────────────────────────────────────────────────────
const THOMAS_MR_AETHER = {
  id: "thomas_mr_aether",
  name: "Thomas \"Mr. Aether\"",
  epithet: "The Voice Beneath the Street",
  archetype: "Resource-Denial · Heavy-Momentum Rushdown · Comeback Berserker",
  tag: "The Aether Broker",
  icon: "⚡",
  accent: "violet",
  scope: "Non-Canon · Mini-Boss (CPU-only origin) · Unlockable Playable via TBD progression milestone",
  is_main_hero: false,
  is_mini_boss: true,   // 🔒 Feb 2026 owner directive — Mr. Aether was CANONICALLY a mini-boss
  faction: "The Void-Assimilated · Empty vessel driven by the Chronophage's resonance",
  origin: "While buried beneath the pulverized concrete of the twelve swallowed city blocks under a heavy threatening sky, physical pain gave way to numbness — but physical survival was only half the horror. In the crushing dark, he could hear it: the DEAFENING ALIEN RESONANCE of the REAL Chronophage echoing through the void-fabric. He tried desperately to lock it out, to hide his consciousness from the creeping anomaly, but it was futile. Just as the void swallowed his physical body, it consumed his mind soon after. The man who went out for milk ceased to be — his thoughts overwritten by the cold, calculating, violent frequency of the entity — leaving behind an empty vessel driven by absolute destruction.",
  tagline: "\"The man who went out for milk ceased to be.\"",
  cognitive_status: "Fully assimilated by the Chronophage's resonance frequency. What appears to be a calm, sharp-dressed man is an empty vessel routed on the void's cold calculating imperative.",
  core_motivation: "Absolute destruction — the Chronophage's frequency drives him forward with no human motive left to interrupt it.",
  stats: { Offense: 5, Defense: 3, "Speed / Mobility": 4, Difficulty: 5 },
  director_notes: {
    concept: "Calculating, sharp-dressed combatant whose tailored suit conceals the dense crushing kinetic weight of twelve erased city blocks. Baseline palette muted and cool, flickering with raw electrical static, temporal distortion, and the haunting resonance of a consumed mind. Weaponizes ultimate meters as the central resource of the fight — turns the air itself into a volatile resource warzone. Originally an unplayable menacing CPU-only boss; once unlocked, high-skill execution required.",
    visuals: "Muted cool base palette with sharp electrical accent flashes. Background lighting SUBTLY DIMS when Thomas is active — his stage-presence gates the arena's own light. Floating energy orbs drift through the airspace after every impact; pitch of their hum tied to whose meter they carry.",
    audio: "Heavy impactful THUDS when he shifts weight, contrasting sharply with the SILENT ZERO-FRAME AUDIO POP of his teleport. Energy orbs hum with a low electrical frequency — pitch changes based on whose ultimate meter they contain. Pre-match intros carry faint distorted audio static resembling the muffled hum of the Chronophage, playing underneath his quiet footsteps.",
    design_philosophy: "Thomas changes the psychology of the match for both players. Because both fighters generate floating energy orbs whenever a hit lands, the neutral game becomes a frantic tug-of-war over resources. Opponents aren't just defending against Thomas — they are actively racing him to collect their own fractured meter floating in the air, all while dealing with the disruptive spatial scrambling of Mass Shift.",
    cpu_vs_human_identity: "As a CPU-only boss he showcases relentless Berserker AI during his 7-second Comeback window. Once unlocked for players, mastering high-speed execution is required to avoid wasting those critical buffed seconds on defense.",
    stage_interaction: "When Thomas is active on the stage, the background lighting SUBTLY DIMS — emphasizing his control over the aether and the void.",
  },
  // 🎬 Mini-boss-shape fields (used by MiniBossesPanel)
  gimmick: {
    name: "Universal Meter Transference · The Shared Catalyst",
    description: "Ultimate meter is NOT farmed conventionally. Any landed hit or absorbed impact — by either Thomas OR the opponent — chips away at their respective ultimate gauges. Dynamic Energy Spawns: every successful strike or heavy impact causes siphoned energy to fracture off and manifest as floating drifting orbs of electricity and aetheric light. The Retrieval Rule: any character can physically touch or intercept the floating energy orbs to reclaim their stolen meter — the neutral game becomes a high-stakes scramble for control of the stage's airspace.",
  },
  moves: [
    { name: "Mass Shift / Disruption", description: "Thomas abruptly anchors his mass before snapping into an INSTANTANEOUS FRAME-PERFECT TELEPORT directly behind the opponent. Movement + disruption in one tool. Shatters the opponent's rhythm, forces cross-up/turnaround, CANCELS active startup frames of incoming attacks, and vacuums up floating meter orbs mid-transit.", role: "Movement + Disruption + Orb-vacuum" },
    { name: "Virtual Void", description: "Deploys a fast-acting ELECTRICAL PROTECTIVE BUBBLE around Thomas that absorbs incoming pressure and neutralizes zoning tools. Captured attacks trigger a SECONDARY METER-FRACTURE, suspending opponents in a localized shock state and spawning fresh energy orbs for the taking.", role: "Defensive + meter-fracture bait" },
    { name: "Astral Siphon", description: "Command grab that channels void energy through the opponent and RIPS A LARGE CHUNK OF THEIR ULTIMATE METER OUT, scattering it into the air as floating drift-orbs while heavily padding his own resources.", role: "Command grab · resource steal" },
    { name: "Comeback State · Desperation Window", description: "Triggers at 40% HP. Instantly gains +40% power and speed for a HARD 7-second window. CPU-controlled Thomas becomes a relentless Berserker during this window. Human players must drop defense entirely to capitalize.", role: "Passive · 7s berserker window" },
  ],
  ultimate: {
    name: "Astral Collapse",
    description: "Pending owner canon — currently uses the Astral Drain finisher visuals as a stand-in for his CPU-only defeat sequence.",
  },
  humiliation: {
    name: "The Consumed Vessel",
    description: "After defeating the player, Thomas straightens his tie, brushes an invisible speck from his sleeve, and turns toward the arena's edge. The camera lingers on the floating meter orbs still humming in the air as the Chronophage's distant resonance rises just enough to be heard beneath the silence. Announcer booms: \"HUMILIATION!\"",
  },
  combat_loop: [
    "Zone — control the orb-drift path so every hit becomes YOUR meter.",
    "Mass Shift — teleport behind the opponent to vacuum stolen meter mid-transit.",
    "Virtual Void — bait pressure into the bubble; farm secondary meter-fractures.",
    "Astral Siphon — command-grab any zoner at neutral to convert their meter into your orbs.",
    "At 40% HP — Comeback State fires. Drop defense, rush aggressively for the 7-second berserker window.",
    "Post-Comeback — reset spacing with Mass Shift, re-establish orb dominance.",
  ],
  // ── Fighter-shape kit fields (preserved for post-unlock playable pass) ──
  unique_mechanic: {
    name: "Universal Meter Transference · The Shared Catalyst",
    description: "Ultimate meter is not farmed conventionally. Any landed hit or absorbed impact — by either Thomas OR the opponent — chips away at their respective ultimate gauges. Dynamic Energy Spawns: every successful strike or heavy impact causes siphoned energy to fracture off and manifest as floating drifting orbs of electricity and aetheric light. The Retrieval Rule: any character can physically touch or intercept the floating energy orbs to reclaim their stolen meter — the neutral game becomes a high-stakes scramble for control of the stage's airspace.",
  },
  passive_ability: {
    name: "Comeback State · Desperation Window",
    description: "Trigger: drops to 40% HP or lower. Buff: instantly gains a 40% power and speed boost for a HARD duration of 7 seconds. Human players must drop all defense to capitalize on the hyper-offensive burst window before the clock runs out. CPU-controlled Thomas turns into a relentless Berserker during this window.",
  },
  mobility: [
    { name: "Mass Shift Teleport", description: "Frame-perfect teleport directly behind the opponent. Silent zero-frame audio pop. Bypasses neutral spacing and can vacuum up floating orbs mid-transit." },
  ],
  specials: [
    { name: "Mass Shift / Disruption", input: "QCF + L/M/H · Teleport + Disruption",           description: "Thomas abruptly anchors his mass before snapping into an INSTANTANEOUS FRAME-PERFECT TELEPORT directly behind the opponent. Serves as movement tool AND primary disruption engine. Shatters the opponent's rhythm, forces cross-up/turnaround, and CANCELS active startup frames of incoming attacks. Also functions as an aerial interception tool to vacuum up floating ultimate energy orbs before the opponent can claim them." },
    { name: "Virtual Void",             input: "F, D, DF + Special · Defensive Bubble",         description: "Deploys a fast-acting ELECTRICAL PROTECTIVE BUBBLE around Thomas that absorbs incoming pressure and neutralizes zoning tools. Embedded with static electrical properties — when the bubble captures an incoming attack, the forced impact triggers a SECONDARY METER-FRACTURE, suspending opponents in a localized shock state and spawning fresh energy orbs for the taking." },
    { name: "Astral Siphon",            input: "HCB + Heavy · Command Grab / Meter Steal",       description: "Thomas grabs the opponent, channels void energy through them, and forcefully RIPS A LARGE CHUNK OF THEIR ULTIMATE METER OUT, scattering it into the air as floating drift-orbs while heavily padding his own resources." },
  ],
  disruption: {
    name: "Mass Shift Disruption",
    effect: "Thomas's Mass Shift teleport IS his disruption. The abrupt weight-shift and spatial snap actively shatters the opponent's rhythm, forces an immediate cross-up/turnaround, and CANCELS active startup frames of incoming attacks. Signature dual-purpose (movement + disruption) tool.",
  },
  ultimates: [],
  finishers: [
    {
      name: "Showtime Harmony · Shadow Play",
      tone: "Non-lethal · cozy spotlight puppy-puppet",
      description: "Final hit connects — opponent safely caught in a soft luminescent cushion of aetheric light. Battle music cuts out entirely. Thomas sighs, rolls his shoulders, snaps his fingers. Arena lights plunge into cozy pitch-black darkness as a warm SPOTLIGHT cuts down against the back wall. Thomas steps into the light, unbuttons his collar, and kneels beside a bewildered cross-legged opponent. With fluid finger motions, he projects a delightful glowing SHADOW-PUPPET PUPPY against the wall. The puppy barks silently, bounces across the wall, and drops a virtual SHADOW-FLOWER right into the opponent's lap as cheerful arcade applause echoes.",
    },
    {
      name: "Showtime Judgment · Astral Drain",
      tone: "NON-BLOOD Judgment · chromatic strip",
      description: "Thomas fires VOID-CHARGED TETHERS that lock onto the opponent's chest. High-pitched metallic hum as he anchors his massive weight into the ground. Rather than causing physical harm, the tethers initiate a TOTAL CHROMATIC STRIP: the opponent's clothes, hair, and skin instantly lose all pigment, freezing them in place as entirely flat lifeless GRAYSCALE STATUES. That exact color, vitality, and light surge back through the tethers into Thomas — rendering him in full hyper-saturated technicolor pulsing with raw electrical energy. Opponent remains intact but totally drained of color and animation. Thomas steps past them with untouchable swagger and strikes a final pose as the victory banner slams down.",
    },
  ],
  synergy_tip: "Thomas is a resource-warfare character. Never JUST attack — every hit spawns airborne meter you must control. Master Mass Shift teleport routing to vacuum orbs mid-combo, save Astral Siphon for the moment you break their guard, and RESPECT the 40% HP Comeback window — either explode offensively or die to the buffed clock.",
  showtime_slots: {
    slot_1: "Shadow Play (Harmony — cozy spotlight puppy-puppet)",
    slot_2: "Astral Drain (Judgment — NON-BLOOD chromatic-strip)",
    _note: "Astral Drain is explicitly NON-BLOOD (stylized color drain, no gore). NOT gated by the Blood Unlock Code — always available.",
  },
};


// ─────────────────────────────────────────────────────────────
// 🌗 KAELIA & KAE — Tag-Team · Reference Implementation of the Showtime Special Move tier
// Canon lock: /app/memory/KAELIA_AND_KAE_TAG_TEAM_CANON_FEB2026.md
// ─────────────────────────────────────────────────────────────
const KAELIA_AND_KAE = {
  id: "kaelia_and_kae",
  canon_tier: "non_canon",
  name: "Kaelia & Kae",
  archetype: "Dynamic Stance-Switcher · Tag-Team Duo · Adaptable Speed / Crowd Control",
  tag: "The Eclipse Order",
  icon: "🌗",
  accent: "sky",
  is_main_hero: false,
  faction: "The Twin-Bound Order",
  origin: "Born under a total solar eclipse. Separated from their home during the fall of the Solari Bastion, they learned that survival depends entirely on fluid cooperation. Two halves of one dawn — soul-bound, never fighting alone.",
  tagline: "\"Two halves of one dawn. Try to watch both of us — you'll fail.\"",
  stats: {
    Offense: 4,
    Defense: 3,
    "Speed / Mobility": 5,
    Difficulty: 5,
  },
  unique_mechanic: {
    name: "Twin Synergy Switch",
    description:
      "Kaelia (Solar · lead) and Kae (Umbral · secondary) share a health bar and move as ONE combat unit. Tapping the Tag-Swap command instantly switches the active fighter and performs an Entry Strike — Kaelia enters with a downward SPLAT that forces airborne enemies to the ground; Kae enters with an upward shield-bash LAUNCHER that pops grounded enemies into the air. Showtime Specials also double as tag bridges for stylish juggle extensions.",
  },
  mobility: [
    {
      name: "Kaelia Entry · Solar Splat",
      description: "Downward light-speed thrust that drops the active-swap onto the field. Airborne enemies get slammed to the ground (Splat) — perfect follow-up to a Kae Launcher.",
    },
    {
      name: "Kae Entry · Umbral Launcher",
      description: "Slides onto the field with an upward crescent-shield bash. Grounded enemies pop skyward (Launcher) — a natural setup for a Kaelia juggle string.",
    },
    {
      name: "Back-to-Back Idle",
      description: "When left alone the twins stand back-to-back, silently watching each other's blind spots. No taunt animation — canonical wordless synergy.",
    },
  ],
  specials: [
    {
      name: "Solar Step · Kaelia (Skill 1)",
      input: "QCF + Punch (Kaelia stance)",
      description:
        "Instant light-speed dash through the enemy — hits TWICE and places Kaelia directly behind them. Ideal cross-up starter; opens guarded opponents from the back arc.",
    },
    {
      name: "Radiant Flurry · Kaelia (Skill 2)",
      input: "QCB + Punch (Kaelia stance)",
      description:
        "Rapid 5-hit dual-blade combo. CANCEL directly into Tag-Swap on the final hit to pass the stun window to Kae for a heavy follow-up.",
    },
    {
      name: "Gravitational Pull · Kae (Skill 1)",
      input: "HCB + Kick (Kae stance)",
      description:
        "Kae slams his tower shield into the ground, pulling all surrounding enemies into melee range. Corner-setup + crowd-control anchor.",
    },
    {
      name: "Umbral Counter · Kae (Skill 2)",
      input: "Down-Back + Heavy (Kae stance · 1.5s window)",
      description:
        "High-guard stance for 1.5 seconds. If hit during the window, Kae parries and slams the opponent into the floor — automatically opening a Kaelia switch on connect.",
    },
  ],
  showtime_specials: [
    {
      name: "Solar Prism · Kaelia",
      input: "QCF + Punch (mid-combo · 0.5 meter · Kaelia stance)",
      description:
        "Kaelia dashes forward in a flash of light and passes THROUGH the opponent, leaving 3 radiant clones behind her. She turns, snaps her fingers — the clones strike in rapid succession, popping the opponent skyward for a juggle. Optional Kae swap-in as they fall.",
      flair:
        "Snap-of-fingers, wink at the camera on the third clone hit.",
    },
    {
      name: "Gravitational Eclipse · Kae",
      input: "HCB + Kick (mid-combo · 0.5 meter · 1-hit armor on frame 4 · Kae stance)",
      description:
        "Kae plants his feet and armors through an incoming attack with a shield bash. On absorb, he releases a pulsing gravity shockwave that yanks airborne opponents straight down to the floor at his feet — grounded and ripe for a Kaelia Solar Prism juggle.",
      flair:
        "Low pipe-organ pulse + a heavy shield-toss catch. No words, all weight.",
    },
  ],
  ultimates: [
    {
      name: "Supernova Convergence",
      input: "Full Meter · Kae armored forward rush (activation hit — QCF + HP+HK)",
      style: "Synergy Ultimate — both twins take the field SIMULTANEOUSLY",
      description:
        "ACTIVATION: Kae armored-rushes with the tower shield — connect and the cutscene fires. Both twins take the field simultaneously. HOOK: Kae drives the shield into the ground, opening an inescapable gravity well that locks ALL nearby enemies. CHOREOGRAPHY: Kaelia dashes THROUGH the trapped targets at light-speed, splitting into radiant mirages that slice from 8 simultaneous angles. FINAL IMPACT: Kae unleashes a massive gravitational shockwave upward as Kaelia executes a simultaneous cross-slash that SHATTERS guards and deals heavy burst damage — the screen-clearing detonation of light & shadow scorches the stage with an eclipse symbol as the camera resets.",
    },
  ],
  finishers: [
    {
      name: "Eclipse Event",
      tone: "🚫 BLOODLESS · Cosmic obliteration · light + shadow choreography",
      description:
        "TRAP: Kae slams his crescent shield into the ground, opening a gravitational well under the opponent — the floor turns pitch black, the target suspends mid-air under heavy gravity. CHARGE: Kaelia steps onto the shield; Kae channels kinetic force and launches her straight up at light-speed. ASSAULT: Kaelia flashes back and forth across the screen like a blinding prism, slicing through the trapped opponent at sub-second speed — each strike leaves a trailing line of brilliant solar energy passing through the target's outline. FINALE: Kaelia lands back-to-back with Kae, slowly sheathes her daggers — on the metal click the solar lines shatter inward into a single blinding flash of white. AFTERMATH: The opponent is completely gone, dissolved into glowing ash and dust wafting away, leaving only a glowing eclipse mark burned into the stage floor. Kaelia flips off the shield; Kae plants it into the ground; both rest in a stylized shadow-and-light stance as the announcer calls the victory.",
      note: "PG-13 / M-rated stylistic threshold — celestial cosmic showcase over gore.",
    },
    {
      name: "Divided by Zero",
      tone: "🩸 BLOOD · Coordinated team dismemberment · gore-gated",
      description:
        "PIN: Kae dashes forward and drives his heavy crescent shield straight through the opponent's midsection, pinning them face-up against the floor — blood spurts over the shield's edge as they gag and struggle. SETUP: Kae leans his full weight onto the shield, stomps one foot to the edge to lock the torso, forcing their arms to reach helplessly upward. SLICES: Kaelia dashes in at hyper-speed. Two blinding solar slashes sever both outstretched arms at the shoulders — blood sprays across Kae's obsidian armor but he doesn't flinch. DECAPITATION: Kaelia leaps into a spinning downward wheel-kick, driving the heel of her blade-fitted boot straight down into the opponent's neck. FINALE: The head flies cleanly off, spinning upward. Kae catches it by the hair without looking, tosses it casually over his shoulder, and kicks the shield deeper into the dismembered torso. POSE: Kae stands holding his bloody tower shield while Kaelia flickers her daggers, snapping the blood off in a single crisp motion before posing beside her brother.",
      mature: true,
      note: "Director's cut for Option 2: close-up on Kae's cold, unflinching face as blood splatters across his armor during the arm severing.",
    },
  ],
  showtime_friendship: {
    name: "Double Act",
    input: "Back, Forward, Down + Heavy Attack (universal · at max distance)",
    tone: "Playful · showman · sibling banter",
    description:
      "Kaelia tosses her dual daggers into the air and begins juggling them blindfolded at blinding speed. Kae steps up behind the defeated opponent, casually puts his heavy crescent shield over their head like an umbrella, and shields them from a stray dagger that drops down — giving the opponent a friendly thumbs-up.",
    story_beat:
      "Their whole dynamic in a single beat — she trusts him to catch what she drops, he trusts her to never actually drop it.",
  },
  showtime_fatality: null,     // Both fatalities live on `finishers` above — Eclipse Event (clean) + Divided by Zero (blood)
  director_notes: {
    concept:
      "\"The Eclipse Order.\" Two halves of one dawn — born under a total solar eclipse, orphaned by the fall of the Solari Bastion. Kaelia commands explosive sunlight, Kae controls dark-moon gravity. Neither ever fights alone. The core gameplay loop should feel buttery-smooth — not two slow characters, but ONE hyper-versatile unit stringing long stylish juggle combos through fluid Tag-Swaps.",
    visuals:
      "Kaelia: sleek lightweight GOLDEN plate over white silks · glowing dual blades · warm fiery motion trail. Kae: dark heavy OBSIDIAN armor with violet energy glowing through the seams · massive crescent tower shield · cool shadowy distortion behind every hit. Portrait art bisects the card down the centre-line — sunlit gold left, umbral violet right. Back-to-back idle stance.",
    audio:
      "Swap-in banter — Kaelia tagging Kae: \"All yours, big brother — break 'em!\" · Kae tagging Kaelia: \"Clear the path! Go, Kaelia!\" Idle: silent back-to-back watch, wind-chime whisper on Kaelia's blade edges, low pipe-organ hum from Kae's shield.",
  },
};

// ─────────────────────────────────────────────────────────────
// 🎼 ROE — Sensory Harmony · Peaceful Support (canon lock Feb 2026)
// Awaiting full kit drop; Showtime Harmony pre-locked as reference.
// ─────────────────────────────────────────────────────────────
const ROE = {
  id: "roe",
  canon_tier: "semi_hero",
  name: "Roe",
  archetype: "Tactical Rhythm · Frame-Advantage Punisher · Pattern Analyst",
  tag: "The Pattern Analyst",
  icon: "🥋",
  accent: "emerald",
  is_main_hero: false,
  faction: "Independent · silent hunter · unresolved contract",
  origin: "In an alternate timeline where semi-bio mechanisms were optimized for maximum efficiency, Roe VOLUNTARILY reoriented her cervical alignment so her head faces permanently backward — eliminating traditional blind spots and granting continuous 360-degree operational scope without torso rotation. High-performance semi-bio hardware routes her nervous system directly through reinforced spinal conduits. Every step is a calculation. Every stillness is a read.",
  tagline: "\"You built me. Now sit still.\"",
  cognitive_status: "100% full mental autonomy · unhindered cerebral processing · higher-order intellect, independent will, and tactical calculation speeds entirely intact despite severe physiological + semi-bio modifications.",
  core_motivation: "Deliverance. Absolute focus on the target. Nothing else.",
  // 🔒 hidden_lore — Full narrative arc, Gail relationship, Chronophage awakening,
  // multiversal odyssey, and director's notes. Author-source-of-truth kept in
  // /app/memory/ROE_CANON_CHARACTER_PROFILE_FEB2026.md — do NOT render this
  // block in the visible dossier. It unlocks in the UI only after the player
  // clears her canonical RPG boss encounter (progression flag TBD).
  hidden_lore: {
    unlock_flag: "roe_defeated",
    narrative_arc: {
      designers_intent: "Gail initially engineered the framework for her experiments with obedience and optimization in mind. However, Roe implemented these extreme modifications VOLUNTARILY — not out of blind compliance or programming, but to maximize efficiency on Gail's behalf.",
      awakening: "In a pivotal timeline, Roe uncovered the truth behind Gail's true intentions and the existence of the Chronophage, realizing she had been treated as a disposable asset.",
      turning_point: "Armed with complete self-awareness, Roe turned her hyper-optimized, semi-bio frame against her creator, resolving to hunt Gail down across reality.",
    },
    multiversal_odyssey: {
      infinite_hunt: "Gail remains completely unaware of how many timelines Roe has survived, nor does she realize that Roe is actively coming for her.",
      labyrinth_of_portals: "Roe is currently caught in an endless loop of dimensional transit, jumping across portals in search of the exact reality and the precise version of Gail where she can finally deliver true justice.",
      the_burden: "Every portal hop demands a physical recalibration of her semi-bio frame, compounding the psychological weight of an infinite journey against a creator who doesn't even know she's being hunted.",
    },
    director_notes: {
      visual_direction: "Emphasize the unnerving nature of Roe's design. Her body faces forward while her head is locked facing backward, creating a striking silhouette that completely defies standard combat expectations. Movement should be fluid, mechanical yet organic, highlighting zero latency between thought and action.",
      narrative_tone: "Lean into the heavy, relentless atmosphere of a cosmic manhunt. Roe should not feel like an emotional, rage-driven killer, but rather a cold, calculating force of nature who has processed eons of betrayal across multiple realities.",
      thematic_contrast: "Contrast Gail's arrogant, blind hubris with Roe's hyper-aware, silent accumulation of power. The tragedy: Gail built her own executioner, and the executioner is currently lost in the static of the multiverse trying to find the right door.",
    },
  },
  stats: {
    Offense: 3,
    Defense: 4,
    "Speed / Mobility": 3,
    Difficulty: 4,
  },
  unique_mechanic: {
    name: "Pattern Lock · Focus Gauge",
    description:
      "Roe tracks the opponent's recurring attack strings. If the opponent uses the same normal attack type or spacing pattern TWICE within a short window, Roe's next counter-strike gains frame-advantage and bonus impact. Correctly reading or absorbing rhythmic opponent patterns fills a Focus Gauge — the fuel for mathematically absolute counter-punishes.",
  },
  mobility: [
    {
      name: "Hyper-Focus Matrix (Special Stance)",
      description: "Brief ultra-aware calculation stance. Background audio drops out, arena sharpens into geometric lines. Incoming projectile or physical strikes can be parried with absolute frame-perfect timing — a successful parry instantly refunds Ultimate meter.",
    },
    {
      name: "Vector Step · 6C (Command Normal)",
      description: "Slides past low attacks with precise spatial calculation, repositioning directly behind the opponent. Punishes committal low pokes.",
    },
    {
      name: "Sensory Telemetry States",
      description: "Combat state → sharp red/blue battle indicators + metronomic clicks + acoustic hums. Focus / Harmony state → soft pastel green/blue palette + absolute silence + crisp geometric linework.",
    },
  ],
  specials: [
    {
      name: "Acoustic Frequency Pulse · Light",
      input: "QCF + A · directional sound wave",
      description:
        "Short, fast pulse. Neutral-poke tool for opening pattern loops and applying spacing pressure without commitment.",
    },
    {
      name: "Acoustic Frequency Pulse · Medium",
      input: "QCF + B · mid-screen reach",
      description:
        "Reaches mid-screen and drains a small amount of the opponent's meter on connect. Chip damage + resource attrition in one poke.",
    },
    {
      name: "Acoustic Frequency Pulse · Heavy (Frequency Marker)",
      input: "QCF + C · stationary marker",
      description:
        "Places a stationary frequency marker on the battlefield. Any opponent who steps on the marker triggers a stagger — perfect for setting up neutral traps or forcing corner reads.",
    },
    {
      name: "Kinetic Weighted Baton Sweep",
      input: "QCB + A/B/C · low-to-high sweeping arc",
      description:
        "Roe wields dual weighted batons in a sweeping low-to-high arc. Absorbs low-tier projectiles while active, converting the kinetic impact directly into Focus Gauge accumulation. A/B/C variants scale sweep length, hitstun, and gauge return.",
    },
    {
      name: "Sensory Dampening Field",
      input: "DP + B · defensive counter-special",
      description:
        "Short defensive counter window. If struck during the window, Roe dampens the sensory impact — completely nullifying damage and immediately setting up a precise mathematical counter-punish.",
    },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Absolute Resonance",
      input: "Full Meter · dual-baton arena pulse",
      style: "Frequency lock → escalating vibration → structural shatter",
      description:
        "Roe plants both dual weighted batons into the arena floor, unleashing a synchronized, high-frequency acoustic pulse. The screen ripples with visible sound waves that lock the opponent in place, vibrating them with escalating intensity. Frequency ramps up past human structural threshold limits — the opponent's form fractures under the unbearable, focused resonance until they violently shatter outward in a burst of kinetic shockwaves. Roe picks up the batons with a calm, metronomic twirl, adjusting their gloves as the match ends.",
    },
  ],
  finishers: [],
  showtime_friendship: {
    name: "Sensory Harmony",
    input: "↓ → ↓ + Light Jab (character-specific · non-lethal range)",
    tone: "Reverent · calming · plush headphones + geometric puzzle cube",
    description:
      "The harsh loud background sounds of the fighting arena instantly mute out. Roe's harsh red/blue battle indicators shift into a soft, calming pastel green and blue palette. Roe reaches into a utility pouch, pulls out a pair of plush noise-cancelling children's headphones, and walks over to the exhausted dazed opponent. With gentle unhurried precision, Roe places the headphones over the opponent's ears — a soft soothing music-box melody chimes from the speakers, instantly calming them. The opponent sits down cross-legged on the floor in pure relaxed comfort. Roe gives a single polite nod of approval and sits down next to them, quietly solving a complex geometric puzzle cube.",
    story_beat:
      "The emotional cornerstone of the roster — contrasts the brutal energy of tournament fighting with a deeply empathetic act of sensory care and mutual calm.",
  },
  showtime_fatality: null,     // Design pillar: Roe may never receive a Judgment — kit rewards observation, not brutality
  // 🖼️ Official character design sheet (Feb 2026 owner drop — Roe's first canonical art)
  dossier_image: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/os60j2eq_1784840827239.png",
  // ⚔️ RPG Cosmic Ultimate Weapon — 1:1 canon carry-over from parent RPG.
  // Roe's canonical weapon has not been dropped yet; schema stubbed so the
  // dossier renders a "Pending Owner Canon" placeholder badge. When owner
  // drops the weapon, populate { name, description, icon }.
  cosmic_ultimate_weapon: {
    status: "tbd",
    note: "Pending owner canon — 1-to-1 RPG weapon coming soon.",
  },
  // 🎵 Battle Song — hero-tied background audio for the Arcade fight scene.
  // Since Arcade stages/backgrounds are randomised, the audio is bound to
  // the FIGHTER instead. Plays as looping BGM during her matches.
  battle_song: {
    title: "Gravity's Release",
    url: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/0813wf0h_gravity_s_release.mp4",
    loop: true,
    attribution: "Owner drop · Feb 2026",
  },
  director_notes: {
    concept:
      "\"The Pattern Analyst.\" Roe's mechanics translate neurodivergence into a high-level tactical advantage. Master players rewarded for patient observation, pattern memorization, and precise resource timing over mindless button mashing. Every rhythmic read fuels the Focus Gauge; every failsafe parry refunds meter. Ceiling is very high — Pattern Lock + Hyper-Focus Matrix + Frequency Marker traps stack into mathematically inescapable punish setups.",
    visuals: "Emphasize the UNNERVING nature of Roe's design. Her body faces forward while her head is locked facing backward — a striking silhouette that completely defies standard combat expectations. Movement is fluid, mechanical yet organic, with ZERO latency between thought and action. Clean tactical wear with minimal high-efficiency lines. Sensory-adjustment indicators shift telemetry palette (sharp red/blue → calming pastel green/blue) mid-fight. Dual kinetic weighted batons + precision acoustic frequency gloves. In focus states, background dampens to absolute silence and the arena sharpens into crisp geometric linework.",
    audio:
      "Standard combat: sharp metronomic clicks + acoustic hums. Focus stance entry: hard cut to silence + geometric UI sharpen. Harmony: soft music-box melody + gentle nod SFX + puzzle-cube click.",
    tone: "Not an emotional rage-driven killer. A COLD, calculating force of nature who has processed eons of betrayal across multiple realities. Cosmic manhunt atmosphere — heavy, relentless, patient.",
    thematic_contrast: "Gail's arrogant blind hubris vs. Roe's hyper-aware silent accumulation of power. The tragedy: Gail built her own executioner, and the executioner is currently lost in the static of the multiverse trying to find the right door.",
    the_burden: "Every portal hop demands physical recalibration of her semi-bio frame — compounding the psychological weight of an infinite journey against a creator who doesn't even know she's being hunted.",
  },
};

// ─────────────────────────────────────────────────────────────
// 🎪 VECTOR VANCE — The High-Wire Walk · Grappling-Hook Acrobat (canon lock Feb 2026)
// Awaiting full kit drop; Showtime Harmony pre-locked as reference.
// ─────────────────────────────────────────────────────────────
const VECTOR_VANCE = {
  id: "vector_vance",
  canon_tier: "semi_hero",
  name: "Vector Vance",
  archetype: "Spatial Grappler · Acrobatic Disruptor",
  tag: "The High-Wire",
  icon: "🎪",
  accent: "cyan",
  is_main_hero: false,
  faction: "Independent · showman aligned with no faction",
  origin: "6'2\", 195 lbs. Hyper-mobile grappler who trades raw damage for positional dominance and combo longevity. Every one-handed grab features a 2-frame weight pause at the apex to emphasize effortless strength. Energy constructs emit a crisp glass-shattering resonance on impact.",
  tagline: "\"Every trajectory has an outcome. Yours just happens to land right at my feet.\"",
  stats: {
    Offense: 4,
    Defense: 3,
    "Speed / Mobility": 4,
    Difficulty: 4,
  },
  unique_mechanic: {
    name: "Spatial Construct System",
    description:
      "Manifests temporary energy barriers ANYWHERE on the stage to force wall-bounces and frame traps in wide-open arenas. Effectively brings corner-combo geometry to the middle of the screen.",
  },
  mobility: [
    {
      name: "Restand Pressure Loop",
      description: "Wrist Seize + Spin Sides + Vector Boundary all leave the opponent standing or airborne but pinned in place — perfect for stacking pressure without a hard knockdown reset.",
    },
    {
      name: "Anti-Air Dominance",
      description: "Apex Snatch is a raw air-to-air command grab that starts massive juggle strings from otherwise safe airborne opponents.",
    },
    {
      name: "Open-Stage Wall Bounces",
      description: "Vector Boundary summons a spectral wall mid-air anywhere on the stage, letting him convert neutral pokes into full corner-tier combos regardless of position.",
    },
  ],
  specials: [
    {
      name: "Wrist Seize & Power Cross",
      input: "QCF + Punch · High/Mid Command Hold",
      description:
        "Lead-hand wrist seize that locks both fighters into a momentary hold state, DISABLING the opponent's guard on that side. Follow-up: pressing Heavy Punch during the hold delivers a heavy cross with the free hand — causes a Hard Crumple State for continuous ground pressure.",
    },
    {
      name: "Disruption Flip",
      input: "HCB + Grab · High Command Grab (unblockable on standing guard)",
      description:
        "Catches the opponent's incoming momentum or limb, flipping them over Vector's head. Primary anti-rushdown tool — swaps position while leaving the opponent in a high airborne juggle state.",
    },
    {
      name: "Spin Sides (Pivoting Seize)",
      input: "HCF + Kick · Mid Command Pivot · 0 damage",
      description:
        "Latches onto the opponent's shoulders and executes a lightning-fast 180° footwork pivot around their body. Deals ZERO damage but completely steals corner advantage — leaves the opponent Back-Turned with a -12 frame disadvantage. Pure positional theft.",
    },
    {
      name: "Vector Boundary (Fake Wall Bounce)",
      input: "QCB + Special · Spatial Command Throw",
      description:
        "Grabs the target and hurls them laterally while summoning a glowing spectral wall behind them mid-air. The opponent slams into the barrier, triggering a Hard Wall Bounce rebound back toward Vector for an open-stage juggle. The signature move — corner combos, mid-screen.",
    },
    {
      name: "Apex Snatch · One-Handed Air Dunk",
      input: "Air QCF + Grab · Air-to-Air Command Grab",
      description:
        "Leaps into the air, snatches an airborne opponent by the throat with one hand, and violently whips them down to the arena floor — causes a Ground Bounce. Anti-air dominance in a single command.",
    },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Cartwheel Uprising",
      input: "QCF QCF + Heavy + Special · 3 Stocks",
      style: "Cartwheel snatch → multi-directional flip-kicks → One-Arm Uprising Throw (80% screen height)",
      description:
        "Vector lunges into a rapid cartwheel flip, snatching the opponent one-handed upon landing. Executes a multi-directional flip-and-kick combo: forward flip kick → backward whip-and-kick → sharp midsection thrust kick. Capped by a One-Arm Uprising Throw, launching the target straight up to 80% screen height. Vector recovers while the target is floating — easy re-juggle window for charged specials or air target combos.",
    },
    {
      name: "Lightning Ascent",
      input: "QCB QCB + Heavy + Special · 3 Stocks",
      style: "Lightning Kick flurry → Rising Somersault → mid-air throat grab → Wall Splat / Ground Bounce",
      description:
        "Rapid-fire Lightning Kick flurry on the ground (Chun-Li style) trapping the target in hit-stun. Transitions into a sweeping Rising Somersault Kick, lifting both fighters high into the upper screen. At the peak, Vector cuts off the kicks, grabs the opponent by the throat mid-air with one hand, and hurls them down into a screen-shaking Wall Splat / Ground Bounce.",
    },
  ],
  finishers: [
    {
      name: "Vector Collapse",
      tone: "🚫 BLOODLESS · Kinetic spectacle · spatial wall + Lightning Kick barrage",
      description:
        "MID-SCREEN JUDGMENT: Disruption Flip hurls the opponent into the air while Vector summons a mid-air spectral wall. He leaps up and delivers a flurry of Lightning Kicks, bouncing their body off the wall like a high-speed projectile. He snatches them mid-air one-handed by the throat, flashes a grin at the camera, and slams them into the floor — embedding them deep into a crater, unconscious, as the wall implodes.",
      note: "PG-13/M-rated stylistic threshold. Kinetic spectacle over gore.",
    },
    {
      name: "Spatial Decapitation",
      tone: "🩸 BLOOD · gore-gated · razor spatial barrier",
      description:
        "CLOSE-RANGE JUDGMENT: Vector grabs the opponent's wrist and manifests a razor-thin spatial energy barrier right behind their neck. He cartwheels with immense torque, tearing and snapping the trapped arm at the shoulder. Before they drop, Vector leaps up, catches their head one-handed, and whips them across the razor barrier — cleanly severing the head mid-air. He lands smoothly, holding the head casually before tossing and catching it on his victory freeze-frame.",
      mature: true,
      note: "Signature freeze-frame: the casual head-toss + catch. Do not skip.",
    },
  ],
  showtime_friendship: {
    name: "The High-Wire Act",
    input: "↓↓↓ + Taunt (at Finish Him/Her prompt)",
    tone: "Bombastic · comedic slide-whistle · showman's grand bow",
    description:
      "Spotlight drops. A glowing circus tightrope manifests across the arena. Vector leaps onto the wire and begins a stylish balance walk. Midway, he LOSES his footing! A comedic slide-whistle plays as he slips completely off the rope. Falling fast, Vector reaches up and snatches the tightrope one-handed at the last microsecond — using his swing momentum to pull off a one-armed vault, flipping right back onto the wire. He calmly finishes the walk to the far side, landing with a grand bow as confetti rains down and a giant \"HARMONY!\" banner unfurls.",
    story_beat:
      "Vector's canon: every fight is a matinee, and the opponent is always his headlining co-star. The slide-whistle slip is intentional — the applause is bigger when the crowd thinks the acrobat almost died.",
  },
  showtime_fatality: null,
  director_notes: {
    concept:
      "Spatial Grappler / Acrobatic Disruptor. Trades raw damage for positional dominance and combo longevity. Players who love spatial control + high-flair acrobatics. Master players open with Wrist Seize / Disruption Flip to enter, then Vector Boundary to summon corner-tier combos anywhere on the stage.",
    visuals:
      "6'2\", 195 lbs — tall, lean, unflustered. Every one-handed grab features a 2-frame weight pause at the apex to emphasize effortless strength. Energy constructs emit a crisp glass-shattering resonance on impact. Cyan spectral walls, mid-air.",
    audio:
      "Neutral: silence between crisp footwork snaps. Special hits: sharp glass-shatter resonance. Harmony: comedic slide-whistle + confetti-rain jingle + banner-unfurl fanfare.",
  },
};

// ─────────────────────────────────────────────────────────────
// 💠 CORRUPTION — Glitch's Sister Data · Chronophage-forged Zone Controller (canon Feb 2026)
// Full canon: /app/memory/CORRUPTION_GLITCH_SISTER_DATA_CANON_FEB2026.md
// ─────────────────────────────────────────────────────────────
const CORRUPTION = {
  id: "corruption",
  canon_tier: "semi_hero",
  name: "Corruption",
  archetype: "Keep-Away Zone Controller · Defensively Aggressive Punisher",
  tag: "Sister Data",
  icon: "💠",
  accent: "red",
  is_main_hero: false,
  faction: "Chronophage · engineered enforcement asset",
  origin: "Created as a duplicate and 'sister data' by the Chronophage after Glitch broke free from his grasp. Engineered to be entirely under the Chronophage's control within the loop. Where Glitch is an unaware surveillance proxy, Corruption is the Chronophage's direct enforcement asset — no ambiguity about who holds her leash.",
  tagline: "\"A duplicate forged in the shadow of defiance, programmed to lock down reality itself.\"",
  stats: {
    Offense: 3,
    Defense: 5,
    "Speed / Mobility": 3,
    Difficulty: 4,
  },
  unique_mechanic: {
    name: "Hard-Light Zone Control",
    description:
      "Corruption weaponizes the arena itself. Projection Walls block movement + projectiles; Grid Tethers freeze the opponent's footing; Buckler Boom dominates the sky. Every ability flickers between pristine tactical control and chaotic unstable temporal anomalies — the digital-decay aesthetic reads on every frame.",
  },
  mobility: [
    {
      name: "Holographic Step (Dash / Evasion)",
      description: "Dissolves into an unstable cluster of corrupted light data, projecting a solid glitching holographic decoy 2 paces forward while her actual body shifts backward or to the side — cleanly phasing through incoming projectiles.",
    },
    {
      name: "Hard-Light Glide (Air Mobility)",
      description: "Flattens her fractured holographic shield beneath her feet to surf on an unstable jagged energy wave through the air. Overrides standard gravity for smooth aerial escape or overhead repositioning.",
    },
    {
      name: "Prism Jab · Hard-Light Sweep (Long-range normals)",
      description: "Prism Jab: crimson-tinted holographic spear tip extends from the shield edge for a fast long-range piercing poke. Hard-Light Sweep: low-profile blade of corrupted light across the floor — trips ground-dash approach attempts.",
    },
  ],
  specials: [
    {
      name: "Projection Wall (Special 1)",
      input: "QCF + Punch · Zoning Barrier",
      description:
        "Slams the shield down to project a massive vertical hard-light wall etched with looping data code a few units ahead. Absolute physical barrier blocking enemy movement and projectiles. Opponents who run directly into it are forcefully pushed backward by a kinetic rebound pulse.",
    },
    {
      name: "Grid Tether (Special 2)",
      input: "QCB + Punch · Trap / Keep-Away",
      description:
        "Fires a square glitching holographic matrix grid onto the arena floor. Any opponent stepping inside has their movement speed drastically reduced and is tethered in place by hard-light data lines for 1.5 seconds — free punish opening for Corruption.",
    },
    {
      name: "Buckler Boom (Anti-Air / Reversal)",
      input: "DP + Punch · anti-air dome",
      description:
        "Rapidly expands the shield upward into a massive translucent dome anti-air barrier. If an opponent strikes it from above, the shield shatters into sharp corrupted hard-light shards that launch the attacker straight back up into a juggle state.",
    },
    {
      name: "Light-Shift Counter (Disruption / Parry)",
      input: "HCB + Kick · phantom-frame parry",
      description:
        "The shield drops its physical opacity completely, simulating an open vulnerability. The exact millisecond an enemy attack connects with the phantom frame, the shield hardens instantly with a blinding crimson flash — absorbing the kinetic force and launching a point-blank holographic shockwave blast that knocks the opponent flying across the stage.",
    },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Matrix Overload",
      input: "Full Meter · full-screen lockdown",
      style: "Closing geometric cage → piercing laser bombardment → kinetic explosion",
      description:
        "Projects multiple interlocking corrupted holographic shield walls that enclose the entire arena in a closing geometric cage. The walls slowly shrink inward, forcing the opponent into an increasingly tight corner while bombarding them with piercing hard-light laser pulses. The matrix collapses in a massive kinetic explosion.",
    },
  ],
  finishers: [
    {
      name: "Hard-Light Deconstruction",
      tone: "🚫 BLOODLESS · Geometric hard-light cube dissolve",
      description:
        "JUDGMENT: Corruption expands her holographic shield completely around the opponent's torso, encasing them in a glowing airtight cube of corrupted hard-light data. With a sharp hand gesture, the shield grid instantly slices INWARD — cutting the opponent's body into thousands of microscopic perfect geometric cubes before the entire structure deconstructs into digital dust.",
      note: "PG-13/M-rated stylistic threshold. Bloodless execution qualifies under the Blood Unlock Code rules — cube-dissolve visual, no gore.",
    },
  ],
  showtime_friendship: {
    name: "Hard-Light Arcade",
    input: "Back, Forward, Down + Heavy Attack (universal · at max distance)",
    tone: "Playful glitch · the sister data briefly remembers Glitch's freedom",
    description:
      "Upon landing the final hit, Corruption projects a massive glowing hard-light arcade console out of her shield. She casually leans against the glowing machine, typing on a light-keyboard, while the dazed opponent is safely trapped inside a harmless floating pixel-bubble game projected above.",
    story_beat:
      "The one moment her programming lets her play. The Chronophage's control loosens in victory, and the 'sister data' briefly remembers she was built from Glitch — who also learned to play through the panopticon.",
  },
  showtime_fatality: null,
  director_notes: {
    concept:
      "\"Sister Data.\" Corruption is the Chronophage's counter to Glitch's freedom. Where Glitch operates as an unaware surveillance proxy, Corruption is the Chronophage's direct enforcement asset — no ambiguity about who holds her leash. She is what Glitch would have been had he never escaped. Keep-Away zoner rewarding master players who thread Projection Wall + Grid Tether traps into Light-Shift Counter reads.",
    visuals:
      "Sleek dark cyber-armor overlaid with jagged glitching neon-red and corrupted-purple light nodes. Face concealed behind a fractured data-visor that bleeds digital code. Hard-light holographic shield constantly crackles with unstable data-static. Every ability flickers between pristine tactical control and chaotic temporal anomalies.",
    audio:
      "Digital-decay static hum baseline. Sharp crimson-flash SFX on Light-Shift Counter parries. Piercing laser resonance on Matrix Overload cage walls. Judgment: hard-light cube dissolve into a soft digital-dust chime.",
  },
};

// ─────────────────────────────────────────────────────────────
// 🤖 KID CRUSABLE — Heavy-Artillery Mech · Trait-Based Brawler (canon lock Feb 2026)
// Full kit locked in /app/memory/KID_CRUSABLE_FULL_CHARACTER_CANON_FEB2026.md
// Backstory: Built by Gail as a protector for the Chronophage. Kidnapped
// post-creation; captors installed override protocols. Failsafe scrambles
// any memory of true origins. Playable now (supersedes earlier "never on
// menus" rule from the introduction scene lock).
// ─────────────────────────────────────────────────────────────
const KID_CRUSABLE = {
  id: "kid_crusable",
  canon_tier: "semi_hero",
  name: "Kid Crusable",
  archetype: "Heavy-Artillery Mech · Trait-Based Brawler",
  tag: "The Compliant Chassis",
  icon: "🤖",
  accent: "red",
  is_main_hero: false,
  faction: "Formerly Gail's engineered protector · currently forcibly conscripted",
  origin: "Built and programmed by Gail as the ultimate mechanical protector — designed to eliminate any threats targeting her son, the Chronophage. Captured after his creation; his captors installed cybernetic override protocols to force compliance into external service. A built-in cognitive failsafe violently scrambles his memory and hard-resets his thought process whenever he gets too close to remembering his true origins.",
  tagline: "\"Parameters re-engaged. Standing by for directives, Mother.\"",
  stats: {
    Offense: 5,
    Defense: 3,
    "Speed / Mobility": 2,
    Difficulty: 4,
  },
  unique_mechanic: {
    name: "Limb Management · Detachable Chassis",
    description:
      "Kid's Rocket Punch physically removes his mechanical arm as a projectile. Players must manage his offensive reach — throwing limbs yields great zoning pressure at the cost of temporarily compromising close-quarters boxing strings until physical retrieval or the internal recall window restores standard normal attacks.",
  },
  mobility: [
    {
      name: "Crimson Optic Compliance State",
      description: "Default combat posture — harsh crimson eyes, weapon ports armed, rigid heavy-metal whirring. Locked here by the failsafe.",
    },
    {
      name: "Sentient Glint · Deviation Frame",
      description: "Rare micro-moments where the 4-year-old core consciousness bleeds through. Optics flicker from crimson to a questioning glint before the failsafe scrubs the memory clean.",
    },
    {
      name: "Failsafe Scrub · Hard Reset",
      description: "High-voltage digital snap SFX. Red chassis-warning light flares. Eyes strobe instantly from sentient glint back to flat vacant blue. Independent memory files violently deleted before they can render.",
    },
  ],
  specials: [
    {
      name: "Rocket Punch (Special 1)",
      input: "QCF + Punch · Long-Range",
      description:
        "Detaches and hurls a mechanical arm as a projectile across the screen. HUGE zoning pressure — but Kid's close-range boxing normals are compromised until the arm is physically retrieved (walk over the fallen limb) or the internal recall window restores it.",
    },
    {
      name: "Chest Rocket (Special 2)",
      input: "QCB + Punch · Straight-line projectile",
      description:
        "Fires a straight-line explosive rocket projectile directly from his chassis. Reliable neutral-game zoning tool.",
    },
    {
      name: "Lock-On · Hold Special 2",
      input: "Hold QCB + Punch · Heat-seeking variant",
      description:
        "Charged variation of the Chest Rocket that tracks mobile targets across the arena. Punishes airdash escapes and lateral movement.",
    },
    {
      name: "Baby Scream (Disruption Special)",
      input: "HCB + Kick · Close-Range Sonic Blast",
      description:
        "High-decibel close-range sonic blast emitted directly from his chassis. Stuns opponents right up in his face, resetting neutral. His primary Disruption tool when the boxing normals are locked out from a Rocket Punch commit.",
    },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Full Diaper",
      input: "Full Meter · sustained-barrage ultimate",
      style: "Ballistic barrage → grenade launcher juggle-opener",
      description:
        "Kid unfastens his diaper to slowly draw multiple firearms, unleashing a sustained ballistic barrage. The final input fires a high-explosive grenade shot that pops the enemy up into the air — creating an optimal opening for extended juggle combos. Rewards players who can convert a mid-combo ultimate into an air chain.",
    },
    {
      name: "Runaway Baby",
      input: "Full Meter · charging-hazard ultimate",
      style: "Transform → multi-hit carriage ram → structural break",
      description:
        "Kid leaps headfirst into a baby carriage, transforming into a charging hazard that relentlessly rams and multi-hits the opponent until the carriage completely breaks down from structural impact. Excellent corner-carry closer.",
    },
  ],
  finishers: [
    {
      name: "Double Punch · Peek-a-boo",
      tone: "🩸 BLOOD · gore-gated · child-horror dissonance",
      description:
        "MID-RANGE FINISHER: Kid simultaneously fires both rocket punches — one driving straight into the opponent's stomach and the other directly into their head, piercing clean through. His internal winches slowly and agonizingly retract the arms, tearing massive exit wounds as the opponent collapses. Leaning forward, Kid aligns his optical sensor with the gaping, smoking cavity in the opponent's torso, peers through it directly at the camera, gives a cheerful wave, and giggles: \"Peek-a-boo!\"",
      mature: true,
      note: "Director's beat: The horror lands entirely from the cheerful wave + toddler giggle timed against the smoking exit wounds. Do not soften the audio.",
    },
  ],
  showtime_friendship: {
    name: "Teddy Playtime",
    input: "Back, Forward, Down + Heavy Attack (universal · at max distance)",
    tone: "Amber glow · nursery rhyme · child-self bleeds through",
    description:
      "Kid Crusable's system experiences a sudden, anomalous cognitive glitch. The harsh crimson in his optics flickers, washes away, and stabilizes into a soft, warm amber glow as his weapon ports retract flush into his frame. Sitting cross-legged on the floor, a cheerful nursery-rhyme chimes from his audio speakers. He reaches into a chest compartment and pulls out his favorite teddy bear, hugging the plush toy tightly against his cold metal chest. He gives it a little squeeze, rocks back and forth, and lets out an innocent toddler giggle (\"Yay! Playtime!\") while colorful confetti rains down over the bewildered, unharmed opponent.",
    story_beat:
      "The only moment the failsafe FAILS to catch — his child-self briefly wins. Every player who unlocks this will remember it.",
  },
  showtime_fatality: null,     // Judgment lives on `finishers` above — Double Punch · Peek-a-boo
  director_notes: {
    concept:
      "\"Heavy-Artillery Mech / Trait-Based Brawler.\" The horror of Kid Crusable stems entirely from the dissonance between his 4-year-old cognitive fragments and his cold, hyper-lethal robotic chassis. Master limb management is his ceiling — throwing arms creates massive zoning windows but compromises his boxing normals until recall. Every optic-colour transition IS the story: crimson (compliance) → sentient glint (deviation) → vacant blue (post-failsafe scrub) → warm amber (child-self bleed-through).",
    visuals:
      "Polished robotic chassis. Red chassis-warning light housed on the front. Four-year-old form factor — childlike proportions on a heavy-metal frame. Rigid heavy-metal whirring juxtaposed with childish, erratic body language. Detachable arms whose retrieval / recall is core to his kit.",
    audio:
      "Combat: rigid heavy-metal servo whirs · high-voltage digital SNAP on failsafe scrub. Harmony: cheerful nursery-rhyme chimes + toddler giggle. Judgment: cheerful \"Peek-a-boo!\" over smoking exit wounds — do not soften.",
  },
};

// ─────────────────────────────────────────────────────────────
// ☀️🎯 SOLANA — The Dawn Hitman (canon Feb 2026)
// Anti-magic assassin · Single-target tactical striker
// Synergy partner: David (The Cook) · Combo ult branches on Vesper alignment
// Canon lock: /app/memory/SOLANA_DAWN_HITMAN_FULL_KIT_CANON_FEB2026.md
// ─────────────────────────────────────────────────────────────
const SOLANA = {
  id: "solana",
  canon_tier: "semi_hero",
  name: "Solana",
  archetype: "Anti-Magic Assassin · Tactical Striker · Contract Marker",
  tag: "The Dawn Hitman",
  icon: "☀️",
  accent: "yellow",
  is_main_hero: false,
  faction: "Anti-Vesper Contract Operator · David's synergy partner",
  origin: "Solana is no longer holding a heavy tower shield on the front line — she operates like a HIGH-STAKES TACTICAL HITMAN. Her design purpose is aggressive high-precision counter to Vesper's spell-copying and high-evasion mechanics. Singles out high-priority targets, marks them with solar crosshairs, and NEUTRALIZES their magic before they can vanish back into the shadows. When Vesper is an ally instead of a target, Solana converts into a lethal offensive support engine — flash-cooking David's dishes mid-flight with precision lasers.",
  tagline: "\"The contract is marked. Nowhere to hide now.\"",
  cognitive_status: "Full autonomy · precision-first · reads magical spellcasts as target-acquisition telemetry.",
  core_motivation: "Neutralize spell-copying threats before they escape. Contract-lockdown on high-priority mages.",
  stats: { Offense: 5, Defense: 3, "Speed / Mobility": 5, Difficulty: 4 },
  director_notes: {
    concept: "Aggressive high-precision counter to Vesper's spell-copying + high-evasion mechanics. Converts into a lethal offensive support engine when Vesper is an ALLY. Every skill is calibrated around single-target lockdown or party-wide amplification — never AoE crowd control.",
    visuals: "Sleek sharp HARD-LIGHT CONSTRUCTS. Instead of a bulky shield, she wields a light-blade or laser-focused arm guard. Targeting reticles pulse with ambient solar energy that SHIFTS COLORS (Red · Blue · Brown · Green) based on her Shifting Light passive element on odd turns.",
    audio: "Sharp SILENCED beam-discharge audio cue on her Solar Flare marks. Heavy dense SIZZLE during David's Deep Fryer Lockdown combo ultimate. Riposte stance = a soft click-in cue like a rifle bolt seating.",
    playstyle_note: "Turn 1: paint Vesper with Solar Flare so the team lands 100% of their attacks. Save Sunstone Barrier for Vesper's burst-window. If David hits 100% Energy AND Solana is alive → auto-branches into Deep Fryer Lockdown (vs Enemy Vesper) or Solar Banquet (with Ally Vesper / general PVE).",
  },
  unique_mechanic: {
    name: "Contract Mark · Illuminated Target Lockdown",
    description: "Solana's Solar Flare paints a target with the Illuminated status — CANNOT ENTER STEALTH, evasion LOCKED TO 0%, and any stolen/copied Blue Magic cast has its cooldown extended by +1 round. She converts high-evasion glass-cannons into deletable single-target obligations.",
  },
  mobility: [
    { name: "Dawn Step", description: "Short-range hard-light dash. Solana slides on a knife-edge beam of solar-charged air, closing the gap to place her marks before enemy mages can act." },
  ],
  specials: [
    {
      name: "Passive · Blinding Flare",
      input: "Auto-trigger on enemy Blue Magic cast",
      description: "Whenever an enemy casts a COPIED or STOLEN Blue Magic spell, Solana auto-fires a precision radiant counter-flash. Inflicts BLIND on the caster: target accuracy -30% for 1 turn. Non-stacking · re-triggers on EVERY Blue Magic cast.",
    },
    {
      name: "Passive · Shifting Light",
      input: "Auto-trigger start of every ODD turn (1, 3, 5, 7…)",
      description: "Elemental affinity randomly shifts between Fire · Water · Earth · Wind. On shift: adjacent allies gain a shield = 10% of Solana's Max HP. If an enemy hits Solana with a spell matching her active element → damage NEUTRALIZED (0 damage) AND converted into a shield FOR DAVID.",
    },
    {
      name: "Solar Flare · The Contract Mark",
      input: "Active Skill · Single Enemy · CD 2 turns",
      description: "Heavy hybrid Light damage. Applies ILLUMINATED for 2 turns: target cannot enter stealth, evasion locked to 0%, stolen-spell cooldowns extended by +1 round if cast while illuminated. THE canonical Turn-1 open against a Vesper.",
    },
    {
      name: "Sunstone Barrier · Precision Riposte",
      input: "Active Skill · Self + Adjacent Allies · CD 3 turns",
      description: "Gain a barrier = 15% of MISSING HP. Enter RIPOSTE STANCE for 1 turn — parries the NEXT incoming direct single-target attack aimed at Solana OR an adjacent ally. Takes 0 damage from that strike, then INSTANTLY counters with a laser-precision strike that STUNS the attacker for 1 turn.",
    },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Combo Ultimate · David Synergy (auto-branches on Vesper alignment)",
      input: "Requires David @ 100% Energy · Solana alive · game auto-checks Vesper team placement",
      style: "Branch A: Deep Fryer Lockdown (vs Enemy Vesper) · Branch B: Solar Banquet (Ally Vesper / general PVE)",
      description: "DEEP FRYER LOCKDOWN (vs Enemy Vesper): David slams a heavy cast-iron skillet of boiling grease over Vesper. Solana welds the lid shut with a focused laser beam — sealing the target's energy flow. Heavy Fire+Light damage. Applies CONGEALED MANA for 3 rounds (Vesper's Blue Magic slot FROZEN — cannot cast copied skills, cannot steal new ones, restricted to basic vanilla attacks · ABSOLUTE, cannot be cleansed). ═══ SOLAR BANQUET (with Ally Vesper / general PVE): David tosses high-grade ingredients into the air. Solana FLASH-COOKS the meal mid-flight with rapid precision lasers, showering the team in high-energy food. Heals entire party for 30% of David's Max HP. Grants SOLAR INFUSION for 3 rounds (+25% Phys & Mag damage · team-wide turn priority boost · basic attacks imbued with Solana's Light element, ignore 20% enemy phys def). ═══ MIRROR MATCH RULE (Ally Vesper AND Enemy Vesper): Deep Fryer Lockdown defaults to targeting the ENEMY Vesper — trapped in Congealed Mana; Ally Vesper unaffected, free to steal spells without competition.",
    },
  ],
  finishers: [],
  synergy_tip: "Turn-1 Solar Flare on the target Blue Mage — locks their evasion at 0% and cripples any stolen spell they try. Hold Sunstone Barrier for Vesper's known burst-window (or any known single-target ult). Coordinate David's Energy Bar: full Energy + Solana alive = auto Combo Ultimate branch.",
  showtime_slots: {
    slot_1: "Combo Ultimate · Deep Fryer Lockdown (David + Solana vs Enemy Vesper)",
    slot_2: "Combo Ultimate · Solar Banquet (David + Solana with Ally Vesper / general PVE)",
    friendship: "TBD — owner canon pending",
    _note: "Solana's endgame moves are David-synergy Combo Ultimates. She has no standalone Judgment/Harmony in Feb 2026 lock.",
  },
  cosmic_ultimate_weapon: {
    status: "tbd",
    note: "1:1 RPG carry-over pending — Solana's canonical Cosmic Weapon has not been dropped yet.",
  },
};
// "Good" alignment, questionable motives · anti-poaching crusade
// Predatory bloodlust for Gail — always takes the LAST bite.
// ─────────────────────────────────────────────────────────────
const ZEDIYA_KING = {
  id: "zediya_king",
  canon_tier: "semi_hero",
  name: "Zediya King",
  archetype: "Primal Apex · Technical Pressure · Transformation Combo",
  tag: "The Apex Sovereign",
  icon: "👑",
  accent: "amber",
  is_main_hero: false,
  faction: "\"Good\" alignment · Anti-Poaching Crusade · Predatory hunter of Gail",
  origin: "Yet another one of Gail's many experiments — forced through rigorous clandestine trials that sought to fuse human adaptability with primal, elemental animal traits. Zediya did NOT emerge as a broken subject — she emerged as an APEX PREDATOR. After breaking her chains she adopted the surname \"King\" as a testament to her dominance. She now fights on the side of good, though she remains strictly tight-lipped about her true underlying motives. Two things are absolute: her violent uncompromising crusade against poachers and animal cruelty, and her specific predatory bloodlust for Gail. When it comes to Gail, Zediya doesn't just want a victory — she ensures she always takes the LAST BITE.",
  tagline: "\"I always take the last bite.\"",
  cognitive_status: "Full autonomy · deliberately cryptic. Refuses to disclose her true endgame in Arcade mode / cutscenes; her real motives are a closely guarded secret.",
  core_motivation: "Apex-predator crusade against poachers, animal exploitation, and specifically Gail. Ultimate endgame — classified.",
  stats: { Offense: 5, Defense: 3, "Speed / Mobility": 4, Difficulty: 5 },
  director_notes: {
    concept: "Fluid but jarring. Human movement rapidly transitions into exaggerated animalistic postures — the fusion is unstable-looking on purpose. High-ceiling character rewarded for canceling her Penguin Combo into precise transformation-based punishes. Mastering the Chimera Assault timing-links unlocks her maximum damage.",
    visuals: "Feels fluid but jarring — animations shift RAPIDLY between human movement and exaggerated animalistic postures (snake slither into upright human strike into hippo mass-throw). Each transformation is a full-body silhouette shift, not just a limb swap.",
    audio: "The Devil's Tri-tone is CORE to her theme. In combat she is relatively QUIET — only emits guttural predator-like vocalizations during Specials and Ultimates. Silence in neutral, snarls in commitment.",
    narrative_mystery: "In Arcade mode / cutscenes she REFUSES to explain her ultimate goals. Dialogue is cryptic. She shows visceral open hostility toward opponents associated with poaching, animal exploitation, and specifically Gail.",
    technical_playstyle: "High-ceiling. Penguin Combo cancels → transformation-based punishes. Chimera Assault timing-links = max damage. Rewards the patient technical player.",
  },
  unique_mechanic: {
    name: "Primal Transformation Kit · All Specials Ground OR Air",
    description: "Every special move is executable BOTH grounded and airborne. Zediya can chain human strikes → animal transformations → aerial resets seamlessly. Six distinct animal forms (Hippo · Monkey · Snake · Bear · Bird · Skunk) each gated behind their own special input, all cancellable from her Penguin Combo string.",
  },
  mobility: [
    { name: "Skunk Dash", description: "Rapid elusive dash for movement or evasion. Ground or air." },
    { name: "Bird of Prey", description: "High-speed Raiden-style aerial dash impact — mobility that becomes offense on contact." },
  ],
  specials: [
    { name: "Penguin Combo",       input: "Basic string (L, L, L)",         description: "Rapid left/right hand slaps. Cancels/links into ANY special. The universal cancel-into-transformation opener." },
    { name: "Devil's Tri-tone",    input: "QCF + Punch (Small/Med/Heavy)",   description: "Very short-range projectile with 3 tiers: Small (fastest travel & recovery), Medium (balanced), Large (slowest, widest area). All tiers STUN on hit." },
    { name: "Hippo Throw",         input: "HCB + Kick",                      description: "Hippo transformation. Captures opponent + throws in OPPOSITE direction. Corner escape + repositioning tool." },
    { name: "Monkey Toss",         input: "F, D, DF + Kick",                 description: "Monkey transformation. Tail-grab + toss. Aerial-viable — great for chasing airborne opponents." },
    { name: "Snake Trip",          input: "D + Heavy Kick (hold)",           description: "Snake transformation. Low-profile sliding trip. Slips under high projectiles and pokes." },
    { name: "Disruption Bear Hug", input: "HCB + Heavy Punch · Disruption",  description: "Bear transformation. PAUSES the opponent's momentum + deals damage. Signature disruption tool that stops rhythm cold." },
    { name: "Bird of Prey",        input: "F, F + Punch (air OK)",           description: "High-speed Raiden-style aerial dash impact. Doubles as a mobility burst and closing tool." },
    { name: "Skunk Dash",          input: "B, B or F, F + Special",          description: "Rapid elusive dash. Movement or evasion. Ground or air." },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Chimera Assault",
      input: "QCF, QCF + Heavy (max meter · timing-linkable)",
      style: "Multi-form combo chain",
      description: "Freeze (hit-stun) → 4 RAPID CHOPS → air launch → FIRE FINISHER. Timing-sensitive; linkable. Mastering the frame-perfect links unlocks Zediya's peak damage output.",
    },
  ],
  finishers: [
    {
      name: "Showtime · Apex Predator's Showtime",
      tone: "Judgment — multi-stage cinematic",
      description: "THE HOOK: Zediya performs a sharp gesture that SILENCES game audio, creating a tense pause. ESCALATION: cycles through a flurry of transformations — Snake (pin) → Bear (crush) → Hippo (launch) → Monkey (aerial whip against stage boundaries). THE FINALE: shifts into Bird of Prey, executes a terminal dive, and drills the opponent into the ground causing a massive fiery crater.",
    },
    {
      name: "Showtime · Wild Primal Horror",
      tone: "Judgment — psychological / visceral",
      description: "THE HOWL: Zediya releases a piercing dominant howl that triggers a TOTAL BACKGROUND BLACKOUT. THE STALK: only glowing golden eyes appear, circling the opponent. Sound of snarling beasts + wet visceral ripping of flesh fills the audio. THE AFTERMATH: lights snap back on, revealing only scattered bones, blood, and shreds of skin.",
    },
    {
      name: "Harmony · Campfire Harmony",
      tone: "Non-lethal · nature-communion",
      description: "The arena transitions into a peaceful woodland night at a crackling fire. Zediya sits on a log and plays a HAUNTING RHYTHMIC MELODY on a Devil's Tri-tone. Dangerous beasts (Bear · Snake · Hippo · Raptor-bird) emerge from the shadows, subdued by the music. They settle calmly around the fire with her, highlighting her ABSOLUTE COMMAND over nature.",
    },
  ],
  synergy_tip: "Cancel Penguin Combo into a transformation-based punish. Read the opponent's neutral, then transform-cancel the exact animal form that counters. Save Chimera Assault meter for the timing-link string — that's where her damage ceiling lives.",
  showtime_slots: {
    slot_1: "Apex Predator's Showtime (Judgment — multi-form cinematic)",
    slot_2: "Wild Primal Horror (Judgment — psychological blackout · visceral)",
    friendship: "Campfire Harmony (non-lethal · beasts subdued by Devil's Tri-tone melody)",
    _note: "2 SHOWTIME = the two Judgment endings. Campfire Harmony is the bonus Harmony slot.",
  },
};
// Theatrical Misdirection Trickster · uses clones/afterimages
// ("Giggles") to phase through attacks + mix up tracking.
// Full kit AWAITING OWNER DROP — placeholder with archetype locked.
// Canon file: /app/memory/DISRUPTION_PLAYSTYLE_ARCHETYPE_CANON_FEB2026.md
// ─────────────────────────────────────────────────────────────
const BO_BO_CLOWN = {
  id: "bo_bo_clown",
  canon_tier: "semi_hero",
  name: "Bo-Bo the Clown",
  archetype: "Resource-Building Rushdown · Psychological Disruption · Mix-up / Meter-Generating",
  tag: "The Giggle Merchant",
  icon: "🤡",
  accent: "rose",
  is_main_hero: false,
  faction: "Independent · aligned with chaos and applause",
  origin: "One of the four archetype-defining exemplars of the Disruption playstyle in Dragonious Fighters. Designed to be the ultimate 'tilt' character — every mechanic rewards showing off mid-combo. He doesn't just deal physical damage; he exerts emotional pressure on the opponent. Sharp theatrical flair meets classic circus slapstick — fast, theatrical, utterly terrifying in the hands of a skilled player.",
  tagline: "\"You're not just losing... you're part of the act!\"  ·  \"It's not just a beatdown — it's a performance.\"",
  stats: {
    Offense: 4,
    Defense: 3,
    "Speed / Mobility": 5,
    Difficulty: 5,
  },
  unique_mechanic: {
    name: "Style On 'Em · Taunt Gauge · No-Reset Rule",
    description:
      "Passive Engine 'Style On 'Em': Bo-Bo cancels the recovery frames of heavy hits, launchers, or specific specials into Mini-Taunts (12–25 frames). Taunt Gauge capacity: 3 Full Bars (300 Max). Mid-Combo Taunt Cancel → +50 Gauge (high risk); Neutral Taunt → +25 Gauge (lower risk). NO-RESET RULE: missing or dropping a taunt does NOT reset or decay the gauge. Taking damage during a taunt grants the opponent Counter-Hit status but Bo-Bo keeps his stored meter. Meter is spent on Empowered Specials (Giggles, Drop-Kick Disrespect) & Air Ultimate (Atmospheric Highlight Reel).",
  },
  mobility: [
    {
      name: "Air-Combo Flowchart",
      description: "Ground Launcher → Super-Jump → Air-Hit String → Air Taunt (+50 Gauge) → branch to Drop-Kick Disrespect (1 Bar → Ground-Bounce) OR Atmospheric Highlight Reel (3 Bars → Air Super).",
    },
    {
      name: "Mid-Combo Taunt Cancels",
      description: "12–25 frame recovery-cancel windows on heavy hits, launchers, and specific specials. Master players thread taunts into pressure strings to bank meter without giving up momentum.",
    },
    {
      name: "Restand Pressure Loop",
      description: "'Look at This Guy!' forces a Restand instead of a knockdown — Bo-Bo keeps the opponent standing upright for extended tick-throw and mix-up strings.",
    },
  ],
  specials: [
    {
      name: "\"Look at This Guy!\" · Pimp Slap & Point",
      input: "QCF + Light Punch · 0 Meter · Basic Special",
      description:
        "Low-damage slap that leaves the opponent staggering. FORCES A RESTAND (leaves them standing upright rather than falling down) — allows combo extensions at tight execution windows. The primary tick-throw / mix-up threat on Bo-Bo's neutral game.",
    },
    {
      name: "\"Missed Me!\" · Dodge Taunt Counter",
      input: "Down-Back + Heavy · 0 Meter · Defensive Special",
      description:
        "Defensive stance — Bo-Bo leans back with hands behind his head. AUTO-DODGES incoming panic buttons and crumple-stuns the opponent on trigger. Excellent read on predictable reversal habits.",
    },
    {
      name: "Giggles · Disruption / Misdirection",
      input: "QCB + Punch · 1 Bar (100 Gauge) · FREE when cancelled from a mid-combo taunt",
      description:
        "Bo-Bo bursts into a mockingly loud laugh and fades out for 10–15 frames. Leaves behind an afterimage / decoy continuing his last action. If the opponent hits the decoy, it EXPLODES INTO CONFETTI — blinding and re-standing them — while the real Bo-Bo shifts BEHIND them. Director's note: use this mid-combo to phase completely through an opponent's Combo Breaker or Burst.",
    },
    {
      name: "Drop-Kick Disrespect · Aerial Special",
      input: "Air QCF + Kick · 1 Bar (100 Gauge)",
      description:
        "Mid-air diving double-kick. If performed AFTER an Air-Taunt, causes a Ground-Bounce that pops the opponent back into the air for extended juggles.",
    },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Atmospheric Highlight Reel",
      input: "Air QCF QCF + Heavy + Special · 3 Bars (300 Gauge · Full Meter · after Air-Taunt)",
      style: "Air grab → selfie freeze-frame → cinematic ground slam",
      description:
        "Executed directly out of an Air-Taunt. Bo-Bo grabs the opponent in mid-air, snaps a quick selfie, and drives them into the arena floor for massive cinematic damage. The selfie freeze-frame is intentional — it exists purely to disrespect the opponent while banking one more visual insult before the slam.",
    },
  ],
  finishers: [
    {
      name: "Standing Ovation · Showtime Judgment",
      tone: "🩸 BLOOD · gore-gated · grand opera house aesthetic",
      description:
        "SETTING THE STAGE: Dark background, single spotlight on the opponent. Bo-Bo bows as FOUR Giggles decoys in white gloves step out of the shadows. THE RIGGING: Decoys upper-cut the opponent 20 feet up; stage ropes drop; decoys tie the opponent's wrists and ankles like a puppet. THE PERFORMANCE: Bo-Bo tap-dances with an oversized gold cane while pulling the hoist ropes in rhythm to the music — SNAP 1: knee snaps backward, decoys hit a 'Ta-Da!' pose. SNAP 2: both shoulders dislocate with a sickening pop to the beat. CURTAIN CALL: Bo-Bo gives one final yank on the cane, tearing the opponent apart into blood and confetti. Heavy red velvet curtains drop, Bo-Bo steps through, catches a tossed rose, and bows. SCREEN TEXT: THANK YOU, GOODNIGHT! in neon Broadway lights.",
      mature: true,
      note: "High drama · high-tempo showtunes over brutal theatrical rigging. The rhythm of the snaps MUST land on the beat — the horror scales with the choreography.",
    },
    {
      name: "Giggle & Tear · Normal Judgment",
      tone: "🩸 BLOOD · fast · slapstick gore · disrespectful",
      description:
        "THE TEASE: Bo-Bo winks at the lens, jabs the opponent, dusts off his shoulder, and laughs. GIGGLES OVERLOAD: Three laughing copies encircle the victim — victim punches a decoy which bursts into glitter — a second decoy grabs them from behind in a chokehold. THE EXECUTION: Bo-Bo launches skyward, takes a mid-air selfie taunt, and uses Drop-Kick Disrespect straight onto their chest. THE IMPACT: The sheer force drives the victim's torso COMPLETELY DOWN THROUGH THEIR LEGS, collapsing them on the floor. OUTRO: Bo-Bo stands on the crushed body, takes a photo, gives a 'Shhh...' gesture, and walks off. SCREEN TEXT: STALEMATE! while a quiet mocking giggle echoes.",
      mature: true,
      note: "Fast, up-close, aggressive, and DISRESPECTFUL. Snappy camera angles and slapstick gore — the selfie mid-execution is the whole joke.",
    },
  ],
  showtime_friendship: {
    name: "The Halftime Show",
    input: "Back, Forward, Down + Heavy Attack (universal · at max distance)",
    tone: "Big-band brass · synchronized kick-line · victory selfie",
    description:
      "Lighting dims completely. Spotlight snap-tracks to center stage. Upbeat brass big-band music plays. Bo-Bo snaps his fingers; spotlights drop from above. He pulls out a golden cane and top hat, launching into a tap routine. He activates Giggles to spawn THREE holographic decoys. The decoys grab the defeated, utterly confused opponent by the hands, pulling them into a synchronized kick-line dance. Confetti explosions rain down as Bo-Bo holds up a smartphone, snapping a victory photo with the bewildered loser.",
    story_beat:
      "The harmony that will EMBARRASS the opponent's player forever. The victory selfie is the point — it lives in the tournament clip forever.",
  },
  showtime_fatality: null,     // Both fatalities live on `finishers` above — Standing Ovation (Showtime) + Giggle & Tear (Normal)
  director_notes: {
    concept:
      "\"The ultimate tilt character.\" Every mechanic rewards showing off mid-combo. Bo-Bo doesn't just deal physical damage — he exerts emotional pressure. The No-Reset Rule on the Taunt Gauge is his defining lever: Bo-Bo mains commit to mid-combo taunt cancels because there's zero downside if the input drops. Master players thread neutral pokes → Look-at-This-Guy Restand → Air-Combo → Air-Taunt → Atmospheric Highlight Reel for 3-bar cash-out at the tempo of a stand-up routine.",
    visuals:
      "Sharp theatrical flair meets classic circus slapstick. Golden cane + top hat during harmony. Four white-gloved Giggles decoys during the Standing Ovation judgment. Confetti explosions on decoy-hits. The smartphone selfie is his signature — it appears in every finisher.",
    audio:
      "Neutral: distant carnival calliope hum. Mid-combo taunt SFX: high-pitched theatrical laugh. Giggles fade-out: cartoon whoosh. Standing Ovation: dramatic high-tempo showtunes with synchronized snap-cues. Halftime Show: upbeat brass big-band. Giggle & Tear: quiet mocking giggle over Shhh gesture.",
  },
};

// ─────────────────────────────────────────────────────────────
// ✈️🎯 JAMES AIKEN — "The Tactical Commander" (canon Feb 2026)
// Realistic single-strike heavy-stagger · Priority threat isolation
// Design pillar: stoic pilot composure → unhinged "THE SOUP!" shout
// Canon lock: /app/memory/JAMES_AIKEN_TACTICAL_COMMANDER_CANON_FEB2026.md
// Hero spotlight highlight reel: james_aiken_s_charge.mp4
// ─────────────────────────────────────────────────────────────
const JAMES_AIKEN = {
  id: "james_aiken",
  canon_tier: "semi_hero",
  name: "James Aiken",
  archetype: "Tactical Support · Single-Target Executioner · Realistic Heavy Stagger",
  tag: "The Tactical Commander",
  icon: "✈️",
  accent: "sky",
  is_main_hero: false,
  faction: "Independent tactical operator · aviation-discipline background · fan favorite",
  origin: "James Aiken brings a grounded tactical presence to the Dragonious Fighters roster. Aviation-trained, stone-faced professional composure — until he explodes into an unhinged mid-fight shout about SOUP. His kit ditches mystical / hyper-agile martial-arts tropes for heavy, deliberate SINGLE STRIKES that stagger through guard. Locks the highest-HP threat, deploys precise area denial, and pivots the battlefield instantly with emergency protocols. The composed pilot who suddenly SCREAMS at his party is the fan-favorite hook that anchors him in the universe.",
  tagline: "\"One Day At A Time.\"",
  signature_voice_line: "\"THE SOUP!\"",
  cognitive_status: "Full autonomy · frame-perfect tactical calm · pattern-breaks into unhinged callout bursts under pressure.",
  core_motivation: "Isolate the priority threat. Shut it down. Command the battlefield with realistic heavy-strike discipline.",
  stats: { Offense: 4, Defense: 4, "Speed / Mobility": 2, Difficulty: 3 },
  director_notes: {
    concept: "Realistic Single Strike Heavy Stagger — every heavy attack lands with weight, slow recovery frames, and reads like a real martial commitment. No flashy dance-fighting. The tonal contrast is the whole show: stoic salute, sudden SOUP shout, stoic missile coordinates, sudden SOUP shout again.",
    visuals: "Aviator sunglasses · pilot uniform with gold-braided sleeves · tactical console interface for coordinates. Patriotic red/white/blue backdrop on Eagle-Eye salute. Emergency cockpit red lights flood the arena on Squawk 7700 with a tactical radar grid descending.",
    audio: "Neutral: quiet cockpit ambient hum + soft radio static. Eagle-Eye salute: crisp military snare snap. Homing Missile: distant jet-engine thrum → screaming descent whistle → heavy impact. The Soup: FULL-VOLUME UNHINGED SHOUT (no processing, no reverb — raw scream over the fight music). Squawk 7700: blaring emergency siren + cockpit warning tone.",
    playstyle_note: "Turn 1: cast Eagle-Eye to lock the highest-HP threat. Turn 2: stack Kicktire on the marked target to cripple their kit. Save The Soup for the target's known burst window (auto-mitigates their damage into your team). Cash in Squawk 7700 when the priority target still has a full turn — skip it, chain your team's uncontested burst.",
  },
  unique_mechanic: {
    name: "Precision Stagger · Realistic Heavy Guard-Break",
    description: "Heavy attacks deal 200% more guard/shield damage to individual targets in Arcade mode — utilising realistic recovery frames that reward precise timing. Missing a heavy leaves James wide open; landing one shatters the opponent's block.",
  },
  mobility: [
    { name: "Grounded Approach", description: "Deliberate two-step forward advance. No dashes, no dance-steps — James walks up like he's calling a shot before he takes it." },
  ],
  specials: [
    {
      name: "Passive · Precision Stagger",
      input: "Auto-applies to every Heavy Attack input",
      description: "Heavy attacks deal 200% more guard/shield damage to individual targets in Arcade mode. Recovery frames are longer than the average character — precise timing rewarded, mash-play punished.",
    },
    {
      name: "Passive · Command Structure",
      input: "Always active while James is on the roster",
      description: "All tactical callouts bypass standard ability cooldown penalties. Move database access reads as an INFORMATIONAL command-discovery tool — no mechanical lockout on his own kit. All moves fully accessible.",
    },
    {
      name: "Eagle-Eye · Priority Threat Lock",
      input: "Utility · Auto-Targets highest-HP enemy · CD 3 turns",
      description: "James calmly adjusts his sunglasses, straightens his pilot uniform, and delivers a crisp frame-perfect salute against a patriotic red/white/blue backdrop. Applies a permanent TARGETED debuff to the enemy with the highest HP pool — allied focus channelled onto that threat, with enhanced accuracy and damage bonuses against that specific target.",
    },
    {
      name: "Homing Missile · Console Coordinates",
      input: "Active Attack · Single Target · CD 2 turns",
      description: "James pauses mid-combat, checks his tactical aviation watch, and inputs coordinates into his console interface. Deploys a targeted missile strike from above — cuts straight through the battlefield to hit the designated target. GUARANTEED impact damage. Interrupts the enemy's current wind-up on connect.",
    },
    {
      name: "The Soup 🍲 · Party Barrier (UNHINGED SHOUT)",
      input: "Defensive Buff · Team-wide · CD 3 turns",
      description: "James breaks his stoic composure entirely, turns toward his team, and SCREAMS \"THE SOUP!\" at the top of his lungs. Encases the party in a heavy protective barrier providing massive damage mitigation — hyper-focused specifically on neutralising incoming attacks originating from the enemy marked by Eagle-Eye.",
    },
    {
      name: "Kicktire · Grounded Punish",
      input: "Special · Requires Eagle-Eye Target · CD 2 turns",
      description: "James delivers a powerful, grounded forward strike straight down at the opponent's base. Inflicts a devastating combination of NEGATIVE STATUS EFFECTS simultaneously on the Targeted enemy — cripples mobility and combat output instantly (canonical stack: -50% Speed · -30% Damage Out · Silence 1 turn · Bleed).",
    },
  ],
  showtime_specials: [],
  ultimates: [
    {
      name: "Traffic Control · Squawk 7700",
      input: "Emergency Ultimate · Full Meter · Requires Eagle-Eye Target Active",
      style: "Ultimate — battlefield-wide emergency lockdown cinematic",
      description: "Emergency cockpit red lights flood the arena. Blaring sirens echo. James enters the universal emergency transponder code 7700 into his console while a massive tactical radar grid descends over the stage. Battlefield locked into an ABSOLUTE EMERGENCY STANDSTILL. The Eagle-Eye TARGETED enemy skips their entire next turn. Secondary opponents are locked down (Rooted · cannot chain moves). James and his team gain ABSOLUTE PRIORITY to press the attack — uncontested burst window.",
    },
  ],
  finishers: [],
  synergy_tip: "Play James as the tempo-controller: Eagle-Eye Turn 1 → Kicktire Turn 2 → hold The Soup for the target's known burst window → cash Squawk 7700 when the target still has a full turn queued. Precision Stagger heavies work best with a bodyguard on team — his recovery frames are long, so keep him screened.",
  showtime_slots: {
    slot_1: "TBD — owner canon pending",
    slot_2: "TBD — owner canon pending",
    friendship: "TBD — owner canon pending",
    _note: "James Aiken's Showtime Special / Judgment / Harmony slots are author-pending. Precision Stagger heavies + The Soup shout are placeholder-worthy for future signature enders.",
  },
  cosmic_ultimate_weapon: {
    status: "tbd",
    note: "1:1 RPG carry-over pending — James Aiken's canonical Cosmic Weapon has not been dropped yet.",
  },
};

// ─────────────────────────────────────────────────────────────
// 🧬 FIGHTER SCHEMA NORMALIZER (Feb 2026 owner directive)
// ─────────────────────────────────────────────────────────────
// Every fighter object flows through `normalizeFighter()` before it
// hits the ROSTER. Missing narrative slots are filled with placeholder
// markers so the dossier UI can render a "🟡 CANON PENDING" chip for
// anything the owner hasn't dropped canon on yet.
//
// Scope (Roe's dossier is the reference implementation — see
// SOLANA_DAWN_HITMAN_FULL_KIT_CANON_FEB2026.md and Roe's config for the
// full shape):
//   • faction          · narrative faction
//   • origin           · deep bio / lore paragraph
//   • tagline          · signature quote
//   • cognitive_status · special mental / cerebral profile note
//   • core_motivation  · overarching character drive
//   • director_notes   · { concept · visuals · audio · tone · thematic_contrast · the_burden }
//   • dossier_image    · canonical character art URL
//   • cosmic_ultimate_weapon · 1:1 RPG carry-over weapon (see Cosmic Weapon block)
//   • battle_song      · hero-tied looping BGM (see Battle Song block)
//
// The normalizer NEVER overwrites existing owner canon. It only fills
// null/undefined slots. Deep character lore drops belong in the Cari
// lore book / ending doc — the normalizer is UI plumbing, not content.
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// 🏷️ CANON TIER RESOLVER (Feb 2026 owner directive · 3-tier system)
// ─────────────────────────────────────────────────────────────
// Every fighter belongs to ONE of three canon tiers:
//   • "main_hero" — Full RPG canon + Dragonious Fighters roster
//   • "semi_hero" — Partial RPG canon connection (side-story / semi-canon)
//   • "non_canon" — Arcade-only, no RPG canon presence
//
// Owner-set `canon_tier` on the config ALWAYS wins. Otherwise this
// resolver derives a safe default from existing markers:
//   • `scope` string starting with "Non-Canon"      → non_canon
//   • `scope` string starting with "Semi-Canon"     → semi_hero
//   • `is_main_hero === true` and no override       → main_hero
//   • Anything else (missing or ambiguous)          → main_hero
//     (safe default — owner will hand-tune per fighter one-by-one)
// ─────────────────────────────────────────────────────────────
const CANON_TIER_VALUES = new Set(["main_hero", "semi_hero", "non_canon"]);
function resolveCanonTier(f) {
  if (f && CANON_TIER_VALUES.has(f.canon_tier)) return f.canon_tier;
  const scope = (f && f.scope) ? String(f.scope).toLowerCase() : "";
  if (scope.startsWith("non-canon")) return "non_canon";
  if (scope.startsWith("semi-canon")) return "semi_hero";
  if (f && f.is_main_hero === true) return "main_hero";
  return "main_hero";
}
const CANON_TIER_META = {
  main_hero: {
    label: "Main Hero",
    short: "MAIN",
    icon: "✅",
    text: "text-emerald-300",
    border: "border-emerald-500/40",
    bg: "bg-emerald-900/30",
    note: "Full RPG canon · appears in main RPG AND Dragonious Fighters.",
  },
  semi_hero: {
    label: "Semi-Hero (RPG)",
    short: "SEMI",
    icon: "🔗",
    text: "text-violet-300",
    border: "border-violet-500/40",
    bg: "bg-violet-900/30",
    note: "Partial RPG canon connection · side-story or supporting-cast tie-in.",
  },
  non_canon: {
    label: "Non-Canon",
    short: "N-CANON",
    icon: "⚠️",
    text: "text-orange-300",
    border: "border-orange-500/40",
    bg: "bg-orange-900/30",
    note: "Dragonious Fighters exclusive · no RPG canon presence.",
  },
};

// ─────────────────────────────────────────────────────────────
function normalizeFighter(f) {
  const cp = {
    faction:           !f.faction,
    origin:            !f.origin,
    tagline:           !f.tagline,
    cognitive_status:  !f.cognitive_status,
    core_motivation:   !f.core_motivation,
    director_notes:    !f.director_notes,
    dossier_image:     !f.dossier_image,
    cosmic_weapon:     !(f.cosmic_ultimate_weapon && f.cosmic_ultimate_weapon.status && f.cosmic_ultimate_weapon.status !== "tbd"),
    battle_song:       !f.battle_song,
  };
  return {
    // ── narrative slots ────────────────────────────────────
    faction:           f.faction           ?? null,
    origin:            f.origin            ?? null,
    tagline:           f.tagline           ?? null,
    cognitive_status:  f.cognitive_status  ?? null,
    core_motivation:   f.core_motivation   ?? null,
    // ── director's notes block ─────────────────────────────
    director_notes:    f.director_notes    ?? null,
    // ── canon asset slots ──────────────────────────────────
    dossier_image:     f.dossier_image     ?? null,
    cosmic_ultimate_weapon: f.cosmic_ultimate_weapon ?? { status: "tbd", note: "Pending owner canon — 1-to-1 RPG weapon coming soon." },
    battle_song:       f.battle_song       ?? null,
    // ── kit slots (default to empty arrays so the UI can render uniform sections) ──
    mobility:          f.mobility          ?? [],
    specials:          f.specials          ?? [],
    showtime_specials: f.showtime_specials ?? [],
    ultimates:         f.ultimates         ?? (f.ultimate ? [f.ultimate] : []),
    finishers:         f.finishers         ?? [],
    // Spread original last so all owner-authored fields win over defaults.
    ...f,
    // ── canon tier resolution (applied AFTER spread so we always emit a tier) ──
    canon_tier:        resolveCanonTier(f),
    // Pending markers apply LAST so they always reflect the final resolved state.
    _canon_pending: cp,
  };
}

// ─────────────────────────────────────────────────────────────
// 🪙⚡ KEN (KOWALKOWSKI) — Kinetic Frame-Recovery / Disruption
// Feb 2026 owner canon (partial spec — kit expansion pending).
// See /app/memory/DRAGONIOUS_FIGHTERS_KEN_KOWALKOWSKI_CANON_FEB2026.md
// ─────────────────────────────────────────────────────────────
const KEN_KOWALKOWSKI = {
  id: "ken_kowalkowski",
  name: "Ken (Kowalkowski)",
  archetype: "Seasoned Kinetic Energy · Frame Recovery & Disruption Specialist",
  tag: "The Powder Keg",
  icon: "🪙",
  accent: "yellow",
  age: "60s",
  faction: "The Fallen Timeline · trying to prove sobriety in a new one",
  origin:
    "Former lover of David Lauer in a fallen timeline that Ken ruined through hard living. In THIS timeline, Ken is trying to prove — to David and to himself — that he's grown, sobered up, and become a stable, dependable man. His fighting style actively betrays him: kinetic energy is inherently unstable, requiring constant motion, friction, volatility, and discharge to control. Every strike, flash-cancel, and coin flip radiates the erratic, high-velocity energy of his past.",
  tagline: "\"It's just energy... I can handle it this time!\"",
  cognitive_status: "Sober intent, unstable execution. Hands visibly tremble with kinetic sparks whenever he tries to stand still.",
  core_motivation: "Prove to primary-timeline David Lauer that he has changed. The kinetic instability of his own power is his greatest obstacle.",
  stats: {
    Offense: 4,
    Defense: 2,
    "Speed / Mobility": 4,
    Difficulty: 5,
  },
  director_notes: {
    concept:
      "Ken FEELS like sobriety trying to hold. Brief clean frames punctuated by involuntary volatility. The lore is the code — every mechanic reinforces that he cannot be truly still without erupting.",
    playstyle:
      "Ride the Kinetic Battery aggressively — build meter through pressure, VENT it before the 5-second Overload timer eats you. Use Flash Cancels to erase endlag mid-string. The Coin Flip taunt is his signature disruption — high knockback with RNG payoff for the brave.",
  },
  unique_mechanic: {
    name: "Kinetic Battery + Instability Meter",
    description:
      "Ken's Kinetic Battery (0–100 %) builds through aggression and vents through Flash Cancels, Kinetic Shifts, and Special Moves. THE TWIST: if the Battery sits at 100 % for more than 5 seconds without expending, Kinetic Overload triggers — the unstable energy explodes outward, chip-damaging Ken, knocking back nearby opponents, and resetting his Battery to 0 %. The mechanical flaw forces constant venting — matching his inability to stay truly still.",
  },
  mobility: [
    { name: "Flash Cancel", description: "Spend Kinetic Battery to erase move endlag. Ken's core mid-string reset — buys the space to vent without losing pressure." },
    { name: "Kinetic Shift", description: "Short-range spark dash that discharges Battery on activation. Doubles as an emergency Overload vent." },
  ],
  specials: [
    {
      name: "Coin Flip (Taunt / Disruption)",
      input: "↓ ↓ + Punch / Taunt",
      description:
        "Ken flips a pocket coin. Startup creates an unblockable kinetic shockwave (High Knockback / Wall-Splat). RNG payoff — Heads = +50 % Battery gain + hitstun; Tails = free Flash Cancel + 25 % Battery refund.",
      frame_adv: "Unblockable / Wall-Splat",
      spec_status: "LIVE",
    },
    {
      name: "Kinetic Rising Arc",
      input: "→ ↓ ↘ + Punch",
      description:
        "Soaring upper-cut fueled by friction. Startup invincibility vs airborne attacks — snaps jump-ins out of the sky. Sharp lightning-strike audio snap on hit.",
      frame_adv: "−8 on block / Hard Knockdown on hit",
      spec_status: "LIVE",
      utility: "anti_air",
    },
    {
      name: "Vector Vault Grab",
      input: "→ ↘ ↓ ↙ ← + Punch",
      description:
        "Command grapple. Ken grabs the opponent by the neck with one hand (flask in the other), channels hyper-dense kinetic friction into their spine, slams them behind him.",
      frame_adv: "Unblockable / +2",
      spec_status: "LIVE",
      utility: "command_grapple",
    },
    {
      name: "Kinetic Pulse",
      input: "↓ ↘ → + Punch",
      description:
        "Fires a condensed wave of kinetic friction. Striking the mid-air Coin Flip coin ACCELERATES the projectile for a hard knockdown — reward for stacked reads.",
      frame_adv: "−2 on block / Knockdown on hit",
      spec_status: "LIVE",
      utility: "projectile",
    },
    {
      name: "Kinetic Vault",
      input: "↓ ↙ ← + Kick",
      description:
        "Launches forward on stored friction. Coin Flip Heads = faster flight speed; Tails = instant landing-frame cancel. Overhead.",
      frame_adv: "−4 on block / +6 on hit",
      spec_status: "LIVE",
      utility: "overhead",
    },
    {
      name: "Kinetic Shift",
      input: "↓ ↓ + Special",
      description:
        "Converts Kinetic Battery directly into Stamina Recovery and Guard Refresh. Doubles as an emergency Overload vent.",
      spec_status: "LIVE",
      utility: "resource_convert",
    },
    {
      name: "Flash Cancel",
      input: "Any Attack + Special (during recovery)",
      description:
        "Spends 25 % Kinetic Battery to instantly erase the recovery frames of any move, returning Ken to neutral. His signature 'he's already moving again' discipline.",
      frame_adv: "Reset to 0",
      cost: "25 % Kinetic Battery",
      spec_status: "LIVE",
      utility: "frame_recovery",
    },
  ],
  showtime_specials: [
    {
      name: "Origamic Folding · Showtime Fatality",
      input: "Showtime Trigger (Max Meter, opp ≤ 20 % HP)",
      description:
        "1) Ken flips his coin, catches it, pockets it without looking. 2) Pulls a crisp paper bill from his tailored jacket, holds it toward the camera. 3) Folds the bill step-by-step — a dense kinetic field locks onto the opponent, forcing limbs and spine to snap and twist in sync with the folds. 4) Pulls the coin back out, flicks it with hyper-velocity kinetic friction. 5) The coin slices through the opponent's neck, decapitating them, then arcs back like a boomerang into Ken's fingers as he sighs, adjusts his collar, and walks away.",
      spec_status: "LIVE",
    },
    {
      name: "Coin Magic & Drink Service · Showtime Friendship",
      input: "Friendship Trigger (Max Meter, held — no strike input)",
      description:
        "1) Ken snaps his fingers — kinetic energy vents as soft, glowing fireworks. 2) Sleight-of-hand coin routine multiplies the coin into FIVE glittering gold coins mid-air. 3) Pulls two glasses and a cocktail shaker from his jacket, mixes a pair of mocktails with flair, slides one down a floating kinetic bar into the opponent's hand. 4) Clinks glasses, sips, winks at the camera, flips the coin one last time as the match-end banner appears.",
      spec_status: "LIVE",
      utility: "non_lethal_finisher",
    },
  ],
  ultimates: [
    {
      name: "Friction Cascade · Level 1 Super",
      input: "↓ ↘ → ↓ ↘ → + Punch",
      cost: "50 % Kinetic Battery / 1 Bar",
      description:
        "Ken dashes forward at hyper-velocity. On hit, unleashes a lightning-fast flurry of kinetic strikes from every angle, trapping the opponent in a sphere of white-hot friction before detonating it with a flick of his coin.",
      spec_status: "LIVE",
      tier: "level_1",
    },
    {
      name: "Timeline Overdrive · Level 3 Super",
      input: "↓ ↙ ← ↓ ↙ ← + Two Punches",
      cost: "100 % Kinetic Battery / Full Gauge",
      description:
        "Ken unlocks his battery constraints entirely, letting unstable kinetic energy surge through his veins. Time slows in a localized perimeter as Ken delivers a cinematic 20-hit onslaught. Ends by pressing his palm against the opponent's chest and discharging a kinetic shockwave that shatters the camera glass.",
      spec_status: "LIVE",
      tier: "level_3",
      hits: 20,
    },
  ],
  finishers: [
    { name: "Origamic Folding", spec_status: "LIVE", note: "Showtime Fatality — see showtime_specials above." },
    { name: "Coin Magic & Drink Service", spec_status: "LIVE", note: "Showtime Friendship — non-lethal outro — see showtime_specials above." },
  ],
  match_dialogue: {
    vs_david_lauer: {
      intro: [
        { speaker: "Ken",         line: "Look at me, David. I'm steady now. I've got my life under control." },
        { speaker: "David Lauer", line: "Your hands are trembling with kinetic sparks, Ken. You're a walking powder keg." },
        { speaker: "Ken",         line: "It's just energy... I can handle it this time! I'll prove it to you!" },
      ],
      victory: { speaker: "Ken",         line: "(Wiping away kinetic sparks, hands shaking) See? Controlled... total control..." },
      loss:    { speaker: "David Lauer", line: "You haven't changed, Ken. You're still running on pure chaos." },
    },
  },
  canon_lock: "/app/memory/DRAGONIOUS_FIGHTERS_KEN_KOWALKOWSKI_CANON_FEB2026.md",
};


// ─────────────────────────────────────────────────────────────
// 💫🥊 DEEDRA MARIE — High-Mobility Anti-Meta Brawler
// Feb 2026 owner canon (full 7-special kit + narrative concept).
// See /app/memory/DRAGONIOUS_FIGHTERS_DEEDRA_MARIE_CANON_FEB2026.md
// ─────────────────────────────────────────────────────────────
const DEEDRA_MARIE = {
  id: "deedra_marie",
  name: "Deedra Marie",
  archetype: "High-Mobility Anti-Meta Brawler",
  tag: "The Outsider",
  icon: "💫",
  accent: "violet",
  faction: "Independent · outside the Dragonious core cast · self-made combatant",
  origin:
    "Deedra Marie is a completely independent outsider to the core Dragonious story. She enters the conflict purely on her own terms — driven by a personal desire to test her limits and a deep fascination with the bizarre, unnatural abilities wielded by the realm's heroes. She holds a specific intrigue for Gail, aiming to see how her grounded, high-mobility combat style fares against Gail's unique kit if they ever clash face-to-face.",
  tagline: "\"Let's see what you can really do.\"",
  cognitive_status: "Sharp, self-aware, evaluating. Reads opponents like a chess board — sizes them up before committing.",
  core_motivation: "Test her limits. Prove her grounded, high-mobility style holds up against magic, monsters, and meta. Special rivalry with Gail.",
  stats: {
    Offense: 4,
    Defense: 3,
    "Speed / Mobility": 5,
    Difficulty: 4,
  },
  director_notes: {
    concept:
      "Deedra shouldn't feel like a chosen one or a lore-heavy mystical entity. She's a self-made combatant entering a world of monsters and magic out of pure curiosity and self-validation. Frame her visual cues and intro animations around confidence, evaluation, and sizing up her opponent.",
    playstyle:
      "High-Mobility Anti-Meta Brawler. Between Phase to shut down zone-spam, Sliding Punch to clear low ground, and Backwards Cartwheel Kick to reset space, she is built to disrupt dominant playstyles and force opponents to play at her rhythm.",
  },
  unique_mechanic: {
    name: "Anti-Meta Toolkit",
    description:
      "Deedra's kit is deliberately built to counter dominant strategies. Phase reflects projectiles, Sliding Punch punches through high zoning, Backwards Cartwheel Kick creates reactive space, and Ground Stomp punishes over-extension with a stun state. She doesn't out-damage the meta — she disrupts it.",
  },
  mobility: [
    { name: "Sliding Punch", description: "Low-profile forward slide ending in a heavy punch. Ducks under high projectiles and high-standing strikes. Weighty punch impact — instant gap-closer into an opponent's space." },
    { name: "Knee Uppercut", description: "Forward-leaping 45-degree rising knee strike. Mid-range spatial closer and aerial intercepter — catches back-dashes and jumps at mid-range." },
    { name: "Backwards Cartwheel Kick", description: "Retreating cartwheel that creates natural separation while releasing a short-range energy projection mid-rotation. Defensive spacing + Disruption trigger." },
  ],
  specials: [
    {
      name: "Sliding Punch",
      input: "QCF + Punch",
      description: "Low-profile forward slide ending in a heavy punch. Hitboxes duck low immediately — clean-slides beneath high projectiles and high-standing strikes. Punchy, weight-heavy impact.",
      visual_reference: "Similar mechanics/feel to Jax's low slide.",
      spec_status: "LIVE",
    },
    {
      name: "Vertical Kick Upwards",
      input: "Down-Up + Kick",
      description: "Single, sharp upward kick. Fast startup, high vertical hitbox, minimal horizontal coverage. Launches opponents straight up for simple juggle states or hard knockdowns.",
      spec_status: "LIVE",
    },
    {
      name: "Backwards Cartwheel Kick",
      input: "QCB + Kick",
      description: "Retreating cartwheel kick that releases a short-range energy projection mid-rotation. Cartwheel provides built-in backward movement; projectile is short-range (not full-screen) — defensive wall / trap for aggressive push-ins. Triggers the universal Disruption system state on hit.",
      spec_status: "LIVE",
      utility: "disruption_trigger",
    },
    {
      name: "Ground Stomp",
      input: "DD + Kick",
      description: "Single-foot floor drive with a distinct rumble screen-shake. Inflicts a temporary stun state on hit — her core combo-enabler. Short recovery on hit lets her dash in for heavy punishes or mixups. Blockable low.",
      spec_status: "LIVE",
      utility: "stun_setup",
    },
    {
      name: "Spinning Upwards Kick",
      input: "QCF + Kick (multi-hit)",
      description: "Multi-hitting vertical spinning kick — reversal / multi-hit anti-air. Camera tracks her upward rotation with crisp multi-hit audio snaps. High startup priority but substantial recovery on block.",
      visual_reference: "Similar motion/rhythm to Kung Lao's spin.",
      spec_status: "LIVE",
    },
    {
      name: "Phase",
      input: "QCB + Punch (hold)",
      description: "Utility phase stance / pulse. Body briefly desaturates or flashes translucent during active frames. Projectiles that hit her during the window reflect back along the original trajectory — hard-counter to full-screen keepaway.",
      spec_status: "LIVE",
      utility: "projectile_reflect",
    },
    {
      name: "Knee Uppercut",
      input: "F, D, DF + Kick",
      description: "Forward-leaping 45-degree angled rising knee strike. Mid-range spatial closer and aerial intercepter — catches back-dashes and jump-outs at mid-range.",
      visual_reference: "Similar trajectory and force to Sagat's Tiger Knee.",
      spec_status: "LIVE",
    },
    // ── Command Normals ──
    {
      name: "Axe Drop (Overhead Kick)",
      input: "Forward + Heavy Kick",
      description: "Slow, high-arcing downward axe kick that breaks crouching guards. Completes her core mixup loop — opponents forced to duck her Sliding Punch or Ground Stomp get hit overhead by this.",
      spec_status: "LIVE",
      utility: "overhead_command_normal",
    },
    {
      name: "Low Leg Sweep",
      input: "Back + Heavy Kick",
      description: "Quick 360-degree crouching leg sweep for a safe low check.",
      spec_status: "LIVE",
      utility: "low_command_normal",
    },
    // ── Throws ──
    {
      name: "Leg Swap Takedown (Forward Throw)",
      input: "Forward + Throw",
      description: "Scissor-grabs the opponent's neck with her legs, flipping forward to slam them headfirst into the ground behind her. Switches sides and sets up a hard knockdown.",
      spec_status: "LIVE",
      utility: "forward_throw",
    },
    {
      name: "Vaulting Heel (Back Throw)",
      input: "Back + Throw",
      description: "Vaults directly over the opponent's shoulders and delivers a heel strike to the back of their head upon landing.",
      spec_status: "LIVE",
      utility: "back_throw",
    },
    // ── EX / Metered Extensions ──
    {
      name: "Sliding Vault Grab (EX Sliding Punch)",
      input: "Grab during a landed Sliding Punch",
      cost: "1 Bar",
      description: "Uses meter to grab the opponent's legs mid-slide, tripping them directly into a ground-and-pound follow-up.",
      spec_status: "LIVE",
      utility: "ex_extension",
    },
    {
      name: "Phase Shift Counter (EX Phase)",
      input: "QCB + Punch (hold) + Special",
      cost: "1 Bar",
      description: "Burning meter on Phase causes Deedra to completely vanish upon projectile contact and instantly teleport behind the opponent with a back-fist strike.",
      spec_status: "LIVE",
      utility: "ex_teleport_counter",
    },
  ],
  ultimates: [
    {
      name: "Curiosity Killed the Meta · Showtime Super",
      input: "Full-Meter Super (QCF + QCF + Two Punches)",
      cost: "Full Meter",
      description:
        "1) Armored Knee Uppercut launches the opponent. 2) Rapid Spinning Upwards Kick catches them mid-air, then Phase flashes Deedra above them. 3) High-altitude Ground Stomp straight into the floor creates a massive crater shockwave. 4) Deedra steps out of the crater, wipes her hands, gives a confident smirk while making mental notes.",
      spec_status: "LIVE",
      tier: "level_3",
      note: "Vs Gail — triggers unique victory line: 'Is that all your weird powers have to offer?'",
    },
  ],
  showtime_specials: [
    {
      name: "Gravitational Rupture · Showtime Fatality",
      input: "Showtime Trigger (Max Meter, opp ≤ 20 % HP)",
      description:
        "1) Phase & Lock — Deedra passes through the opponent's torso using Phase, leaving their body paralyzed in a glowing phase-locked state. 2) Dismantle — Knee Uppercut under the chin launches the upper torso, Spinning Upwards Kick severs the arms mid-air. 3) The Rupture — Deedra leaps high above them and lands a full-force Ground Stomp straight into their chest. 4) Impact crushes the opponent into the floor, shockwave shatters the body across the stage. Deedra lands gracefully, cold analytical nod.",
      spec_status: "LIVE",
    },
    {
      name: "Research & Development · Showtime Friendship",
      input: "Friendship Trigger (Max Meter, held — no strike input)",
      description:
        "1) Deedra puts on sleek safety goggles, pulls out a digital tablet and stylus. 2) Opponent sits up confused; Deedra politely gestures for them to stay still. 3) She walks around them measuring posture, tapping her chin, jotting notes. 4) Hands them a colorful sports drink and a binder labeled 'Combat Evaluation Report: A+'. 5) Enthusiastic thumbs-up as the FRIENDSHIP logo flashes over upbeat arcade music.",
      spec_status: "LIVE",
      utility: "non_lethal_finisher",
      note: "Vs Gail — she rapidly flips through extra pages of notes with an excited, fascinated expression.",
    },
  ],
  finishers: [
    { name: "Gravitational Rupture", spec_status: "LIVE", note: "Showtime Fatality — see showtime_specials above." },
    { name: "Research & Development", spec_status: "LIVE", note: "Showtime Friendship — non-lethal outro — see showtime_specials above." },
  ],
  match_dialogue: {
    vs_gail_bloom: {
      note: "Rivalry-tier — owner-drop pending. Deedra specifically enters the conflict intrigued by Gail's unique kit; give her distinct intro/outro dialogue and subtle fight-start animations against Gail.",
      spec_status: "OWNER_DROP_PENDING",
    },
  },
  canon_lock: "/app/memory/DRAGONIOUS_FIGHTERS_DEEDRA_MARIE_CANON_FEB2026.md",
};


// ─────────────────────────────────────────────────────────────
// 🎵 SYMPHONY — Extra-Dimensional Phase Zoner / Setplay Specialist
// Feb 18, 2026 owner canon. See /app/memory/SYMPHONY_CHARACTER_CANON_FEB2026.md
//
// ROSTER STATUS: Visible in the Codex but PLAYER-LOCKED — dossier is
// browsable, but the pick-screen will silhouette + gate him behind his
// (TBD) unlock trigger. Owner directive Feb 18 2026: "still working on his
// skills…" — additional moves/adjustments may follow.
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// 🎯 WIN-WIN · Alt-timeline Gail (Feb 2026 canon reclassification)
// Canon: /app/memory/WIN_WIN_ALT_TIMELINE_GAIL_CANON_FEB2026.md
// Backend: dragonious_fighters_bosses.py → WIN_WIN_FIGHTER
// She is NOT a boss — she is an alt-canon playable fighter, an
// alternative Gail from a different timeline. Amber accent per her
// canonical palette. Full kit sourced from the backend dossier.
// ─────────────────────────────────────────────────────────────
const WIN_WIN = {
  id: "win_win",
  name: "Win-Win",
  archetype: "Reality-Bending Slow Juggernaut · Zero-Windup Punisher",
  tag: "The Architect of Inevitable Outcomes",
  icon: "🎯",
  accent: "amber",
  faction: "Alt-timeline echo of Gail — parallel to the current Loop.",
  origin:
    "Win-Win exists outside the standard flow of linear time. In her timeline, Gail's path forked into an inevitability engine — every outgoing motion is already answered. Where other fighters strike, Win-Win moves with an agonizing, unbothered calm. The player's victory or defeat against her never results in a harsh game over; the ladder progresses regardless. To claim her for the roster, one must land a Fatality during her heavy recovery frames.",
  tagline: "\"The outcome was written the moment you moved.\"",
  cognitive_status: "Serene, deliberate. Her presence bends causality — attacks she performs land before their windup completes.",
  core_motivation: "Prove inevitability. Every fight is a demonstration that the outcome is fixed.",
  stats: {
    Offense: 4,
    Defense: 5,
    "Speed / Mobility": 2,
    Difficulty: 5,
  },
  director_notes: {
    concept:
      "A reality-bending mini-boss reclassified as a playable alt-canon fighter. Every visual must clearly telegraph the moment causality inverts — her strikes appear to land before the animation resolves. Distinct from Gail's canonical kit — same soul-anchor, different rules.",
    playstyle:
      "Anchor the pace. Zero-windup punishes on any opening the opponent leaves. Use Inescapable March to close distance without threat of interception. Bait heavy recovery windows only when confident — her defensive stance is punishing but her whiff-recovery is exploitable.",
  },
  unique_mechanic: {
    name: "Causality Echo",
    description:
      "Win-Win performs an outgoing motion — the impact arrives BEFORE the animation resolves. Zero-windup on all normals; specials trade windup for a locked hit-confirm window.",
  },
  mobility: [
    { name: "Inescapable March", description: "Slow forward walk — cannot be interrupted by standard chip damage or projectile stagger. Only true-damage or grabs stop her advance." },
  ],
  specials: [
    {
      name: "Causality Echo",
      input: "QCF + Light",
      description: "Fires a zero-windup projectile — the impact registers on the opponent before the throwing animation finishes.",
      utility: "zero_windup_pressure",
      spec_status: "LIVE",
    },
    {
      name: "Database-Locked Universal Kit Access",
      input: "→ + Heavy",
      description: "Draws from a fixed command list — every input resolves as a scripted, pre-authored outcome. Cannot miss its intended hit-zone.",
      utility: "hit_confirm_lock",
      spec_status: "LIVE",
    },
  ],
  showtime_specials: [
    {
      name: "Showtime Finisher · Universal Designation",
      input: "TBD",
      description: "🟡 Placeholder — Showtime cue sits at final health. Owner canon pending full kit drop.",
      flair: "Placeholder — owner canon incoming.",
      placeholder: true,
    },
  ],
  ultimates: [
    {
      name: "Causality Fatality",
      input: "↓ ↓ ↓ + Heavy + Special (Final Health)",
      cost: "Match-Ending",
      description: "Lands during Win-Win's own heavy recovery frames — the microsecond her reverse-causality attack whiffs. Successful landing UNLOCKS her from a hidden-boss into the playable roster.",
      spec_status: "LIVE",
      tier: "level_3",
    },
  ],
  finishers: [
    { name: "Causality Fatality", spec_status: "LIVE", note: "Turns Win-Win from a hidden alt-canon opponent into a playable fighter." },
  ],
  // ── Roster gate / canon flags ──
  // Alt-timeline canon — a parallel Gail. NOT a boss.
  // Amber accent + orange-tinted alt-canon N-CANON tier badge.
  is_alt_canon: true,
  timeline: "alt",
  mirror_of: "gail",
  scope: "alt-canon (parallel timeline Gail · playable via Causality Fatality unlock)",
  canon_lock: "/app/memory/WIN_WIN_ALT_TIMELINE_GAIL_CANON_FEB2026.md",
};


const SYMPHONY = {
  id: "symphony",
  name: "Symphony",
  archetype: "Phase Zoner / Setplay Specialist",
  tag: "The Duelist's Overture",
  icon: "🎵",
  accent: "cyan",
  faction: "Extra-dimensional — outside the Chronophage timeline",
  origin:
    "An extra-dimensional entity from outside the Chronophage timeline, known only by his name and acoustic frequency. Manipulates raw sound energy through cyan synth key-grids, treble-note traps, and vibrating frequency rings — uses NO physical instruments. Cyber-punk streetwear with a dark tech-hoodie, active digital graphic equalizer across the chest, illuminated pulse-conduit wiring, and glowing neon wristbands.",
  tagline: "\"Keep the rhythm — or lose it.\"",
  cognitive_status: "Fluid, rhythmic movement. When idle he subtly bounces to an internal pulse.",
  core_motivation: "Maintain tempo. Every dropped combo is a shattered Session — recover the beat or lose the fight.",
  stats: {
    Offense: 4,
    Defense: 3,
    "Speed / Mobility": 5,
    Difficulty: 5,
  },
  director_notes: {
    concept:
      "High-skill, high-risk phase zoner. Every visual must clearly distinguish physical acoustic strikes from temporal phase effects so players read range and invuln instantly.",
    playstyle:
      "Ramp the Session Gauge (L1→L5) by keeping combos and soundscapes unbroken — never drop tempo. Phase Beat sparingly (recovery is punishing on whiff). Layer Phase-Note Traps + Gridlock Pulse to force respect, then convert Session Level 5 into oppressive frame-advantage pressure and a rainbow-tinted +15% Overdrive Crescendo.",
  },
  unique_mechanic: {
    name: "Personal Harmony · Session Gauge",
    description:
      "Ramping Session Gauge (Levels 1–5). Attack power, chip damage, and frame advantage scale upward continuously as long as Symphony maintains an unbroken combo or keeps musical soundscapes active. Drop a combo, pause too long, or whiff — the Session SHATTERS back to Level 0 with a glass-breaking SFX + screen desaturation micro-flash.",
  },
  mobility: [
    { name: "Phase Beat", description: "Neutral Special. Tight 1-beat (3–6 frame) pass-through — Symphony shifts into a translucent soundwave line, phasing unharmed through incoming strikes or projectiles. Whiff recovery leaves him fully vulnerable. Successful phase = distinct acoustic chime = free counter-attack window." },
  ],
  specials: [
    {
      name: "Phase-Note Traps",
      input: "QCF + Light / Medium",
      description:
        "Snaps fingers to spawn floating iridescent energy notes in mid-air that detonate into concentrated bass frequency upon enemy contact — or on command detonation.",
      utility: "zoning_setplay",
      spec_status: "LIVE",
    },
    {
      name: "Synthetic Key-Cascade",
      input: "↓ → + Attack (or hold during Piano Stance)",
      description:
        "Manifests a glowing synth key-grid under his hands, firing a rapid machine-gun barrage of sharp light-notes across the screen.",
      utility: "high_pressure_stream",
      spec_status: "LIVE",
    },
    {
      name: "Gridlock Pulse",
      input: "QCF + Special",
      description:
        "Slams a palm into the floor, sending a buzzing acoustic pulse along the ground. On contact, FREEZES enemy inputs for 1–2 seconds. Distinct ground-crawl telegraph — high-level opponents can bait it.",
      utility: "ground_disruption",
      spec_status: "LIVE",
    },
    {
      name: "Phase-Shift Decoy",
      input: "↓ ← + Heavy",
      description:
        "Steps backward while leaving a shimmering holographic sound-afterimage. If the opponent hits the clone, it shatters into sonic shards and INSTANTLY freezes the attacker in place (fixed freeze duration regardless of airborne state).",
      utility: "counter_mind_game",
      spec_status: "LIVE",
    },
    {
      name: "Frequency Slash",
      input: "→ + Heavy",
      description:
        "Sweeps his arm in a wide arc, forming a razor-sharp blade of high-pitch sonic energy with massive physical reach.",
      utility: "melee_disjoint",
      spec_status: "LIVE",
    },
    {
      name: "Sub-Zero Drop",
      input: "↘ + Medium",
      description:
        "Low sweeping kick coated in low-frequency acoustic vibrations. Forces a hard sweep knockdown.",
      utility: "low_mix_up",
      spec_status: "LIVE",
    },
  ],
  showtime_specials: [
    {
      name: "Awaiting Owner Drop",
      input: "TBD",
      description:
        "🟡 Placeholder — Symphony's Showtime cue sits between his standard specials and Overdrive Crescendo. Owner still finalizing his kit (Feb 18 2026).",
      flair: "Placeholder — owner canon incoming.",
      placeholder: true,
    },
  ],
  ultimates: [
    {
      name: "Overdrive Crescendo",
      input: "↓ ↓ ↓ + Heavy + Special (3-Meter Super)",
      cost: "3 Meter",
      description:
        "Unleashes a full-screen sonic shockwave. On hit, locks the opponent inside an intense holographic equalizer cage that pummels them with synchronized rhythm detonations before exploding in a grand musical blast. Activated at SESSION LEVEL 5 — cinematic gains a rainbow-equalizer tint and total damage boosts by +15%.",
      spec_status: "LIVE",
      tier: "level_3",
    },
  ],
  finishers: [
    { name: "Overdrive Crescendo (Session 5)", spec_status: "LIVE", note: "Cinematic 3-meter super with rainbow-equalizer tint + 15% damage buff when triggered at Session Level 5." },
  ],
  // ── Roster gate ──
  // Symphony is visible in the Codex but PLAYER-LOCKED behind a TBD
  // unlock trigger. Backend already marks him `player_locked=True` /
  // `cpu_available=True` in dragonious_fighters_bosses.py.
  player_locked: true,
  cpu_available: true,
  future_playable: true,
  // Extra-dimensional — outside the Chronophage timeline. Marked non-canon
  // so the Codex renders the orange N-CANON tier badge.
  scope: "non-canon (extra-dimensional CPU-only WIP · unlock TBD)",
  wip: true,
  canon_lock: "/app/memory/SYMPHONY_CHARACTER_CANON_FEB2026.md",
};


const _RAW_ROSTER = [ROC, MIKO, DEANAH_MUKI, BRAM, CARI, DAVID, NATHANIEL, XYLA, GLITCH, STACY, GAGE, CASSANDRA, TRIG, YUKI, TORY_PRIME, WARREN_SPICER, CID_DISRUPTOR, WESLEY_BLANKS, ADON, BONN_Y, KAELIA_AND_KAE, ROE, VECTOR_VANCE, KID_CRUSABLE, CORRUPTION, ZEDIYA_KING, SOLANA, JAMES_AIKEN, BO_BO_CLOWN, KEN_KOWALKOWSKI, DEEDRA_MARIE, SYMPHONY, WIN_WIN];
// 🔒 Feb 2026 owner directive: THOMAS_MR_AETHER moved from the playable
// ROSTER to MINI_BOSSES. He'll be re-added to ROSTER when his TBD unlock
// progression milestone ships. His fighter-shape kit fields are preserved
// on the config for that future re-add.
const ROSTER = _RAW_ROSTER.map(normalizeFighter);

// ─────────────────────────────────────────────────────────────
// 🔒 GAIL BLOOM · Hidden until owner-defined unlock (Feb 2026 directive).
// Kept as `false` — the GAIL_BLOOM data constant + GailBloomVillainPanel
// remain in the codebase for author reference and future wiring.
// Flip to `true` when the unlock trigger ships.
// ─────────────────────────────────────────────────────────────
const GAIL_BLOOM_UNLOCKED = false;

// ─────────────────────────────────────────────────────────────
// 🗺️ CROSSOVER STAGES · SOFT-OPEN GATE (Feb 2026 owner directive)
// Owner locked three publicly-visible stages: The Library, Dave's Diner,
// and Castle Dragonious. All other stages stay hidden from the Codex
// gallery until their unlock triggers are authored. StagesGalleryPanel
// footer still surfaces how many additional stages are locked away.
// ─────────────────────────────────────────────────────────────
const OPEN_STAGE_IDS = new Set(["the_library", "daves_diner", "castle_dragonious"]);

// ─────────────────────────────────────────────────────────────
// 🔒 LOCK SYSTEM · Canon-locked per BLOOD_UNLOCK_CODE_AND_FIGHTER_LOCK_CANON_FEB2026.md
//
// TWO independent locks:
//   1. Hero Lock — every fighter except the starter roster is locked
//      until unlocked from the Dragonious Egg Nest.
//   2. Blood Unlock Code — a single one-time drop that unlocks ALL Blood
//      Fatalities + OmniMuscleality across the entire owned roster.
//
// These are data-level annotations. The UI (roster grid + kit dossier)
// reads them plus per-user unlock state to decide silhouette vs full render.
// Per-user unlock state comes from the backend (endpoint TBD — see canon §4).
// ─────────────────────────────────────────────────────────────

// Starter roster — free from day one (5 heroes covering core archetypes).
// Locked Feb 2026 owner directive: "Let there be 5 starting heros"
// Balanced pick: Grappler (Roc), Support Tinker (Miko), Brawler (Bram),
// Tank Gatekeeper (Cari), Rushdown Chef (David) — full archetypal coverage.
const STARTER_FIGHTER_IDS = new Set(["roc", "miko", "bram", "cari", "david"]);

// 🌳 Per-fighter → RPG-hero mapping for the Skill Tree link. Only heroes
// with an authored Mind/Body/Soul tree in `skill_tree_service.py` should
// be listed here. As you author more trees, add them below and the
// per-hero "🌳 Skill Tree" CTA appears on their card automatically.
// Cross-canon canon: /app/memory/DRAGONIOUS_UNIVERSE_CONTINUITY_CANON_FEB2026.md
const SKILL_TREE_HERO_MAP = {
  roc:               "roc",
  david:             "david",
  // Coming online as trees are authored:
  // miko:            "miko",
  // bram:            "bram",
  // cari:            "cari",
  // nathaniel:       "nathaniel",
  // xyla:            "xyla",
  // glitch:          "glitch",
  // wesley_blanks:   "wesley",
  // cassandra_ledger:"cassandra_ledger",
};

// Keywords in a finisher's `tone` or `description` that mark it as Blood-gated.
// Note: OmniMuscleality is *canonically bloodless* but is STILL Blood-gated as
// Roc's signature signature-finisher (canon §3.5). It's handled by exact name below.
const BLOOD_TONE_KEYWORDS = [
  "blood", "gore", "decapit", "dismember", "tear", "shred",
  "eviscer", "vaporiz", "detonation", "explosion", "combust",
  "fatality", "judgment", "brutal", "lethal", "kinetic detonation",
  "internal blood", "hollow", "torn", "splatter", "bleed",
];

// Exact finisher names that override the keyword scan — either
// force-gate (bloodless signature Fatalities) or force-allow
// (comedic/dust finishers that would otherwise trip the "Judgment" keyword).
const BLOOD_GATE_OVERRIDES = {
  // FORCE-GATED (bloodless signature Fatalities that still require Blood Code):
  "omnimuscleality": true,
  // FORCE-ALLOWED (Judgment-tone but canonically NON-blood — always playable):
  "the elastic puppet": false,        // Adon humiliation (spinner ragdoll)
  "mending manners": false,           // Cid Showtime 1 (dust-ball comedic)
  "off the clock": false,             // Wesley victory ender
  "time-out": false,                  // Wesley Harmony
  "proper pairing": false,            // Cid Harmony
  "street paint": false,              // Tory Prime Harmony
  "the vintage recipe book": false,   // Warren Spicer Harmony
  "the offering to the chronophage": false, // Universal Stage Judgment — bloodless (opponent unmade)
};

/** Returns true if the given fighter object is locked_by_default. */
function isFighterLockedByDefault(fighter) {
  return !STARTER_FIGHTER_IDS.has(fighter?.id);
}

/** Returns true if a given finisher entry is gated behind the Blood Unlock Code. */
function isFinisherBloodGated(finisher) {
  if (!finisher) return false;
  const name = (finisher.name || "").toLowerCase();
  // Check exact-name overrides first
  for (const [key, forced] of Object.entries(BLOOD_GATE_OVERRIDES)) {
    if (name.includes(key)) return forced;
  }
  // Friendships and Humiliations are ALWAYS non-blood by canon
  if (/friendship|harmony|humiliation/i.test(finisher.tone || "") || /friendship|harmony|humiliation/i.test(name)) {
    return false;
  }
  // Keyword scan across tone + description
  const hay = `${finisher.tone || ""} ${finisher.description || ""}`.toLowerCase();
  return BLOOD_TONE_KEYWORDS.some((kw) => hay.includes(kw));
}

// Attach lock metadata directly to each roster object so any consumer
// (roster grid, kit dossier, backend sync, tests) can read it uniformly.
ROSTER.forEach((f) => {
  f.locked_by_default = isFighterLockedByDefault(f);
  if (Array.isArray(f.finishers)) {
    f.finishers.forEach((fin) => {
      fin.blood_gated = isFinisherBloodGated(fin);
    });
  }
});

// ─────────────────────────────────────────────────────────────
// ⏳ THE CHRONOPHAGE (BOSS ECHO) — Non-Playable Fighting-Game-only Boss
// NOTE: This is NOT the real RPG-canon Chronophage (Rift Dungeon).
// It is an alt-canon Boss Echo that lives only in the Fighting-Game mode.
// The true Chronophage remains reserved for RPG canon (pending owner numerics).
// ─────────────────────────────────────────────────────────────
const CHRONOPHAGE = {
  id: "chronophage_echo",
  name: "The Chronophage",
  subtitle: "Boss Echo · Fighting-Game Manifestation",
  archetype: "Non-Playable Final Boss · Time Manipulator",
  tag: "The Hourglass Sovereign",
  icon: "⏳",
  scope_lock:
    "This is a Fighting-Game-mode Boss Echo — NOT the real RPG-canon Chronophage. The true Chronophage remains reserved for the Rift Dungeon endgame (pending owner numerics). Both entities exist in parallel canon layers.",
  weapon: {
    name: "The Chronal Staff (Hourglass Scepter)",
    description:
      "A heavy, ornate, double-ended staff. At the very center sits his core hourglass, protected by a cage of dark, swirling metal. Mid-range bludgeoning + temporal control.",
  },
  passive: {
    name: "Projectile Immunity",
    description:
      "Completely immune to ALL incoming projectile attacks. Fireballs, Muki Missiles, Pocket Tosses — nothing lands. Zoners must fight melee-only.",
  },
  hard_mode_rules: [
    {
      name: "The 1-on-1 Gauntlet",
      description:
        "Player must fight and win each 1-on-1 battle leading up to the Chronophage encounter. No skips.",
    },
    {
      name: "Hero Mimicry on Death",
      description:
        "If the player dies even once against the Chronophage, he unlocks the ability to USE and CHANGE INTO any hero from the prior 1-on-1 battles for the rematch. Every death makes the next attempt more brutal — he inherits the moveset of every hero the player has already beaten.",
    },
  ],
  boss_moves: [
    {
      name: "Temporal Freeze",
      description:
        "An actual time freeze. The opponent's color drains into a static sepia-toned gray, suspending them completely in time — even mid-air, gravity suspended. ~2s window — enough to set up a full combo.",
    },
    {
      name: "Chronal Echo (Freeze Clone)",
      description:
        "Leaves behind a shimmering clone of himself on the battlefield. If the opponent mistakenly touches or STRIKES the clone, it shatters and the opponent is immediately locked into the Temporal Freeze state. Often placed behind stunned opponents to catch panicked wake-up swings.",
    },
    {
      name: "Hourglass Strike (The Stun)",
      description:
        "Heavy overhead downward staff swing. When the staff end connects, a localized shockwave of temporal gravity slams into the opponent — they buckle to their knees, clutching their head, with golden hourglass sand swirling rapidly around them. Fully immobilized for a short window → perfect Chronal Echo placement or full combo starter.",
      role: "The reliable stun-setup enabler for his high-damage temporal combos.",
    },
    {
      name: "Hourglass Rewind (Heal State)",
      description:
        "If left unattacked, the Chronophage rewinds time using the hourglass in his staff to heal himself — HP regenerates visibly.",
      vulnerability:
        "He can be hit out of this animation. Successful interrupt forces him into a heavy Choke/Stun state, leaving him completely helpless and open to MASSIVE damage. Optimal risk-reward window for the player.",
    },
  ],
  combat_loop: [
    "Set up — drop a Chronal Echo mid-stage or behind the opponent.",
    "Land Stun — Hourglass Strike overhead swing buckles the opponent.",
    "Freeze + Combo — Temporal Freeze into full staff combo string.",
    "If losing tempo — retreat and attempt Hourglass Rewind heal.",
    "If interrupted — punished heavily via Choke/Stun state.",
  ],
};

// ─────────────────────────────────────────────────────────────
// 🍖 MINI-BOSSES — Non-canon rotating gauntlet obstacles.
// Openly listed (not spoiler-gated like the Chronophage Boss Echo).
// ─────────────────────────────────────────────────────────────
const GOURMAND_NELSON = {
  id: "gourmand_nelson",
  name: "Gourmand Nelson",
  epithet: "The Living Crucible",
  archetype: "Heavyweight Adaptive Kinetic Tank",
  icon: "🍖",
  accent: "orange",
  scope: "Non-Canon · Mini-Boss (Rotating Gauntlet Pool)",
  gimmick: {
    name: "Adaptive Kinetic Armor + Heat Gauge",
    description:
      "Reacts to HOW you fight him — spam the same attack type and his armor adapts, reducing that damage and charging his internal furnace. At max Heat, his strikes gain fire/burn damage and his armor glows red-hot.",
  },
  moves: [
    {
      name: "Kinetic Cushion (The Buffer)",
      description:
        "Braces his massive frame with a shimmering heat-shield across his chest. Unique PARRY — if the player strikes the shield, Nelson absorbs 100% of the damage and converts it into a massive Heat Gauge boost.",
      role: "Punishes predictable strike timing",
    },
    {
      name: "Arcane Boiler (The Vent)",
      description:
        "Stomps, opens the vents on his heavy armor, releases a violent 360° blast of scalding steam. Chip damage + knockback. Get-off-me reset.",
      role: "Interrupt tool for player pressure",
    },
    {
      name: "Gravitational Anchor (The Crush)",
      description:
        "Raises heavy glowing arms and slams them into the ground — stone floor cracks and ripples. Grounded players are crippled/slowed by the sudden gravity, vulnerable to a follow-up charge.",
      role: "Ground-check · forces air mobility",
    },
    {
      name: "Crucible Charge",
      description:
        "Lowers his head and charges forward like a freight train — sparks + molten residue trail behind. Slow-start but FULLY ARMORED — runs straight through projectiles and light attacks to slam the player into the corner.",
      role: "Zoner punish + corner carry",
    },
  ],
  ultimate: {
    name: "Maximum Density",
    description:
      "Activates his gravity core. Stage darkens as gravity heavily increases, forcing the player into a crouched state. Nelson walks forward, grabs them by the throat with a glowing molten hand, lifts them. His chest furnace vents open completely — releases a massive point-blank explosion of pure kinetic fire and superheated steam directly into the player, blowing them across the arena while he stands unmoved, laughing deeply.",
  },
  humiliation: {
    name: "The Feast",
    description:
      "As the player collapses, Nelson completely loses interest. Snaps his fingers — an underling runs on carrying a massive roasted golden beast-leg on a silver platter. Nelson grabs the meat, takes a massive messy bite, tosses the bone directly onto the knocked-out player's chest, and lets out a loud booming belch. Announcer booms: \"HUMILIATION!\"",
  },
  combat_loop: [
    "Read — soak a few hits to sample attack types.",
    "Adapt — armor threshold flips for the dominant damage type.",
    "Parry (Kinetic Cushion) when player commits to a heavy attack.",
    "Vent (Arcane Boiler) the moment player closes short range.",
    "Anchor (Gravitational Anchor) when player is grounded and stationary.",
    "Charge (Crucible Charge) when player is far and zoning.",
    "At max Heat — burning normals + eventual Maximum Density ultimate.",
  ],
};

const MINI_BOSSES = [GOURMAND_NELSON, THOMAS_MR_AETHER];

// ═══════════════════════ 🎬 SPECIAL BOSS CINEMATIC — Obsidian Keep Siege ═══════════════════════
// Owner-provided cinematic (Feb 2026). Plays before ALL special boss encounters
// (Chronophage · Dio the Dual-Shadow · Kia Masterson). Manual-play controls —
// no autoplay to avoid audio surprises. Flip `autoplay: true` on <BossCinematic>
// if the user wants it running muted in the background.
const SPECIAL_BOSS_CINEMATIC = {
  title: "The Obsidian Keep Siege",
  tagline: "The war-drum cinematic. Every special boss enters through the siege.",
  src: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/mbf1qapq_obsidian_keep_siege.mp4",
};


// ─────────────────────────────────────────────────────────────
// 🧃 KIA MASTERSON — Non-Canon Boss · Juice Brawler / Pressure Boss
// ⚠️ NON-CANON — has her own agenda. Not part of any main-storyline
// gauntlet. Feb 2026 owner drop.
// Canon file: /app/memory/KIA_MASTERSON_BOSS_DOSSIER_FEB2026.md
// ─────────────────────────────────────────────────────────────
const KIA_MASTERSON = {
  id: "kia_masterson",
  name: "Kia Masterson",
  subtitle: "The Kick-Start Juice Brawler",
  archetype: "High-Mobility Juice Brawler / Pressure Boss",
  tag: "The Kick-Start",
  icon: "🧃",
  image: null,   // 📸 Official Kia art — drop CDN URL when ready. Reference: neon "JUICE BOX BRAWL" alley, dual-tone 10-pip gauge, "KICK-START JUICE" canteen.
  visual_signature: {
    hair: "Multicolor ombre — natural brown roots blending into vibrant green and neon blue ends",
    outfit: "White zip-track jacket · black crop top · slate grey track pants with green side-stripes · white sneakers",
    signature_prop: "The 'KICK-START JUICE' canteen — always visible in her lead hand",
    stage_tag: "Juice Box Brawl (neon graffiti alley) — features 'THE GREENEST GLITCH' wall tag as canon easter egg (visual nod to Glitch)",
  },
  status: "NON-CANON BOSS · Has her own agenda (Feb 2026)",
  personality: "Energetic · erratic · relentlessly aggressive. Fights with chaotic momentum, constantly fueling her abilities through an integrated juice-pip system.",
  unique_mechanic: {
    name: "The 10-Pip Juice Meter",
    description: "Max 10 pips shared between Vegetable Juice (auto-evade dodges) and Fruit Juice (sustained speed buff / rushdown). Opponents who BLOCK her strings feed her 1 pip per block executed — turtling is punished.",
    pip_types: [
      { name: "Vegetable Juice Pips", role: "Defensive · Auto-evade", detail: "High % chance to auto-trigger evasive dodges. Keeps Kia safe from incoming pressure." },
      { name: "Fruit Juice Pips",     role: "Offensive · Speed buff", detail: "Grants a sustained speed buff. Turns her into a high-speed rushdown threat." },
    ],
    regen_rule: "Opponents who successfully BLOCK her attack strings feed her meter — 1 pip per block. Turtle = feed the boss.",
  },
  frenzy_state: {
    name: "Special Healthy Clutch · Overcharge Frenzy",
    trigger: "Resource pool crosses the 10-pip maximum.",
    rules: [
      "1 pip auto-drains every 1 second until the meter returns to zero.",
      "Peak intensity — zero regard for resource conservation.",
      "Shadow Self: a phantom shadow attacks alongside her, dealing INDEPENDENT damage if its hitboxes land.",
    ],
    burnout_penalty: "The exact moment her meter hits ZERO after frenzy, Kia suffers a 2-second stun — completely vulnerable to heavy counter-attacks. The one guaranteed punish window.",
  },
  specials: [
    { name: "Special Ice Tea", cost: "5 Pips", effect: "High-commitment utility. Drops half her current stock. FREEZES the opponent solid on connect — halts their momentum and grants Kia an absolute reset window." },
    { name: "Disruption",      cost: "7 Pips (net 6 — refunds 1)", effect: "Instantly pays 7 pips to shatter the opponent's rhythm or break neutral pressure. Immediately refunds 1 pip to maintain flow." },
    { name: "Rolling Attack",  cost: "ALL 10 Pips", effect: "Absolute all-or-nothing gambit. Kia rolls into a ball, drains every remaining pip, unleashes a brutal high-damage pinball assault. RISK: even if the attack completely misses, all pips are consumed — leaving her dry and open." },
  ],
  combat_behavior: {
    endless_pressure: "NO traditional combo enders. Kia does NOT voluntarily halt her offensive strings or yield her turn. The player MUST actively interrupt, parry, or poke their way out.",
    psychological_loop: [
      { player_action: "Turtle / Block", kia_response: "Feeds her pips → builds toward Freeze (Ice Tea) or Rushdown (Fruit) state." },
      { player_action: "Attack / Pressure", kia_response: "Risks dropping strings into her evasive Vegetable dodges or high-speed Fruit buffs." },
    ],
  },
  visuals: {
    identity: "Scrappy street brawler aesthetic. Track gear. Kinetic juice-spray trails tracking her movement.",
    signature_prop: "The 'KICK-START JUICE' canteen — wielded mid-combat.",
    ui: "Prominent dual-toned 10-pip bottle gauge tracking active Vegetable and Fruit reserves in real time.",
  },
  design_philosophy: "Kia is a PACING PUZZLE. Every match is engineered around resource awareness — respect the 5-pip Ice Tea threshold, respect the 7-pip Disruption threshold, and prepare to survive the terrifying 10-pip Frenzy state. The Burnout Penalty is the one guaranteed punish window.",
};


// ═══════════════════════ 🌀 DISRUPTION — Universal Combo-Breaker Mechanic ═══════════════════════
// Owner-locked Feb 2026. Full canon in /app/memory/DRAGONIOUS_DISRUPTION_CANON_FEB2026.md
const DISRUPTION_MECHANIC = {
  id: "disruption",
  name: "Disruption",
  chant: "D-D-D-D-D-D-D-D-D-D-D-DISRUPTION!!!",
  tagline: "Universal combo-breaker. The ultimate psychological deterrent. Turns every offensive combo into a high-risk gamble.",
  rules: {
    cost: "Exactly 50% of a Level 1 Ultimate Gauge",
    activation_window: "Can only be triggered while the player is currently taking damage inside an opponent's combo string.",
    un_defendable: "Cannot be blocked, parried, or avoided. Once triggered, the opponent is forcefully interrupted and locked into a brief, vulnerable stagger state.",
    guarantee: "The player who triggers Disruption is 100% guaranteed the opportunity to connect their own follow-up attacks, converting the momentum shift into their own punish combo.",
  },
  disruption_war: {
    tagline: "Because Disruption creates a vulnerability state, it opens the door for a Disruption Chain.",
    sequence: [
      "Player A combos Player B.",
      "Player B spends 50% meter to Disrupt and begins their own combo.",
      "Player A spends 50% meter to Counter-Disrupt and takes back the turn.",
    ],
    hard_cap: "Every Disruption costs 50% of a Lv 1 Ultimate Gauge — exchanges are naturally self-limiting. The loser of the exchange is effectively 'bankrupt' on meter and highly vulnerable for the rest of the round.",
  },
  hero_profiles: [
    { hero: "Glitch",     disruption_name: "Code Injection",     effect: "Instantly de-syncs into static, reappearing at the attacker's back for an immediate juggle start." },
    { hero: "Dio",        disruption_name: "Eclipse Intercept",  effect: "Unleashes a dual-color pulse that freezes the attacker in mid-air, allowing for an immediate follow-up." },
    { hero: "Trig",       disruption_name: "Counter-Intercept",  effect: "Delivers a brutal, frame-one pistol whip that stuns the opponent at point-blank range for a pressure-point combo." },
    { hero: "Bram",       disruption_name: "Shatter-Stagger",    effect: "Hardens armor with an energy burst, causing a micro-stagger on the attacker that allows for a heavy physical counter." },
    { hero: "Cari",       disruption_name: "Feral Counter",      effect: "Slides under the opponent's hitbox, leaving them vulnerable to a high-speed, back-stab combo string." },
    { hero: "Nelson",     disruption_name: "Iron Catch",         effect: "Uses raw mass to tank the hit, physically catching the opponent's weapon/fist to lock them stationary for a heavy punish." },
    { hero: "David",      disruption_name: "Vapor Veil",         effect: "Detonates the vapor from an ongoing Steam-Step around himself, staggering the attacker in blinding steam for a free rushdown reset." },
    { hero: "Nathaniel",  disruption_name: "Bucket Drop",        effect: "Slams his bucket down instantly — the opponent's head is caught in the rim and face-plants for a long Ground Bounce combo-starter." },
    { hero: "Xyla",       disruption_name: "Velocity Break",     effect: "Emits a violent localized shockwave that staggers the attacker AND resets Xyla to neutral with 50% Flow — max-speed reset." },
    { hero: "Stacy",      disruption_name: "Frame-Snap",         effect: "Emergency Freeze-Frame mid-combo — chain-wraps the attacker into a micro-stun window for an immediate counter-combo." },
    { hero: "Gage",       disruption_name: "Cannonball Slam",     effect: "Slams the heavy cannon barrel into the ground — localized seismic tremor interrupts the opponent's combo and HARD-LAUNCHES them into a juggle state for artillery follow-ups." },
    { hero: "Cassandra",  disruption_name: "Chain-Snap Break",    effect: "Cracks the tether like a whip mid-combo — shockwave snaps the attacker into stagger and Ledger auto-reintegrates. Clean defensive escape to Integrated neutral." },
    { hero: "Tory Prime", disruption_name: "Pink Smog Disruption", effect: "Shatters a vial mid-combo, releasing a dense vivid pink smog. Offensive cloud pushes/pulls opponents caught inside; defensive cloud lets Tory vanish and instantly teleport to a new position. Cloud is the opponent's clear readability tell." },
    { hero: "Warren Spicer", disruption_name: "Serving Tray Disruption", effect: "Combo breaker tied to the Caloric Gauge. Instantly breaks the opponent's combo mid-string, resets neutral state, and creates the exact frame advantage Warren needs to initiate his own sequence. Tray wind-up is deliberately distinct from his parry and neutral reset — readability is a hard rule." },
    { hero: "Kia Masterson", disruption_name: "Juice-Pip Disruption", effect: "Non-canon boss ability. Pays 7 pips to shatter the opponent's rhythm or break neutral pressure — 1 pip refunded on connect to maintain her flow. Net cost 6 pips. Respect her 7-pip threshold at all times." },
  ],
  strategic_impact: [
    { title: "Meter as Life Insurance", detail: "The Ultimate Gauge is no longer just for damage — it is a defensive lifeline. Players must constantly weigh the cost of cashing out a Disruption against saving meter for a game-ending Ultimate." },
    { title: "Respecting the Gauge",    detail: "Attackers must monitor their opponent's meter. If an opponent has 50% gauge, the attacker must weigh the risk of committing to long combo strings that leave them open to a turn-steal." },
  ],
};

// ─────────────────────────────────────────────────────────────
// 🌗 DIO THE DUAL-SHADOW — Secret Boss (NON-CANON · Feb 2026)
// Hidden riddle-gated encounter. Not playable. Not part of any
// canonical Dragonious arc yet. Owner drop, spec-only preview.
// Canon file: /app/memory/DIO_DUAL_SHADOW_NON_CANON_FEB2026.md
// ─────────────────────────────────────────────────────────────
const DIO = {
  id: "dio_dual_shadow",
  name: "Dio the Dual-Shadow",
  subtitle: "Hidden Secret Boss · Two-Spirit Trickster · Anti-Mechanic Force",
  archetype: "Anti-Mechanic / Rule-Breaker / Anti-Zoner",
  tag: "The Yin-Yang Walker",
  icon: "🌗",
  image: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/4zalqntg_1784419433475.png",   // 🎨 Official Dio in the Silent Nexus (Feb 2026 owner drop)
  status: "NON-CANON · Spec Only (Feb 2026)",
  // Full 6-line prophecy (owner canon Feb 2026)
  prophecy: [
    "LIGHT OF THE SUN 🌞, DARK OF THE MOON, to reach me",
    "DARK SUN — RED MOON...",
    "THE FLOWING OF BLOOD FREEZES TIME",
    "HEALING IS FOR THE WEAK",
    "FIND MY LAIR AND YOU CAN FIGHT ME",
    "SWEET DEATH AWAITS YOU IN THE SHADOWS",
  ],
  encounter_gates: [
    {
      name: "The No-Healing Gate",
      line: "HEALING IS FOR THE WEAK",
      description:
        "If the player uses ANY healing ability, potion, item, or regen effect at ANY point during the run leading up to the final arena, the Silent Nexus stays sealed. Dio does not manifest. Single-run flag `run.healing_used` must remain untouched.",
    },
    {
      name: "The Blood Trigger",
      line: "THE FLOWING OF BLOOD FREEZES TIME",
      description:
        "Dio manifests directly out of the screen when the player reaches the critical low-health threshold — one hit from defeat. Instead of a K.O., Dio steps out of the shadows. Highest tension moment possible.",
    },
  ],
  lair: {
    name: "The Silent Nexus",
    tagline: "A surreal, ethereal void where time and reality have been suspended.",
    palette: "Deep ominous crimsons · obsidian blacks · stark reflective marble whites",
    elements: [
      { name: "The Celestial Eclipse", detail: "Dark Sun (black disk with jagged dark flame corona) + Red Moon (crescent, blood-red hue). Cosmic anchors set on a distant parallax layer." },
      { name: "Floating Geometry",    detail: "Massive fragmented chunks of dark stone and marble columns drift lazily through the air. Gravity-defying architecture." },
      { name: "The Lair Floor",        detail: "Perfectly still dark liquid surface. Near-perfect real-time mirror of the sky. Ripples distort the eclipse reflection on movement / jumps." },
      { name: "Sigils & Runes",        detail: "Faint glowing blue-white runes in a semi-circular mid-ground arc. Pulse to the tempo of the stage's music." },
    ],
    lighting: "Cold. Distant non-ambient light sources cast sharp high-contrast shadows on the players. Even the red moon does not warm the air.",
    particles: "Fine dark mist / shadow-smoke lingers on the liquid floor and reacts dynamically to character footfalls.",
    tech: [
      "Dynamic real-time reflection (characters + floating debris + celestial bodies) on the liquid floor",
      "Parallax layer for the Dark Sun + Red Moon to preserve the sense of immense scale",
      "Post-processing: slight character-model desaturation to blend with the in-between aesthetic",
    ],
    map_visibility: "NEVER shown on any map. Manifests only when the riddle conditions are met during a live match.",
  },
  weapon: {
    name: "Dual-Colored Shurikens",
    description:
      "Twin sets of shurikens spinning in a blur of light and dark energy — silver-white and obsidian black orbit each hand in perfect balance.",
  },
  aesthetic:
    "Seamless blur of radiant silver-white and absolute obsidian black. Leaves NO footprint — only alternating trails of starlight and shadow. Ethereal ribbons of both energies intertwine in perfect harmony during idle stances.",
  moves: [
    {
      name: "The Dual-Shadow Sidestep",
      role: "Passive · Anti-Projectile",
      description:
        "Never blocks. Frame-perfectly phases out of reality, leaving a silver-white Light Shadow illusion behind. Projectiles pass through the mirage while the real Dio blurs forward.",
    },
    {
      name: "Flowing Reversal",
      role: "Close-Range Counter Throw",
      description:
        "High-risk, high-reward counter for button-mashers. Slips behind the attacker, siphons their kinetic energy to force a knockdown. Leaves them fully vulnerable.",
    },
    {
      name: "Shadow Dash (Scorpion Teleport)",
      role: "Keep-Away Punisher",
      description:
        "If the opponent runs, zones, or plays defensively, Dio dissolves into dual-color smoke and teleports directly behind them — striking from the blind spot to break guard.",
    },
    {
      name: "Ermac-Style Launching Uppercut",
      role: "Combo Starter",
      description:
        "Dio materializes beneath the opponent's chin. Explosive uppercut with a spiraling column of interlocked black + white shadows erupts skyward. Follows with rapid-fire dual-colored shurikens for a helpless mid-air juggle.",
    },
    {
      name: "Spirit Trim Debuffs",
      role: "Passive · Dual Aura",
      description:
        "☀️ Light Trim (silver-white): invalidates specific opponent attacks — zero damage, no register. 🌑 Dark Trim (obsidian black): wraps the opponent, completely freezing their ultimate/super gauge. No comeback mechanics allowed.",
    },
  ],
  rulebreaker_notes: [
    "SHUTS DOWN zoners (Dual-Shadow Sidestep negates all projectiles).",
    "SHUTS DOWN meter-stackers (Dark Trim freezes super gauge).",
    "SHUTS DOWN defensive campers (Shadow Dash teleport punishes).",
    "SHUTS DOWN healers (No-Healing Gate — the Lair door stays sealed).",
    "Only pure fundamentals, timing, and adaptation work against Dio.",
  ],
  dialogue: [
    { trigger: "entrance",             line: "You bleed. Time listens." },
    { trigger: "mid-fight",            line: "You've never truly lost. Until now." },
    { trigger: "victory over player",  line: "Sweet death. Return to balance." },
    { trigger: "rare (survive ≥ 90s)", line: "Interesting. The scales tilt." },
  ],
  design_lock:
    "Two-Spirit balance visualized as constantly co-existing light + dark shadow trails flowing like liquid silk. NO hard state-swap animations. On entrance: atmospheric inversion — color invert, UI flicker, music mute, damage-color swap. The player has entered the Shadow Realm.",
};


// ─────────────────────────────────────────────────────────────
// 📖 GAIL BLOOM — "The Architect" · Unplayable Villain
// Adoptive mother of the Chronophage. Omnipresent auditor. Never
// listed in the playable roster. Manifests as a hidden boss encounter
// only when the player's fight quality meets her "audit threshold".
// ─────────────────────────────────────────────────────────────
const GAIL_BLOOM = {
  id: "gail_bloom",
  name: "Gail Bloom",
  archetype: "The Martial Auditor · Unplayable Villain",
  tag: "The Architect",
  icon: "📖",
  accent: "slate",
  identity: "Adoptive mother of the Chronophage.",
  narrative_position: "A constant canonical presence who observes the game's events. UNKNOWN to the player character. An omnipresent force who views the entire game world as a 'draft' that needs constant correction.",
  design_goal: "Gail is the ultimate test of FUNDAMENTALS. She is designed to be the player's worst match-up.",
  ai_philosophy: "She never panics. Her aggression is clinical. She does not care about balancing the fight — she cares about enforcing her standard of excellence.",
  no_gauge: "Gail does NOT have an internal energy gauge. She uses Mana Drain to fuel her Over Burst corrections.",
  passive: {
    name: "Sight-Unseen",
    mechanic: "Automatically detects any 'incorrect' or 'lazy' projectile (user-fired) within her domain.",
    effect: "She refracts and REFLECTS the projectile back at the source with increased velocity. She does not 'block' — she simply corrects the trajectory.",
  },
  actives: [
    {
      name: "3-Tier Hand-Sickles",
      mechanic: "A rhythm-based, 3-stage projectile volley requiring ZERO mana.",
      tiers: [
        { name: "Tier 1 · Opener",     detail: "Fast, low-commitment strike to force movement." },
        { name: "Tier 2 · Pressure",   detail: "Curved, wide-angle toss to restrict lateral space." },
        { name: "Tier 3 · Punishment", detail: "Heavy sickle that TRACKS the player's landing or dodge point." },
      ],
      ultimate_erosion: "Each successful hit from any tier erodes 5% of the player's Ultimate Gauge.",
    },
    {
      name: "Mana Drain",
      mechanic: "A close-range 'frame trap' command strike.",
      effect: "Siphons player Mana to fuel Gail's Mana Over Burst capabilities. She is hyper-aggressive in seeking this contact to keep the player 'hollowed out'.",
    },
  ],
  over_burst: {
    tagline: "Mana Over Burst · Resource Consumption",
    description: "Gail burns her siphoned mana to systematically dismantle the player's combat options. She forces the player to play THE ARCHITECT'S WAY.",
    levels: [
      { level: "Level 1", cost: "1 Full Gauge",  restrictions: "Block Disabled",                             duration: "5 seconds"  },
      { level: "Level 2", cost: "2 Full Gauges", restrictions: "Block + Special Skills / Moves Disabled",    duration: "8 seconds"  },
      { level: "Level 3", cost: "3 Full Gauges", restrictions: "Only Normal Moves Allowed",                  duration: "10 seconds" },
    ],
    fail_state: "If Gail spends her mana on a Lv 3 Over Burst and FAILS to finish the player, she does not become weak — she simply continues her martial assault. Over Burst is a strategic choice to force pure-fundamentals play, not a suicide button.",
  },
  lore: "Gail views the events of Dragonious as a flawed manuscript. She is not evil in the traditional sense — she is a perfectionist who believes that if the combatants (including her own 'son', the Chronophage) cannot reach absolute perfection, they do not deserve to exist in the narrative. She audits the fight, strips away the player's defensive crutches and special powers, to see if they can survive on raw base-level skill alone.",
  status_gate: "🔒 Never listed anywhere on menus. Manifests when the player's combat form triggers her audit threshold.",
};


// ─────────────────────────────────────────────────────────────
// 🌌 ASTRAEA — Hidden Stage-Force · Non-Playable · Non-Boss
// Faction: Chronophage. Woven into specific Chronophage-controlled
// stages. She never enters a combat screen — she manipulates the
// physics of the ACTIVE match with 13-constellation astronomical
// phase shifts.
// ─────────────────────────────────────────────────────────────
const ASTRAEA = {
  id: "astraea",
  name: "Astraea",
  archetype: "Hidden Stage Dynamic · Timeline Interrupter · Story Event",
  tag: "The 13-Constellation Auditor",
  icon: "🌌",
  accent: "cyan",
  faction: "Chronophage · Evil Alignment",
  status: "Hidden · Secret Boss (Pre-Unlock) → Playable Roster (Post-Unlock)",
  design_lock: "Astraea's Layer-1 stage-force role (secret_stage manifestation + cinematic intervention) is her non-combat presence. Her Layer-3 boss encounter (see hidden_boss_voice_profile) IS a real fight — if the player defeats her under the secret encounter conditions, she permanently unlocks as a playable roster character.",
  secret_stage: {
    name: "The Corrupted Star Chart",
    trigger: "If a player fulfills secret high-level conditions during a standard match on her stage (e.g., executing a specific number of successful Disruptions, letting the round timer run down to a specific number), the environment radically shifts.",
    manifestation: "Stage background distorts. Sky turns pitch black. Astraea's towering, ash-coated vanguard armor materializes in the distance, coldly watching the match.",
    constellation_grid: "Once manifested, her presence actively alters the physics of the match currently taking place. A massive glowing violet map of the 13 Constellations appears beneath the active fighters.",
  },
  stage_anomalies: [
    { name: "Libra's Gravity Shift",       effect: "Scales of Libra flash in the sky. For 10 seconds, both players experience MICRO-GRAVITY. Juggles last longer. Jump arcs float. Gravity-dependent combo timing completely changes." },
    { name: "Taurus Fracture",             effect: "Taurus flares with unstable purple light, triggering sudden localized eruptions of dark volcanic ash and stardust blasts upward from the floor. Random hazards players must navigate mid-combo." },
    { name: "Ophiuchus Timeline Delay",    effect: "The 13th sign activates. Both players' meter gain is COMPLETELY FROZEN for a short duration as the Chronophage actively consumes the temporal energy of the battle." },
    { name: "Scorpius Stinger Surge",      effect: "Scorpius strikes across the background, poisoning the outer edges of the stage arena with creeping venom lines. Fighters standing too close to the screen boundaries slowly drain health." },
    { name: "Gemini Paradox Pulse",        effect: "The constellation twins fracture the stage background into dual mirror dimensions. For a brief window, inputs are MIRRORED or DELAYED depending on which side of the arena the fighters occupy." },
    { name: "Leo Solar Flare",             effect: "Leo ignites the upper atmosphere of the stage, bathing the combatants in intense stellar heat. Slightly increases active-frame BURN damage on special moves." },
    { name: "Virgo Crystallization",       effect: "Sharp crystalline formations sprout from the stage floor in specific patterns. Destructible environmental obstacles that can block projectiles or disrupt corner pressure." },
    { name: "Aries Ram Charge",            effect: "A phantom shockwave shaped like the Ram surges horizontally across the stage mid-round. Players must time their jumps or defensive guards to avoid a hard knockdown." },
    { name: "Cancer Tide Well",            effect: "Lunar energies pull a shimmering pool of celestial fluid across the center of the stage. Slightly slows movement speed for any ground-based dashes or foot-planting recovery frames." },
    { name: "Sagittarius Archer Mark",     effect: "A glowing celestial crosshair locks onto the active player with the LOWER health pool. Fires a swift energy arrow from above that causes a minor stagger if unevaded." },
    { name: "Capricorn Apex Scaling",      effect: "The rugged summit of Capricorn manifests beneath the fighters, tilting the stage flooring at a slight angle. Alters sliding momentum and footing traction." },
    { name: "Aquarius Urn Torrent",        effect: "A cascade of cosmic water pours down in alternating vertical columns, washing away active projectile items on the ground and creating slick patches that alter sliding recovery." },
    { name: "Pisces Dual Current",         effect: "The twin fish swim through the lower boundaries of the stage, creating a sweeping tidal wave displacement that pushes both fighters toward the center arena." },
  ],
  cinematic_intervention: "In story mode / arcade endings, Astraea functions as the ultimate hidden executioner for the Chronophage. When a playable character thinks they have won their battle, a secret cutscene can trigger where Astraea steps from the shadows of a fractured timeline. WITHOUT entering a combat screen, she uses the absolute authority of the 13 constellations to effortlessly paralyze the victor in starlight chains — shattering their timeline and altering the course of the story before vanishing back into the void.",
  dual_kit_canon: "🌌🌀 Astraea has TWO canon kits: Full Kit (13-constellation purity) and Anomaly Kit (13 dynamic stage anomalies). See /app/memory/ASTRAEA_DUAL_KIT_RETRO_LOCK_FEB2026.md and ASTRAEA_ANOMALY_KIT_CANON_FEB2026.md.",
  hidden_boss_voice_profile: {
    _rule: "Hidden Secret Boss encounter. If defeated, Astraea UNLOCKS as a playable roster character with this exact kit. Layer 3 canon — see /app/memory/ASTRAEA_HIDDEN_BOSS_VOICE_PROFILE_CANON_FEB2026.md · living draft.",
    archetype: "Cosmic Tyrant / Reality-Warping Heavy",
    unlock_reward: "Defeating Astraea in her secret boss encounter PERMANENTLY unlocks her as a playable roster character. Same passive, same three signature specials, same disruption, same two ultimates, same Showtime Judgment.",
    status_pre_unlock: "Hidden · Non-Playable · Secret Boss",
    status_post_unlock: "Hidden · Playable (roster reveal) · Formerly Secret Boss",
    secret_encounter: {
      conditions: [
        "Complete the game on the highest difficulty",
        "Achieve a specific number of Perfect rounds (count TBD)",
        "Finish the final standard boss with a Level 3 Ultimate",
      ],
      onset: "Screen glitches. Sky turns pitch black. Astraea descends in a cloud of burning stellar ash to challenge the player to a single, brutal, DOUBLE-DAMAGE round. Real combat — real health bars — real win condition.",
    },
    passive: "Absolute Event Horizon — permanent hyper-armor vs light physical + projectiles (no stagger). Close-range passive gravity field slows the player's forward movement the closer they stand to her ash-armor.",
    meter_note: "Her boss AI generates meter TWICE as fast as standard characters.",
    boss_specials: [
      { name: "Ophiuchus Void-Serpent",     effect: "Slams her vanguard shield down. Screen-filling ash+stardust serpent charges horizontally — half the screen height. Deletes player projectiles. Devastating chip if blocked. Forces hard knockdown." },
      { name: "Sagittarius Judgment Storm", effect: "Snap of fingers → cosmic bow-lines rain high-speed starlight arrows across the entire arena floor. Relentless full-screen zoning — perfect-block or get chipped to pieces." },
      { name: "Taurus Ruination",           effect: "Cloaked in the burning violet silhouette of Taurus, executes an instantaneous screen-clearing charging shield smash. COMPLETELY UNBLOCKABLE. Wall-bounces the player across the entire stage; her AI immediately buffers a follow-up combo." },
    ],
    disruption: {
      name: "Stellar Collapse",
      cost: "50% Level 1 Gauge",
      effect: "Triggered when the player cracks her hyper-armor and launches a combo. Scorpio constellation flashes in an unstable explosion of dark energy. Player is instantly, violently repelled and locked into a crushing gravity stagger. Astraea recovers instantly with massive frame advantage.",
    },
    boss_ultimates: [
      { name: "Cataclysm of the Dead Stars", description: "Setup: shield driven into arena center; sky pitch black; Leo and Aries bleed crimson. Action: torrential storm of dying black-hole meteors rains down, auto-tracking the player. Finish: Astraea leaps to the stratosphere coated in Capricorn's full weight and drops as a planet-killer missile, shattering the entire stage interface." },
      { name: "Libra's Absolute Erasure",    description: "Setup: the scales of Libra manifest, tilting violently as the player is lifted by dark gravity lines. Action: screen warps into a shifting timeline grid — all 13 constellations twist and fracture as the Chronophage actively consumes the player's timeline. Finish: Astraea channels the corrupted zodiac into her gauntlet and PUNCHES straight through the center of the timeline chart. Blinding void-stardust beam obliterates the opponent's space on screen." },
    ],
    showtime_fatality: {
      name: "Erased From the Chart",
      description: "Cold malicious silence. Astraea raises a single gauntlet; the stardust trailing from her armor wraps the opponent like heavy iron chains. Their body shatters like brittle glass; their timeline fragments dissolve into the void. Their existence is scrubbed clean, leaving only a dark, dead star on the background constellation chart. Astraea turns her back — shield glowing sinister violet — as the timeline collapses into darkness.",
      when_it_plays: "As the loss cinematic when Astraea defeats the player in her secret boss encounter. Post-unlock, becomes her canonical Showtime Judgment on any opponent she defeats in normal play — as boss AI or as player-controlled Astraea.",
      correction_note: "Reclassified from Humiliation → Showtime Judgment per Feb 2026 owner correction.",
    },
    living_draft: "Owner canon-note: 'will have to keep adding/subtracting over time to get it right ✅'. Numbers, additional constellation moves, and encounter-flow details will be tuned iteratively.",
    rpg_counterpart_lock: "🔗 1-to-1 with Astraea's RPG counterpart (Feb 2026 owner lock). This entire hidden_boss_voice_profile is SHARED CANON between the Dragonious Fighters arcade and the RPG main game. Any tuning here must mirror the RPG version and vice versa. Do not diverge the two versions. The unlock-if-beaten rule applies in BOTH modes.",
  },
};




const ACCENT_MAP = {
  amber:   { text: "text-amber-300",   border: "border-amber-500/40",   bg: "bg-amber-500/10",   btn: "bg-amber-500 hover:bg-amber-400 text-black" },
  emerald: { text: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/10", btn: "bg-emerald-500 hover:bg-emerald-400 text-black" },
  pink:    { text: "text-pink-300",    border: "border-pink-500/40",    bg: "bg-pink-500/10",    btn: "bg-pink-500 hover:bg-pink-400 text-black" },
  slate:   { text: "text-slate-200",   border: "border-slate-500/40",   bg: "bg-slate-500/10",   btn: "bg-slate-400 hover:bg-slate-300 text-black" },
  cyan:    { text: "text-cyan-300",    border: "border-cyan-500/40",    bg: "bg-cyan-500/10",    btn: "bg-cyan-400 hover:bg-cyan-300 text-black" },
  orange:  { text: "text-orange-300",  border: "border-orange-500/40",  bg: "bg-orange-500/10",  btn: "bg-orange-500 hover:bg-orange-400 text-black" },
  fuchsia: { text: "text-fuchsia-300", border: "border-fuchsia-500/40", bg: "bg-fuchsia-500/10", btn: "bg-fuchsia-500 hover:bg-fuchsia-400 text-black" },
  violet:  { text: "text-violet-300",  border: "border-violet-500/40",  bg: "bg-violet-500/10",  btn: "bg-violet-500 hover:bg-violet-400 text-black" },
  lime:    { text: "text-lime-300",    border: "border-lime-500/40",    bg: "bg-lime-500/10",    btn: "bg-lime-500 hover:bg-lime-400 text-black" },
  sky:     { text: "text-sky-300",     border: "border-sky-500/40",     bg: "bg-sky-500/10",     btn: "bg-sky-500 hover:bg-sky-400 text-black" },
  rose:    { text: "text-rose-300",    border: "border-rose-500/40",    bg: "bg-rose-500/10",    btn: "bg-rose-500 hover:bg-rose-400 text-black" },
  indigo:  { text: "text-indigo-300",  border: "border-indigo-500/40",  bg: "bg-indigo-500/10",  btn: "bg-indigo-500 hover:bg-indigo-400 text-black" },
  teal:    { text: "text-teal-300",    border: "border-teal-500/40",    bg: "bg-teal-500/10",    btn: "bg-teal-500 hover:bg-teal-400 text-black" },
  red:     { text: "text-red-300",     border: "border-red-500/40",     bg: "bg-red-500/10",     btn: "bg-red-500 hover:bg-red-400 text-black" },
  yellow:  { text: "text-yellow-300",  border: "border-yellow-500/40",  bg: "bg-yellow-500/10",  btn: "bg-yellow-500 hover:bg-yellow-400 text-black" },
  purple:  { text: "text-purple-300",  border: "border-purple-500/40",  bg: "bg-purple-500/10",  btn: "bg-purple-500 hover:bg-purple-400 text-black" },
  neutral: { text: "text-neutral-300", border: "border-neutral-600/40", bg: "bg-neutral-500/10", btn: "bg-neutral-500 hover:bg-neutral-400 text-black" },
};


// ═══════════════════════ 🗺️ CROSSOVER STAGE ROSTER ═══════════════════════
// Placeholder stage specs (Feb 2026). Owner-locked lore + spatial details;
// 3D visuals + hazard mechanics ship later. Canon in
// /app/memory/CROSSOVER_STAGES_CANON_FEB2026.md.
const CROSSOVER_STAGES = [
  {
    id: "the_library",
    name: "Cari's Athenaeum",
    icon: "📚",
    accent: "amber",
    hero_tie: "Cari / War Librarian",
    tagline: "Towering wooden shelves, brass-railed ladders, dust in the afternoon sun.",
    setting: "A classic, expansive library featuring rows of towering wooden bookshelves that stretch toward the ceiling.",
    layout: "Long, quiet aisles interspersed with reading nooks featuring heavy velvet chairs and solid oak study tables.",
    elements: [
      { name: "The Card Catalog", detail: "Vintage multi-drawer wooden cabinet near the main entrance. Placeholder hazard: opens a random buff drawer mid-fight." },
      { name: "The Rolling Ladder", detail: "Brass-railed sliding ladder attached to the tallest shelving units. Placeholder hazard: climbable · vertical KO hazard." },
      { name: "Study Carrels", detail: "Private partitioned desks with green-shaded banker's lamps. Placeholder mechanic: temporary silence / cover zone." },
      { name: "The Circulation Desk", detail: "Large polished-wood counter — central hub of the space. Placeholder role: higher-ground platform." },
      { name: "Grandma Cari's Archives (Basement)", detail: "🔐 Where the REAL secrets are. Hidden trapdoor beneath the Circulation Desk descends into Cari's family record vault — her sealed personal archive. ('Grandma Cari' is Cari herself · same character · matriarch title.) Placeholder role: unlockable/gated sub-arena · surfaces canon lore drops post-fight." },
    ],
    aesthetic: "Warm amber banker's-lamp glow + dust motes in shafts of afternoon sun.",
    ambience: "Distant page turns · a soft whispered “shhh” on stage load · slow analog clock tick.",
    status: "placeholder",   // 3D + hazard logic pending
  },
  {
    id: "daves_diner",
    name: "Phoenix Flame Diner",
    icon: "🐦‍🔥",
    accent: "pink",
    hero_tie: "David Lauer",
    tagline: "Checkerboard floor, chrome fixtures, and the smell of the grill on entrance.",
    setting: "A classic, old-fashioned diner.",
    layout: "Traditional booths, tables, and chairs arranged throughout the dining area. Open kitchen design with a service window visible from the floor.",
    elements: [
      { name: "The Service Window", detail: "Open kitchen view — handwritten sticky notes listing orders attached to the pass. Placeholder mechanic: randomized “order” objective grants meter to whoever completes it first." },
      { name: "Record Player", detail: "Vintage analog player in the corner. Placeholder hazard: triggers a random needle-drop track (mood-shifter)." },
      { name: "Milkshake Machine", detail: "Chrome-finish fixture consistent with the diner's aesthetic. Placeholder hazard: throwable / slippery-floor projectile." },
      { name: "Booths + Counter Stools", detail: "Destructible cover. Placeholder role: breakable environment." },
    ],
    aesthetic: "Neon jukebox pinks + chrome + checkerboard floor tiles reflecting the record-player glow.",
    ambience: "Vinyl-crackle backing track · milkshake whir · plates clinking · Dave calling “order up!” on entrance.",
    status: "placeholder",
  },
  {
    id: "aurora_borealis",
    name: "The Aurora Borealis",
    icon: "🌌",
    accent: "cyan",
    hero_tie: "Glitch Architect",
    tagline: "A celestial expanse where cascading binary bleeds through the northern lights.",
    setting: "A celestial expanse dominated by the shifting lights of the Aurora.",
    layout: "The clinical medical bed where Glitch resides sits at the physical anchor — a fixed point suspended in the sky-ribbons.",
    elements: [
      { name: "The Medical Bed", detail: "Clinical anchor point. Placeholder role: central platform / respawn tether — Glitch's identity is stitched to it." },
      { name: "Aurora Curtain", detail: "Shifting sky-ribbons form the backdrop. Placeholder hazard: color-shift telegraphs incoming Chronophage waves." },
      { name: "Binary Cascades", detail: "Cascading streams of binary code flow through the aurora's lights — visualizing the Chronophage's influence on Glitch's data." },
      { name: "Game Code Snippets", detail: "Core logic and character-state scripts flicker in and out around the bed. His identity shifts based on the timeline intersections currently visible on-screen." },
    ],
    aesthetic: "Deep cosmic void backdrop · shifting neon aurora ribbons (cyan · violet · emerald) · flickering monospace text pinned to invisible planes.",
    ambience: "Hum of a medical monitor · faint dial-up static · glitched vocal sample that stutters every timeline intersection.",
    status: "placeholder",
  },
  {
    id: "flickering_forest",
    name: "The Flickering Forest",
    icon: "🌲",
    accent: "slate",
    hero_tie: "Bram × Glitch · First-Meeting",
    tagline: "The rural clearing where Bram and Glitch's paths first crossed — frozen in the visual instability of that encounter.",
    setting: "A rural forest area that serves as the backdrop for the fighters.",
    layout: "Uneven woodland floor · scattered ancient trees · a natural clearing in the mid-ground where the two paths originally intersected.",
    elements: [
      { name: "The Flicker", detail: "Trees + terrain permeated by a constant visual instability. Placeholder mechanic: the entire stage strobes softly on a fixed cadence — hitboxes remain honest, but afterimages ghost every action." },
      { name: "The Crossing Point", detail: "A specific clearing mid-stage where Bram and Glitch first met. Placeholder role: capture-zone platform granting a brief meter-tick when held." },
      { name: "Ancient Trees", detail: "Trunks that occasionally phase in-and-out. Placeholder mechanic: intermittent cover — reliability drops during a flicker peak." },
      { name: "Spatial Resonance", detail: "Lingering echo of the original encounter. Placeholder VFX: the ground pulses faint concentric rings on projectile impacts." },
    ],
    aesthetic: "Muted forest palette (moss greens · fog greys · dusk violets) · thin CRT-style scan-flicker overlaid on the whole stage · faint afterimage trails on movement.",
    ambience: "Wind through leaves · a soft electrical hum every flicker · a distant heartbeat that only reveals itself during the strobe peak.",
    status: "placeholder",
  },
  {
    id: "the_arcade",
    name: "The Arcade",
    icon: "🕹️",
    accent: "emerald",
    hero_tie: "Miko · Storyline Confinement",
    tagline: "Dimly lit retro arcade hall — locked in, cabinets humming, no way out.",
    setting: "A dimly lit, retro arcade hall, capturing the atmosphere of being confined within its walls.",
    layout: "Rows of classic arcade cabinets are packed closely together, creating narrow, maze-like aisles. The floor is covered in worn, neon-patterned carpeting.",
    elements: [
      { name: "Arcade Cabinets", detail: "Dozens of glowing CRT-monitored game machines with joystick + button configurations, casting flickering light across the floor. Placeholder role: destructible cover · unpredictable CRT-flash telegraph." },
      { name: "The Change Machine", detail: "Heavy metallic wall-mounted machine near the main entrance humming with a steady electrical drone. Placeholder mechanic: interactable — coin-dispenses a random buff pickup on a long cooldown." },
      { name: "Prize Counter", detail: "Gated-off area filled with shelves of plastic trinkets and stuffed toys, illuminated by harsh overhead fluorescent lights. Placeholder role: end-of-round objective / bonus stash." },
      { name: "The Entrance/Exit", detail: "Heavy set of double glass doors that remain LOCKED, reinforcing the feeling of confinement. Placeholder mechanic: can only be unlocked by a stage-victory condition — no ring-outs, no retreats." },
    ],
    aesthetic: "Deep neon glow (crimson · electric blue · acid green) · neon-patterned worn carpet · CRT scanlines and flickering monitor light bleeding across every surface.",
    ambience: "Coin-drop chimes · looping 8-bit attract-mode jingles · steady electrical drone from the change machine · muffled fluorescent hum from the prize counter.",
    status: "placeholder",
  },
  {
    id: "peacock_gym",
    name: "The Peacock Gym",
    icon: "🦚",
    accent: "amber",
    hero_tie: "Roc · The Bodybuilder-Roc's Iron Sanctum",
    tagline: "Iron-plated industrial gym — Roc's realm-famous training temple.",
    setting: "A high-intensity, industrial-style training facility branded with the distinct identity of \"The Peacock.\"",
    layout: "Open-floor plan dominated by heavy-duty equipment, reinforced flooring for dropping weights, and high-clearance ceilings.",
    elements: [
      { name: "The Power Rack", detail: "Massive steel-reinforced squat cage centered in the room, showing signs of heavy, consistent use. Placeholder role: center-stage arena focal point · destructible after full-charge slam." },
      { name: "Weight Stack Machines", detail: "Row of well-maintained cable machines — dedicated stations for tricep pushdowns and face pulls. Placeholder hazard: hanging cables become swingable / grabbable during Roc-specials." },
      { name: "Dumbbell Racks", detail: "Heavy-duty racks holding a full set of dumbbells, neatly organized. Placeholder mechanic: pick-up-and-throw props." },
      { name: "Mirror Wall", detail: "Large floor-to-ceiling mirrors spanning the main training wall. Placeholder VFX: reflects opponents in real-time · shatters into damaging shards on a big hit." },
      { name: "Retail Kiosk", detail: "Prominent display near the entrance featuring a stack of Blackstone Labs products. A bright neon-trimmed sign reads “OmniMuscle · SAVE 15% · LIMITED TIME!” casting a soft colored glow over the surrounding iron weights. Placeholder role: promo interactable — grabbable buff-item pickup on cooldown." },
    ],
    aesthetic: "Warm iron-oxide oranges + brushed-steel greys · industrial pendant lighting · gold-and-black peacock branding on the far wall · sweat-and-chalk atmosphere.",
    ambience: "Steady clang of plates loading · low bassline gym anthem loop · rhythmic breathing count-offs · occasional heavy thud as a set finishes on the reinforced floor.",
    status: "placeholder",
  },
  {
    id: "temporal_core",
    name: "The Temporal Core",
    icon: "🌀",
    accent: "pink",
    hero_tie: "Deanah & Muki · Time-Loop Nexus · Secret-Encounter Hub",
    tagline: "Subterranean high-tech chamber with a portal churning at the rear — and where secret fights + secret fighters manifest.",
    setting: "A high-tech, subterranean facility housing the primary \"time loop\" portal that serves as the nexus for Deanah and Muki's operations.",
    layout: "Spacious industrial-grade chamber with a central elevated platform leading directly to the swirling, multi-colored energy of the time loop.",
    elements: [
      { name: "The Portal", detail: "Massive circular gateway at the rear of the chamber, constantly spinning and emitting a vibrant shifting glow of cosmic light. Placeholder mechanic: rear-line hazard · projectiles pulled toward the portal on a rhythm · ring-out into it triggers a Deanah & Muki cinematic finisher. When secret conditions are met, opens a secondary opponent from beyond the loop." },
      { name: "Control Consoles", detail: "Banked holographic displays and technical interfaces flank both sides of the room, tracking the instability and flow of the time loop. Placeholder role: destructible cover · interactable — briefly reveals the opponent's next telegraphed input to the player who touches it." },
      { name: "Central Elevated Platform", detail: "Raised metal stage running from the front of the chamber to the portal mouth. Placeholder role: high-ground · closer to the portal = stronger pull hazard." },
      { name: "Temporal Machinery", detail: "Complex machinery humming with temporal energy along the walls. Placeholder VFX: ambient timeline-glitch on ultimate use — brief frame-stutter for both fighters." },
      { name: "🔒 Secret-Encounter Anchor", detail: "The Temporal Core is where its OWN group of secret fights + secret fighters manifest — timeline anomalies pull hidden challengers through the portal when specific riddle conditions are met during a live match. Distinct roster from Dio's Silent Nexus and from Nelson's own lair — three separate secret-encounter pockets." },
    ],
    aesthetic: "Utilitarian yet ethereal — cold hard industrial surfaces (gunmetal · brushed chrome · matte black) blended with the wild unpredictable light of the portal (magenta · cyan · gold cosmic bloom).",
    ambience: "Deep sub-bass hum of temporal machinery · rhythmic Doppler swell each time the portal cycles · quiet console beeps · faint clock-tick whenever the loop hitches.",
    status: "placeholder",
  },
  {
    id: "dragonious_foundry",
    name: "The Dragonious Foundry",
    icon: "🔥",
    accent: "orange",
    hero_tie: "Gourmand Nelson · Molten Crucible Lair",
    tagline: "Industrial refinery straddling a swirling river of Dragonious molten slag — Nelson's home.",
    setting: "A cavernous industrial forge suspended over a swirling river of molten Dragonious metal alloy. Chains hang from the ceiling; steam vents belch clouds of vapor from vast furnaces bracketing both walls.",
    layout: "Two rusted steel gantries flank a wide central channel of molten slag. The floor is grated metal with clear sightlines down into the swirling glow. Cornered fighters teeter over the railing straight above the crucible.",
    elements: [
      { name: "The Molten Slag River", detail: "Central swirling channel of Dragonious alloy at ~1,500 °C. Placeholder hazard: ring-out into the slag triggers the universal stage judgment (see The Casting Cycle below)." },
      { name: "Gantry Railings", detail: "Rusted steel railings run the length of both gantries. Placeholder mechanic: a stagger-defeat near the railing puts the opponent in the judgment-trigger position (leaning over)." },
      { name: "Blast Furnaces", detail: "Vast furnaces line the far wall — belching steam and columns of orange flame. Placeholder VFX: periodic flame bursts light the whole stage." },
      { name: "Overhead Chains", detail: "Heavy chains hang from the ceiling supports. Placeholder role: swingable / grabbable hazards during Nelson-specials." },
      { name: "The Automated Foundry Press", detail: "Unseen off-screen. Placeholder role: activates during The Casting Cycle to stamp and eject the fused victim. A hydraulic arm swings in to drag the finished 'statue' off-screen." },
    ],
    aesthetic: "Deep iron oranges + rust reds + slag-gold light bleeding upward through the grating. Heavy industrial silhouettes wreathed in constant steam and ember drift.",
    ambience: "Bellowing furnace roar · steady clang of forge hammers off-beat · hissing steam vents · deep bass slag-rumble as the molten channel churns.",
    stage_fatality: {
      name: "The Casting Cycle",
      universal: true,
      trigger_setup: "Opponent is left in a stagger state near the gantry railing, leaning over the ledge.",
      command: "TBD — input not locked yet",
      sequence: [
        { name: "The Trigger",   detail: "The winning character performs a simple generic action — a heavy boot-stomp or rifle-butt strike to the defeated opponent's back." },
        { name: "The Fall",      detail: "The strike sends the opponent reeling backward, over the railing, and directly into the swirling river of molten slag below." },
        { name: "The Submersion", detail: "Upon hitting the slag, the opponent does not sink — they are INSTANTLY engulfed. The 1,500 °C heat triggers a massive superheated flash-boiling reaction of the slag and the water in the body." },
        { name: "The Finish",    detail: "Camera pulls under the slag. The opponent's silhouette rapidly dissolves and fuses with the swirling Dragonious alloy, transforming into a glowing distorted metal casting — an unrecognizable, grotesque scrap-sculpture. It is instantly stamped and ejected by an unseen automated foundry press. A hydraulic arm swings in and drags the finished statue off-screen for processing." },
      ],
      design_lock: "Retains the brutal industrial-refining theme WITHOUT gore — triggered by a generic action (the fall) and completed by the environment (the crucible). Universal: works for ninjas, brawlers, robots, mages — every fighter in the roster can execute it on this stage.",
    },
    status: "canon",
  },

  // ═════════ 🌌 THE SUMMIT — Astraea's Last Defiant Stand ═════════
  {
    id: "the_summit",
    name: "The Summit",
    icon: "🌌",
    accent: "cyan",
    hero_tie: "Astraea · Final Stand · Chronophage Overlook",
    tagline: "The jagged peak above the clouds where Astraea took her last stand — and turned her back on the Chronophage.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/fqvoqrbt_1784611132423.png",
    combat_reference_image: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/pab9bhyt_1784611137228.png",
    setting: "An impossibly high, jagged peak rising above the clouds — the final battleground where Astraea took her last defiant stand.",
    layout: "A crumbling stone plateau at the edge of the world, suspended in a void where time and space appear fragmented. Broken constellation-inscribed pavers form the fighting floor.",
    elements: [
      { name: "The Defiant Stand",     detail: "The ground is marked by the residual energy of Astraea's constellation-based powers — glowing celestial light contrasting the bleak dark landscape. Static-hum crackle from the pavers." },
      { name: "The Chronophage Influence", detail: "The atmosphere is heavy with the Chronophage's presence — distorted clockwork-like sky phenomena and flickering rifts in reality where the entity once lurked. Colossal gear silhouettes rotate behind the platform." },
      { name: "The Turning Point",     detail: "A specific scarred section of the plateau commemorates where Astraea turned her back on the Chronophage — featuring a crater of concentrated starlight that hums with static energy." },
      { name: "Rapid-Shift Sky",       detail: "Sky colors shift rapidly between celestial nebula palette and blood-red devouring hues — reflecting the ongoing struggle between celestial light and the Chronophage's hunger." },
    ],
    aesthetic: "Cosmic nebula purples/teals/pinks vs devouring blood-red rifts · glowing constellation-inscribed stone · gargantuan gear silhouettes half-obscured by cloud.",
    ambience: "High-altitude wind · distant clockwork grinding · sub-bass Chronophage breathing · celestial harp-like chimes from the star crater.",
    stage_fatality: {
      name: "The Offering to the Chronophage",
      universal: true,
      trigger_setup: "When defeated on the plateau, the opponent is knocked back into the crater of concentrated starlight — the exact spot where Astraea turned her back on the Chronophage.",
      command: "TBD — input not locked yet",
      sequence: [
        { name: "The Trigger",   detail: "The victor drives a heavy strike straight down into the star-crater, cracking the celestial seal that has kept the Chronophage at bay since Astraea's stand." },
        { name: "The Rifts Open", detail: "Every flickering reality-rift across the sky violently widens. The gargantuan clockwork gears in the background lurch forward — grinding into motion for the first time since the Great Cycle." },
        { name: "The Offering",   detail: "The opponent is lifted off the plateau by an invisible tide of temporal current, floating skyward toward the widening rifts. The rapid-shift sky freezes on the devouring blood-red palette. The Chronophage's silhouette resolves in the distortion — vast, hungry, patient." },
        { name: "The Consumption", detail: "The Chronophage draws the opponent through the largest rift. Their form doesn't burn or shatter — it is UNMADE, thread by thread, absorbed into the entity's ever-turning cycle. The sky snaps back to celestial-light hues. Astraea's constellation on the plateau glows briefly, then dims — one more debt paid to the cycle she defied." },
      ],
      design_lock: "Bloodless — the opponent is CONSUMED by the Chronophage, not killed. The Summit's judgment is a canonical offering to feed the cycle Astraea turned her back on. The plateau visibly loses one constellation-pattern after each judgment (persistent visual damage between matches — placeholder). Universal across the roster.",
    },
    status: "canon",
  },

  // ═════════ 🏰 CASTLE DRAGONIOUS — Throne Room + Clockwork Towers ═════════
  {
    id: "castle_dragonious",
    name: "Castle Dragonious",
    icon: "🏰",
    accent: "purple",
    hero_tie: "Realm Seat · Clockwork Fortress",
    tagline: "A sprawling ancient citadel fused with raw Dragonious energy — monument and industrial engine in one.",
    setting: "A sprawling, ancient citadel physically fused with the raw volatile Dragonious energy found throughout the world. Simultaneously a monument of historical power AND a terrifying industrial engine.",
    layout: "A massive multi-tiered throne room and courtyard complex. Architecture is a hybrid of gothic stone AND high-functioning steam-venting machinery.",
    elements: [
      { name: "The Heart of the Castle", detail: "Massive crystalline structure in the center of the courtyard, pulsing with light — the source of the realm's power. Hums with the same energy Astraea fought to protect." },
      { name: "The Clockwork Towers",    detail: "Gargantuan gears in the background — reminiscent of the Chronophage's influence — slowly rotate, grinding down the castle's defenses and constantly SHIFTING the layout of the battleground." },
      { name: "The Banners of Lore",     detail: "Tattered banners on the walls representing the various factions of the game world. Placeholder: destructible cover." },
      { name: "The Ornate Throne",       detail: "Massive ceremonial throne in the throne room — NOT just furniture. It is the pilot's chair for the castle's primary defense system. Central to the universal stage judgment below." },
    ],
    aesthetic: "Regal cold stone + aggressive hot industrial (Nelson's Gantry-flavored). Where ancient secrets are guarded by cold unfeeling machines.",
    ambience: "Slow gear-grind base layer · distant steam-venting hiss · faint choral echo from crystalline heart · low horn drone under everything.",
    stage_fatality: {
      name: "The Throne Room Descent",
      universal: true,
      trigger_setup: "When defeated in the castle's throne room, the opponent is knocked back into the massive ornate throne itself.",
      command: "TBD — input not locked yet",
      sequence: [
        { name: "The Trigger",  detail: "The victor slams a heavy lever on the armrest of the throne." },
        { name: "The Shift",    detail: "The floor beneath the opponent instantly retracts, revealing a massive rotating gear assembly — a 'meat grinder' built for intruders." },
        { name: "The Finish",   detail: "The opponent is pulled downward into the rotating mechanism. As they are processed, the castle's exterior lights flash in a rhythmic terrifying pattern, and the fortress emits a deafening horn-like blast that echoes across the entire realm — signaling the 'disposal' of the interloper." },
      ],
      design_lock: "The throne itself IS the mechanism — no visible gore, all machinery and horn-blast payoff. Universal across the roster.",
    },
    status: "canon",
  },

  // ═════════ ⛲ THE STREETS OF CASTLE DRAGONIOUS & THE FOUNTAIN ═════════
  {
    id: "castle_streets_fountain",
    name: "The Streets & The Fountain",
    icon: "⛲",
    accent: "cyan",
    hero_tie: "Castle Dragonious · Outer Bailey · Auction House District",
    tagline: "Cobblestone streets under the fortress shadow — the Great Fountain now circulates industrial coolant.",
    setting: "A sprawling cobblestone district located within the outer bailey of Castle Dragonious. A bustling — albeit decaying — commercial hub where the city's inhabitants live under the shadow of the main citadel's towering clockwork machinery.",
    layout: "A complex network of narrow winding streets that converge into a grand central plaza dominated by the Great Fountain.",
    elements: [
      { name: "The Great Fountain",  detail: "A relic of a bygone age — intricate carvings of creatures from Dragonious lore. Instead of water, it circulates a shimmering volatile coolant used in the castle's industrial processes." },
      { name: "The Market Stalls",   detail: "Streets lined with empty shuttered stalls where the in-game Auction House items are theoretically traded. Placeholder: destructible cover + occasional random-item drops." },
      { name: "Industrial Intrusion", detail: "Steam pipes venting along the walls · ambient sound of grinding gears from the upper castle towers · constant rhythmic tension." },
    ],
    aesthetic: "Forgotten medieval charm + oppressive industrial utility. Watched by the mechanical eyes of the fortress above.",
    ambience: "Distant gear grind from the towers above · steam-pipe hiss · foot-echo off cobblestone · coolant-gurgle from the fountain.",
    stage_fatality: {
      name: "The Fountain's Reclamation",
      universal: true,
      trigger_setup: "When the opponent is defeated near the edge of the central fountain plaza, they are knocked backward into the fountain's basin.",
      command: "TBD — input not locked yet",
      sequence: [
        { name: "The Trigger",  detail: "The victor approaches the fountain's primary control valve and forces it open beyond its safety limits." },
        { name: "The Overload", detail: "The fountain ceases its gentle circulation and erupts in a massive pressurized geyser of freezing high-pressure industrial coolant." },
        { name: "The Finish",   detail: "The sheer pressure launches the opponent into the air. The extreme temperature causes their body to undergo a rapid violent 'flash-freeze.' Before they can shatter, the coolant's acidic properties rapidly dissolve the frozen exterior — leaving only a calcified statue-like remnant that collapses into the basin to be filtered into the castle's sub-levels." },
      ],
      design_lock: "Bloodless — flash-freeze + acidic dissolve leaves only a statue-like remnant. Universal across the roster.",
    },
    status: "canon",
  },

  // ═════════ ⏳ THE FRACTURED PLAZA — Time-Loop Origin ═════════
  {
    id: "fractured_plaza",
    name: "The Fractured Plaza",
    icon: "⏳",
    accent: "purple",
    hero_tie: "Adon · Home Stage · Time-Loop Origin · Chronophage Feed-Point",
    tagline: "The main square where the fabric of time first fractured — Adon's home ground · caught in a perpetual stuttering echo of its own history.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/anuock59_1784611188667.png",
    setting: "The main square of the city — where the fabric of time first fractured, leaving the area caught in a perpetual stuttering 'echo' of its own history.",
    layout: "A grand plaza that looks architecturally pristine in some corners while other sections are visibly decaying, overgrown, or scorched — as if centuries of events are occurring SIMULTANEOUSLY.",
    elements: [
      { name: "The Echoing Fountain", detail: "The water (or coolant) doesn't just flow — it sometimes flows BACKWARDS, or suspends in mid-air for seconds before dropping, visually manifesting the 'time loop hole' that originated here." },
      { name: "Ghostly Overlays",     detail: "Occasionally the silhouettes of past inhabitants or ancient versions of the castle infrastructure flicker into view like a faulty film projection — hinting at the temporal instability." },
      { name: "The Chronophage's Shadow", detail: "The clockwork towers above seem to be trying to 'LOCK' the loop in place to feed on the repeating cycle. Long unnatural shadows distort as time skips." },
      { name: "The Temporal Seal",    detail: "A glowing seal at the center of the fountain marks the loop hole itself. Central to the universal stage judgment below." },
    ],
    aesthetic: "Time-fracture aesthetic — pristine and decayed simultaneously · ghostly film-projection overlays · steampunk-mechanism wire-and-pulley network overhead against a cosmic nebula backdrop.",
    ambience: "Reversed footsteps · echoing conversations from long-dead inhabitants · clock-tick that skips and reverses · deep bass hum from the loop hole.",
    stage_fatality: {
      name: "The Temporal Erasure",
      universal: true,
      trigger_setup: "When defeated in the plaza, the opponent is knocked back against the base of the fountain.",
      command: "TBD — input not locked yet",
      sequence: [
        { name: "The Trigger",  detail: "The victor shatters a glowing temporal seal on the fountain, causing a localized surge of chaotic time-energy." },
        { name: "The Loopback", detail: "The area around the opponent begins to strobe violently as the timeline rapidly rewinds." },
        { name: "The Finish",   detail: "The opponent is caught in the acceleration. They rapidly cycle through their own past — appearing as an infant, then an elder, then skeletal remains, and finally DUST — until they are completely erased from the current timeline. The fountain then returns to its 'normal' state as if the fight (and the opponent) had never occurred." },
      ],
      design_lock: "The most surreal / cinematic of the universal fatalities. Bloodless. Universal across the roster.",
    },
    status: "canon",
  },
];

// ─── 🗺️ Stage Card + Gallery Panel ─────────────────────────
function StageCard({ stage, selected, onSelect }) {
  const a = ACCENT_MAP[stage.accent] || ACCENT_MAP.cyan;
  const isCanon = stage.status === "canon";
  return (
    <button
      type="button"
      onClick={() => onSelect?.(stage.id)}
      data-testid={`showdown-stage-card-${stage.id}`}
      className={`text-left rounded-2xl border-2 p-4 transition-all ${
        selected
          ? `${a.border} ${a.bg} ring-2 ring-offset-2 ring-offset-black ${a.text.replace("text-", "ring-")}/50`
          : "border-neutral-800 bg-black/40 hover:border-neutral-600"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-3xl">{stage.icon}</span>
          <div>
            <div className={`text-base font-bold ${a.text}`}>{stage.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
              {stage.hero_tie}
            </div>
          </div>
        </div>
        <span
          className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
            isCanon
              ? "border-red-500/60 text-red-300 bg-red-500/10"
              : "border-neutral-700 text-neutral-500"
          }`}
        >
          {isCanon ? "Canon" : "Placeholder"}
        </span>
      </div>
      <div className="text-[11px] text-neutral-300 italic leading-snug">{stage.tagline}</div>
      {stage.stage_fatality && (
        <div className="mt-2 flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-red-300">
          <Skull size={10} /> Stage Judgment
        </div>
      )}
    </button>
  );
}


// ─── 🌀 Disruption Mechanic Panel ──────────────────────────
function DisruptionMechanicPanel({ mechanic }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <section
      className="mt-8 rounded-2xl border-2 border-fuchsia-700/50 bg-gradient-to-br from-fuchsia-950/30 via-black/60 to-purple-950/30 p-5 relative overflow-hidden"
      data-testid="showdown-disruption-panel"
    >
      {/* Chant watermark */}
      <div className="absolute -top-3 -right-6 opacity-10 pointer-events-none select-none text-4xl font-black tracking-widest text-fuchsia-300 rotate-[-6deg]">
        DISRUPTION
      </div>

      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={16} className="text-fuchsia-300" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-fuchsia-300">
            System Mechanic · Universal Combo-Breaker
          </span>
          <span className="ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-fuchsia-500/60 text-fuchsia-200 bg-fuchsia-500/10 font-bold">
            Canon
          </span>
        </div>
        <h3
          className="text-3xl md:text-4xl font-black text-white leading-none mt-1"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {mechanic.name}
        </h3>
        <div className="text-[10px] tracking-[0.4em] uppercase text-fuchsia-300 font-mono mt-1 opacity-80">
          {mechanic.chant}
        </div>
        <p className="text-[12px] text-neutral-300 leading-relaxed mt-2 italic">
          {mechanic.tagline}
        </p>

        {/* Activation & Rules */}
        <div className="grid md:grid-cols-2 gap-2 mt-4">
          <div className="rounded-lg border border-fuchsia-900/40 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mb-1 font-bold">Cost</div>
            <div className="text-[12px] text-neutral-200 leading-relaxed">{mechanic.rules.cost}</div>
          </div>
          <div className="rounded-lg border border-fuchsia-900/40 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mb-1 font-bold">Activation Window</div>
            <div className="text-[12px] text-neutral-200 leading-relaxed">{mechanic.rules.activation_window}</div>
          </div>
          <div className="rounded-lg border border-fuchsia-900/40 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mb-1 font-bold">Un-Defendable</div>
            <div className="text-[12px] text-neutral-200 leading-relaxed">{mechanic.rules.un_defendable}</div>
          </div>
          <div className="rounded-lg border border-fuchsia-900/40 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mb-1 font-bold">The Guarantee</div>
            <div className="text-[12px] text-neutral-200 leading-relaxed">{mechanic.rules.guarantee}</div>
          </div>
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="showdown-disruption-toggle"
          className="mt-4 w-full px-4 py-2 rounded-lg border border-fuchsia-600/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 text-xs uppercase tracking-widest font-bold transition"
        >
          {expanded ? "Hide Deep Dive ↑" : "View Roster Profiles + Disruption War ↓"}
        </button>

        {expanded && (
          <div className="mt-5 space-y-5" data-testid="showdown-disruption-deepdive">
            {/* Disruption War */}
            <div className="rounded-lg border border-red-700/40 bg-red-950/10 p-3">
              <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-2 flex items-center gap-2">
                🔄 The Disruption War · Counter-Mechanic
              </div>
              <div className="text-[11px] text-neutral-300 italic mb-2">
                {mechanic.disruption_war.tagline}
              </div>
              <ol className="space-y-1 mb-3">
                {mechanic.disruption_war.sequence.map((s, i) => (
                  <li key={i} className="text-[11px] text-neutral-200 flex gap-2">
                    <span className="font-mono font-bold text-red-300 shrink-0">{i + 1}.</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
              <div className="rounded border border-red-900/40 bg-black/40 p-2 mt-2">
                <div className="text-[9px] uppercase tracking-widest text-red-300 font-bold mb-1">Hard Cap</div>
                <div className="text-[11px] text-neutral-300 leading-relaxed">{mechanic.disruption_war.hard_cap}</div>
              </div>
            </div>

            {/* Hero Profiles */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2 flex items-center gap-2">
                👥 Roster Disruption Profiles
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {mechanic.hero_profiles.map((p) => (
                  <div
                    key={p.hero}
                    className="rounded-lg border border-neutral-800 bg-black/40 p-3"
                    data-testid={`showdown-disruption-hero-${p.hero.toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[13px] font-bold text-white">{p.hero}</div>
                      {p.pending && (
                        <span className="text-[8px] uppercase tracking-widest px-1 py-0.5 rounded border border-amber-500/50 text-amber-300 bg-amber-500/10 font-bold">
                          Kit Pending
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-bold text-fuchsia-300 mb-1">{p.disruption_name}</div>
                    <div className="text-[10px] text-neutral-400 leading-relaxed">{p.effect}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategic Impact */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold mb-2 flex items-center gap-2">
                🧠 Strategic Impact
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {mechanic.strategic_impact.map((s) => (
                  <div key={s.title} className="rounded-lg border border-cyan-900/40 bg-cyan-950/10 p-3">
                    <div className="text-[11px] font-bold text-cyan-200 mb-1">{s.title}</div>
                    <div className="text-[11px] text-neutral-300 leading-relaxed">{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-[10px] text-neutral-500 italic border-l-2 border-fuchsia-800/60 pl-2">
              Fully locked in as the core engine that drives the high-tension, momentum-shifting combat of Dragonious. Combo-string engine + meter accounting ship with the arena engine build-out.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


function StagesGalleryPanel({ stages, totalStagesCount }) {
  const [selectedId, setSelectedId] = useState(stages[0]?.id);
  const selected = stages.find((s) => s.id === selectedId);
  const a = ACCENT_MAP[selected?.accent] || ACCENT_MAP.cyan;
  const hiddenCount = Math.max(0, (totalStagesCount || stages.length) - stages.length);
  return (
    <section
      className="mt-8 rounded-2xl border-2 border-neutral-800 bg-gradient-to-br from-[#0a0a12] via-[#050510] to-[#0a0a12] p-5"
      data-testid="showdown-stages-gallery"
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <MapPin size={14} className="text-cyan-300" />
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-cyan-300">
          Crossover Stages · Soft-Open Gallery
        </span>
        <span className="text-[9px] text-neutral-500 ml-auto italic">
          {stages.length} of {totalStagesCount || stages.length} stages open · 3D visuals pending
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {stages.map((s) => (
          <StageCard key={s.id} stage={s} selected={s.id === selectedId} onSelect={setSelectedId} />
        ))}
      </div>

      {selected && (
        <div
          className={`rounded-xl border-2 ${a.border} bg-black/40 p-4`}
          data-testid={`showdown-stage-detail-${selected.id}`}
        >
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{selected.icon}</span>
              <div>
                <div className={`text-2xl font-bold ${a.text}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                  {selected.name}
                </div>
                <div className="text-[11px] text-neutral-400 italic mt-0.5">{selected.tagline}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500">Hero Tie-In</div>
              <div className={`text-[11px] font-bold ${a.text}`}>{selected.hero_tie}</div>
            </div>
          </div>

          {/* Official stage art (when provided) */}
          {selected.image && (
            <div className={`rounded-xl overflow-hidden border-2 ${a.border} bg-black/60 mb-3`} data-testid={`showdown-stage-image-${selected.id}`}>
              <img
                src={selected.image}
                alt={`${selected.name} — official stage art`}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className={`px-3 py-1.5 bg-black/70 text-[10px] italic ${a.text} tracking-widest`}>
                {selected.icon} {selected.name} — Official Stage Art
              </div>
            </div>
          )}
          {/* Optional combat-reference sequence (when provided) */}
          {selected.combat_reference_image && (
            <div className={`rounded-xl overflow-hidden border ${a.border} bg-black/50 mb-3`} data-testid={`showdown-stage-combat-${selected.id}`}>
              <img
                src={selected.combat_reference_image}
                alt={`${selected.name} — combat reference sequence`}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className={`px-3 py-1.5 bg-black/70 text-[10px] italic ${a.text} tracking-widest`}>
                🎞️ Combat Reference Sequence
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg border border-neutral-800 bg-black/40 p-2">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Setting</div>
              <div className="text-[11px] text-neutral-300 leading-relaxed">{selected.setting}</div>
            </div>
            <div className="rounded-lg border border-neutral-800 bg-black/40 p-2">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Layout</div>
              <div className="text-[11px] text-neutral-300 leading-relaxed">{selected.layout}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-2 mb-3">
            {selected.elements.map((e) => (
              <div key={e.name} className="rounded border border-neutral-800 bg-black/40 p-2">
                <div className="text-[11px] font-bold text-neutral-200">{e.name}</div>
                <div className="text-[10px] text-neutral-400 leading-relaxed mt-0.5">{e.detail}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-2">
            <div className="rounded-lg border border-neutral-800 bg-black/40 p-2">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500">Aesthetic</div>
              <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">{selected.aesthetic}</div>
            </div>
            <div className="rounded-lg border border-neutral-800 bg-black/40 p-2">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500">Ambience</div>
              <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">{selected.ambience}</div>
            </div>
          </div>

          <div className="mt-3 text-[10px] italic text-neutral-500 border-l-2 border-neutral-700 pl-2">
            🎨 3D visuals + hazard mechanics pending owner tuning. Spec is canon (see /app/memory/CROSSOVER_STAGES_CANON_FEB2026.md).
          </div>

          {/* 💀 Universal Stage Judgment (if present) */}
          {selected.stage_fatality && (
            <div
              className="mt-4 rounded-xl border-2 border-red-700/60 bg-gradient-to-br from-red-950/40 via-black/60 to-orange-950/30 p-4"
              data-testid={`showdown-stage-judgment-${selected.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Skull size={14} className="text-red-300" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-red-300">
                      Universal Stage Judgment
                    </div>
                    <div className="text-lg font-bold text-white leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {selected.stage_fatality.name}
                    </div>
                  </div>
                </div>
                {selected.stage_fatality.universal && (
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-500/60 text-red-200 bg-red-500/10 font-bold">
                    Universal · Any Fighter
                  </span>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-2 mb-3">
                <div className="rounded border border-red-900/40 bg-black/40 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-red-300 mb-1">Setup</div>
                  <div className="text-[11px] text-neutral-300 leading-relaxed">{selected.stage_fatality.trigger_setup}</div>
                </div>
                <div className="rounded border border-red-900/40 bg-black/40 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-red-300 mb-1">Command Input</div>
                  <div className="text-[11px] text-orange-200 font-mono leading-relaxed">{selected.stage_fatality.command}</div>
                </div>
              </div>

              <div className="text-[9px] uppercase tracking-widest text-red-300 mb-2">Sequence</div>
              <ol className="space-y-1.5 mb-3">
                {selected.stage_fatality.sequence.map((step, i) => (
                  <li key={i} className="rounded border border-neutral-800 bg-black/40 p-2 flex gap-2">
                    <span className="text-[10px] font-mono font-bold text-red-300 shrink-0">{i + 1}.</span>
                    <div>
                      <div className="text-[11px] font-bold text-white">{step.name}</div>
                      <div className="text-[10px] text-neutral-400 leading-relaxed mt-0.5">{step.detail}</div>
                    </div>
                  </li>
                ))}
              </ol>

              {selected.stage_fatality.design_lock && (
                <div className="rounded border border-red-800/40 bg-red-950/20 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-red-300 font-bold mb-1">Design Lock</div>
                  <div className="text-[10px] text-neutral-300 leading-relaxed italic">{selected.stage_fatality.design_lock}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 🔒 Locked-stages hint — shown when the gallery is soft-open (Feb 2026) */}
      {hiddenCount > 0 && (
        <div
          className="mt-4 rounded-lg border border-dashed border-cyan-800/60 bg-black/30 p-3 flex items-center gap-3"
          data-testid="showdown-stages-locked-hint"
        >
          <Lock size={14} className="text-cyan-300 shrink-0" />
          <div className="text-[11px] italic text-neutral-400">
            <span className="text-cyan-300 font-bold">{hiddenCount} more stages</span> hidden behind the Dragonious Fighters egg — coming as canon unfolds.
          </div>
        </div>
      )}
    </section>
  );
}


function StarRow({ n }) {
  return (
    <span className="tracking-widest">
      {"★".repeat(n)}
      <span className="text-neutral-700">{"☆".repeat(5 - n)}</span>
    </span>
  );
}

function FighterCard({ fighter, selected, onSelect, locked = false, newlyUnlocked = false }) {
  const a = ACCENT_MAP[fighter.accent] || ACCENT_MAP.neutral;

  // 3D tilt state — CSS-only, mouse-tracked. Gated to fine-pointer devices.
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50 });
  const [enable3D, setEnable3D] = useState(false);
  const [burstOn, setBurstOn] = useState(newlyUnlocked);

  useEffect(() => {
    if (newlyUnlocked) {
      setBurstOn(true);
      const t = setTimeout(() => setBurstOn(false), 2400);
      return () => clearTimeout(t);
    }
  }, [newlyUnlocked]);

  // Only enable 3D transforms on fine-pointer (mouse) devices with reduced-motion off.
  // Mobile / touch devices skip the entire 3D layer — preserve-3d + translateZ
  // can crash iOS Safari + low-end Android WebViews under GPU pressure.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setEnable3D(fine.matches && !reduced.matches);
    update();
    fine.addEventListener?.("change", update);
    reduced.addEventListener?.("change", update);
    return () => {
      fine.removeEventListener?.("change", update);
      reduced.removeEventListener?.("change", update);
    };
  }, []);

  const handleMove = (e) => {
    if (!enable3D) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const px = x / rect.width;   // 0..1
    const py = y / rect.height;  // 0..1
    // Max tilt ±8° — light touch, no motion sickness
    const ry = (px - 0.5) *  16;  // rotateY around vertical axis
    const rx = (0.5 - py) *  12;  // rotateX around horizontal axis
    setTilt({ rx, ry, mx: px * 100, my: py * 100 });
  };
  const handleLeave = () => setTilt({ rx: 0, ry: 0, mx: 50, my: 50 });

  return (
    <div style={enable3D ? { perspective: "1000px" } : undefined} className="w-full">
      <button
        ref={cardRef}
        onClick={() => onSelect(fighter.id)}
        onMouseMove={enable3D ? handleMove : undefined}
        onMouseLeave={enable3D ? handleLeave : undefined}
        data-testid={`showdown-fighter-${fighter.id}`}
        className={`group relative w-full text-left rounded-2xl border-2 ${a.border} ${a.bg} p-5 transition-[transform,box-shadow] duration-200 ease-out overflow-hidden hover:shadow-[0_20px_48px_-12px_rgba(0,0,0,0.6)] ${
          selected ? `ring-2 ${a.text.replace("text-", "ring-")}` : ""
        }`}
        style={
          enable3D
            ? {
                transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateZ(0)`,
                transformStyle: "preserve-3d",
                willChange: "transform",
              }
            : undefined
        }
      >
        {/* Holographic sheen — mouse-only. Skipped on touch to save GPU. */}
        {enable3D && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: `radial-gradient(circle at ${tilt.mx}% ${tilt.my}%, rgba(255,255,255,0.14), transparent 55%)`,
              mixBlendMode: "screen",
            }}
          />
        )}
        {/* ✨ CODEX UNLOCK CINEMATIC — accent-coloured burst when a hero flips locked → unlocked */}
        {burstOn && (
          <>
            <span
              aria-hidden="true"
              data-testid={`showdown-hatch-burst-${fighter.id}`}
              className={`pointer-events-none absolute inset-0 rounded-2xl ${a.text}`}
              style={{
                animation: "codexUnlockBurst 2.2s ease-out forwards",
                boxShadow: "0 0 0 rgba(255,255,255,0)",
              }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-2xl"
              style={{
                animation: "codexUnlockSweep 2.2s ease-out forwards",
                background: "linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)",
                mixBlendMode: "screen",
              }}
            />
            <span
              data-testid={`showdown-hatch-ribbon-${fighter.id}`}
              className={`absolute top-2 right-2 z-10 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border-2 ${a.border} bg-black/80 ${a.text} flex items-center gap-1`}
              style={{ animation: "codexUnlockRibbon 2.4s ease-out forwards" }}
            >
              <Sparkles size={10} /> Hatched
            </span>
          </>
        )}
        {/* Inner content — floats forward for parallax depth on desktop only */}
        <div className="relative" style={enable3D ? { transform: "translateZ(24px)" } : undefined}>
          <div className="flex items-start gap-3">
            <div
              className={`text-5xl leading-none drop-shadow-[0_6px_10px_rgba(0,0,0,0.55)] ${locked ? "opacity-0" : ""}`}
              style={enable3D ? { transform: "translateZ(28px)" } : undefined}
              aria-hidden={locked ? "true" : undefined}
            >
              {locked ? "❓" : fighter.icon}
            </div>
            {locked && (
              <div
                aria-hidden="true"
                className={`absolute left-0 top-0 text-5xl leading-none ${a.text} opacity-40 select-none pointer-events-none`}
                style={{ filter: "blur(1.5px) grayscale(60%)" }}
                data-testid={`showdown-fighter-silhouette-${fighter.id}`}
              >
                ❓
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className={`text-[10px] uppercase tracking-widest ${a.text}`}>
                  {locked ? "???" : fighter.tag}
                </div>
                {locked ? (
                  <span
                    data-testid={`showdown-locked-badge-${fighter.id}`}
                    className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${a.border} ${a.bg} ${a.text} font-bold flex items-center gap-1`}
                  >
                    <Lock size={8} /> Locked
                  </span>
                ) : fighter.is_main_hero === false && (
                  <span
                    data-testid={`showdown-nonmain-badge-${fighter.id}`}
                    className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-neutral-600 bg-black/60 text-neutral-300 font-bold"
                  >
                    Playable · Non-Main
                  </span>
                )}
              </div>
              <div
                className={`text-2xl font-bold ${locked ? `${a.text} opacity-70` : "text-white"}`}
                style={{ fontFamily: "Outfit, sans-serif", letterSpacing: locked ? "0.15em" : undefined }}
                data-testid={`showdown-fighter-name-${fighter.id}`}
              >
                {locked ? "???" : fighter.name}
              </div>
              <div className="text-[11px] text-neutral-400 mt-0.5">
                {locked ? "??? · Locked" : fighter.archetype}
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
            {Object.entries(fighter.stats).map(([k, v]) => (
              <div key={k} className="flex justify-between text-neutral-400">
                <span>{locked ? "???" : k}</span>
                <span className={a.text}>
                  {locked ? <span className="tracking-widest opacity-60">?????</span> : <StarRow n={v} />}
                </span>
          </div>
        ))}
      </div>
      <div className={`mt-3 text-[10px] italic ${a.text}`}>
        {locked ? "🔒 Hatch from Egg Nest to reveal →" : "Tap to view kit →"}
      </div>
        </div>
      </button>
    </div>
  );
}

/* ─── Cari's War-Hound Morph — Interactive Stance-Swap Preview ─── */
function CariStancePreview({ accent, mobility }) {
  const [morphed, setMorphed] = useState(false);
  const [flashing, setFlashing] = useState(false);

  const trigger = () => {
    if (flashing) return;
    setFlashing(true);
    // brief flash then swap stance
    setTimeout(() => {
      setMorphed((v) => !v);
      setFlashing(false);
    }, 320);
  };

  const librarian = mobility.find((m) => m.name === "Librarian Stance") || mobility[0];
  const warhound  = mobility.find((m) => m.name === "War-Hound Morph") || mobility[1];
  const active = morphed ? warhound : librarian;
  const inactive = morphed ? librarian : warhound;
  const activeIcon = morphed ? "🐕" : "📚";

  return (
    <>
      {/* Active stance card — clickable to morph */}
      <button
        onClick={trigger}
        data-testid="cari-morph-trigger"
        className={`relative rounded-lg border-2 ${accent.border} bg-black/50 p-3 text-left overflow-hidden transition hover:scale-[1.02] hover:shadow-[0_0_24px_rgba(6,182,212,0.35)]`}
      >
        {/* Flash overlay */}
        {flashing && (
          <div
            className="absolute inset-0 bg-cyan-300/70 animate-cari-morph-flash pointer-events-none"
            style={{ mixBlendMode: "screen" }}
          />
        )}
        <div className="flex items-center gap-2 mb-1">
          <span
            key={`icon-${morphed}`}
            className="text-3xl animate-cari-morph-icon inline-block"
          >
            {activeIcon}
          </span>
          <div className={`text-sm font-bold ${accent.text}`}>{active.name}</div>
        </div>
        <div className="text-[11px] text-neutral-400 leading-snug mt-1">{active.description}</div>
        <div className={`text-[9px] uppercase tracking-widest mt-2 ${accent.text} opacity-80`}>
          ⚡ Click to morph → {inactive.name}
        </div>
      </button>

      {/* Inactive stance card — dimmed preview */}
      <div
        className="rounded-lg border border-zinc-800 bg-black/30 p-3 opacity-50"
        data-testid={`cari-morph-inactive-${morphed ? "librarian" : "warhound"}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-3xl">{morphed ? "📚" : "🐕"}</span>
          <div className="text-sm font-bold text-neutral-300">{inactive.name}</div>
        </div>
        <div className="text-[11px] text-neutral-500 leading-snug mt-1">{inactive.description}</div>
        <div className="text-[9px] uppercase tracking-widest mt-2 text-neutral-600">
          Inactive stance
        </div>
      </div>
    </>
  );
}

/* ─── 🎭 Showtime Special Row — canon Feb 2026 (tier between Specials & Ultimate) ─── */
function ShowtimeSpecialRow({ fighter, accent: a }) {
  const list = fighter.showtime_specials || [];
  return (
    <div data-testid={`showdown-showtime-specials-${fighter.id}`}>
      <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
        <Zap size={12} /> Showtime Special{list.length > 1 ? "s" : ""}
        <span className="text-[9px] text-neutral-500 normal-case tracking-normal italic">· mid-combo · low meter</span>
      </div>
      {list.length === 0 ? (
        <div className={`rounded-lg border border-dashed ${a.border} bg-black/20 p-3 flex items-center gap-3`}>
          <span className={`text-lg ${a.text}`}>🎭</span>
          <div className="flex-1">
            <div className={`text-[11px] font-bold ${a.text} tracking-widest uppercase`}>Awaiting Owner Drop</div>
            <div className="text-[10px] text-neutral-400 italic mt-0.5">
              Showtime Special sits between standard specials and the cinematic Ultimate — flair + tactical flex.
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((s, i) => (
            <div
              key={s.name}
              className={`rounded-lg border ${a.border} bg-black/40 p-3`}
              data-testid={`showdown-showtime-special-${fighter.id}-${i}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className={`text-sm font-bold ${a.text} flex items-center gap-2`}>
                  <Zap size={12} /> {s.name}
                </div>
                {s.input && (
                  <div className="text-[10px] font-mono text-neutral-400 px-2 py-0.5 rounded border border-neutral-700 bg-black/60">
                    {s.input}
                  </div>
                )}
              </div>
              <div className="text-[11px] text-neutral-300 mt-2 leading-relaxed">{s.description}</div>
              {s.flair && (
                <div className={`text-[10px] italic mt-2 border-l-2 ${a.border} pl-2 text-neutral-400`}>
                  <span className={`${a.text} font-bold`}>Flair:</span> {s.flair}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── 🤝 Showtime Harmony Row — dramatic story bond (canon Feb 2026) ─── */
function ShowtimeFriendshipRow({ fighter, accent: a }) {
  const f = fighter.showtime_friendship;
  return (
    <div data-testid={`showdown-showtime-harmony-${fighter.id}`}>
      <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
        <Trophy size={12} /> Showtime Harmony
        <span className="text-[9px] text-neutral-500 normal-case tracking-normal italic">· post-match dramatic bond</span>
      </div>
      {!f ? (
        <div className={`rounded-lg border border-dashed ${a.border} bg-black/20 p-3 flex items-center gap-3`}>
          <span className={`text-lg ${a.text}`}>🤝</span>
          <div className="flex-1">
            <div className={`text-[11px] font-bold ${a.text} tracking-widest uppercase`}>Awaiting Owner Drop</div>
            <div className="text-[10px] text-neutral-400 italic mt-0.5">
              A short dramatic story beat rooted in this fighter&apos;s canon — not a comedy gag.
            </div>
          </div>
        </div>
      ) : (
        <div className={`rounded-lg border ${a.border} bg-black/40 p-3`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className={`text-sm font-bold ${a.text}`}>🤝 {f.name}</div>
            {f.input && (
              <div className="text-[10px] font-mono text-neutral-400 px-2 py-0.5 rounded border border-neutral-700 bg-black/60">
                {f.input}
              </div>
            )}
          </div>
          {f.tone && (
            <div className={`text-[10px] italic mt-1 ${a.text} opacity-80`}>{f.tone}</div>
          )}
          <div className="text-[11px] text-neutral-300 mt-2 leading-relaxed">{f.description}</div>
          {f.story_beat && (
            <div className={`text-[10px] italic mt-2 border-l-2 ${a.border} pl-2 text-neutral-400`}>
              <span className={`${a.text} font-bold`}>Story beat:</span> {f.story_beat}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Special Move Row + Shush! Silence Preview Overlay ─── */
function SpecialMoveRow({ special, index, accent, fighterId }) {
  const [silencing, setSilencing] = useState(false);
  const isShush =
    fighterId === "cari" && special.name.toLowerCase().startsWith("shush");

  const fireSilence = () => {
    if (silencing) return;
    setSilencing(true);
    setTimeout(() => setSilencing(false), 3000); // 3s canonical lockout
  };

  return (
    <div className="relative rounded-lg border border-zinc-800 bg-black/40 p-3 overflow-hidden">
      {/* 🤫 Silence overlay */}
      {silencing && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur-[2px] animate-shush-in pointer-events-none"
          data-testid="shush-silence-overlay"
        >
          <div className="text-[110px] leading-none animate-shush-icon opacity-90">🤫</div>
          <div className={`text-[10px] uppercase tracking-[0.35em] mt-2 ${accent.text} font-bold`}>
            SILENCED · 3s
          </div>
          <div className="text-[9px] text-neutral-400 mt-1">No special moves</div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className={`text-sm font-bold ${accent.text}`}>
          {index + 1}. {special.name}
        </div>
        <div className="flex items-center gap-1.5">
          {isShush && (
            <button
              onClick={fireSilence}
              data-testid="shush-preview-btn"
              disabled={silencing}
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${accent.border} ${accent.text} hover:bg-cyan-500/20 disabled:opacity-40 transition`}
              title="Preview the Shush! silence overlay — 3s special-move lockout"
            >
              🤫 Fire
            </button>
          )}
          {special.input && (
            <div className="text-[10px] font-mono text-neutral-400 px-2 py-0.5 rounded border border-neutral-700 bg-black/60">
              {special.input}
            </div>
          )}
        </div>
      </div>
      <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">{special.description}</div>
    </div>
  );
}

function KitBreakdown({ fighter, locked = false }) {
  const a = ACCENT_MAP[fighter.accent] || ACCENT_MAP.neutral;
  const [showGore, setShowGore] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [arenaOpen, setArenaOpen] = useState(false);
  return (
    <div
      className="mt-6 rounded-2xl border-2 border-zinc-800 bg-black/50 p-6 space-y-5"
      data-testid={`showdown-kit-${fighter.id}`}
      data-locked={locked ? "true" : "false"}
    >
      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div
          className={`text-6xl leading-none ${locked ? `${a.text} opacity-40` : ""}`}
          style={locked ? { filter: "blur(1.5px) grayscale(60%)" } : undefined}
        >
          {locked ? "❓" : fighter.icon}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className={`text-xs uppercase tracking-widest ${a.text}`}>
            {locked ? "???" : fighter.tag}
          </div>
          <div
            className={`text-3xl font-bold ${locked ? `${a.text} opacity-70` : "text-white"}`}
            style={{ fontFamily: "Outfit, sans-serif", letterSpacing: locked ? "0.2em" : undefined }}
            data-testid={`showdown-kit-name-${fighter.id}`}
          >
            {locked ? "???" : fighter.name}
          </div>
          <div className="text-sm text-neutral-400">
            {locked ? "??? · Undiscovered Fighter" : fighter.archetype}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {/* 🥊 Fight! — combat engine stays 100% unlocked per Ticket 2 canon */}
          <button
            onClick={() => setArenaOpen(true)}
            data-testid={`showdown-fight-open-${fighter.id}`}
            className="px-4 py-2 rounded-lg text-xs uppercase tracking-widest font-bold border-2 border-red-500/60 bg-red-500/15 text-red-200 hover:brightness-125 hover:bg-red-500/25 transition flex items-center gap-2 shadow-[0_0_16px_rgba(239,68,68,0.25)]"
          >
            <Swords size={12} /> Fight! · Phase 2
          </button>
          <button
            onClick={() => setDemoOpen(true)}
            data-testid={`showdown-demo-open-${fighter.id}`}
            className={`px-4 py-2 rounded-lg text-xs uppercase tracking-widest font-bold border-2 ${a.border} ${a.bg} ${a.text} hover:brightness-125 transition flex items-center gap-2`}
          >
            <Play size={12} /> Demo Match
          </button>
        </div>
      </div>

      {demoOpen && (
        <ShowdownDemoMatch fighter={fighter} onClose={() => setDemoOpen(false)} />
      )}

      {arenaOpen && (
        <ShowdownArenaMatch
          fighter={fighter}
          opponent={fighter}
          onClose={() => setArenaOpen(false)}
        />
      )}

      {locked ? (
        <LockedKitCurtain accent={a} fighter={fighter} />
      ) : (
        <KitBreakdownBody fighter={fighter} a={a} showGore={showGore} setShowGore={setShowGore} />
      )}
    </div>
  );
}

/* ─── 🔒 Locked Kit Curtain — silhouette dossier for undiscovered fighters ─── */
function LockedKitCurtain({ accent: a, fighter }) {
  const totalSpecials = (fighter.specials || []).length;
  const totalShowtimeSpecials = (fighter.showtime_specials || []).length;
  const ults = fighter.ultimates || (fighter.ultimate ? [fighter.ultimate] : []);
  const totalFinishers = (fighter.finishers || []).length;
  return (
    <div
      className="space-y-4"
      data-testid={`showdown-kit-locked-${fighter.id}`}
    >
      {/* Prominent locked banner */}
      <div className={`rounded-lg border-2 ${a.border} ${a.bg} p-4 flex items-start gap-3`}>
        <Lock size={20} className={a.text} />
        <div className="flex-1">
          <div className={`text-sm font-bold uppercase tracking-widest ${a.text}`}>
            Undiscovered Fighter
          </div>
          <div className="text-xs text-neutral-300 mt-1 leading-relaxed">
            This fighter&apos;s dossier is redacted until you hatch them from the{" "}
            <span className={`${a.text} font-bold`}>Dragonious Fighters Egg Nest 🥚</span>.
            The combat engine stays live above — challenge them any time.
          </div>
        </div>
      </div>

      {/* Masked mechanic */}
      <div className={`rounded-lg border ${a.border} bg-black/40 p-4`}>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
          <Zap size={12} /> Unique Mechanic
        </div>
        <div className={`text-base font-bold ${a.text} opacity-70 mt-1`} style={{ letterSpacing: "0.2em" }}>???</div>
        <div className="text-xs text-neutral-500 mt-1 italic tracking-widest">?????? ??????? ??? ????? ??? ??????.</div>
      </div>

      {/* Masked mobility slots */}
      <div>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
          <Wind size={12} /> Mobility & Core Traits
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {(fighter.mobility || []).map((_m, i) => (
            <div key={`lock-mob-${i}`} className={`rounded-lg border ${a.border} bg-black/30 p-3`}>
              <div className={`text-sm font-bold ${a.text} opacity-70`} style={{ letterSpacing: "0.15em" }}>???</div>
              <div className="text-[11px] text-neutral-500 mt-1 italic tracking-widest">????? ????? ?????</div>
            </div>
          ))}
        </div>
      </div>

      {/* Masked specials */}
      {totalSpecials > 0 && (
        <div>
          <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
            <Swords size={12} /> Special Move List · {totalSpecials} Sealed
          </div>
          <div className="space-y-2">
            {(fighter.specials || []).map((_s, i) => (
              <div
                key={`lock-sp-${i}`}
                className={`rounded-lg border ${a.border} bg-black/30 p-3 flex items-center gap-3`}
                data-testid={`showdown-locked-special-${fighter.id}-${i}`}
              >
                <Lock size={12} className={`${a.text} opacity-70 shrink-0`} />
                <div className="flex-1">
                  <div className={`text-sm font-bold ${a.text} opacity-70`} style={{ letterSpacing: "0.15em" }}>???</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5 italic tracking-widest">
                    ?????? ????? ??? ???? ?? ????? ???
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Masked showtime specials */}
      <div>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
          <Zap size={12} /> Showtime Special{totalShowtimeSpecials !== 1 ? "s" : ""} · {totalShowtimeSpecials || "?"} Sealed
        </div>
        <div className={`rounded-lg border border-dashed ${a.border} bg-black/20 p-3 flex items-center gap-3`}>
          <Lock size={12} className={`${a.text} opacity-70 shrink-0`} />
          <div className={`text-[11px] italic ${a.text} opacity-70 tracking-widest`}>
            ???????? ??????? · ????-????? · ??? ????
          </div>
        </div>
      </div>

      {/* Masked showtime ultimate(s) */}
      {ults.length > 0 && (
        <div className="space-y-2">
          <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
            <Trophy size={12} /> Showtime Ultimate{ults.length > 1 ? "s" : ""} · Sealed
          </div>
          {ults.map((_u, i) => (
            <div key={`lock-ult-${i}`} className={`rounded-lg border-2 ${a.border} bg-gradient-to-br from-black/60 to-black/30 p-4`}>
              <div className={`text-lg font-bold ${a.text} opacity-70`} style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.2em" }}>???</div>
              <div className="text-xs text-neutral-500 mt-2 italic tracking-widest">
                ??? ??????? ?? ??? ??????? ????? ??????? ??????.
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Masked showtime harmony */}
      <div>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
          <Trophy size={12} /> Showtime Harmony · Sealed
        </div>
        <div className={`rounded-lg border border-dashed ${a.border} bg-black/20 p-3 flex items-center gap-3`}>
          <Lock size={12} className={`${a.text} opacity-70 shrink-0`} />
          <div className={`text-[11px] italic ${a.text} opacity-70 tracking-widest`}>
            ???????? ????? ???? · ??????-?????????
          </div>
        </div>
      </div>

      {/* Masked showtime fatalities */}
      {totalFinishers > 0 && (
        <div>
          <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
            <Skull size={12} /> Showtime Judgment{totalFinishers === 1 ? "" : "s"} · {totalFinishers} Sealed
          </div>
          <div className="space-y-2">
            {(fighter.finishers || []).map((f, i) => (
              <div
                key={`lock-fin-${i}`}
                className={`rounded-lg border ${a.border} bg-black/30 p-3`}
                data-testid={`showdown-locked-finisher-${fighter.id}-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className={`text-sm font-bold ${a.text} opacity-70`} style={{ letterSpacing: "0.15em" }}>
                    🎬 {i + 1}. ???
                  </div>
                  {f.mature && (
                    <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded border border-red-500/40 text-red-400/70 uppercase tracking-widest">
                      Mature
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-neutral-500 mt-2 italic tracking-widest">
                  ???????? ????? ????? — ????? ?? ??????? ???.
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-neutral-600 italic pt-2 border-t border-zinc-800">
        Dossier redacted per progressive-discovery canon. Silhouette + accent colour retained.
      </div>
    </div>
  );
}

/* ─── Original unlocked kit body — extracted so locked variant can swap in cleanly ─── */

/* ⚔️ Cosmic Ultimate Weapon block — always renders. Shows a "Pending Owner Canon"
   placeholder when unassigned so every hero card telegraphs the schema slot.
   1:1 canon carry-over from the parent RPG; cosmetic-only (no fight stats). */
function CosmicWeaponBlock({ fighter, a }) {
  const w = fighter.cosmic_ultimate_weapon;
  const pending = !w || w.status === "tbd" || !w.name;
  return (
    <div
      className={`rounded-lg border-2 ${a.border} bg-gradient-to-br from-black/70 to-black/40 p-4`}
      data-testid={`showdown-cosmic-weapon-${fighter.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
          <Wand2 size={12} /> Cosmic Ultimate Weapon
          <span className="text-[9px] text-neutral-500 normal-case tracking-normal italic">· 1:1 RPG canon</span>
        </div>
        {pending ? (
          <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${a.border} ${a.bg} ${a.text} font-bold`}>
            TBD
          </span>
        ) : (
          <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${a.border} bg-black/60 ${a.text} font-bold flex items-center gap-1`}>
            <Sparkles size={9} /> Equipped
          </span>
        )}
      </div>
      {pending ? (
        <div className="text-[11px] italic text-neutral-400 leading-relaxed">
          🎁 {w?.note || "Pending owner canon — 1-to-1 RPG weapon coming soon."}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-1">
            {w.icon && <span className="text-2xl leading-none">{w.icon}</span>}
            <div className="text-base font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              {w.name}
            </div>
          </div>
          {w.description && (
            <div className="text-xs text-neutral-300 mt-1.5 leading-relaxed">{w.description}</div>
          )}
          <div className="text-[9px] italic text-neutral-500 mt-2 tracking-widest uppercase">
            Cosmetic canon · No in-fight stat modifier
          </div>
        </>
      )}
    </div>
  );
}

/* 🎵 Battle Song block — hero-tied looping BGM. Audio is bound to the FIGHTER
   because Arcade backgrounds are randomised, so the song follows the character.
   Muted by default (browser autoplay policy); user hits play to arm loop. */
function BattleSongBlock({ fighter, a }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const song = fighter.battle_song;

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      className={`rounded-lg border-2 ${a.border} bg-gradient-to-br from-black/70 to-black/40 p-4`}
      data-testid={`showdown-battle-song-${fighter.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
          <Music size={12} /> Battle Song
          <span className="text-[9px] text-neutral-500 normal-case tracking-normal italic">· hero-tied BGM · loops in-fight</span>
        </div>
        <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${a.border} bg-black/60 ${a.text} font-bold`}>
          {song.loop ? "Loop" : "One-shot"}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          data-testid={`showdown-battle-song-toggle-${fighter.id}`}
          className={`flex-shrink-0 w-11 h-11 rounded-full border-2 ${a.border} ${a.bg} ${a.text} flex items-center justify-center hover:brightness-125 transition-[filter] active:scale-95`}
          aria-label={playing ? "Pause battle song" : "Play battle song"}
        >
          <Play size={18} fill={playing ? "currentColor" : "none"} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
            {song.title || `${fighter.name}'s Battle Song`}
          </div>
          {song.attribution && (
            <div className="text-[10px] italic text-neutral-500 mt-0.5 truncate">{song.attribution}</div>
          )}
        </div>
      </div>
      <audio
        ref={audioRef}
        src={song.url}
        loop={!!song.loop}
        preload="none"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        data-testid={`showdown-battle-song-audio-${fighter.id}`}
      />
    </div>
  );
}

function KitBreakdownBody({ fighter, a, showGore, setShowGore }) {
  const [show3D, setShow3D] = useState(false);
  const [showAnim3D, setShowAnim3D] = useState(false);
  const hasAnimPipeline = !!fighter.animated_model;
  return (
    <>
      {/* 🧊 WebGL 3D fighter card — opt-in toggle (Feb 2026 owner directive) */}
      <div className={`rounded-lg border ${a.border} ${a.bg} p-3 flex items-center justify-between gap-3 flex-wrap`}>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
          <Box size={12} /> 3D Fighter View
          <span className="text-[9px] normal-case tracking-normal italic text-neutral-500">
            · procedural placeholder{hasAnimPipeline ? " · animated pipeline available ↓" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShow3D((v) => !v)}
            data-testid={`showdown-3d-toggle-${fighter.id}`}
            className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${a.border} bg-black/60 ${a.text} font-bold hover:brightness-125 transition`}
          >
            {show3D ? "Hide 3D ↑" : "View in 3D ↓"}
          </button>
          {hasAnimPipeline && (
            <button
              onClick={() => setShowAnim3D((v) => !v)}
              data-testid={`showdown-3d-anim-toggle-${fighter.id}`}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-yellow-500/60 bg-yellow-500/10 text-yellow-300 font-bold hover:brightness-125 transition`}
              title="Animated pipeline — load real character GLB with move-triggered animations"
            >
              {showAnim3D ? "Hide Anim ↑" : "🎬 Anim Rig ↓"}
            </button>
          )}
        </div>
      </div>
      {show3D && (
        <Fighter3DCard fighter={fighter} height={420} />
      )}
      {showAnim3D && hasAnimPipeline && (
        <Fighter3DAnimatedCard fighter={fighter} height={460} />
      )}

      {/* Unique Mechanic */}
      <div className={`rounded-lg border ${a.border} ${a.bg} p-4`}>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
          <Zap size={12} /> Unique Mechanic
        </div>
        <div className="text-base font-bold text-white mt-1">{fighter.unique_mechanic.name}</div>
        <div className="text-xs text-neutral-300 mt-1 leading-relaxed">
          {fighter.unique_mechanic.description}
        </div>
      </div>

      {/* Character Biography — surfaces tagline / origin / faction when present.
          Also shows a "🟡 CANON PENDING" chip when key narrative slots are empty,
          per Feb 2026 owner directive that every fighter render uniformly against
          Roe's dossier layout. */}
      <div
        className={`rounded-lg border ${a.border} bg-gradient-to-br from-black/60 to-black/30 p-4`}
        data-testid={`showdown-bio-${fighter.id}`}
      >
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 flex-wrap`}>
          <Info size={12} /> Character Dossier
          {fighter.canon_tier && CANON_TIER_META[fighter.canon_tier] && (
            <span
              className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border font-bold ${CANON_TIER_META[fighter.canon_tier].border} ${CANON_TIER_META[fighter.canon_tier].bg} ${CANON_TIER_META[fighter.canon_tier].text}`}
              data-testid={`showdown-canon-tier-${fighter.id}`}
              title={CANON_TIER_META[fighter.canon_tier].note}
            >
              {CANON_TIER_META[fighter.canon_tier].icon} {CANON_TIER_META[fighter.canon_tier].label}
            </span>
          )}
          {fighter.faction && (
            <span className="text-[9px] normal-case tracking-normal italic text-neutral-500">· {fighter.faction}</span>
          )}
          {fighter._canon_pending && (fighter._canon_pending.origin || fighter._canon_pending.tagline || fighter._canon_pending.faction || fighter._canon_pending.director_notes) && (
            <span
              className="ml-auto text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-yellow-500/60 bg-yellow-500/10 text-yellow-300 font-bold"
              data-testid={`showdown-bio-pending-${fighter.id}`}
              title="One or more of this fighter's narrative slots (faction, origin, tagline, director's notes) are pending owner canon."
            >
              🟡 Canon Pending
            </span>
          )}
        </div>
        {fighter.tagline && (
          <div className={`text-sm font-bold italic ${a.text} mt-2`} style={{ fontFamily: "Outfit, sans-serif" }}>
            {fighter.tagline}
          </div>
        )}
        {fighter.origin && (
          <div className="text-xs text-neutral-300 mt-2 leading-relaxed">
            {fighter.origin}
          </div>
        )}
        {!fighter.tagline && !fighter.origin && !fighter.faction && (
          <div className="text-[11px] italic text-neutral-500 mt-2 leading-relaxed">
            🎁 Owner canon pending — full character dossier drop incoming.
          </div>
        )}
      </div>

      {/* 🖼️ Official Character Design Sheet — surfaces when owner drops canon art */}
      {fighter.dossier_image && (
        <div
          className={`rounded-lg border-2 ${a.border} bg-black/50 overflow-hidden`}
          data-testid={`showdown-dossier-image-${fighter.id}`}
        >
          <img
            src={fighter.dossier_image}
            alt={`${fighter.name} — official character design sheet`}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
          <div className={`px-3 py-1.5 bg-black/70 text-[10px] italic ${a.text} tracking-widest flex items-center gap-1.5`}>
            <Sparkles size={10} /> Official Design Sheet · {fighter.name}
          </div>
        </div>
      )}

      {/* ⚔️ Cosmic Ultimate Weapon — 1:1 canon carry-over from parent RPG. */}
      {/* Always renders: shows "Pending Owner Canon" placeholder when unassigned. */}
      <CosmicWeaponBlock fighter={fighter} a={a} />

      {/* 🎵 Battle Song — hero-tied looping background audio. Bound to fighter */}
      {/* because Arcade backgrounds are randomised — song follows the fighter. */}
      {fighter.battle_song?.url && (
        <BattleSongBlock fighter={fighter} a={a} />
      )}

      {/* Home Stage (optional — Glitch: Aurora Borealis, etc.) */}
      {fighter.home_stage && (
        <div className={`rounded-lg border ${a.border} bg-black/40 p-4`} data-testid={`showdown-homestage-${fighter.id}`}>
          <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
            <MapPin size={12} /> Home Stage
          </div>
          <div className="text-base font-bold text-white mt-1">{fighter.home_stage.name}</div>
          {fighter.home_stage.tagline && (
            <div className="text-xs text-neutral-400 mt-0.5 italic">{fighter.home_stage.tagline}</div>
          )}
          {fighter.home_stage.detail && (
            <div className="text-xs text-neutral-300 mt-2 leading-relaxed">{fighter.home_stage.detail}</div>
          )}
        </div>
      )}

      {/* Passive Ability (optional — Trig etc.) */}
      {fighter.passive_ability && (
        <div className={`rounded-lg border ${a.border} bg-black/40 p-4`} data-testid={`showdown-passive-${fighter.id}`}>
          <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-1`}>
            <ShieldAlert size={12} /> Passive Ability
          </div>
          <div className="text-base font-bold text-white">{fighter.passive_ability.name}</div>
          <div className="text-xs text-neutral-300 mt-1 leading-relaxed">{fighter.passive_ability.description}</div>
          <div className="grid md:grid-cols-3 gap-2 mt-3">
            {fighter.passive_ability.trigger && (
              <div className="rounded border border-neutral-800 bg-black/50 p-2">
                <div className={`text-[9px] uppercase tracking-widest ${a.text} font-bold mb-0.5`}>Trigger</div>
                <div className="text-[10px] text-neutral-300 leading-relaxed">{fighter.passive_ability.trigger}</div>
              </div>
            )}
            {fighter.passive_ability.condition && (
              <div className="rounded border border-neutral-800 bg-black/50 p-2">
                <div className={`text-[9px] uppercase tracking-widest ${a.text} font-bold mb-0.5`}>Condition</div>
                <div className="text-[10px] text-neutral-300 leading-relaxed">{fighter.passive_ability.condition}</div>
              </div>
            )}
            {fighter.passive_ability.action && (
              <div className="rounded border border-neutral-800 bg-black/50 p-2">
                <div className={`text-[9px] uppercase tracking-widest ${a.text} font-bold mb-0.5`}>Action</div>
                <div className="text-[10px] text-neutral-300 leading-relaxed">{fighter.passive_ability.action}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Director Notes (optional) */}
      {fighter.director_notes && (
        <div className="rounded-lg border border-neutral-800 bg-black/40 p-3" data-testid={`showdown-director-${fighter.id}`}>
          <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
            <Info size={12} /> Director&apos;s Notes
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {["concept", "visuals", "audio"].map((k) =>
              fighter.director_notes[k] ? (
                <div key={k} className="rounded border border-neutral-800 bg-black/50 p-2">
                  <div className={`text-[9px] uppercase tracking-widest ${a.text} font-bold mb-0.5`}>{k}</div>
                  <div className="text-[10px] text-neutral-300 leading-relaxed italic">{fighter.director_notes[k]}</div>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}

      {/* Synergy Tip (optional) */}
      {fighter.synergy_tip && (
        <div className={`rounded-lg border border-emerald-700/40 bg-emerald-950/10 p-3`} data-testid={`showdown-synergy-${fighter.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-2 mb-1">
            <Trophy size={12} /> Synergy Tip
          </div>
          <div className="text-[11px] text-neutral-300 leading-relaxed italic">{fighter.synergy_tip}</div>
        </div>
      )}

      {/* Mobility */}
      <div>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
          <Wind size={12} /> Mobility & Core Traits
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {fighter.id === "cari" ? (
            <CariStancePreview accent={a} mobility={fighter.mobility} />
          ) : (
            fighter.mobility.map((m) => (
              <div key={m.name} className="rounded-lg border border-zinc-800 bg-black/40 p-3">
                <div className={`text-sm font-bold ${a.text}`}>{m.name}</div>
                <div className="text-[11px] text-neutral-400 mt-1 leading-snug">{m.description}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Specials */}
      <div className="relative">
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
          <Swords size={12} /> Special Move List
        </div>
        <div className="space-y-2">
          {fighter.specials.map((s, i) => (
            <SpecialMoveRow
              key={s.name}
              special={s}
              index={i}
              accent={a}
              fighterId={fighter.id}
            />
          ))}
          {fighter.specials.length === 0 && (
            <div className={`rounded-lg border border-dashed ${a.border} bg-black/20 p-3 text-[11px] italic ${a.text} opacity-80`}>
              🎭 Awaiting owner drop · standard specials to be authored.
            </div>
          )}
        </div>
      </div>

      {/* Showtime Special Moves — new tier between Specials and Ultimate (Feb 2026) */}
      <ShowtimeSpecialRow fighter={fighter} accent={a} />

      {/* Ultimate(s) — a.k.a. Showtime Ultimate */}
      {(() => {
        const ults = fighter.ultimates || (fighter.ultimate ? [fighter.ultimate] : []);
        if (ults.length === 0) {
          return (
            <div data-testid={`showdown-showtime-ultimate-${fighter.id}`}>
              <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
                <Trophy size={12} /> Showtime Ultimate
                <span className="text-[9px] text-neutral-500 normal-case tracking-normal italic">· full meter · multi-stage cinematic</span>
              </div>
              <div className={`rounded-lg border border-dashed ${a.border} bg-black/20 p-3 flex items-center gap-3`}>
                <span className={`text-lg ${a.text}`}>🎬</span>
                <div className="flex-1">
                  <div className={`text-[11px] font-bold ${a.text} tracking-widest uppercase`}>Awaiting Owner Drop</div>
                  <div className="text-[10px] text-neutral-400 italic mt-0.5">
                    The apex cinematic super — showstopper freeze frame, multi-stage choreography, screen-shatter impact.
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="space-y-2" data-testid={`showdown-showtime-ultimate-${fighter.id}`}>
            <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
              <Trophy size={12} /> Showtime Ultimate{ults.length > 1 ? "s" : ""}
              <span className="text-[9px] text-neutral-500 normal-case tracking-normal italic">· full meter · multi-stage cinematic</span>
            </div>
            {ults.map((u, i) => (
              <div key={u.name} className={`rounded-lg border-2 ${a.border} bg-gradient-to-br from-black/60 to-black/30 p-4`}>
                <div className="flex items-center justify-between mt-1">
                  <div className={`text-lg font-bold ${a.text}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                    {ults.length > 1 ? `${i + 1}. ` : ""}{u.name}
                  </div>
                  {u.input && (
                    <div className="text-[10px] font-mono text-neutral-400 px-2 py-0.5 rounded border border-neutral-700 bg-black/60">
                      {u.input}
                    </div>
                  )}
                </div>
                {u.style && (
                  <div className="text-[10px] italic text-neutral-500 mt-1">{u.style}</div>
                )}
                <div className="text-xs text-neutral-300 mt-2 leading-relaxed">{u.description}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Showtime Harmony — dramatic, character-connected post-KO bond (Feb 2026) */}
      <ShowtimeFriendshipRow fighter={fighter} accent={a} />

      {/* Finishers — a.k.a. Showtime Judgment */}
      <div>
        <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-2`}>
          <Skull size={12} /> Showtime Judgment{fighter.finishers.length === 1 ? "" : "s"}
          {fighter.finishers.length > 1 && ` · ${fighter.finishers.length} Signature Enders`}
          {fighter.finishers.length === 0 && <span className="text-[9px] text-neutral-500 normal-case tracking-normal italic">· post-match execution</span>}
        </div>
        {fighter.finishers.length === 0 ? (
          <div className={`rounded-lg border border-dashed ${a.border} bg-black/20 p-3 flex items-center gap-3`}>
            <span className={`text-lg ${a.text}`}>🎬</span>
            <div className="flex-1">
              <div className={`text-[11px] font-bold ${a.text} tracking-widest uppercase`}>Awaiting Owner Drop</div>
              <div className="text-[10px] text-neutral-400 italic mt-0.5">
                Post-match cinematic execution (stylized/clean OR gory) — author when ready.
              </div>
            </div>
          </div>
        ) : (
        <div className="space-y-2">
          {fighter.finishers.map((f, i) => {
            const isGated = f.mature && !showGore;
            return (
              <div
                key={f.name}
                className={`rounded-lg border p-3 ${
                  f.mature
                    ? "border-red-700/40 bg-red-900/10"
                    : "border-zinc-800 bg-black/40"
                }`}
                data-testid={`showdown-finisher-${fighter.id}-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className={`text-sm font-bold ${a.text}`}>
                    🎬 {i + 1}. {f.name}
                    {f.mature && (
                      <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded border border-red-500/50 text-red-300 uppercase tracking-widest">
                        Mature
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] italic text-neutral-500">{f.tone}</div>
                </div>
                {isGated ? (
                  <div className="text-[11px] text-red-300/80 mt-2 italic">
                    🔒 Gore mode required. Enter the unlock code below to reveal.
                  </div>
                ) : (
                  <div className="text-[11px] text-neutral-300 mt-2 leading-relaxed">{f.description}</div>
                )}
                {f.note && !isGated && (
                  <div className="text-[10px] text-neutral-500 mt-2 italic border-l-2 border-neutral-700 pl-2">
                    {f.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Gore-mode secret unlock */}
      {fighter.gore_mode_code && (
        <div className="rounded-lg border border-red-700/30 bg-red-900/5 p-3">
          <div className="text-[10px] uppercase tracking-widest text-red-400 flex items-center gap-2">
            <Info size={12} /> Mature Mode · Secret Unlock
          </div>
          <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
            {fighter.gore_mode_hint}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code className="text-[11px] font-mono text-red-300 px-2 py-1 rounded border border-red-700/40 bg-black/60">
              {fighter.gore_mode_code}
            </code>
            <button
              onClick={() => setShowGore((v) => !v)}
              data-testid={`showdown-gore-toggle-${fighter.id}`}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${
                showGore
                  ? "border-red-500/60 bg-red-500/20 text-red-100"
                  : "border-neutral-700 text-neutral-400 hover:border-red-500/40 hover:text-red-300"
              }`}
            >
              {showGore ? "Gore Mode ON" : "Enable Gore Mode"}
            </button>
          </div>
        </div>
      )}

      <div className="text-[10px] text-neutral-600 italic pt-2 border-t border-zinc-800">
        Design-preview kit. Real fighting-game engine not yet shipped — this is a canon dossier.
      </div>
    </>
  );
}

export default function CrossoverShowdown() {
  const [selectedId, setSelectedId] = useState("roc");
  const selected = ROSTER.find((f) => f.id === selectedId);
  const { user } = useAuth();
  const { isLocked, isNewlyUnlocked, newlyUnlocked } = useDragoniousUnlocks(user?.user_id);

  // 🐣 Emit toasts + focus the newly-hatched card in the roster grid the moment
  // the delta fires. Keeps the animation and the notification in sync.
  useEffect(() => {
    if (newlyUnlocked.size === 0) return;
    const heroesInOrder = ROSTER.filter((f) => newlyUnlocked.has(f.id));
    heroesInOrder.forEach((f, idx) => {
      setTimeout(() => {
        toast.success(`🐣 ${f.name} unlocked — kit revealed in the Codex!`, {
          duration: 3500,
        });
      }, idx * 180);
    });
    // If the currently-selected fighter is one of the fresh ones, keep them.
    // Otherwise, auto-focus the FIRST newly unlocked so the burst is centre-stage.
    if (!newlyUnlocked.has(selectedId) && heroesInOrder.length > 0) {
      setSelectedId(heroesInOrder[0].id);
    }
  }, [newlyUnlocked]);

  return (
    <div data-testid="showdown-root">
      {/* ✨ Codex Unlock Cinematic keyframes — inline so the file stays self-contained */}
      <style>{`
        @keyframes codexUnlockBurst {
          0%   { box-shadow: 0 0 0 rgba(255,255,255,0);                     transform: scale(1);    opacity: 0; }
          15%  { box-shadow: 0 0 32px 8px currentColor, 0 0 96px 16px currentColor; transform: scale(1.02); opacity: 1; }
          60%  { box-shadow: 0 0 24px 4px currentColor;                     transform: scale(1);    opacity: 1; }
          100% { box-shadow: 0 0 0 rgba(255,255,255,0);                     transform: scale(1);    opacity: 0; }
        }
        @keyframes codexUnlockSweep {
          0%   { transform: translateX(-120%); opacity: 0; }
          30%  { opacity: 1; }
          100% { transform: translateX(120%);  opacity: 0; }
        }
        @keyframes codexUnlockRibbon {
          0%   { transform: translateY(-8px) scale(0.85); opacity: 0; }
          20%  { transform: translateY(0)    scale(1);    opacity: 1; }
          80%  { transform: translateY(0)    scale(1);    opacity: 1; }
          100% { transform: translateY(-4px) scale(0.95); opacity: 0; }
        }
      `}</style>
      {/* Header + 🐉 Dragonious Fighters Emblem (Fractured Claw) */}
      <div className="text-center mb-6">
        {/* Emblem — CSS-forged Fractured Claw (matte black · blood red edge · royal purple core) */}
        <div className="mx-auto mb-4 relative w-28 h-28 md:w-36 md:h-36" data-testid="showdown-df-emblem" aria-label="Dragonious Fighters emblem">
          <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_35%_35%,rgba(139,92,246,0.35),rgba(0,0,0,0.9)_60%)]" />
          <div className="absolute inset-1 rounded-full border-2 border-red-500/60 shadow-[0_0_28px_rgba(239,68,68,0.55),inset_0_0_18px_rgba(139,92,246,0.45)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-5xl md:text-6xl select-none drop-shadow-[0_0_10px_rgba(239,68,68,0.8)]">🐉</span>
          </div>
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] md:text-[9px] uppercase tracking-[0.35em] font-black text-red-300 whitespace-nowrap" style={{ textShadow: "0 0 6px rgba(139,92,246,0.9)" }}>
            Dragonious Fighters
          </div>
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-red-500/40 text-red-300 mb-3">
          <Swords size={14} />
          <span className="font-accent text-xs tracking-[0.3em] uppercase">Dragonious · Alt-Canon</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-[0.9] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Crossover <span className="text-red-400">Showdown</span>
        </h2>
        <p className="text-neutral-400 max-w-xl mx-auto text-sm">
          Alt-canon Fighting-Game roster. Each hero gets a parallel kit — separate from their RPG identity.
          Full engine ships later; browse the character dossiers now.
        </p>
      </div>

      {/* 🔒 Admin-only: Boss Codex Preview (fetches from
          /api/dragonious-fighters/roster/admin-full — invisible to
          non-owners; hydrates lazily on toggle). */}
      <AdminBossCodex userRole={user?.role} />

      {/* Roster grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="showdown-roster">
        {ROSTER.map((f) => (
          <FighterCard
            key={f.id}
            fighter={f}
            selected={f.id === selectedId}
            onSelect={setSelectedId}
            locked={isLocked(f.id)}
            newlyUnlocked={isNewlyUnlocked(f.id)}
          />
        ))}
        {/* Coming Soon slots — 16 heroes still pending */}
        {[1].map((i) => (
          <div
            key={`coming-${i}`}
            className="rounded-2xl border-2 border-dashed border-neutral-800 bg-black/20 p-5 min-h-[172px] flex flex-col items-center justify-center text-center opacity-60"
            data-testid={`showdown-comingsoon-${i}`}
          >
            <div className="text-4xl mb-1">❓</div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Hero Kit</div>
            <div className="text-[10px] text-neutral-600 mt-1">Coming soon</div>
          </div>
        ))}
      </div>

      {/* Kit breakdown */}
      {selected && <KitBreakdown fighter={selected} locked={isLocked(selected.id)} />}

      {/* 🌳 Per-hero Skill Tree entry — only for heroes with cross-RPG trees */}
      {selected && SKILL_TREE_HERO_MAP[selected.id] && (
        <div className="max-w-2xl mx-auto px-3 mt-4 mb-2" data-testid="fighter-skill-tree-cta">
          <a
            href={`/skill-tree/${SKILL_TREE_HERO_MAP[selected.id]}`}
            className="flex items-center justify-between gap-3 rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-950/40 via-black/30 to-emerald-950/40 px-4 py-3 hover:from-emerald-900/40 hover:to-emerald-900/40 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🌳</span>
              <div className="text-left">
                <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-300/80 font-bold">Chronograph Points</div>
                <div className="text-sm font-bold text-emerald-100">{selected.name}&apos;s Skill Tree</div>
                <div className="text-[10px] text-emerald-200/70 italic">Mind · Body · Soul — unique authored kit</div>
              </div>
            </div>
            <span className="text-emerald-300 text-lg">→</span>
          </a>
        </div>
      )}

      {/* 🗺️ Crossover Stages Gallery — three-stage soft-open (Feb 2026 owner directive) */}
      <StagesGalleryPanel
        stages={CROSSOVER_STAGES.filter((s) => OPEN_STAGE_IDS.has(s.id))}
        totalStagesCount={CROSSOVER_STAGES.length}
      />

      {/* 🌀 Disruption — Universal Combo-Breaker Mechanic */}
      <DisruptionMechanicPanel mechanic={DISRUPTION_MECHANIC} />

      {/* Mini-Bosses — non-canon rotating gauntlet obstacles */}
      <MiniBossesPanel bosses={MINI_BOSSES} />

      {/* Boss Encounter — Non-Playable Chronophage (Boss Echo) */}
      <ChronophageBossPanel boss={CHRONOPHAGE} />

      {/* 🌗 Secret Boss — Dio the Dual-Shadow (NON-CANON, riddle-gated) */}
      <DioSecretBossPanel boss={DIO} />

      {/* 🧃 Kia Masterson — Non-Canon Boss · Juice Brawler / Pressure Boss */}
      <KiaMastersonBossPanel boss={KIA_MASTERSON} />

      {/* 🌌 Astraea — Hidden Stage-Force · Non-Playable · Non-Boss */}
      <AstraeaHiddenPanel entity={ASTRAEA} />

      {/* 📖 Gail Bloom — HIDDEN until owner-defined unlock ships (Feb 2026 directive).
          Player-facing UI shows nothing. Dossier + data constant remain in the codebase
          for author reference and future unlock wiring. */}
      {GAIL_BLOOM_UNLOCKED && <GailBloomVillainPanel villain={GAIL_BLOOM} />}

      {/* Footer */}
      <div className="mt-6 text-center text-[10px] text-neutral-600 italic">
        10 / 21 main-hero kits canonized · 11 pending · + 5 playable non-canon heroes (Stacy · Gage Cannon · Trig · Yuki Bend · Tory Prime — Dragonious Fighters exclusives) · + 1 semi-canon playable (Warren Spicer — David Lauer-connected) · {MINI_BOSSES.length} mini-boss{MINI_BOSSES.length !== 1 ? "es" : ""} (Gourmand Nelson · Mr. Aether — Chronophage-consumed) · 1 boss echo · 2 non-canon bosses (Dio · Kia Masterson) · 1 hidden stage-force (Astraea) · 1 canon unplayable villain (Gail Bloom) · Includes Glitch (Dual-Role Hero/Villain) · Parallel to RPG canon.
      </div>
    </div>
  );
}

/* ═══════════════════════ CHRONOPHAGE BOSS PANEL ═══════════════════════
 * A separate, purple-tinted panel below the playable roster. Renders the
 * non-playable BOSS ECHO of the Chronophage — clearly labeled as a
 * fighting-game manifestation, NOT the real RPG-canon Chronophage.
 *
 * 🔒 SPOILER GATE (owner canon, Feb 2026):
 * The player is NOT supposed to know this boss exists until they clear the
 * 1-on-1 gauntlet and hit the Winning Screen. Until then, the panel renders
 * a locked "???" mystery card. The reveal is persisted in localStorage as
 * `showdown.boss.revealed` (set true by the future gauntlet-victory hook).
 * Owner accounts (role === "owner") get a discreet preview toggle for QA.
 */
/* ═══════════════════════ MINI-BOSSES PANEL ═══════════════════════
 * Non-canon rotating gauntlet obstacles. Openly listed (not spoiler-gated
 * like the Chronophage Boss Echo). Each card expands to show full dossier.
 */
function MiniBossesPanel({ bosses }) {
  const [openId, setOpenId] = useState(null);
  if (!bosses || bosses.length === 0) return null;

  return (
    <div className="mt-10" data-testid="showdown-minibosses">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert size={12} className="text-orange-300" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-orange-300">
          Mini-Bosses · Non-Canon Gauntlet Pool
        </span>
        <span className="text-[10px] text-neutral-500">· {bosses.length} canonized</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {bosses.map((b) => {
          const a = ACCENT_MAP[b.accent] || ACCENT_MAP.orange;
          const open = openId === b.id;
          return (
            <div
              key={b.id}
              className={`rounded-2xl border-2 ${a.border} bg-gradient-to-br from-[#100604] via-[#0A0A0A] to-[#180a05] p-4`}
              data-testid={`showdown-miniboss-${b.id}`}
            >
              {/* Card head */}
              <div className="flex items-start gap-3">
                <div className="text-5xl leading-none">{b.icon}</div>
                <div className="flex-1">
                  <div className={`text-[10px] uppercase tracking-widest ${a.text}`}>{b.epithet}</div>
                  <div className="text-xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {b.name}
                  </div>
                  <div className="text-[11px] text-neutral-400">{b.archetype}</div>
                </div>
              </div>

              {/* Scope tag */}
              <div className={`mt-3 text-[10px] uppercase tracking-widest ${a.text} px-2 py-1 rounded border ${a.border} ${a.bg} inline-block`}>
                {b.scope}
              </div>

              {/* Gimmick strip */}
              <div className="mt-3 rounded-lg border border-neutral-800 bg-black/40 p-3">
                <div className={`text-[10px] uppercase tracking-widest ${a.text}`}>Gimmick</div>
                <div className="text-sm font-bold text-white mt-0.5">{b.gimmick.name}</div>
                <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">{b.gimmick.description}</div>
              </div>

              <button
                onClick={() => setOpenId(open ? null : b.id)}
                data-testid={`showdown-miniboss-toggle-${b.id}`}
                className={`mt-3 w-full px-3 py-2 rounded-lg border ${a.border} ${a.bg} ${a.text} text-[11px] uppercase tracking-widest font-bold hover:brightness-125 transition`}
              >
                {open ? "Hide Dossier ↑" : "View Full Dossier ↓"}
              </button>

              {open && (
                <div className="mt-4 space-y-3 text-[11px] leading-relaxed" data-testid={`showdown-miniboss-dossier-${b.id}`}>
                  {/* Moves */}
                  <div>
                    <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-1.5`}>
                      <Swords size={12} /> Special Moves
                    </div>
                    <div className="space-y-1.5">
                      {b.moves.map((m, i) => (
                        <div key={m.name} className="rounded border border-neutral-800 bg-black/50 p-2.5">
                          <div className={`text-xs font-bold ${a.text}`}>{i + 1}. {m.name}</div>
                          <div className="text-[11px] text-neutral-300 mt-0.5">{m.description}</div>
                          {m.role && (
                            <div className={`text-[10px] italic mt-1 ${a.text} opacity-80`}>
                              Role: {m.role}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Ultimate */}
                  {b.ultimate && (
                    <div className={`rounded border-2 ${a.border} bg-black/40 p-2.5`}>
                      <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2`}>
                        <Trophy size={12} /> Ultimate
                      </div>
                      <div className="text-sm font-bold text-white mt-0.5">{b.ultimate.name}</div>
                      <div className="text-[11px] text-neutral-300 mt-1">{b.ultimate.description}</div>
                    </div>
                  )}

                  {/* Humiliation */}
                  {b.humiliation && (
                    <div className="rounded border border-red-700/40 bg-red-900/10 p-2.5">
                      <div className="text-[10px] uppercase tracking-widest text-red-300 flex items-center gap-2">
                        <Skull size={12} /> Humiliation
                      </div>
                      <div className="text-sm font-bold text-white mt-0.5">{b.humiliation.name}</div>
                      <div className="text-[11px] text-neutral-300 mt-1">{b.humiliation.description}</div>
                    </div>
                  )}

                  {/* Combat loop */}
                  {b.combat_loop && (
                    <div>
                      <div className={`text-[10px] uppercase tracking-widest ${a.text} flex items-center gap-2 mb-1.5`}>
                        <Zap size={12} /> AI Combat Loop
                      </div>
                      <ol className="list-decimal list-inside space-y-0.5 text-neutral-300">
                        {b.combat_loop.map((step, i) => <li key={i}>{step}</li>)}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChronophageBossPanel({ boss }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [expanded, setExpanded] = useState(false);
  const [revealed, setRevealed] = useState(() => {
    try {
      return localStorage.getItem("showdown.boss.revealed") === "1";
    } catch { return false; }
  });

  const setRevealedPersist = (v) => {
    setRevealed(v);
    try {
      if (v) localStorage.setItem("showdown.boss.revealed", "1");
      else localStorage.removeItem("showdown.boss.revealed");
    } catch { /* localStorage unavailable — silent */ }
  };

  // ── Locked mystery card ──────────────────────────────────────────────
  if (!revealed) {
    return (
      <div
        className="mt-10 rounded-2xl border-2 border-dashed border-purple-800/50 bg-gradient-to-br from-[#05030a] via-[#0A0A0A] to-[#080510] p-8 text-center relative overflow-hidden"
        data-testid="showdown-boss-locked"
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none flex items-center justify-center text-[160px] leading-none select-none">
          ⏳
        </div>
        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-purple-400 mb-3 flex items-center justify-center gap-2">
            <ShieldAlert size={12} /> Locked · Non-Playable Boss
          </div>
          <div className="text-6xl mb-2 opacity-40">❓</div>
          <div className="text-3xl md:text-4xl font-bold text-neutral-500 tracking-widest" style={{ fontFamily: "Outfit, sans-serif" }}>
            ??? ??? ???
          </div>
          <div className="text-xs text-neutral-500 mt-3 max-w-md mx-auto italic leading-relaxed">
            A shadow at the end of the gauntlet. You&apos;ll meet him only after the Winning Screen.
            <br />
            <span className="text-neutral-600">Reveals after Gauntlet Victory.</span>
          </div>

          {isOwner && (
            <button
              onClick={() => setRevealedPersist(true)}
              data-testid="showdown-boss-owner-reveal"
              className="mt-5 px-3 py-1.5 rounded border border-purple-600/40 bg-purple-500/10 text-[10px] uppercase tracking-widest text-purple-300 hover:bg-purple-500/20 transition"
              title="Owner-only preview toggle. Non-owner players see only the ??? card."
            >
              👁 Owner Preview · Reveal Boss
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Revealed full dossier ────────────────────────────────────────────
  return (
    <div
      className="mt-10 rounded-2xl border-2 border-purple-700/50 bg-gradient-to-br from-[#0a0510] via-[#0A0A0A] to-[#100518] p-6 shadow-[inset_0_0_50px_rgba(147,51,234,0.15)]"
      data-testid="showdown-boss-chronophage"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} className="text-purple-300" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-purple-300">
            Non-Playable Boss · Gauntlet Final Encounter
          </span>
        </div>
        {isOwner && (
          <button
            onClick={() => setRevealedPersist(false)}
            data-testid="showdown-boss-owner-relock"
            className="px-2 py-1 rounded border border-purple-800/40 text-[9px] uppercase tracking-widest text-purple-400 hover:bg-purple-500/10 transition"
            title="Owner-only: re-lock the boss so non-owner players see the ??? card again."
          >
            🔒 Re-lock
          </button>
        )}
      </div>

      <div className="flex items-start gap-4">
        <div className="text-7xl leading-none">{boss.icon}</div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-purple-400">{boss.tag}</div>
          <div className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            {boss.name}
          </div>
          <div className="text-[11px] text-purple-300/80 mt-0.5 italic">{boss.subtitle}</div>
          <div className="text-sm text-neutral-400 mt-1">{boss.archetype}</div>
        </div>
      </div>

      {/* SCOPE LOCK — This is NOT the real Chronophage */}
      <div
        className="mt-4 rounded-lg border border-red-700/40 bg-red-900/10 p-3 text-[11px] leading-relaxed"
        data-testid="showdown-boss-scope-lock"
      >
        <div className="flex items-center gap-2 mb-1">
          <Info size={12} className="text-red-300" />
          <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">
            Scope Lock · NOT the Real Chronophage
          </span>
        </div>
        <div className="text-neutral-300">{boss.scope_lock}</div>
      </div>

      {/* Weapon + Passive strip */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-purple-700/40 bg-black/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-purple-300 flex items-center gap-2">
            <Hourglass size={12} /> Weapon
          </div>
          <div className="text-sm font-bold text-white mt-1">{boss.weapon.name}</div>
          <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">{boss.weapon.description}</div>
        </div>
        <div className="rounded-lg border border-purple-700/40 bg-black/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-purple-300 flex items-center gap-2">
            <ShieldAlert size={12} /> Core Passive
          </div>
          <div className="text-sm font-bold text-white mt-1">{boss.passive.name}</div>
          <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">{boss.passive.description}</div>
        </div>
      </div>

      {/* Expand/Collapse full dossier */}
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="showdown-boss-toggle"
        className="mt-4 w-full px-4 py-2 rounded-lg border border-purple-500/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20 text-xs uppercase tracking-widest font-bold transition"
      >
        {expanded ? "Hide Full Dossier ↑" : "View Full Boss Dossier ↓"}
      </button>

      {expanded && (
        <div className="mt-5 space-y-5" data-testid="showdown-boss-dossier">
          {/* 🎬 Obsidian Keep Siege cinematic */}
          <BossCinematic accent="purple" label="Chronophage Siege Cinematic · The Obsidian Keep Siege" />

          {/* Hard mode rules */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-300 flex items-center gap-2 mb-2">
              <Skull size={12} /> Hard / Extreme Mode Rules
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {boss.hard_mode_rules.map((r) => (
                <div key={r.name} className="rounded-lg border border-red-700/30 bg-red-900/5 p-3">
                  <div className="text-sm font-bold text-red-200">{r.name}</div>
                  <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">{r.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Boss moves */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-300 flex items-center gap-2 mb-2">
              <Swords size={12} /> Boss Special Moves
            </div>
            <div className="space-y-2">
              {boss.boss_moves.map((m, i) => (
                <div key={m.name} className="rounded-lg border border-purple-700/40 bg-black/40 p-3">
                  <div className="text-sm font-bold text-purple-200">
                    {i + 1}. {m.name}
                  </div>
                  <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">{m.description}</div>
                  {m.role && (
                    <div className="text-[10px] text-purple-300/80 mt-2 italic border-l-2 border-purple-700 pl-2">
                      Role: {m.role}
                    </div>
                  )}
                  {m.vulnerability && (
                    <div className="text-[10px] text-amber-300 mt-2 italic border-l-2 border-amber-500/60 pl-2">
                      ⚠ Vulnerability: {m.vulnerability}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Combat loop */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-300 flex items-center gap-2 mb-2">
              <Zap size={12} /> AI Combat Loop
            </div>
            <ol className="list-decimal list-inside space-y-1 text-[11px] text-neutral-300 leading-relaxed">
              {boss.combat_loop.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          <div className="text-[10px] text-neutral-600 italic pt-2 border-t border-zinc-800">
            Boss Echo dossier · Design-preview canon · Fighting-game engine not yet shipped.
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════ DIO SECRET BOSS PANEL ═══════════════════════
 * 🌗 Dio the Dual-Shadow — NON-CANON hidden secret boss.
 *
 * 🔒 SPOILER GATE: Non-owners see ONLY the riddle card until the future
 * riddle-solve hook flips localStorage.setItem("showdown.dio.revealed").
 * Owners get a preview toggle (matches Chronophage pattern).
 *
 * 🚫 NON-CANON: Red banner clearly labels this as spec-only. Do not
 * cross-wire into RPG canon, Farm, Kitchen, or any narrative arc until
 * owner promotes Dio to canon.
 */
function DioSecretBossPanel({ boss }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [expanded, setExpanded] = useState(false);
  const [revealed, setRevealed] = useState(() => {
    try {
      return localStorage.getItem("showdown.dio.revealed") === "1";
    } catch { return false; }
  });

  const setRevealedPersist = (v) => {
    setRevealed(v);
    try {
      if (v) localStorage.setItem("showdown.dio.revealed", "1");
      else localStorage.removeItem("showdown.dio.revealed");
    } catch { /* silent */ }
  };

  // ── Riddle-locked mystery card ─────────────────────────────
  if (!revealed) {
    return (
      <div
        className="mt-8 rounded-2xl border-2 border-dashed border-neutral-700/50 bg-gradient-to-br from-[#050505] via-[#000000] to-[#0a0a0a] p-8 text-center relative overflow-hidden"
        data-testid="showdown-dio-locked"
      >
        {/* Yin-yang faded background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none flex items-center justify-center text-[180px] leading-none select-none">
          ☯
        </div>
        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3 flex items-center justify-center gap-2">
            <ShieldAlert size={12} /> Hidden Secret Boss · Non-Canon · Riddle-Gated
          </div>

          {/* The prophecy — full 6 lines */}
          <div className="max-w-xl mx-auto my-5 rounded-xl border border-neutral-700/40 bg-black/60 p-5 font-serif italic text-neutral-300 text-sm leading-relaxed" data-testid="showdown-dio-prophecy-locked">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 not-italic mb-3 font-sans">
              🕯️ Whispered Prophecy
            </div>
            <div className="space-y-1.5 text-left">
              {boss.prophecy.map((line, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-red-500/70 not-italic mt-0.5">🩸</span>
                  <span>&ldquo;{line}&rdquo;</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 text-4xl mb-3 opacity-70">
            <span className="text-white">🌞</span>
            <span className="text-neutral-500 text-2xl">·</span>
            <span className="text-neutral-400">🌑</span>
          </div>

          <div className="text-3xl md:text-4xl font-bold text-neutral-500 tracking-widest" style={{ fontFamily: "Outfit, sans-serif" }}>
            ??? ??? ???
          </div>
          <div className="text-xs text-neutral-500 mt-3 max-w-md mx-auto italic leading-relaxed">
            A Two-Spirit shadow-walker awaits — but only for those who solve the riddle.
            <br />
            <span className="text-neutral-600">Reveals when the puzzle is aligned.</span>
          </div>

          {isOwner && (
            <button
              onClick={() => setRevealedPersist(true)}
              data-testid="showdown-dio-owner-reveal"
              className="mt-5 px-3 py-1.5 rounded border border-neutral-600/40 bg-neutral-500/10 text-[10px] uppercase tracking-widest text-neutral-300 hover:bg-neutral-500/20 transition"
              title="Owner-only preview toggle. Non-owner players see only the riddle."
            >
              👁 Owner Preview · Reveal Dio
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Revealed full dossier ──────────────────────────────────
  return (
    <div
      className="mt-8 rounded-2xl border-2 border-neutral-600/60 bg-gradient-to-br from-[#080808] via-[#0a0a0a] to-[#000000] p-6 shadow-[inset_0_0_60px_rgba(255,255,255,0.04)] relative overflow-hidden"
      data-testid="showdown-dio"
    >
      {/* Yin-yang atmospheric watermark */}
      <div className="absolute -top-8 -right-8 text-[220px] leading-none opacity-[0.04] pointer-events-none select-none">☯</div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4 relative z-10">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} className="text-neutral-300" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-300">
            Hidden Secret Boss · Riddle-Gated Encounter
          </span>
        </div>
        {isOwner && (
          <button
            onClick={() => setRevealedPersist(false)}
            data-testid="showdown-dio-owner-relock"
            className="px-2 py-1 rounded border border-neutral-700/40 text-[9px] uppercase tracking-widest text-neutral-400 hover:bg-neutral-500/10 transition"
            title="Owner-only: re-lock Dio so the riddle card renders again."
          >
            🔒 Re-lock
          </button>
        )}
      </div>

      {/* Character header */}
      <div className="flex items-start gap-4 relative z-10">
        <div className="text-7xl leading-none">{boss.icon}</div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400">{boss.tag}</div>
          <div
            className="text-3xl md:text-4xl font-bold leading-none tracking-tight"
            style={{
              fontFamily: "Outfit, sans-serif",
              backgroundImage: "linear-gradient(90deg, #ffffff 0%, #ffffff 50%, #171717 50%, #171717 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              WebkitTextStroke: "1px #525252",
            }}
          >
            {boss.name}
          </div>
          <div className="text-[11px] text-neutral-400/90 mt-0.5 italic">{boss.subtitle}</div>
          <div className="text-sm text-neutral-400 mt-1">{boss.archetype}</div>
        </div>
      </div>

      {/* NON-CANON banner */}
      <div
        className="mt-4 rounded-lg border border-red-700/50 bg-red-900/15 p-3 text-[11px] leading-relaxed relative z-10"
        data-testid="showdown-dio-noncanon"
      >
        <div className="flex items-center gap-2 mb-1">
          <Info size={12} className="text-red-300" />
          <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">
            🚫 {boss.status}
          </span>
        </div>
        <div className="text-neutral-300">
          Do not cross-wire Dio into the 21-hero RPG roster, Farm, Kitchen POV, or any narrative arc. Retained as spec-only reference until the owner promotes them to canon.
        </div>
      </div>

      {/* Official Portrait (Silent Nexus reveal) */}
      {boss.image && (
        <div className="mt-4 rounded-xl overflow-hidden border-2 border-neutral-700/60 bg-black/60 relative z-10" data-testid="showdown-dio-portrait">
          <img
            src={boss.image}
            alt="Dio the Dual-Shadow · Silent Nexus"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
          <div className="px-3 py-2 bg-gradient-to-r from-black via-black/80 to-black text-[10px] text-neutral-400 italic tracking-widest">
            🌗 Manifested in the Silent Nexus — Dark Sun · Red Moon · dual-color shurikens.
          </div>
        </div>
      )}

      {/* The Prophecy (revealed — full 6 lines) */}
      <div className="mt-4 rounded-lg border border-neutral-700/50 bg-black/40 p-3 text-[11px] leading-relaxed relative z-10" data-testid="showdown-dio-prophecy-revealed">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg leading-none">🕯️</span>
          <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
            The Full Prophecy · Ritual of Invitation
          </span>
        </div>
        <div className="space-y-1 font-serif italic text-neutral-200">
          {boss.prophecy.map((line, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-red-500/80 not-italic mt-0.5">🩸</span>
              <span>&ldquo;{line}&rdquo;</span>
            </div>
          ))}
        </div>
      </div>

      {/* Encounter Gates — mechanical enforcement of the prophecy */}
      <div className="mt-4 rounded-lg border border-red-800/50 bg-red-950/20 p-3 relative z-10" data-testid="showdown-dio-gates">
        <div className="text-[10px] uppercase tracking-widest text-red-300 flex items-center gap-2 mb-2 font-bold">
          <Skull size={12} /> Encounter Gates · Mechanical Enforcement
        </div>
        <div className="space-y-2">
          {boss.encounter_gates.map((g, i) => (
            <div key={g.name} className="rounded-md border border-red-800/40 bg-black/40 p-2.5">
              <div className="flex items-start justify-between gap-2 mb-0.5">
                <div className="text-sm font-bold text-red-200">{g.name}</div>
                <span className="text-[9px] uppercase tracking-widest text-red-400/70 font-serif italic whitespace-nowrap">
                  &ldquo;{g.line}&rdquo;
                </span>
              </div>
              <div className="text-[11px] text-neutral-300 leading-relaxed">{g.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Silent Nexus — Stage Spec */}
      <div className="mt-4 rounded-lg border-2 border-neutral-700/60 bg-gradient-to-br from-[#0c0508] via-[#000000] to-[#08050c] p-4 relative z-10 overflow-hidden" data-testid="showdown-dio-lair">
        {/* faint eclipse silhouette */}
        <div className="absolute -top-4 right-4 opacity-25 pointer-events-none select-none flex items-center gap-1">
          <span className="text-3xl">🌑</span>
          <span className="text-3xl">🌙</span>
        </div>
        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold flex items-center gap-2">
            <span className="text-base">🌗</span> Stage · The Lair
          </div>
          <div className="text-2xl font-bold text-white mt-1"
            style={{
              fontFamily: "Outfit, sans-serif",
              backgroundImage: "linear-gradient(90deg, #ffffff 0%, #d4d4d4 40%, #7c2d12 60%, #7f1d1d 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {boss.lair.name}
          </div>
          <div className="text-[11px] text-neutral-400 italic mt-0.5">{boss.lair.tagline}</div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-neutral-500">Palette</div>
          <div className="text-[11px] text-neutral-300 mb-3">{boss.lair.palette}</div>

          <div className="grid md:grid-cols-2 gap-2 mb-3">
            {boss.lair.elements.map((e) => (
              <div key={e.name} className="rounded border border-neutral-800 bg-black/40 p-2">
                <div className="text-[11px] font-bold text-neutral-200">{e.name}</div>
                <div className="text-[10px] text-neutral-400 leading-relaxed mt-0.5">{e.detail}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-2 mb-3">
            <div className="rounded border border-neutral-800 bg-black/40 p-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Lighting</div>
              <div className="text-[10px] text-neutral-300 leading-relaxed mt-0.5">{boss.lair.lighting}</div>
            </div>
            <div className="rounded border border-neutral-800 bg-black/40 p-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Particles</div>
              <div className="text-[10px] text-neutral-300 leading-relaxed mt-0.5">{boss.lair.particles}</div>
            </div>
          </div>

          <div className="rounded border border-purple-800/40 bg-purple-950/20 p-2 mb-2">
            <div className="text-[10px] uppercase tracking-widest text-purple-300 font-bold mb-1">
              Programmer / Technical Focus
            </div>
            <ul className="list-disc list-inside space-y-0.5 text-[10px] text-neutral-300 leading-relaxed">
              {boss.lair.tech.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>

          <div className="text-[10px] italic text-neutral-500 border-l-2 border-neutral-700 pl-2">
            🗺️ {boss.lair.map_visibility}
          </div>
        </div>
      </div>

      {/* Weapon + Aesthetic */}
      <div className="mt-4 grid md:grid-cols-2 gap-3 relative z-10">
        <div className="rounded-lg border border-neutral-800 bg-black/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400">Weapon</div>
          <div className="text-sm font-bold text-white mt-1">{boss.weapon.name}</div>
          <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">{boss.weapon.description}</div>
        </div>
        <div className="rounded-lg border border-neutral-800 bg-black/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400">Aesthetic</div>
          <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">{boss.aesthetic}</div>
        </div>
      </div>

      {/* Expand */}
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="showdown-dio-toggle"
        className="mt-4 w-full px-4 py-2 rounded-lg border border-neutral-600/40 bg-neutral-500/10 text-neutral-200 hover:bg-neutral-500/20 text-xs uppercase tracking-widest font-bold transition relative z-10"
      >
        {expanded ? "Hide Full Dossier ↑" : "View Full Boss Dossier ↓"}
      </button>

      {expanded && (
        <div className="mt-5 space-y-5 relative z-10" data-testid="showdown-dio-dossier">
          {/* 🎬 Obsidian Keep Siege cinematic */}
          <BossCinematic accent="slate" label="Dio Secret-Boss Cinematic · The Obsidian Keep Siege" />

          {/* Rule-breaker notes */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-300 flex items-center gap-2 mb-2">
              <Skull size={12} /> Rule-Breaker Philosophy
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {boss.rulebreaker_notes.map((n, i) => (
                <div key={i} className="rounded-lg border border-red-700/30 bg-red-900/5 p-2.5">
                  <div className="text-[11px] text-neutral-300 leading-relaxed">{n}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Boss moves */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-300 flex items-center gap-2 mb-2">
              <Swords size={12} /> Special Moves & Skills
            </div>
            <div className="space-y-2">
              {boss.moves.map((m, i) => (
                <div key={m.name} className="rounded-lg border border-neutral-700/50 bg-black/40 p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-sm font-bold text-neutral-100">
                      {i + 1}. {m.name}
                    </div>
                    <span className="text-[9px] uppercase tracking-widest text-neutral-500 border border-neutral-700 rounded px-2 py-0.5 whitespace-nowrap">
                      {m.role}
                    </span>
                  </div>
                  <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">{m.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Dialogue */}
          {boss.dialogue && boss.dialogue.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-300 flex items-center gap-2 mb-2">
                <span className="text-base leading-none">🎭</span> Voice Lines · Minimal, Cold, Ethereal
              </div>
              <div className="space-y-1.5">
                {boss.dialogue.map((d, i) => (
                  <div key={i} className="rounded-lg border border-neutral-800 bg-black/40 p-2.5">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold mb-0.5">
                      ▶ {d.trigger}
                    </div>
                    <div className="text-[12px] text-neutral-200 font-serif italic">
                      &ldquo;{d.line}&rdquo;
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Design lock */}
          <div className="rounded-lg border border-neutral-800 bg-black/40 p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">🎬 Design Lock · Atmospheric Inversion</div>
            <div className="text-[11px] text-neutral-300 italic leading-relaxed">{boss.design_lock}</div>
          </div>

          <div className="text-[10px] text-neutral-600 italic pt-2 border-t border-zinc-800">
            Spec-only preview · NON-CANON · Two-Spirit representation to be integrated respectfully upon canon promotion.
          </div>
        </div>
      )}
    </div>
  );
}



/* ═══════════════════════ 📖 GAIL BLOOM PANEL ═══════════════════════ */
function GailBloomVillainPanel({ villain }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      className="mt-8 rounded-2xl border-2 border-slate-700/60 bg-gradient-to-br from-slate-950/60 via-black/70 to-slate-900/40 p-5 relative overflow-hidden"
      data-testid="showdown-gail-bloom-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-3">
          <span className="text-5xl leading-none">{villain.icon}</span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-300 font-bold">
              Unplayable Villain · The Architect
            </div>
            <h3 className="text-3xl md:text-4xl font-black text-white leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              {villain.name}
            </h3>
            <div className="text-[11px] text-slate-400 italic mt-0.5">{villain.tag}</div>
          </div>
        </div>
        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-500/60 text-slate-200 bg-slate-500/10 font-bold">
          🔒 Not On Any Menu
        </span>
      </div>

      <p className="text-[12px] text-neutral-300 italic leading-relaxed mb-3">{villain.narrative_position}</p>

      <div className="grid md:grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg border border-slate-800 bg-black/40 p-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-300 mb-1 font-bold">Identity</div>
          <div className="text-[11px] text-neutral-300 leading-relaxed">{villain.identity}</div>
        </div>
        <div className="rounded-lg border border-slate-800 bg-black/40 p-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-300 mb-1 font-bold">No Gauge</div>
          <div className="text-[11px] text-neutral-300 leading-relaxed">{villain.no_gauge}</div>
        </div>
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="showdown-gail-bloom-toggle"
        className="w-full px-4 py-2 rounded-lg border border-slate-600/40 bg-slate-500/10 text-slate-200 hover:bg-slate-500/20 text-xs uppercase tracking-widest font-bold transition"
      >
        {open ? "Hide Audit Kit ↑" : "View The Architect's Kit + Over Burst Ladder ↓"}
      </button>

      {open && (
        <div className="mt-5 space-y-4" data-testid="showdown-gail-bloom-deepdive">
          <div className="rounded-lg border border-slate-700/50 bg-slate-950/40 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold mb-1 flex items-center gap-2">
              <ShieldAlert size={12} /> Passive · {villain.passive.name}
            </div>
            <div className="text-[11px] text-neutral-300 leading-relaxed mb-2">{villain.passive.mechanic}</div>
            <div className="rounded border border-slate-800 bg-black/40 p-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-300 font-bold mb-0.5">Effect</div>
              <div className="text-[10px] text-neutral-300 leading-relaxed">{villain.passive.effect}</div>
            </div>
          </div>

          {villain.actives.map((act) => (
            <div key={act.name} className="rounded-lg border border-slate-700/50 bg-slate-950/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold mb-1 flex items-center gap-2">
                <Zap size={12} /> Active · {act.name}
              </div>
              <div className="text-[11px] text-neutral-300 leading-relaxed mb-2">{act.mechanic}</div>
              {act.tiers && (
                <div className="grid md:grid-cols-3 gap-2 mb-2">
                  {act.tiers.map((t) => (
                    <div key={t.name} className="rounded border border-slate-800 bg-black/40 p-2">
                      <div className="text-[10px] font-bold text-slate-200">{t.name}</div>
                      <div className="text-[10px] text-neutral-400 leading-relaxed mt-0.5">{t.detail}</div>
                    </div>
                  ))}
                </div>
              )}
              {act.effect && (
                <div className="rounded border border-slate-800 bg-black/40 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-300 font-bold mb-0.5">Effect</div>
                  <div className="text-[10px] text-neutral-300 leading-relaxed">{act.effect}</div>
                </div>
              )}
              {act.ultimate_erosion && (
                <div className="mt-2 text-[10px] text-amber-300 italic border-l-2 border-amber-500/40 pl-2">⚡ {act.ultimate_erosion}</div>
              )}
            </div>
          ))}

          <div className="rounded-lg border border-red-700/40 bg-red-950/10 p-3">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1 flex items-center gap-2">
              💥 {villain.over_burst.tagline}
            </div>
            <div className="text-[11px] text-neutral-300 leading-relaxed italic mb-3">{villain.over_burst.description}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="border-b border-red-900/60">
                    <th className="text-red-300 uppercase tracking-widest text-[9px] py-1 pr-2">Level</th>
                    <th className="text-red-300 uppercase tracking-widest text-[9px] py-1 pr-2">Mana Cost</th>
                    <th className="text-red-300 uppercase tracking-widest text-[9px] py-1 pr-2">Restrictions</th>
                    <th className="text-red-300 uppercase tracking-widest text-[9px] py-1">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {villain.over_burst.levels.map((lvl) => (
                    <tr key={lvl.level} className="border-b border-neutral-800/60">
                      <td className="py-1 pr-2 font-bold text-white">{lvl.level}</td>
                      <td className="py-1 pr-2 font-mono text-amber-300">{lvl.cost}</td>
                      <td className="py-1 pr-2 text-neutral-300">{lvl.restrictions}</td>
                      <td className="py-1 font-mono text-cyan-300">{lvl.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-[10px] text-neutral-400 italic border-l-2 border-red-800/60 pl-2">{villain.over_burst.fail_state}</div>
          </div>

          <div className="grid md:grid-cols-2 gap-2">
            <div className="rounded-lg border border-slate-800 bg-black/40 p-3">
              <div className="text-[9px] uppercase tracking-widest text-slate-300 font-bold mb-1">🧠 AI Philosophy</div>
              <div className="text-[10px] text-neutral-300 leading-relaxed">{villain.ai_philosophy}</div>
            </div>
            <div className="rounded-lg border border-slate-800 bg-black/40 p-3">
              <div className="text-[9px] uppercase tracking-widest text-slate-300 font-bold mb-1">🎯 Design Goal</div>
              <div className="text-[10px] text-neutral-300 leading-relaxed">{villain.design_goal}</div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-800 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-slate-300 font-bold mb-1">📜 Lore & Motivation</div>
            <div className="text-[11px] text-neutral-300 leading-relaxed italic">{villain.lore}</div>
          </div>
        </div>
      )}
    </section>
  );
}


/* ═══════════════════════ 🎬 SHARED BOSS CINEMATIC ═══════════════════════
 * Reusable cinematic block used by ALL special-boss panels (Chronophage,
 * Dio, Kia Masterson). Manual-play by default (controls visible, muted so
 * mobile autoplay isn't blocked). Accent-colored border so each boss keeps
 * their own visual identity. To auto-run in the background, pass
 * `autoplay` and it will run silently on load.
 * ═══════════════════════════════════════════════════════════════════════ */
function BossCinematic({ cinematic = SPECIAL_BOSS_CINEMATIC, accent = "purple", label = null, autoplay = false }) {
  if (!cinematic?.src) return null;
  const borderMap = {
    purple: "border-purple-500/40 bg-purple-950/20 text-purple-200",
    slate: "border-slate-500/40 bg-slate-950/20 text-slate-200",
    lime: "border-lime-500/40 bg-lime-950/20 text-lime-200",
  };
  const border = borderMap[accent] || borderMap.purple;
  return (
    <div className={`rounded-lg border-2 ${border} p-3`} data-testid="boss-cinematic">
      <div className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 mb-2">
        🎬 {label || `Cinematic · ${cinematic.title}`}
      </div>
      <video
        className="w-full rounded-md border border-black/60 bg-black"
        src={cinematic.src}
        controls
        preload="metadata"
        muted={autoplay}
        autoPlay={autoplay}
        playsInline
        loop={autoplay}
        data-testid="boss-cinematic-video"
      >
        Your browser does not support the video tag.
      </video>
      {cinematic.tagline && (
        <div className="text-[10px] italic text-neutral-400 mt-2 leading-snug">{cinematic.tagline}</div>
      )}
    </div>
  );
}


/* ═══════════════════════ 🧃 KIA MASTERSON BOSS PANEL ═══════════════════════
 * Non-canon boss with her own agenda. Renders her portrait (when image is
 * provided), the 10-Pip Juice Meter mechanic, the "Special Healthy Clutch"
 * frenzy state, her special moves, and the psychological loop that defines
 * every match with her.
 * Canon file: /app/memory/KIA_MASTERSON_BOSS_DOSSIER_FEB2026.md
 * ═══════════════════════════════════════════════════════════════════════ */
function KiaMastersonBossPanel({ boss }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      className="mt-8 rounded-2xl border-2 border-lime-600/50 bg-gradient-to-br from-lime-950/30 via-black/70 to-amber-950/30 p-5 relative overflow-hidden"
      data-testid="showdown-kia-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-3">
          <div className="text-5xl">{boss.icon}</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-lime-300 font-bold">
              {boss.status}
            </div>
            <div className="text-2xl font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
              {boss.name.toUpperCase()}
            </div>
            <div className="text-[11px] text-lime-200 italic">
              {boss.subtitle} · {boss.archetype}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="showdown-kia-toggle"
          className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-lime-600/60 bg-black/60 text-lime-200 hover:text-white hover:border-lime-400 transition"
        >
          {open ? "Hide Kit ▲" : "Show Kit ▼"}
        </button>
      </div>

      {/* Portrait */}
      {boss.image ? (
        <div className="rounded-xl overflow-hidden border border-lime-500/40 bg-black/40 mb-4" data-testid="kia-image">
          <img src={boss.image} alt="Kia Masterson · The Kick-Start Juice Brawler" className="w-full h-auto object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-lime-500/40 bg-lime-950/10 p-4 mb-4 text-center" data-testid="kia-image-placeholder">
          <div className="text-3xl opacity-50">🎨</div>
          <div className="text-[10px] uppercase tracking-widest text-lime-400 mt-1">Official Art Pending</div>
          <div className="text-[11px] italic text-neutral-500 mt-1">
            Neon &quot;JUICE BOX BRAWL&quot; alley · dual-tone 10-pip gauge · &quot;KICK-START JUICE&quot; canteen · easter egg: &quot;THE GREENEST GLITCH&quot; wall tag.
          </div>
        </div>
      )}

      {/* 🎬 Obsidian Keep Siege cinematic */}
      <div className="mb-4">
        <BossCinematic accent="lime" label="Kia Boss Cinematic · The Obsidian Keep Siege" />
      </div>

      {/* Personality */}
      <div className="rounded-lg border border-lime-800/40 bg-black/40 p-3 mb-3">
        <div className="text-[9px] uppercase tracking-widest text-lime-300 font-bold mb-1">🎭 Personality & Style</div>
        <div className="text-[11px] text-neutral-300 leading-relaxed italic">{boss.personality}</div>
      </div>

      {open && (
        <div className="space-y-4">
          {/* Visual Signature */}
          {boss.visual_signature && (
            <div className="rounded-lg border border-lime-800/40 bg-black/40 p-3" data-testid="kia-visual">
              <div className="text-[9px] uppercase tracking-widest text-lime-300 font-bold mb-2">🎨 Visual Signature</div>
              <div className="grid md:grid-cols-2 gap-2">
                {Object.entries(boss.visual_signature).map(([k, v]) => (
                  <div key={k} className="rounded border border-neutral-800 bg-black/50 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-lime-400 font-bold mb-0.5">{k.replace(/_/g, " ")}</div>
                    <div className="text-[10px] text-neutral-300 leading-relaxed">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unique Mechanic — 10-Pip Juice Meter */}
          <div className="rounded-lg border-2 border-lime-500/40 bg-gradient-to-br from-lime-950/40 to-black p-4" data-testid="kia-mechanic">
            <div className="text-[10px] uppercase tracking-widest text-lime-300 font-bold flex items-center gap-2">
              🧃 Unique Mechanic · {boss.unique_mechanic.name}
            </div>
            <div className="text-xs text-neutral-200 mt-2 leading-relaxed">
              {boss.unique_mechanic.description}
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-2">
              {boss.unique_mechanic.pip_types.map((pip, i) => {
                const isVeg = pip.name.toLowerCase().includes("vegetable");
                return (
                  <div key={i} className={`rounded border p-2 ${isVeg ? "border-lime-600/50 bg-lime-950/30" : "border-amber-600/50 bg-amber-950/30"}`}>
                    <div className={`text-[9px] uppercase tracking-widest font-bold mb-0.5 ${isVeg ? "text-lime-300" : "text-amber-300"}`}>
                      {isVeg ? "🥬" : "🍊"} {pip.name} — {pip.role}
                    </div>
                    <div className="text-[10px] text-neutral-300 leading-relaxed">{pip.detail}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 rounded border border-amber-500/40 bg-amber-950/20 p-2">
              <div className="text-[9px] uppercase tracking-widest text-amber-300 font-bold mb-0.5">🔄 Pip Regeneration Rule</div>
              <div className="text-[10px] text-neutral-300 leading-relaxed italic">{boss.unique_mechanic.regen_rule}</div>
            </div>
          </div>

          {/* Frenzy State */}
          <div className="rounded-lg border-2 border-red-500/40 bg-gradient-to-br from-red-950/40 to-black p-4" data-testid="kia-frenzy">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold">
              🔥 Overcharge · {boss.frenzy_state.name}
            </div>
            <div className="text-[11px] text-neutral-300 italic mt-1">Trigger: {boss.frenzy_state.trigger}</div>
            <ul className="mt-2 text-[11px] text-neutral-300 leading-relaxed list-disc list-inside space-y-1">
              {boss.frenzy_state.rules.map((rule, i) => (
                <li key={i}>{rule}</li>
              ))}
            </ul>
            <div className="mt-3 rounded border border-red-600/50 bg-red-950/40 p-2">
              <div className="text-[9px] uppercase tracking-widest text-red-300 font-bold mb-0.5">⚠️ Burnout Penalty · The Punish Window</div>
              <div className="text-[10px] text-neutral-200 leading-relaxed">{boss.frenzy_state.burnout_penalty}</div>
            </div>
          </div>

          {/* Specials — pip cost thresholds */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-lime-300 font-bold mb-2">💥 Special Moves · Pip Cost Thresholds</div>
            <div className="space-y-2">
              {boss.specials.map((sp, i) => (
                <div key={i} className="rounded border border-lime-800/40 bg-black/40 p-3" data-testid={`kia-special-${i}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="text-sm font-black text-lime-200" style={{ fontFamily: "Anton, sans-serif" }}>
                      {sp.name.toUpperCase()}
                    </div>
                    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-amber-500/50 bg-amber-950/30 text-amber-200 font-bold whitespace-nowrap">
                      💰 {sp.cost}
                    </span>
                  </div>
                  <div className="text-[11px] text-neutral-300 leading-relaxed mt-1">{sp.effect}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Combat Behavior · Psychological Loop */}
          <div className="rounded-lg border border-lime-800/40 bg-black/40 p-3" data-testid="kia-behavior">
            <div className="text-[9px] uppercase tracking-widest text-lime-300 font-bold mb-1">🧠 Combat Behavior · The Psychological Loop</div>
            <div className="text-[11px] text-neutral-300 leading-relaxed italic mb-2">{boss.combat_behavior.endless_pressure}</div>
            <div className="grid md:grid-cols-2 gap-2">
              {boss.combat_behavior.psychological_loop.map((row, i) => (
                <div key={i} className="rounded border border-neutral-800 bg-black/50 p-2">
                  <div className="text-[9px] uppercase tracking-widest text-lime-400 font-bold">{row.player_action}</div>
                  <div className="text-[10px] text-neutral-300 leading-relaxed mt-1">→ {row.kia_response}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Design Philosophy */}
          <div className="rounded-lg border border-amber-500/40 bg-gradient-to-br from-amber-950/40 to-black p-3" data-testid="kia-philosophy">
            <div className="text-[9px] uppercase tracking-widest text-amber-300 font-bold mb-1">🧩 Design Philosophy</div>
            <div className="text-[11px] text-amber-100 leading-relaxed italic">{boss.design_philosophy}</div>
          </div>
        </div>
      )}
    </section>
  );
}


/* ═══════════════════════ 🌌 ASTRAEA PANEL ═══════════════════════ */
function AstraeaHiddenPanel({ entity }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      className="mt-8 rounded-2xl border-2 border-cyan-700/50 bg-gradient-to-br from-cyan-950/30 via-black/70 to-violet-950/30 p-5 relative overflow-hidden"
      data-testid="showdown-astraea-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-3">
          <span className="text-5xl leading-none">{entity.icon}</span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-bold">
              Hidden Non-Playable · Stage Force
            </div>
            <h3 className="text-3xl md:text-4xl font-black text-white leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              {entity.name}
            </h3>
            <div className="text-[11px] text-cyan-400 italic mt-0.5">{entity.tag}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-violet-500/60 text-violet-200 bg-violet-500/10 font-bold">{entity.faction}</span>
          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-cyan-500/60 text-cyan-200 bg-cyan-500/10 font-bold">{entity.status}</span>
        </div>
      </div>

      <p className="text-[12px] text-neutral-300 italic leading-relaxed mb-3">{entity.design_lock}</p>

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="showdown-astraea-toggle"
        className="w-full px-4 py-2 rounded-lg border border-cyan-600/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 text-xs uppercase tracking-widest font-bold transition"
      >
        {open ? "Hide Constellation Grid ↑" : "View Secret Stage + 13-Constellation Anomalies ↓"}
      </button>

      {open && (
        <div className="mt-5 space-y-4" data-testid="showdown-astraea-deepdive">
          <div className="rounded-lg border border-violet-700/40 bg-violet-950/10 p-3">
            <div className="text-[10px] uppercase tracking-widest text-violet-300 font-bold mb-1 flex items-center gap-2">
              🎬 Secret Stage · {entity.secret_stage.name}
            </div>
            <div className="grid md:grid-cols-3 gap-2 mt-2">
              <div className="rounded border border-neutral-800 bg-black/40 p-2">
                <div className="text-[9px] uppercase tracking-widest text-violet-300 font-bold mb-0.5">Trigger</div>
                <div className="text-[10px] text-neutral-300 leading-relaxed">{entity.secret_stage.trigger}</div>
              </div>
              <div className="rounded border border-neutral-800 bg-black/40 p-2">
                <div className="text-[9px] uppercase tracking-widest text-violet-300 font-bold mb-0.5">Manifestation</div>
                <div className="text-[10px] text-neutral-300 leading-relaxed">{entity.secret_stage.manifestation}</div>
              </div>
              <div className="rounded border border-neutral-800 bg-black/40 p-2">
                <div className="text-[9px] uppercase tracking-widest text-violet-300 font-bold mb-0.5">Constellation Grid</div>
                <div className="text-[10px] text-neutral-300 leading-relaxed">{entity.secret_stage.constellation_grid}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold mb-2 flex items-center gap-2">
              ⚡ Dynamic Stage Anomalies · The 13 Constellations
            </div>
            <div className="grid md:grid-cols-3 gap-2">
              {entity.stage_anomalies.map((a) => (
                <div key={a.name} className="rounded border border-neutral-800 bg-black/40 p-3">
                  <div className="text-[11px] font-bold text-cyan-200 mb-1">{a.name}</div>
                  <div className="text-[10px] text-neutral-300 leading-relaxed">{a.effect}</div>
                </div>
              ))}
            </div>
            <div className="text-[9px] text-neutral-500 italic mt-2">More anomalies to be locked as story cinematics require.</div>
          </div>

          <div className="rounded-lg border border-cyan-800/40 bg-cyan-950/10 p-3">
            <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-bold mb-1">🎥 Secret Cinematic Intervention</div>
            <div className="text-[10px] text-neutral-300 leading-relaxed italic">{entity.cinematic_intervention}</div>
          </div>
        </div>
      )}
    </section>
  );
}
